/*
 * @Copyright 2026. Institute for Future Intelligence, Inc.
 *
 * 简体中文。键结构与 en.ts 完全一致。
 *
 * 术语沿用中国大陆半导体行业的通行译法，而不是逐字直译，因此它与 zh_tw.ts 的差别
 * 不只是字形转换：刻蚀 / 蝕刻、光刻 / 微影、等离子体 / 電漿、工艺 / 製程、设备 /
 * 機台、仿真 / 模擬。设备资产编号（ETCH-01 等）和单位符号一律不翻译。
 */

export const i18n_zh_cn = {
  translation: {
    mission: {
      whereThePhotonsWent: {
        Title: '光子去哪儿了',
        Briefing: 'EUV-22 慢了四分之一，夜班已经把一次三十小时的收集镜更换排到了明天。EUV 光刻机丢光的地方有三处，只有一处是收集镜。在有人打开光源腔之前，先沿着光路走一遍。',
        Debrief: '光根本没被产生出来。主激光脉冲打到液滴上时已经晚了，所以等离子体只给出约 60% 的合格脉冲能量，之后每一处的功率都下降了同样的比例。收集镜在等离子体之后：它不可能让等离子体处测到的脉冲能量变低，而正是这一个读数省下了三十个小时，和一块本来就在好好工作的收集镜。',
        objective: {
          timing: '做一次液滴-脉冲时序扫描',
          call: '做出正确的判断',
        },
      },
      theRulerIsWide: {
        Title: '尺子变宽了',
        Briefing: '从周末起，SEM-23 量过的每条线都宽了约一纳米，光刻那边已经调高过一次剂量来把 CD 拉回来。在他们再调一次之前：先弄清楚动的是线，还是仪器。',
        Debrief: '动的是仪器。冷场发射针尖好几天没做闪烁处理，能量展宽几乎成了原来的三倍，而在 500 eV 下这就占了束斑的大头——色差模糊与 ΔE 比 E 成正比。束斑变宽，每条边缘都会被读得偏外一点。光刻机被调离了目标，去纠正一个本来正确的图形，这就是量测故障的代价：不是报废一片晶圆，而是在别处做了一个错误的决定。',
        objective: {
          spread: '测量电子枪的能量展宽',
          call: '做出正确的判断',
        },
      },
      coldPlateWarmQubit: {
        Title: '冷盘是冷的，量子比特是热的',
        Briefing: 'QDT-24 按时到了 10 mK，上面每个温度计都正常。里面这颗芯片上个月在另一台制冷机里测得很好，在这里却读得一塌糊涂。会有人提议回温换芯片。先弄清楚量子比特能“听到”什么。',
        Debrief: '驱动线上少了一个 20 dB 的衰减器，于是这条线带下来的室温噪声是应有值的一百倍：量子比特模式里有八分之一个光子，10 mK 的冷盘上坐着一个 109 mK 的量子比特。芯片没问题，制冷机也没问题。量子比特的温度不是它拧在上面的那块金属的温度，而是它线路的温度，而唯一能读出这个温度的温度计，就是量子比特自己。',
        objective: {
          line: '测量驱动线的插入损耗',
          call: '做出正确的判断',
        },
      },
      fiftyDegreesHot: {
        Title: '没人设定的五十度',
        Briefing: 'THRM-04 出来的方块电阻偏低，结也偏深。已经有人要求注入组去查他们的剂量，他们很不高兴。在 IMP-10 为一次剂量审核搭上一个班之前：剂量偏低不会造成这种现象。',
        Debrief: 'RTP 的高温计读数偏低，于是灯被驱动到比屏幕显示的设定值高五十度。扩散系数服从阿伦尼乌斯规律：五十度几乎让 Dt 翻倍，掺杂扩散开来，结变深，方块电阻下降。剂量不足会让方块电阻朝相反方向变化，而结的位置不变。在任何人动手测量之前，误差的方向就已经指明了是哪台机器。',
        objective: {
          pyrometer: '用校准片核对高温计',
          call: '做出正确的判断',
        },
      },
      oxygenClimb: {
        Title: '氧含量的攀升',
        Briefing: '器件工程部门以间隙氧超标为由，退回了最近三根单晶锭的晶圆，而拉晶炉一个月未改动配方。拉速相同、转速相同、氩气设定值也相同。找出到底哪里不一样。',
        Debrief: '石英坩埚已接近寿命终点。坩埚只要处于高温就会持续溶入熔体，而一只用旧的坩埚在上个月还管用的同一配方下，会更快地释放氧。氩气流量正常，这正好排除了吹扫这条路径——氧含量升高的另一种可能——而调高氩气只会把问题再掩盖一根锭。',
        objective: {
          oxygen: '测量最近一根单晶锭的间隙氧',
          call: '作出判断，并且判断正确',
        },
      },
      flatAndSlow: {
        Title: '平坦，但是慢',
        Briefing: '抛光工序每个批次都超出节拍时间，操作员一直靠多抛几分钟来凑够去除量。平坦度仍然合格。没有人改动过抛光液或下压力。',
        Debrief: '抛光垫已经钝化——表面被填塞、不再切削，于是去除率下降，而所有人采用的补救办法（延长时间）是在用设备寿命换数字。磨料粒径在规格内，这排除了磨料本身；钝化的抛光垫和失效的抛光液都会让去除率下降，但只有其中一个能靠修整解决。',
        objective: {
          rate: '用监控片测量去除率',
          call: '作出判断，并且判断正确',
        },
      },
      sharpEnoughToPass: {
        Title: '勉强合格的清晰度',
        Briefing: 'LITH-03 的关键尺寸在漂移，最直觉的答案是剂量。在动光源之前请注意：失焦的扫描机所印出的关键尺寸误差，与剂量漂移看起来一模一样，而两者需要的修正方向恰好相反。',
        Debrief: '问题出在工件台失焦，而不是光源。离焦不仅损失关键尺寸，还会同时损失对比度与 NILS；而剂量误差只改变线宽、不动边缘陡度——这正是空间像参数能区分的地方。用剂量去补偿，只会保住数字，而下面的工艺窗口仍在继续收窄。',
        objective: {
          focus: '执行聚焦探测',
          call: '作出判断，并且判断正确',
        },
      },
      theSlowLeak: {
        Title: '缓慢的泄漏',
        Briefing: 'ETCH-01 抽不到本底真空，上一班把它记为泵老化。换泵要停机四小时，而全厂只有一台备品，所以在动用之前请先确认。',
        Debrief: '是真实泄漏，不是泵老化。两者都会抬高本底压力，在真空计上完全一样；区别在于气体从哪里来——泄漏以恒定速率引入大气，而泵速本身正常。备用泵还在货架上，这正是先做检查的价值所在。',
        objective: {
          seal: '执行密封检查',
          call: '作出判断，并且判断正确',
        },
      },
      theCupThatLies: {
        Title: '说谎的法拉第杯',
        Briefing: '注入批次的方块电阻偏低，而注入机声称每一次剂量都命中目标。这两句话必有一句是错的，而其中一方是在对自己下结论的设备。',
        Debrief: '法拉第杯发生了漂移，设备统计到的离子少于实际注入量，于是为了凑够自己的数字而过量注入。一台自己测量自己的剂量监测器无法发现这一点——只有晶圆能，这正是方块电阻成为决定性读数的原因。设备上其余的台面读数全部正常，而它们全都是无辜的。',
        objective: {
          rho: '注入一片监控片并测量方块电阻',
          call: '作出判断，并且判断正确',
        },
      },
      thePitchThatWalks: {
        Title: '会走位的节距',
        Briefing: 'PAT-21 的平均关键尺寸合格，却在电性测试中失败，且失败沿着线条逐条交替出现。这些线条来自同一次曝光，因此套刻不可能是答案。',
        Debrief: '这是节距走位。侧墙薄膜偏薄，而在自对准四重成像单元中，侧墙厚度就是半节距，因此由一根芯轴衍生出的四条线不再等距。平均值毫发无损，这正是关键尺寸能合格的原因；再怎么调扫描机也无济于事：这是一个披着光刻投诉外衣的薄膜沉积问题。',
        objective: {
          walk: '逐条测量线条节距',
          call: '作出判断，并且判断正确',
        },
      },
      dishingOut: {
        Title: '碟形凹陷',
        Briefing: 'CMP-11 把宽的铜结构抛过头了，而抛光垫周二才刚换过。最明显的嫌疑人有不在场证明。',
        Debrief: '抛光液已经老化，其中的缓蚀剂已经耗尽。缓蚀剂的作用是阻止化学成分侵蚀那些抛光垫没有压到的铜面，缺了它，宽结构就会凹陷，而窄结构看起来仍然正常——这种轮廓特征无法靠调整下压力来纠正。用新垫配旧液，只会更快地抛出同样的缺陷。',
        objective: {
          inhibitor: '化验抛光液中的缓蚀剂',
          call: '作出判断，并且判断正确',
        },
      },
      voidsInTheVia: {
        Title: '通孔里的空洞',
        Briefing: 'ECD-12 电镀厚度达标，但通孔回来时中间带着一条缝。电流密度、镀液温度和接触电阻的读数全部正常。',
        Debrief: '有机添加剂已经耗尽。自底向上填充不是靠电流参数实现的，而是靠加速剂与抑制剂决定铜往哪里长；失去它们的镀液只会共形沉积，在填满之前就把通孔夹断。厚度自始至终都是对的，这正是只有剖面分析才能揭示问题的原因。',
        objective: {
          xsection: '对通孔做剖面分析',
          call: '作出判断，并且判断正确',
        },
      },
      theToolThatCriesWolf: {
        Title: '喊狼来了的机台',
        Briefing: 'INSP-13 报出的缺陷太多，工程部门已经开始无视它的缺陷图。要么产线这周急剧恶化，要么恶化的是这台检测设备。',
        Debrief: '收集光路已经雾化。浑浊镜片散射出的杂光抬高了探测器所面对的背景，于是设备报出晶圆上并不存在的缺陷；而如此高的虚报率比一台失明的设备危害更大，因为人们会连真实的缺陷图也不再去看。来料粗糙度正常，这排除了晶圆本身。',
        objective: {
          nuisance: '用已知晶圆验证虚报率',
          call: '作出判断，并且判断正确',
        },
      },
      goodDieCalledBad: {
        Title: '好芯片被判成坏芯片',
        Briefing: 'PRB-14 的良率下降了四个百分点，而损失全部集中在晶圆边缘。探针上周才更换，寿命还绰绰有余。',
        Debrief: '探针卡失去了平面度。与卡盘不平行的探针卡，一侧压得比另一侧重，于是较远的那一边始终无法可靠接触，好的芯片被判为开路。探针磨损量正常——这正是所有人最先想到的相似故障——换探针只会白白搭上一次换卡，却什么都修不好。',
        objective: {
          contact: '测量全片范围内的接触电阻',
          call: '作出判断，并且判断正确',
        },
      },
      wideKerf: {
        Title: '变宽的切割道',
        Briefing: '封装端因边缘崩裂而不断损失芯片，而切割工序报告每个批次都在规格内。划片街宽没有改动，胶带也是当前批号。',
        Debrief: '刀片已经超过额定切割次数。磨损的刀片切出更宽的切口、在边缘造成更多崩裂，而能撑到压模工序的，是那些原本就没有裂纹的芯片。胶带剥离力正常，这排除了切割过程中芯片位移——崩裂上升的另一条路径，也是会让你去找错供应商的那一条。',
        objective: {
          kerf: '测量切口宽度',
          call: '作出判断，并且判断正确',
        },
      },
      theQuietDispenser: {
        Title: '沉默的点胶机',
        Briefing: 'BOND-16 出来的封装体在温度循环中失效，而所有引线的拉力测试都合格。失效发生在芯片下方，而不是在线弧上。',
        Debrief: '点胶量不足。粘接胶太少会在芯片底下留下空洞，而空洞既是热量出不去的地方，也是应力无处释放的地方——于是产品通过了所有键合测试，却在第一次温度循环时失效。拉力与球剪切力都正常，这正是宣告引线键合机无罪的依据。',
        objective: {
          void: '对芯片贴装空洞成像',
          call: '作出判断，并且判断正确',
        },
      },
      popcorn: {
        Title: '爆米花效应',
        Briefing: 'PKG-17 的产品在客户端回流焊时开裂，而不是在我们这里。传递压力正常，模塑料也在有效期内。',
        Debrief: '产品在存放期间吸了潮。封装在模塑料中的水分在回流温度下汽化，从内部把封装体撑裂——这正是它在客户端失效、却能通过本厂所有测试的原因。模塑空洞正常，同时排除了压机和模塑料，而这两项维修都碰不到真正出问题的干燥包装流程。',
        objective: {
          popcorn: '执行湿气敏感度回流试验',
          call: '作出判断，并且判断正确',
        },
      },
      theEarlyFailures: {
        Title: '早期失效',
        Briefing: 'FT-18 在老化后剔除的产品比老化前还多，而测试机今早刚通过量具重复性研究。分选机温度也在设定值上。',
        Debrief: '问题出在这批产品本身带有早期失效——那是来自上游的缺陷分布，而不是测试问题。老化本来就是用来筛出它们的；剔除率上升意味着这批货进厂时就带着缺陷，答案在上游，不在测试机。量具标准差与分选机温度都正常，正是这一点把「我们的测试坏了」变成「我们的晶圆坏了」，而这两句话要找的是不同的人。',
        objective: {
          fallout: '分析老化剔除数据',
          call: '作出判断，并且判断正确',
        },
      },
      theValveThatWont: {
        Title: '关不上的阀',
        Briefing: 'GAS-19 的例行安全验证。气瓶间运行正常，也没有任何报警——而这恰恰是值得测试防护装置的时刻，因为这是唯一能测的时候。',
        Debrief: '过流阀已经卡死在开启位置。气体、洗涤塔和排风都没有任何问题，而且在管路断裂之前也不会有——可一旦断裂，唯一应当关闭的装置却关不上。一个从不做动作试验的防护装置，只是一个你在假设的装置，而释放模型正是把这个假设变成一个数字的东西。',
        objective: {
          release: '结合阀门试验运行释放模型',
          call: '作出判断，并且判断正确',
        },
      },
      leakOrOutgas: {
        Title: '泄漏还是放气',
        Briefing: 'FAC-06 保不住本底真空，记录显示腔体上周五曾对大气开放。腔壁上的水和真实泄漏都会造成这个现象。其中一个可以靠烘烤去掉，另一个不行，而单看真空计读数无法区分。',
        Debrief: '是真实泄漏。区分二者的是压升的形状：通过密封件进入的大气呈线性上升且永不停止，而从腔壁释出的水汽先上升、随后随着表面耗尽而趋于平缓。本底压力相同，曲线却是两条，而烘烤会白费一个班次、什么也改变不了。',
        objective: {
          rise: '记录压升曲线，而不只是那个数字',
          call: '作出判断，并且判断正确',
        },
      },
      innocentInstrument: {
        Title: '无辜的仪器',
        Briefing: 'MET-05 把氧化层读高了 4%，半个厂都想让它重新校准。在把一台好仪器停机之前：对错误假设的可重复测量，看起来和坏掉的仪器一模一样，只有一种读数能把它们分开。',
        Debrief: '反射仪是无辜的——变的是薄膜。沉积机漂了，而测量配方还在假设旧材料，于是对错误模型的完美拟合可重复地报出了更厚的膜。椭偏仪不假设折射率而是解出它，正是它把\'仪器坏了\'和\'世界变了\'分开——而一次重新校准只会把真正的漂移埋在一个被修正过的谎言下面。',
        objective: {
          ellipsometer: '在椭偏仪上交叉验证',
          call: '正确地做出判断',
        },
      },
      atomsToFabs: {
        Title: '从原子到晶圆厂',
        Briefing: '一个批次，走完整条衬底产线。在 CZ-07 拉出一根可用的晶体并把晶锭交下去；在 WPR-08 切片；在 CLN-09 做进厂清洗；在 EPI-20 长出合格的器件层，把晶圆交给前段。你做的每个决定都随流转卡去到下一台机——包括做错的那些。',
        Debrief: '从熔体到器件层：这座厂的名字，被你走了一遍。离开 EPI-20 的晶圆在流转卡上带着你的熔体掺杂、你的切片、你的清洗和你的外延，下游每一台机都要活在你刚定下的预算里。这就是\'从原子到晶圆厂\'的含义——不是口号，是一个批次的履历。',
        objective: {
          grow: '在 CZ-07 长出可用的晶锭',
          handCz: '把晶锭交给晶圆制备',
          slice: '在 WPR-08 切片、研磨、抛光',
          clean: '在 CLN-09 做进厂清洗',
          epi: '在 EPI-20 长出合格的器件层',
          handEpi: '把完成的衬底交下去',
        },
      },
      holdTheClass: {
        Title: '守住洁净等级',
        Briefing: '例行认证。证明洁净间在真正要紧的地方守住了 ISO 等级——在产品上方，而不是墙上的监测器。便携计数器要花一小时；它就是干这个用的。',
        Debrief: '墙上的监测器拧在墙所在的地方，而要紧的是敞开产品上方一米的空气。你花了一小时弄清这个差别。这个习惯，就是这间房。',
        objective: {
          enter: '进入洁净间并打开 GOWN-00',
          workzone: '在工作区跑一次便携计数器',
        },
      },
      theWallLies: {
        Title: '墙在撒谎',
        Briefing: '经过这间洁净间的批次缺陷密度在悄悄上升，而墙上的监测器读数稳稳在等级内。有人错了。查明是谁，并说出来。',
        Debrief: '无尘服超过了洗涤寿命——这是一个房间通风来不及稀释就落到晶圆上的源。墙读的是房间；晶圆读的是俯身其上的人。',
        objective: {
          workzone: '在产品所在的位置测量',
          call: '正确地做出判断',
        },
      },
      twoRoomsAway: {
        Title: '故障在两个房间之外',
        Briefing: '凌晨 02:00。ETCH-01 的选择比和均匀性超出规格，卡盘温度偏高，全厂每台刻蚀机都报着同样的警报。批次在刻蚀工序堆积。刻蚀机本身没有任何东西坏了。找出坏的是什么，决定等待期间产线怎么办，然后把厂房恢复健康——按这个顺序，否则你在错误房间里花的每一小时，产线都要付出代价。',
        Debrief: '故障是地下层的冷水机；刻蚀机只是症状，隔着两个房间。通知之后几小时腔体就回来了——队列却没有。每个在刻蚀前等过的批次都晚到了下一台设备，产线用了一整天的富余产能去消化。打开"产线"看看你的决定相对另外两个选项的代价：在这个负荷下按交期丢的交期最少；暂停投片对队列毫无帮助，还让被扣住的批次少了余量。在制品、产出和周期时间被利特尔法则绑在一起，而一条 95% 负荷的产线到明天还在消化。',
        objective: {
          etch: '去刻蚀机看警报',
          call: '正确上报——问题不在这台设备',
          decide: '决定等待期间产线怎么办：打开"产线"并做出选择',
          descend: '走楼梯下到地下层',
          repair: '恢复冷冻水供应',
          green: '让厂房恢复健康',
        },
      },
      theResistClock: {
        Title: '光刻胶的时钟',
        Briefing: '同一个夜晚，另一条路线：这个产品的光刻胶从曝光机到刻蚀机只有八小时的保质期，而每台刻蚀机三个腔体中的两个刚刚因温控失效而停机。从现在起曝光机曝光的每个批次，时钟都在朝着一个不存在的腔体走。决定曝光机怎么办，然后让厂房恢复健康。',
        Debrief: '时限从来不是跳机的问题。跳机退回的批次在腔体停机前就已曝光，谁也救不了。时限做的事是把刻蚀队列的每一小时都变成返工，整天、每天——在 95% 负荷下这就是产线的大部分。设闸才让时限可行：把曝光扣住，直到刻蚀机能及时消化，在唯一一个队列只花时间的工序上。这是这个车间里唯一正确的扣批，而且只因为它是在时钟开始前扣住的。',
        objective: {
          scanner: '去曝光机看看等着曝光的批次',
          decide: '决定曝光机怎么办：打开"产线"的排队时限并做出选择',
          call: '在刻蚀机正确上报',
          descend: '走楼梯下到地下层',
          repair: '恢复冷冻水供应',
          green: '让厂房恢复健康',
        },
      },
      theSilentTool: {
        Title: '沉默的设备',
        Briefing: 'DEP-02 已经沉积偏厚好几个小时，它自己的屏幕却说一切正常，因为它信赖的热电偶正是坏掉的东西。车间里还没人知道。量测抽检一部分来访批次；其中一个会是告诉你答案的批次。决定抽检力度，找到那台设备，并做出判断。',
        Debrief: '在抽检发现之前经过那台设备的每个批次都有风险，这个数字 = 经过设备的产出 × 它在无人察觉下运行的小时数——从良率一侧看到的利特尔法则。加大抽检能缩短这段时间，代价是量测设备的队列，而全厂每个批次都在那个队列里。抽检率是一个决定：工厂更愿意承担哪一种代价，"产线"把两种价格都摆出来。',
        objective: {
          metrology: '去量测——只有它能看见这件事',
          decide: '决定抽检率：打开"产线"的失控事件并做出选择',
          call: '正确判断沉积设备',
        },
      },
      theDueChamber: {
        Title: '到期的腔体',
        Briefing: 'ETCH-01 的 C 腔体今晚到了保养期：计划内四小时，在瓶颈上，在一个已经落后的班次里。值班领班宁愿让它跑完这周。看看设备，然后决定——并准备好说出继续跑的代价。',
        Debrief: '计划内保养只花它的小时数。推迟是拿磨损的腔体下注，而"产线"说这注每周都输：腔体会坏，计划外维修花两倍时间，落在没人选择的时刻，在那台一小时就是全厂一小时的设备上。领班说今晚四小时很疼，没错。他错的是周四八小时的代价。',
        objective: {
          etch: '去刻蚀机',
          decide: '决定保养：打开"产线"的保养并做出选择',
        },
      },
      theStopwatch: {
        Title: '秒表',
        Briefing: '同样的人数下洁净间读数变脏，开门事件后恢复明显变慢。天花板过滤器上个月刚换过。诊断它——并准备好为你信任的那个读数辩护。',
        Debrief: '恢复时间只由换气次数决定，别的什么都动不了它，所以过滤器干净而恢复缓慢，问题在大楼，不在房间。抓住它的仪器是一块秒表。维修不在这一层——但那是另一张工单。',
        objective: {
          recovery: '给恢复计时',
          call: '正确地上报',
        },
      },
      firstShift: {
        Title: '工作台上的第一个班',
        Briefing: '检查站的缺陷密度在经过 CLN-09 的所有批次上翻了一倍。工作台没有报警，看起来一如往常。你的第一次诊断：先挣来一个读数，再下结论。',
        Debrief: '一种化学失效、另一种正常——这是槽液耗尽的特征，而透明液体从来不会看起来耗尽。你为陪测晶圆付了钱，它把钱还给了你。',
        objective: {
          visit: '在 CLN-09 接班',
          reading: '至少挣来一个读数',
          call: '正确地做出判断',
        },
      },
      invisibleFault: {
        Title: '看不见的故障',
        Briefing: '一个产品的栅氧完整性在下滑，谁也找不到缺陷。颗粒计数完美。不管这是什么，便宜的测量看不见它。',
        Debrief: '一个单层的铜对楼里所有光学检查机都是隐形的，它坐在看似完美的晶圆上进了炉子。那个昂贵的破坏性测量之所以存在，正是因为这种故障存在。',
        objective: {
          metals: '下单表面金属测量',
          call: '正确地做出判断',
        },
      },
      shipsAnyway: {
        Title: '照样出货',
        Briefing: '没有任何针对 EPI-20 的报告。每一站都在规格内。这张工单存在，是因为认证数据看起来稍稍偏离趋势，而楼上有人相信你的判断胜过规格限。',
        Debrief: '车间里最难的一个班：一台坏了的机台在出合格晶圆。没有任何东西强迫你刚才的判断——这正是它值得做的原因。',
        objective: {
          reading: '在 EPI-20 挣来一个读数',
          call: '照样正确地做出判断',
        },
      },
      notThisTool: {
        Title: '不在这台机',
        Briefing: 'CLN-09 在同一次访问里颗粒和金属都超标，而两个槽今早都刚配的。在下单八小时的台面维修之前：问题真的在这个工作台吗？',
        Debrief: '两种化学同时失效是任何单个槽都造不出的特征——晶圆看到的最后一道水是大楼的水。你拒绝下单的那个维修会花八小时，什么也修不好。',
        objective: {
          phone: '致电厂务查询公用系统面板',
          call: '正确地上报',
        },
      },
      takeTheStairs: {
        Title: '走楼梯下去',
        Briefing: '上报已经完成：冲洗回路读 11 MΩ·cm，嫌疑是抛光混床。现在你是这个故事的厂务一侧。下去，把大楼恢复健康。',
        Debrief: '混床在几乎整个寿命里稳住 18.2，然后在最后几个百分点内崩溃——这正是水厂按处理量再生、绝不按表针再生的原因。面板绿了；楼上的工作台下一批就恢复。',
        objective: {
          descend: '走 FAC-06 后面的楼梯下去',
          repair: '再生抛光混床',
          green: '让大楼恢复健康',
        },
      },
      oneEventTwoSymptoms: {
        Title: '一个事件，两种症状',
        Briefing: '相隔一小时的两起投诉：ALD 生长又快又不均匀，炉管氧化层偏厚。两台机、两个工艺组、两张工单。先都去看过，再相信任何一个。',
        Debrief: '干燥气总管里的水毁掉 ALD，因为水是它的共反应物；又通过完全不同的机制让炉氧变厚。两种症状除了病因毫无共同点——而在你在两台机之间走过之前，两个组没有任何理由对表。',
        objective: {
          ald: '去看 ALD 机台',
          furnace: '去看炉管',
          call: '在任一台正确地上报',
        },
      },
      wrongRepair: {
        Title: '让大楼恢复健康',
        Briefing: '地下厂务层有什么东西病了。工单只说了这一句。下面的每次维修不管对错都照收工时——仪表就是你避免付两次钱的办法。',
        Debrief: '在你花掉一个小时之前，仪表就排除了无辜的设备：厂务层大部分是好的，这件事本身就是诊断的大部分。如果你先修了健康的设备，收工报告会精确展示那份确定的代价。',
        objective: {
          descend: '下去读仪表',
          repair: '修真正生病的那台设备',
          green: '让大楼恢复健康',
        },
      },
    },
    orders: {
      Button: '工单',
      Title: '工单',
      BoardNote:
        '一份任务说明、一栋处于某种状态的大楼，以及对证据的期待。接受工单会布置好世界；' +
        '你做的每件事都会记入日志，证据齐了工单就完成。没有评分——收工报告只展示你的确定' +
        '花了多少代价。',
      TrackRoom: '这间房',
      TrackTool: '眼前的机台',
      TrackBuilding: '这栋大楼',
      TrackCapstone: '整条产线',
      ClassLink: '课堂链接',
      LinkCopied: '已复制链接',
      Accept: '接受',
      Again: '再来一次',
      Abandon: '放弃',
      Active: '进行中',
      Completed: '已完成',
      Done: '工单完成',
      Cost: '花了什么',
      TotalHours: '机台总工时',
    },
    subfab: {
      mau: {
        Title: '新风机组 MAU-1',
        Intro: '室外空气进，洁净室空气出。除湿就是冷却到露点再再热；冬天靠蒸汽加湿；压力是扣除排风之后剩下的。',
        Readings: '机组读数',
        WeatherSummer: '夏季设计日',
        WeatherWinter: '冬季设计日',
        WeatherMild: '温和天气',
        Reset: '重置',
        KnobOutsideC: '室外空气温度',
        KnobOutsideRh: '室外空气湿度',
        KnobSupplyC: '送风温度设定',
        KnobSupplyRh: '送风湿度设定',
        KnobFanPct: '送风机，设计值的 %',
        KnobExhaustPct: '工艺排风，设计值的 %',
        Flow: '送风 / 排风',
        Moisture: '含湿量 进 / 出',
        DewPoint: '送风露点',
        CoilLeaving: '冷却盘管出风温度',
        Dehumidifying: '除湿中',
        Cooling: '冷却盘管',
        Heating: '预热 + 再热盘管',
        Steam: '加湿器蒸汽',
        FanPower: '风机功率',
        Power: '功率（冷机按 COP 折算）',
        Pressure: '洁净室压力',
        WarnFreeze: '冷冻水盘管被要求达到接近冰点的露点：结霜风险。',
        WarnCooling: '冷却盘管超出额定能力；送风会偏热偏湿。',
        WarnHeating: '热水盘管超出额定能力；送风会偏冷。',
        WarnSteam: '加湿器超出额定能力；洁净室会变干。',
        WarnPressureLow: '洁净室正压丧失：走廊空气正从缝隙倒灌进来。',
        WarnPressureHigh: '压力过高：门会顶着闭门器，FFU 静压箱会被顶起。',
        Note: '铭牌：12 000 m³/h，制冷 240 kW，供热 130 kW，蒸汽 110 kg/h。楼上还没有任何设备感知这台机组；这些数字是焓湿计算，不是故障模型。',
      },
      BoardTitle: '厂务层公用系统',
      SkidNote:
        '这是大楼这一侧的故事。在这里做的维修是真实的：无论对错工时照收，只有真正生病的' +
        '那台设备能换回任何东西。',
      RepairCleared:
        '维修完成，回路重新认证。公用系统面板重新变绿——上楼看看各台设备恢复吧。',
      RepairWasted:
        '修好了，重新认证了……而楼上的症状纹丝不动。这台设备本来是健康的；工时已经花掉了。',
      RepairIdle: '这台设备没有任何问题。预防性维护，已记录并计费。',
      BeltsSlipping: '带载打滑',
      BeltsTight: '张紧良好',
      reading: {
        headerLeakUp: '总管漏率',
        headerAdder: '各腔体附加压力',
        pcwSupply: '供水温度相对额定值',
        gasMoisture: '干燥器出口总管水分',
        upwLoop: '回路电阻率（25 °C 补偿）',
        ahuFlow: '送风量相对设计值',
        ahuBelts: '传动皮带检查',
        forelineMET05: '前级压力，MET-05',
        forelineINSP13: '前级压力，INSP-13',
        forelinePRB14: '前级压力，PRB-14',
        trayCMP11: '双套管托盘，CMP-11 管线',
        trayECD12: '双套管托盘，ECD-12 管线',
        chemInterlock: '输送联锁',
        mauFlow: '新风送风量 / 设计值',
        mauFilterDp: '初效过滤器压降',
        roomPressure: '洁净室相对大气压力',
      },
      PumpsPulling: '全部正常抽气',
      TrayDry: '干燥',
      TraysDry: '托盘干燥',
      InterlockShut: '已关停——{{tag}} 输送停止',
      InterlockOpen: '开放，所有管线正常输送',
      Tight: '密封良好',
    },
    name: {
      SimFab: 'SimFab',
      Institute: '未来智能研究所',
      Tagline: 'SimFab — 从原子到晶圆厂',
    },

    language: {
      Language: '语言',
      Change: '切换语言',
    },

    splash: {
      Eyebrow: '从原子到晶圆厂',
      Headline: '你正站在<em>更衣气闸室</em>前。',
      Sub:
        '欢迎！你的任务是运行通道里的设备：打开一台设备，设定工艺，请站上的技术员' +
        '解释正在发生的事，分析数据，生成报告。你也可以按工单来做。',
      KeyWalk: '<span>行走 · <kbd>Shift</kbd> 奔跑</span>',
      KeyLook: '<span>环视 · 或用 <kbd>←</kbd><kbd>→</kbd> 转身</span>',
      KeyOpen: '<span>打开设备</span>',
      KeyAsk: '<span>助手替你把实验跑出来</span>',
      CapLook: '右键拖动',
      CapOpen: '点击',
      CapAsk: '提问',
      KeyFind: '<span>按名称查找任何设备并跳转过去</span>',
      EnterFab: '进入 {{fab}}',
      CapTap: '点按',
      CapDrag: '拖动',
      KeyWalkTouch: '<span>厂区里的任意设备——你会走到它面前</span>',
      KeyLookTouch: '<span>环视四周</span>',
      KeyOpenTouch: '<span>打开面前的设备</span>',
    },

    vehicle: {
      Hoist: '天车（OHT）',
      Robot: '移动机器人',
      Carrying: '载有批次 {{lot}}，晶圆盒 {{carrier}}',
      BoundFor: '前往 {{tool}} 的 {{port}} 号装载口',
      BoundEnd: '沿车道返回',
      Cruising: '以 {{speed}} m/s 行驶中',
      Holding: '在前车后方等待',
      Transferring: '正在装载口交接晶圆盒',
      Moving: '以 {{speed}} m/s 移动中',
      Delivering: '正在装载口交付',
      Yielding: '已停车——安全区域内有人',
      Dismiss: '按 <kbd>Esc</kbd> 或点击地面关闭',
      DismissTouch: '点按地面关闭',
    },
    talk: {
      Title: '正在与 {{name}} 交谈',
      Placeholder: '问{{name}}一个问题…',
      Send: '发送',
      Close: '结束对话',
      Stop: '停止',
      Stopped: '已停止',
      Thinking: '…',
      Local: '本地回复——无法连接托管模型',
      VoiceOn: '朗读回复',
      VoiceOff: '停止朗读回复',
      MicStart: '说出你的问题',
      MicStop: '停止聆听',
      Listening: '正在聆听…',
      MicWhere: '语音输入由浏览器处理，音频可能会发送到其语音服务。',
      MicDenied: '无法使用麦克风。请在浏览器中允许后再说话。',
      predict: {
        Waiting: '在结果出来之前：',
        Placeholder: '说说你认为会往哪个方向变…',
        Skip: '直接看结果',
        SkipTitle: '不作判断，直接显示结果',
      },
      knock: {
        Wants: '{{name}} 想跟你说句话',
        Listen: '去听听',
        Dismiss: '暂不',
        OutOfSpec: '关于 {{tag}} 刚跑出规格的那片',
        WrongCall: '关于 {{tag}} 的故障判断',
        Stalled: '关于你手上这张工单',
      },
      experiment: {
        Running: '这就跑给你看…',
        Progress: '第 {{done}} / {{total}} 个条件',
        Unavailable: '我从这里够不到设备——到它前面按 E，在那里问。',
        Failed: '这次运行没有跑完。设备没有被动过。',
        LockedOut: '{{tag}} 挂了保养锁定牌——检查项签字之前，它上面什么都不能跑',
      },
      line: {
        ChambersDown: '工艺冷却水正在变热，全厂每台刻蚀机三个腔体中的两个因此停机——每台刻蚀机只剩一个腔体在承担全部刻蚀。全车间的批次都在刻蚀前堆积。',
        Scenario: {
          chiller: '冷水机跳机',
          qtime: '排队时限',
          excursion: '失控事件',
          pm: '到期腔体',
        },
        Decided: {
          chiller: {
            reroute: '让所有批次排队等剩下的腔体，先到先做',
            hold: '腔体恢复前暂停投片',
            'by-date': '刻蚀按交期排产',
          },
          qtime: {
            run: '让曝光机照常运行',
            gate: '在曝光机前设闸，直到刻蚀机能及时刻蚀',
          },
          excursion: {
            '0.05': '抽检 5% 的量测来访',
            '0.1': '抽检 10% 的量测来访',
            '0.25': '抽检 25% 的量测来访',
            '0.5': '抽检 50% 的量测来访',
          },
          pm: {
            now: '现在保养腔体',
            defer: '让腔体跑完这周',
          },
        },
      },
      demo: {
        // The controls a scene puts under itself in a conversation, where
        // its panel would have wired them to a recipe (app/talk/demoScenes.tsx).
        knob: {
          RingWear: '聚焦环磨损',
          IonEnergy: '离子平均能量',
          Downflow: '下降气流速度',
          Tilt: '束流相对晶轴的倾斜',
        },
        Show: '再放一次',
        Loading: '正在调出…',
        Failed: '这个在这里放不出来。',
        'crystal-lattice': {
          Title: '硅晶体到底是什么',
          About: '硅原子按金刚石结构排列，在真实分子动力学下振动。',
          Watch: '每个原子都在振动，却没有一个离开位置。固体就是这样：把它们固定住的只有彼此。',
          Try: '把「晶体温度」往 1600 K 拉，看振动越来越大，再拉回来。没有一个原子会离开自己的位置。',
        },
        bands: {
          Title: '硅为什么是半导体',
          About: '硅的能带结构，以及决定其余一切的那道禁带。',
          Watch: '看满带和空带之间的那道间隙。太宽就不导电，太窄就关不断。',
          Try: '这里没有可调的东西——能带只解一次。沿着下方坐标轴逐点看竖直方向的间隙，找出它最窄的位置。',
        },
        carriers: {
          Title: '掺杂硅里的载流子从哪来',
          About: '冻析区、非本征区、本征区：一个掺杂浓度到底给你多少载流子。',
          Watch: '拖动掺杂滑块，看载流子数怎么不跟着走。在低温那一端，大多数掺杂原子还攥着自己的电子没放。',
          Try: '把掺杂滑块从一头拉到另一头，看三个区依次经过：冻析区、非本征区，最后是硅本身接管的本征区。',
        },
        'si-surface': {
          Title: '硅表面，以及它为什么会反应',
          About: '(100) 面的重构，以及这条线上每道工艺所依靠的悬挂键。',
          Watch: '看最上面一层怎么两两配对。那些倾斜的二聚体就是悬挂键——这条线上每道工艺都在跟它们反应。',
          Try: '先让它稳定下来，再把温度滑块往上推，看那些二聚体照样保持倾斜——重构不是加热能抹掉的。',
        },
        'diffusion-hop': {
          Title: '原子在固体里怎么移动',
          About: '一个自间隙原子在硅中一次次跳跃，温度越来越高，画出它的阿伦尼乌斯直线。',
          Watch: '盯着那个标出来的原子跟邻居换位——一次跳跃就是一次扩散事件。把炉温调高，跳得更勤；而完美晶体一次也不跳。',
          Try: '把「炉温」拉到 1600 K 左右再按一次 Run——跳跃会快上好几倍。然后切到「完美晶体（对照）」，看它无论多热都停在零。',
        },
        'film-growth': {
          Title: '薄膜是怎么一个原子一个原子长起来的',
          About: '气相原子落到热晶圆上并留下来，以及它们画出的生长曲线。',
          Watch: '原子不断落上来，有的留下有的又跑掉。旁边那条曲线就是膜在变厚，它拐弯的地方就是来和走打平的地方。',
          Try: '把「晶圆温度」往下拉再跑一次：晶圆越冷，落上来的留得越多，曲线也就更接近整份气相装料。',
        },
        tunnelling: {
          Title: '电子隧穿只有几个原子厚的栅氧化层',
          About: '一个电子波包撞上由几层原子构成的栅氧化层：大部分以条纹的形式弹回来，一个微弱的副本隧穿过去，放大后你能看见它。',
          Watch: '看波包一边前进一边变宽，然后堆在原子前形成明亮的条纹——那是入射波和反射波在交叠。墙的另一侧，放大之后，一个微弱的副本穿过了一堵它没有足够能量翻越的墙。这就是隧穿。',
          Try: '把「氧化层厚度」拉到 0.6 nm——两层原子——看墙后面那部分变亮，放大倍数标签会告诉你亮了多少。再按「氧化层作为平板」，把原子换成它们平均而成的光滑势垒，看穿过去的比例几乎一样。 按「三维 · 体积」，同一堵墙会变成一层层的小球，在工作线程上几秒算完。',
        },
        'plasma-emission': {
          Title: '为什么辉光能告诉你薄膜已经刻完',
          About: '刻蚀机的终点检测是一台对着等离子体的光谱仪。每条谱线都是一个原子在两个能级间跌落，由分布尾部的电子点亮。',
          Watch: '看两条曲线：硅刻穿时 SiF 谱带熄灭，同一刻氟线上升，因为再没有什么在消耗氟。',
          Try: '把 T_e 从 3 eV 拉到 2 eV，看氩的 750 nm 线塌下去，而 440 nm 谱带几乎不动：13.5 eV 的阈值对 2.9 eV 的阈值。',
        },
        'dopant-atom': {
          Title: '掺杂原子就是一个氢原子——直到它们太多了',
          About: '磷原子多出来的那个电子绕着它运动，像氢原子一样，只是被晶体按比例改了：轨道 2.4 nm，束缚能几十 meV。由薛定谔方程解出，不是画上去的。',
          Watch: '看左图带阴影的轨道和穿过它的三条线——玻尔半径、平均半径，以及到下一个掺杂原子的一半距离。右图里，束缚能随掺杂浓度下降，在莫特转变处归零。',
          Try: '把“掺杂浓度”拉到 10¹⁸ 以上，看那条一半距离的线穿进轨道，同时屏蔽把轨道撑大——然后束缚态消失，电子自由了。切换到砷化镓，看模型表现最好的情形。',
        },
        'photon-counting': {
          Title: '一幅像是由什么构成的',
          About: '把空间像作为被计数的光子交付——每个像素一次泊松抽样——再经光刻胶模糊并显影。印出边缘的粗糙度是测出来的，不是假定的。',
          Watch: '看中间那幅图：20 mJ/cm² 的 EUV 下，像是一片颗粒，每像素约 100 个光子，右边显影出的边缘在游走。虚线是同一幅像若由 ArF 光子构成的情形，平滑 √14 倍。',
          Try: '把"剂量"拉到 2 mJ/cm²，看粗糙度按 1/√剂量 上升；切到"EUV 的像，ArF 的光子"，同一条边缘就变平滑。在 0.5 mJ/cm² 下把光刻胶作用距离缩到 2 nm，缺陷行就出现了。',
        },
        'channel-quantisation': {
          Title: '阈值为何不在公式所说之处',
          About: '栅极把能带压弯成一纳米宽的阱，这么窄的阱只允许分立的能级。沟道电荷离开了氧化层，而探针卡测到的就是这个差别。',
          Watch: '看左图的阴影形状：沟道里的每个电子在氧化层处为零，在一纳米深处达到峰值。右图中，两条电容都够不到氧化层电容；实线的量子电容更低，因为每一伏带来的电荷落得离氧化层更远几埃。',
          Try: '把"栅压"调高，看能级下沉、形状向氧化层挤压——更近，却永远不贴上去。把"氧化层厚度"减到 1 nm，两条电容曲线的差距会变大：氧化层只有十来埃时，几埃的间距就更要紧。',
        },
        'raman-strain': {
          Title: '拉曼仪盯着移动的那条谱线',
          About: '由真实的 Stillinger–Weber 力常数算出硅的声子，以及拉曼光谱仪用来读取应变的区心光学模。',
          Watch: '看六支色散：三支声学支从零出发，三支光学支在 593 cm⁻¹ 附近；旁边的拉曼谱线标出了实测的 520——模型偏高，并且如实说明。',
          Try: '把"双轴应变"拖到 −1%，谱线上移并分裂成一个单态和一个双态；推到 +1% 则下移。切换到 Ge，一切都往下掉：更重的原子架在更软的键上。',
        },
        'quantum-well': {
          Title: '几个纳米的合金对一个空穴意味着什么',
          About: '夹在硅之间的一薄层硅锗，对空穴而言就是一个盒子，而生长的厚度就是盒宽。允许的能级由所生长的剖面解出，不是画上去的。',
          Watch: '看势阱内那一架虚线能级，以及落在最低一级上的阴影曲线——它显示那个空穴真正所在之处。每一级都是这层材料所允许的一个能量；在它们之间，空穴无处可取。',
          Try: '把"生长厚度"从 8 nm 往下拉，看每一级都往上爬；再看最上面那一级触到壁而消失——原本容纳四级的阱变成三级，再变成两级。把"锗组分"调高使阱变深，它们又会回来。',
        },
        'resist-absorption': {
          Title: '光刻胶为什么只响应某一种颜色的光',
          About: '计算得到的共轭分子吸收带，并标出曝光所用的各条波长。把链拉长，吸收带便沿光谱移动，依次越过每一条谱线。',
          Watch: '看哪几条标线变成琥珀色。分子只响应其吸收带所覆盖的谱线；而吸收带会移动，是因为 π 电子处在一个盒子里，盒长即共轭长度——链越长能级越密，吸收的光也越红。',
          Try: '把"共轭长度"从 1 个双键拉到 11 个。到 4 个时吸收带正落在 365 nm 的 i 线上；到 6 个就已越过它落到 g 线，i 线随之熄灭。这里无论怎么调都落不到 193 nm 的 ArF 上，这正是 193 nm 需要另一族分子的原因。',
        },
        chemisorption: {
          Title: '前驱体与晶圆逐键反应',
          About: '反应性力场下硅烷落在硅晶圆上：一个 Si-H 键在悬挂键处断裂，生成一个 Si-Si 键，这个硅便并入薄膜。画出的键棒来自键级，而非脚本。',
          Watch: '看一个分子飘下来贴到表面，随后把一个氢交给它——两者之间的键棒消失，通向晶圆的新键棒出现。绿色曲线数的是已并入薄膜的硅，蓝色数的是留在表面的氢，正是它挡住了下一个分子。',
          Try: '按下"硅基自由基（SiH₃，等离子体 CVD）"，再把"晶圆温度"拉到 600 K：自由基能附着，而完整的硅烷不能——这正是等离子体 CVD 可以低几百开尔文运行的原因。然后按"氢终止"，看生长如何等待氢先离开。',
        },
        'electron-diffraction': {
          Title: '电子波穿过双缝',
          About: '高斯电子波包遇到双缝或一排原子，盒子边缘按角度记录到达的一切。各束落在 sin θ = mλ/p 所指的位置，一旦波长超过间距便消失。',
          Watch: '看波包一边前进一边展宽，在狭缝处分开，作为分离的光束离去。图就是屏幕：每个峰都落在一条虚线上，而那些虚线是由间距预测的衍射级。峰不是谁画上去的——是波形成的。',
          Try: '把"电子能量"拉向 4 eV，看外侧各级先张开、再随波长超过间距而消失。按"一排原子，间距 0.5 nm"换成更细的靶，那里要看到任何结构都需要短得多的波长。',
        },
        'light-absorption': {
          Title: '硅晶圆为什么对红光透明',
          About: '由紧束缚能带算出的衬底光吸收：整个布里渊区内从满带到空带的每一次跃迁。厚度拟合默认使用的折射率正是从这里来的。',
          Watch: '看曲线从零抬起的位置。硅在 3 eV 附近，即其直接带隙，远高于通常引用的 1.1 eV——单个光子无法提供间接跃迁所需的动量。琥珀色标线是反射仪的扫描范围，几乎整段都落在透明区。',
          Try: '按"Ge"，再按"GaAs"。两者的吸收起点都低得多，把整个可见扫描范围吞掉；所以它们都做不成窗口，也所以薄膜之下的晶圆才是厚度拟合所需要的那个透明体。',
        },
        'doped-lattice': {
          Title: '掺杂原子，落在它的晶格里',
          About: '硼占据一个硅原子位，在真实的分子动力学下振动——这才是"p 型"的真面目。',
          Watch: '带光晕的原子是占据硅位的硼。这个替位本身就是掺杂——不是晶圆的某种属性，而是一个坐错位置的原子，重复很多次。看图：轻的硼明显比周围的硅振动得更快。',
          Try: '把"掺杂浓度（已夸大）"上下拖动，再按"高亮掺杂原子"取消光晕，看看真实比例。真实掺杂是百万分之几——在 216 个原子里根本看不见，所以这个视图作了弊。',
        },
        channelling: {
          Title: '为什么离子注入要倾斜七度',
          About: '离子注入真正到达的深度，以及决定它的那点倾斜。',
          AxisDepth: '进入晶圆的深度（nm）',
          AxisIons: '停下来的离子数',
          LabelQualified: '倾斜 7°（已认证）',
          LabelLive: '当前倾斜',
          Status: '临界角 ψ₁ = {{a}}° · 沟道化束流 {{b}}% · 最深的 1% 达到 {{c}} nm',
          Tracing: '正在追踪 3,000 个离子…',
          Watch: '虚线是经过认证的 7° 配方。倾斜为零时，四分之一的束流找到敞开的原子列一穿而过——剂量相同、能量相同，深度却翻了一倍。',
          Try: '把"束流相对晶轴的倾斜"从 0 拖到 12°，看那条深拖尾在哪里消失。它在 2° 和 4° 几乎毫发无损，然后骤然塌缩——塌缩的那个角度，正是图下方标出的 ψ₁。',
        },
        sheath: {
          Title: '晶圆边缘上方的等离子体鞘',
          About: '把乱走的离子变成垂直束流的那段压降，以及磨损的聚焦环怎么把它带歪。',
          Watch: '晶圆上方那条亮带就是压降。等势线就是离子走的路——在晶圆上方是平的，到磨损的聚焦环那里就弯了。',
          Try: '把「聚焦环磨损」从 0 拉到 100%，看晶圆边缘上方的等势线怎么从平的变成弯的。弯的那一段，就是离子不再垂直入射的地方。',
        },
        'trench-arde': {
          Title: '窄沟槽为什么刻得慢',
          About: '同一团等离子体下四条不同宽度的沟槽，窄的那几条被饿着。',
          Watch: '四条沟槽，同一团等离子体。看刻蚀剂怎么到得了宽沟的底，却始终到不了窄沟的底。',
          Try: '把「离子平均能量」从 93 eV 往上拉，看窄沟不但没变好反而更糟：离子越硬，表面消耗刻蚀剂就越快，快过窄孔能送进去的速度。',
        },
        'thermal-budget': {
          Title: '热对掺杂剖面做了什么',
          About: '一个流程让掺杂剂移动多远，即 D(T) 对时间的积分：两片相同的注入，其中一片在 900 °C 流程中经历十分钟 1200 °C 尖峰。',
          Watch: '两片晶圆，注入了同样的硼。左边那片在过程中间被加热到 1200 °C 十分钟；右边那片全程保持 900 °C。看看左边的硼扩散得远多少。',
          Try: '',
        },
        'drive-in': {
          Title: '一次完整的推进退火',
          About: '硼在炉中被推进硅里，温度和剖面随着运行一起变化。',
          Watch: '硼往硅里推进。看扩散前缘一路往下走，右边的曲线随着各个探针先后感受到它而依次抬起来。',
          Try: '用画布下面的炉温设定：一百度的作用远大于把时间加倍，从探针曲线抬起来的快慢就看得出来。',
        },
        'line-chiller': {
          Title: '产线：冷水机跳机对整个车间做了什么',
          About: '把厂房看成排队系统：刻蚀机三个腔体停了两个、停机六小时，值班的三种应对，每一种都逐批次与同一周无故障的情形比较。',
          Watch: '蓝线是没人做决定时的情形。看腔体停机时刻蚀在制批次猛增，再看腔体恢复之后产线还要付出多久的代价。然后看表格：同一次故障，三个决定，各自在交期上的代价。',
          Try: '把负荷切到 95%，看同样的六小时跳机要用几天才能消化。再把通知时间切到 08:00，看在错误设备上耗掉的六小时值多少。',
        },
        'bay-airflow': {
          Title: '一个 bay 里的层流下降气流',
          About: '把颗粒往下带走的层流空气，以及站在里面的人对它做了什么。',
          Watch: '看空气笔直往下走，到地面再往两侧散开。它带着的东西也就跟着走了——除非有人正站在中间挡着。',
          Try: '把「下降气流速度」往 0.10 m/s 拉，看那根笔直的气柱怎么散掉。这就是「把颗粒扫走的气流」和「任它乱飘的气流」之间的差别。',
        },
      },
      cast: {
        sarah: { Name: '李娜', Short: '李娜', Role: '刻蚀技术员', Post: '04 号工作区 ETCH-01 的操作屏前' },
        jake: { Name: '王伟', Short: '王伟', Role: '光刻技术员', Post: '05 号工作区 LITH-03 涂胶显影轨道的操作屏前' },
        emily: { Name: '张敏', Short: '张敏', Role: 'CMP 技术员', Post: '08 号工作区 CMP-11 的操作屏前' },
        nicole: { Name: '周静', Short: '周静', Role: '离子注入技术员', Post: '03 号工作区 IMP-10 的操作屏前' },
        tom: { Name: '刘强', Short: '刘强', Role: '维护技术员', Post: '02 号工作区 THRM-04 打开的面板内，站在脚凳上' },
        owen: { Name: '马涛', Short: '马涛', Role: '外延技术员', Post: '02 号工作区 EPI-20 的操作屏前' },
        grace: { Name: '王芳', Short: '王芳', Role: '晶圆测试技术员', Post: '09 号工作区 PRB-14 的操作屏前' },
        laura: { Name: '徐蕾', Short: '徐蕾', Role: '量测技术员', Post: '07 号工作区 MET-05 的操作屏前' },
        ryan: { Name: '杨帆', Short: '杨帆', Role: '沉积技术员', Post: '04 号工作区 DEP-02 的操作屏前' },
        hannah: { Name: '林雪', Short: '林雪', Role: 'EUV 光刻机技术员', Post: '06 号工作区 EUV-22 的操作屏前' },
        brian: { Name: '高峰', Short: '高峰', Role: 'CD-SEM 量测技术员', Post: '06 号工作区 SEM-23 的操作屏前' },
        claire: { Name: '何琳', Short: '何琳', Role: '低温技术员', Post: '12 号工作区 QDT-24 的操作屏前' },
        kevin: { Name: '陈杰', Short: '陈杰', Role: '值班主管', Post: '主通道上，正在巡视车间' },
        marcus: { Name: '赵磊', Short: '赵磊', Role: '刻蚀设备技术员', Post: '04 号工作区后方维护通道内 ETCH-01 的背面，一块后面板已拆下' },
        rachel: { Name: '孙丽', Short: '孙丽', Role: '沉积设备技术员', Post: '04 号工作区后方维护通道内 DEP-02 的辅助机柜旁' },
        dave: { Name: '吴刚', Short: '吴刚', Role: '厂务电工', Post: '08 号工作区后方维护通道内的 MCC 配电柜前' },
        megan: { Name: '郭婷', Short: '郭婷', Role: '厂务技术员', Post: 'ETCH-01 这一排后方的维护通道走道上，正在巡检' },
      },
      floor: {
        Walking: '在主通道上，与 {{bay}} 号工作区（{{tags}}）齐平，正朝{{toward}}走',
        TowardGas: '东边的气体站',
        TowardGowning: '西边的更衣站',
        AtTool: '{{bay}} 号工作区的 {{tag}}',
        Down: '{{tag}} 正在进行预防性维护，已停机',
        LayoutBallroom:
          '大开间式厂房：同样的设备立于同一开放地面，没有隔墙。两排设备面向主通道，每排身后各有一排同型机台（如 ETCH-01 身后的 ETCH-02）面向外侧通道，无人值守、不能打开，问题请找第一排。泵、冷水机、射频电源和气柜都在地下层。更衣站在西端，气体站在东端；工作区从更衣站旁的 01 号起编号，在任何通道上都相同。',
        Layout:
          '这里是 Fab A。工作区加维修通道式厂房：设备正面嵌在单条主通道两侧的隔墙内，隔墙后是维修通道；有架空搬运环线和地面机器人运送晶圆盒，西端是更衣站，东端是气体站。工作区沿主通道编号，01 号最靠近更衣站。',
      },
      local: {
        Greeting: '你好，我是{{name}}，现在在{{post}}。想了解什么？',
        Doing: '我在 {{tag}}，设备{{state}}。屏幕显示 {{headline}}（{{caption}}）。',
        DoingNoTool: '我正在工作区之间走动，沿主通道往前。',
        About: '{{tag}} 是一台{{name}}，做的是{{process}}。{{blurb}}',
        Screen: '现在读数是 {{headline}}——{{caption}}。{{rows}}',
        Order: '当前打开的工单是“{{title}}”：{{total}} 个目标已完成 {{done}} 个。',
        NoOrder: '现在没有打开的工单。',
        Down: '{{tag}} 停机做 PM：面板已拆下，挂了上锁挂签，晶舟已降到升降机上。检查签字后就恢复运行。',
        Showing: '这个我在这里跑不了，不过可以给你放出来看：{{title}}。',
        CantSay: '这个我在这里说不了。到 {{tag}} 前按 E，问设备自己的助手——它能把实验跑出来。',
        CantSayNoTool: '这个我在这里说不了。设备自己的面板能把实验跑出来——到设备前按 E。',
        NotHuman: '我不是真人——你在跟模拟器说话。现在也没有任何 AI 模型在回答；这些话是模拟器里预置的固定台词。',
        States: { idle: '空闲', running: '运行中', alarm: '报警中' },
      },
    },
    hud: {
      OpenLive: '点击设备，或按 <kbd>E</kbd> 打开',
      OpenPending: '点击或按 <kbd>E</kbd> 查看站点范围——尚未仿真',
      OpenLiveTouch: '点按设备即可打开',
      OpenPendingTouch: '点按查看站点范围——尚未仿真',
      Idle: '待机',
      Open: '打开',
      Running: '运行中 {{speed}}x',
      Rate: '速率',
      NonUniformity: '不均匀度',
      Cleared: '已清除',
      Time: 't',
    },

    panel: {
      Close: 'Esc — 回到车间',
      Failed: '这个面板绘制失败（{{message}}）。按 Esc 回到车间。',
    },

    hmi: {
      Status: '状态',
      Idle: '待机',
      LastStep: '上一道工序',
      Down: '停机',
      PmInProgress: '预防性维护中',
    },

    xr: {
      EnterVr: '进入 VR',
      ExitVr: '退出 VR',
      Unavailable: 'VR 不可用',
      ViewInVr: 'VR',
      ViewInVrTitle: '用手机 VR 眼镜盒观看',
      HolderHint: '轻点居中 · 长按退出',
      NoMotion: '这部手机没有报告朝向。',
      MotionDenied: '运动权限被拒绝。请在浏览器的本站设置中开启。',
    },

    routes: {
      Button: '流程',
      ButtonTitle: '晶圆走过厂区的顺序',
    },

    line: {
      DecideLabelQtime: '你在曝光机前的决定',
      DecideLabelExcursion: '你的决定：量测来访抽检多少',
      DecideLabelPm: '你对到期腔体的决定',
      ProductsLabel: '产品',
      ProductsOne: '一种',
      ProductsTwo: '两种 —— 30% 双重图形',
      ChartCtTitleMix: '改道策略下各产品的周期时间（虚线：无故障）',
      ProductsThree: '三种 —— 25% EUV，3% 量子比特芯片',
      ProductEuv: 'EUV 产品',
      ProductQuantum: '量子比特芯片',
      NewToolsTitle: '光刻机、CD-SEM 和制冷机群，与瓶颈并排来看',
      ColTool: '设备',
      ColInBuilding: '全厂台数',
      ColUtilisation: '忙碌率',
      ColDown: '停机率',
      ColWait: '平均等待',
      NewToolsNote: '四分之一的投片是先进产品：EUV-22 印它的栅极和最下面四层金属，SEM-23 在 ETCH-01 刻蚀之前把每一次曝光都量一遍。百分之三是量子比特芯片，它们走完八道工序就离开前道，然后在制冷机里待两天。把最后一列和第三列对着看。制冷机群只用了大约一半的产能，远低于刻蚀机，它的批次却等得最久，差了一个数量级：批次等的是一台制冷机空出来，而一台制冷机一忙就是两天。等待时间取决于排在你前面的那件活要干多久，而不只是设备有多忙。光刻机是另一课：全厂只有一台，每八小时就有一小时停机，而批次没有别处可去。冷水机跳机会同时停掉三族水冷设备：每三个刻蚀腔中的两个、那台光刻机，以及每一台制冷机。',
      ProductA: '单次图形',
      ProductB: '双重图形',
      ChartCtTitleProducts: '改道策略下各产品的周期时间',
      TabQtime: '排队时限',
      TabExcursion: '失控事件',
      TabPm: '保养',
      QtimeRun: '放任不管',
      QtimeGate: '在曝光机前设闸',
      QtimeNote: '同一次跳机，但工艺路线上的光刻胶有保质期：离开曝光机后八小时内没有刻蚀的批次要去胶、重新曝光。每个已曝光却卡在停机腔体后面的批次，时钟都在走。两种做法：放任不管，或在曝光机前设闸——目标刻蚀机以现有可用腔体无法在时限内刻蚀的批次就不曝光，让批次在时钟开始前等待。',
      QtimeChartTitle: '跳机以来退回曝光机的批次',
      ColReworks: '退回的批次',
      ColReworksControl: '无故障情形',
      ColReworkHours: '损失的曝光机小时',
      QtimeFinding: '先看“无故障”一栏：只要刻蚀队列的等待超过时限，排队时限本身就是一台返工机器——85% 时每天几个批次，95% 时是大多数。设闸是让时限可行的关键——它把曝光扣在曝光机前（那里的队列只花时间），直到刻蚀机能及时消化——85% 时代价是几个交期，95% 时没有代价。跳机本身增加的很少：它退回的批次是腔体停机时已经曝光的那些，任何策略都救不了。这是唯一一种扣住批次是对的情形，而且只因为它是在时钟开始前扣住的。',
      ExcursionNote: '这次没有跳机。凌晨两点，一台沉积设备悄悄失控并继续运行。车间里没有任何提示。量测设备抽检一部分来访批次；抽到一个经过了坏设备的批次，才会发现。在此之前经过该设备的每个批次都已暴露。按六周不同运气取平均，因为一次发现就是一次抽签。',
      ExcursionChartTitle: '抽检率买到的与付出的',
      ExcursionChartX: '被抽检的量测来访比例',
      ExcursionChartY: '批次 · 等待 × 10 小时',
      ExcursionSeriesExposed: '暴露批次，均值',
      ExcursionSeriesWait: 'MET-05 等待，× 10 小时',
      ColRate: '抽检比例',
      ColDetect: '发现用时',
      ColExposed: '暴露批次，均值',
      ColExposedWorst: '最差一周',
      ColInspections: '每天抽检数',
      ColMetWait: 'MET-05 等待',
      ColMetUtil: 'MET-05 忙碌',
      ExcursionFinding: '暴露批次数 = 经过坏设备的产出 × 发现前的小时数——这是从良率一侧看到的利特尔法则。抽检力度加十倍，发现时间减半，风险批次减半，但量测设备被填满：队列随抽检率增长，全厂每个批次都要在周期时间上付出代价。抽检率是一个决定：工厂更愿意承担哪一种代价。',
      PmNow: '现在保养',
      PmDefer: '继续运行',
      PmNote: '一台刻蚀机的一个腔体在凌晨两点到了保养期。现在做：今晚计划内的四小时。或者继续跑：一个磨损的腔体终究会坏，坏的时候，计划外维修要花两倍时间，在没人选择的时刻。推迟是一场赌博，所以两种做法都按八周不同运气评判，并与既不保养也不磨损的同样八周比较。',
      ColChamberDown: '腔体停机，周均',
      ColBeyondControl: '超出两者皆无的一周',
      ColWorstWeek: '最差一周',
      ColFailed: '保养前就坏了',
      ColDelayLotHours: '净延误批次小时',
      PmFinding: '计划内保养只花它的 {{pm}} 小时。磨损的腔体寿命缩到 {{worn}} 小时，每次都在一周内坏掉，计划外维修花了 {{repair}} 小时，而表中的维修时间是 {{mttr}} 小时——计划内保养的两倍，在没人选择的时刻，发生在瓶颈上。瓶颈的一小时就是全厂的一小时：最后两列里每一个延误批次小时都是这个差值沿产线传下去的结果。',
      DecideLabel: '腔体停机期间，你的决定',
      Undecided: '尚未决定。工单需要一个决定；下面的数字不受影响。',
      Decided: '你选择了：{{policy}}。已记入日志。',
      Button: '产线',
      ButtonTitle: '把整座厂看成一条产线：批次、排队，以及地下层的一次故障对它们意味着什么',
      Title: '产线 —— 凌晨 02:00 的冷水机跳机',
      Intro: '十个工作区，一条工艺路线，每台设备前都有队列。凌晨两点，地下层的一台冷水机跳机：工艺冷却水变热，每台刻蚀机三个腔体中的两个失去温控。第三个腔体照常运行。批次仍在不断到达。你正在值班。',
      Bottleneck: '瓶颈：{{tag}} —— {{n}} 台刻蚀机，每台三个腔体，每天约 {{starts}} 片晶圆投片',
      LoadLabel: '产线负荷',
      Load85: '85% —— 健康的产线',
      Load95: '95% —— 满负荷的产线',
      CallLabel: '通知设施部门',
      CallPrompt: '立即通知 —— 腔体 6 小时后恢复',
      CallLate: '08:00 才通知 —— 腔体 12 小时后恢复',
      ViewLabel: '视图',
      TabScenario: '冷水机跳机',
      TabCurve: '运行曲线',
      Running: '正在运行产线……',
      PolicyReroute: '改道',
      PolicyRerouteNote: '所有批次排队等剩下的那个腔体，先到先做。没人做决定时就是这样。',
      PolicyHold: '暂停投片',
      PolicyHoldNote: '腔体恢复前不再投新批次。',
      PolicyByDate: '按交期',
      PolicyByDateNote: '刻蚀机下一个做离交期最近的批次，不论是否加急。',
      Control: '无故障',
      ControlNote: '同一周，冷水机正常运行。',
      ChartWipTitle: '刻蚀在制批次超出无故障情形的部分',
      ChartWipY: '批次',
      ChartX: '跳机后的小时数',
      ChartCtTitle: '逐日完工批次的周期时间',
      ChartCtY: '小时',
      MarkTrip: '跳机',
      MarkRestore: '恢复',
      CostTitle: '这次故障的代价，对比同一周无故障的情形',
      ColPolicy: '策略',
      ColPeak: '刻蚀在制峰值',
      ColDrain: '队列恢复正常',
      ColDelayed: '被延误的批次',
      ColMeanDelay: '平均延误',
      ColDates: '丢失的交期',
      ColHot: '丢失的加急批次',
      ColChamberHours: '损失的腔体小时',
      DrainNever: '两周内未恢复',
      HoursAfter: '恢复后 {{h}} 小时',
      Hours: '{{h}} 小时',
      CostNote: '在跳机后的两周内逐批次与对照组比较：批次完工晚于无故障情形即算延误，对照组能按期而这里没有即算丢失交期。腔体停机 {{h}} 小时。',
      LittleTitle: '稳态产线上的利特尔法则',
      LittleLine: '跳机前：产线内 {{wip}} 个批次 = {{thr}} 批次/小时 × {{ct}} 小时周期时间（比值 {{ratio}}）。在制品、产出和周期时间不可能各自独立变化。',
      FindingsTitle: '产线告诉我们什么',
      FindingDrain: '腔体几小时就回来了，队列却没有。每个在刻蚀前等过的批次都晚到了下一台设备，后面的批次又晚到了它们的设备，产线只能用富余产能——扣除已有到达之后剩下的那部分——去消化。85% 时还有富余；95% 时几乎没有，同样的六小时要用几天才能消化。',
      FindingByDate: '刻蚀按交期排产在两种负荷下丢的交期都最少，因为"离交期最近"接近"最老的先做"，而这会把批次推出再入式产线，而不是再绕一圈。它的代价只在 95% 时显现：加急批次不再插队，而队列已经很长，于是大多数加急批次失约。85% 时正确的规则，正是 95% 时丢掉加急批次的规则。',
      FindingHold: '暂停投片对刻蚀毫无影响。接下来六小时会堆在那里的批次早已在厂内；你没投的那些批次离刻蚀还有八天。暂停做到的只是让被扣住的批次少了六小时的余量。',
      CurveNote: '健康产线（无故障）的周期时间随负荷的变化。产线半空时平坦、接近纯工艺时间，八十几开始上翘，九十几陡升。85% 和 95% 下的冷水机跳机是从内部看到的这条曲线上的两个点：斜率就是队列一旦形成要多久才能消化。',
      CurveTitle: '周期时间随负荷的变化',
      CurveX: '瓶颈负荷',
      CurveY: '× 工艺时间',
      CurveSeries: '健康产线',
      CurveHere: '当前产线',
      ColLoad: '负荷',
      ColCt: '周期时间',
      ColCtRaw: '× 纯工艺时间',
      ColWip: '产线在制批次',
    },
    finder: {
      Button: '查找设备',
      Placeholder: '输入资产编号、设备名或工艺名……',
      Hint: '<kbd>↑</kbd><kbd>↓</kbd> 选择 · <kbd>Enter</kbd> 前往 · <kbd>Esc</kbd> 关闭',
      Empty: '没有匹配“{{q}}”的设备。',
    },
    place: {
      subfab: {
        Name: '地下设备层（子厂房）',
        Where: '设备层，FAC-06 后方楼梯',
      },
    },
    zone: {
      entry: '入口与规程',
      substrate: '衬底——从硅到晶圆',
      frontend: '前道——把器件做出来',
      metrology: '量测与公用设施',
      interconnect: '平坦化与互连',
      sort: '检测与分选',
      assembly: '装配、封装与测试',
      quantum: '量子器件——同一座厂，另一种产品',
      support: '次厂房与化学品供应',
    },

    station: {

      etch: {
        Name: '电感耦合等离子体刻蚀机',
        Process: '等离子体刻蚀',
        Blurb: '多晶硅栅极刻蚀。完整仿真：反应器输运、鞘层物理、表面动力学。',
      },
      deposition: {
        Name: 'ALD / CVD 沉积集束设备',
        Process: '薄膜沉积',
        Blurb:
          '用 ALD 生长 Al2O3。完整仿真：前驱体输运、受位点限制的表面化学、饱和行为与温度窗口。',
      },
      litho: {
        Name: '浸没式扫描光刻机与涂胶显影联机',
        Process: '光刻',
        Blurb:
          '曝光剂量与焦距决定关键尺寸。完整仿真：由衍射级次合成的空间像、光刻胶化学、' +
          '工艺窗口与光子统计。',
      },
      patterning: {
        Name: '自对准四重图形化单元',
        Process: '多重图形化',
        Blurb:
          '先印粗的，再切两次，最终节距取决于一层膜的厚度，而不是扫描机分辨得出的任何东西。' +
          '完整仿真：侧墙几何、节距行走，以及为什么一个漂亮的平均值会掩盖一个已经坏掉的图形。',
      },
      thermal: {
        Name: '立式炉管与快速热处理',
        Process: '热处理工艺',
        Blurb:
          '氧化、掺杂扩散与激活。完整仿真：Deal-Grove 氧化生长、菲克扩散，以及作为公共账户的热预算。',
      },
      metrology: {
        Name: '椭偏仪与反射仪',
        Process: '量测与统计过程控制',
        Blurb: '漂移检测与控制图。完整仿真：反射率拟合、量具能力，以及扣留批次还是停机的判断。',
      },
      facilities: {
        Name: '气体输送与尾气处理',
        Process: '厂务与安全',
        Blurb:
          '真空、气体输送与尾气处理。完整仿真：流导、抽真空过程，以及区分漏气与腔室吸湿的升压率测试。',
      },
      gowning: {
        Name: '更衣气闸与规程间',
        Process: '洁净室规程',
        Blurb: '在你换好无尘服之前，你就是这栋楼里最大的颗粒源。范围已定义，尚未仿真。',
      },
      crystal: {
        Name: '直拉法单晶炉',
        Process: '晶体生长',
        Blurb: '多晶硅与一颗籽晶长成单晶硅锭。范围已定义，尚未仿真。',
      },
      waferprep: {
        Name: '切片、研磨与抛光线',
        Process: '晶圆制备',
        Blurb: '硅锭变成晶圆，平整度以微米计。范围已定义，尚未仿真。',
      },
      epi: {
        Name: '减压外延反应器',
        Process: '硅外延',
        Blurb:
          '生长器件真正栖身的那一层。完全仿真:Grove 串联的两个生长阻力、来自晶体炉所掺衬底的自掺杂,以及约束配方的滑移与雾状缺陷。',
      },
      clean: {
        Name: 'RCA 湿法清洗台',
        Process: '表面预处理',
        Blurb: '厂里跑得最多、也最不起眼的工序。范围已定义，尚未仿真。',
      },
      implant: {
        Name: '大束流离子注入机',
        Process: '离子注入',
        Blurb: '厂里唯一一台会数自己原子的设备。范围已定义，尚未仿真。',
      },
      cmp: {
        Name: '化学机械抛光机',
        Process: '平坦化',
        Blurb: '正是平坦度让多层金属互连成为可能。范围已定义，尚未仿真。',
      },
      plating: {
        Name: '铜电镀槽',
        Process: '互连',
        Blurb: '铜是镀上去而不是刻出来的，靠三种添加剂才能填满。范围已定义，尚未仿真。',
      },
      inspection: {
        Name: '图形晶圆光学检测与复检',
        Process: '缺陷检测',
        Blurb: '灵敏度与虚报率之争，以及一个缺陷特征的代价。范围已定义，尚未仿真。',
      },
      probe: {
        Name: '晶圆针测与分选',
        Process: '晶圆测试',
        Blurb: '晶圆在这里变成裸片，良率在这里变成一个数字。范围已定义，尚未仿真。',
      },
      dicing: {
        Name: '背面减薄与切割',
        Process: '晶圆减薄与切割',
        Blurb: '薄到能够堆叠，脆到不容忽视。范围已定义，尚未仿真。',
      },
      bonding: {
        Name: '贴片与引线键合机',
        Process: '装配',
        Blurb: '压力、超声功率与时间——一个由曲面构成的工艺窗口。范围已定义，尚未仿真。',
      },
      packaging: {
        Name: '塑封、底部填充与先进封装',
        Process: '封装',
        Blurb: '好裸片在这里因为力学问题变成坏产品。范围已定义，尚未仿真。',
      },
      finaltest: {
        Name: '终测与老化',
        Process: '最终测试',
        Blurb: '测到什么程度算够，是算出来的，不是凭偏好定的。范围已定义，尚未仿真。',
      },
      euv: {
        Name: 'EUV 光刻机（锡光源）',
        Process: 'EUV 光刻',
        Blurb:
          '现代晶圆厂最重要的设备，从头到尾都是量子物理。完整模拟：用薛定谔方程求解锡离子的电离能、产生 13.5 nm 光的等离子体、用传递矩阵计算的钼/硅反射镜，以及在光刻胶里打出粗糙边缘的光电子级联。',
      },
      ebeam: {
        Name: 'CD-SEM 与电子束检测',
        Process: '电子束量测',
        Blurb:
          '测量光学分辨不了的东西。完整模拟：用 THRM-04 的隧穿引擎计算电子枪的 Fowler-Nordheim 场发射、衍射与像差所允许的束斑，以及一条由蒙特卡罗生成、边缘要靠机台判定的线扫描信号。',
      },
      quantum: {
        Name: '低温量子器件测试站',
        Process: '量子比特表征',
        Blurb:
          '同一座厂、同一批设备，做的是另一种产品。完整模拟：用传递矩阵计算约瑟夫森结的氧化层、精确求解 transmon 哈密顿量、频率碰撞良率，以及 EPI-20 生长的硅量子阱中的能谷劈裂。',
      },
      gas: {
        Name: '气瓶间与化学品输送',
        Process: '气体与化学品管理',
        Blurb: '硅烷、砷烷、氢氟酸。在这个站点上出错，代价不是报废那么简单。范围已定义，尚未仿真。',
      },
    },

    assistant: {
      // Suggestion chips, keyed by intent id. The English example stays
      // in the intent as the source of record and is what actually routes.
      topic: {
        'ion-enhanced etching': '离子增强刻蚀',
        'sheath and ion energy': '鞘层与离子能量',
        'ion energy distribution': '离子能量分布',
      },
      example: {
        'etch-qual': '这台刻蚀机通过认定可以跑产品了吗？',
        'cmp-qual': '这台抛光机通过认定可以跑产品了吗？',
        'litho-qual': '这台扫描机通过认定可以曝产品了吗？',
        'thermal-qual': '这台炉子通过认定可以跑产品了吗？',
        selectivity: '我能不能直接把偏压调高来达到速率目标？',
        'cmp-overpolish': '过抛该跑多少？',
        'cmp-speeds': '为什么抛光盘和载具要同速旋转？',
        'cmp-density': '为什么焊盘会下凹而密集阵列不会？',
        'dep-saturation': '我的剂量真的饱和了吗？',
        'dep-window': '这该在什么温度下跑？',
        'dep-purge': '能缩短吹扫换产能吗？',
        'dep-conformality': '为什么我沟槽底部的膜更薄？',
        'dep-uniformity': '为什么我的膜边缘更厚？',
        'fac-conductance': '抽气慢——需要换台更大的泵吗？',
        'fac-leak': '本底压力偏高——是漏气还是放气？',
        'edge-fast': '为什么我的晶圆边缘刻得更快？',
        loading: '同一份配方为什么在另一种产品上跑得更快？',
        sheath: '到底是什么决定了晶圆处的离子能量？',
        endpoint: '怎么知道刻蚀什么时候刻完？',
        'what-if': '把压力调高会怎样？',
        diagnose: '什么都没动——刻蚀为什么变慢了？',
        'litho-dose-focus': 'CD 偏了——是剂量还是焦距？',
        'litho-window': '这个图形有可用的工艺窗口吗？',
        'litho-stochastics': '完全相同的曝光为什么印出来不一样？',
        'metro-trust': '这个膜厚读数能信吗？',
        'metro-capability': '这台量具够格干这活吗？',
        'metro-signal': '控制图报警了——是真的吗？',
        'thermal-budget': '没人动过注入——结为什么移了？',
        'thermal-oxide': '氧化层为什么不长了？',
        'thermal-junction': '怎么把结做浅而不牺牲方块电阻？',
        'thermal-lateral': '推进退火只是把掺杂原子往下推吗？',
        'bond-window': '键合窗口在哪里，两端各会发生什么？',
        'bond-wires': '同一份键合配方，为什么线更多的器件良率更差？',
        'bond-imc': '金属间化合物随时间对焊点做了什么？',
        'clean-longer': '把 SC-1 跑久点晶圆就更干净吗？',
        'clean-queue': 'HF 之后晶圆等多久有关系吗？',
        'clean-metals': 'SC-2 槽到底是干什么用的？',
        'gown-monitor': '监测点说在洁净等级内——我的晶圆干净吗？',
        'gown-garments': '好好穿洁净服真能测出差别吗？',
        'gown-recovery': '扰动之后这个区要多久恢复？',
        'cz-segregation': '晶棒两端的电阻率为什么不一样？',
        'cz-voronkov': '这根晶体最快能拉多快？',
        'cz-oxygen': '晶圆里的氧是从哪来的？',
        'dice-thin': '晶圆能磨多薄才不至于开始碎？',
        'dice-chipping': '能切快一点而不崩到裸片吗？',
        'dice-wheel': '更细的磨轮划得来吗？',
        'euv-dose': '提高剂量要花掉多少每小时晶圆数？',
        'euv-power': '光源功率翻倍，光刻机能快多少？',
        'sem-landing': '为什么不把 CD-SEM 的着陆能量调高来得到更锐的图像？',
        'sem-current': '更大的束流会让 CD 量测重复得更好吗？',
        'cryo-attenuation': '驱动线需要多少衰减？',
        'cryo-frequency': '为什么量子比特做在 5 GHz 而不是 1 GHz？',
        'epi-hotter': '为什么再升温也不再加快生长？',
        'epi-bake': '能缩短 H2 烘烤省点周期吗？',
        'epi-faster': '能直接加大 TCS 让外延长快点吗？',
        'ft-guardband': '保护带的代价是什么？',
        'ft-burnin': '老化值得花那些时间吗？',
        'ft-coverage': '测试时间到底要多久才算够？',
        'gas-purge': '吹扫几个循环才能安全开箱？',
        'gas-detector': '探测器报警来得及让我撤出去吗？',
        'gas-abatement': '排放口出去的到底是什么？',
        'imp-channelling': '我的结为什么比表里查的深？',
        'imp-damage': '剂量到多少注入会把硅打成非晶？',
        'imp-profile': '怎么把结做得更浅？',
        'insp-threshold': '要不要调低阈值多抓些缺陷？',
        'insp-limit': '杀死那颗裸片的缺陷我们为什么没看见？',
        'insp-coverage': '需要扫完整片晶圆吗？',
        'pkg-popcorn': '一颗好裸片为什么在回流时坏了？',
        'pkg-warpage': '封装为什么翘曲，什么时候才要紧？',
        'pkg-mould': '模后固化到底在干什么？',
        'saqp-walk': '为什么我的间隔一宽一窄交替？',
        'saqp-pitch': '最终节距到底由什么决定？',
        'saqp-collapse': '这份配方为什么塌了？',
        'ecd-fill': '我的沟槽里为什么有空洞？',
        'ecd-terminal': '为什么晶圆边缘的铜更厚？',
        'ecd-rate': '要镀多久才能到我要的厚度？',
        'prb-area': '晶粒面积大了良率为什么差这么多？',
        'prb-clustering': '泊松良率模型为什么低估我这条线？',
        'prb-signature': '这些损失是晶圆的还是探针卡的？',
        'wpr-kerf': '晶棒里有多少最后变成了晶圆？',
        'wpr-damage': '要去掉多少硅才能把锯伤除干净？',
        'wpr-flatness': '什么会让晶圆弓弯，为什么要紧？',
      },
      Who: '探究助手',
      OffThread: '实验在主线程之外运行，因此你仍然可以在厂内自由走动。',
      InlineThread: '实验在主线程内运行，运行期间画面可能会短暂卡顿。',
      Placeholder: '就你看到的现象提问……（按 Enter 发送）',
      Run: '运行实验',
      Designing: '正在设计实验……',
      RunningCondition: '正在运行第 {{done}} / {{total}} 组条件 — {{label}}',
      Promise:
        '我给出的每一句结论，都是用真实跑出来的数字拼出来的。如果无法把你的问题变成一个实验，' +
        '我会直说，而不是编一个出来。',
      Hypothesis: '假设',
      Design: '实验设计',
      Measured: '实测',
      Findings: '发现',
      CanInvestigate: '我可以研究的问题',
      ConditionsRun: '已运行的条件',
      Condition: '条件',
      Descend: '↓ 下探到微观尺度 — {{topic}}',
      NextQuestion: '你的下一个问题',
      NoMatch: '没有匹配的实验',
      CannotRun:
        '我还没法把"{{question}}"变成一个我会跑的实验，所以我不会去猜一个答案。我说的每一句话都来自' +
        '真实跑过的仿真，而这个问题我没有可跑的东西。在这台设备上，我能调查的是这些：',
      NoMatchFollowUp: '可以从上面挑一个，或者改用你在晶圆上看到的现象来重新提问。',

      NearestMatch: '没有完全匹配 — 最接近的实验',
      NearestLead:
        '我没法完全按你问的方式跑"{{question}}"。在这台设备上，我会跑的最接近的是"{{example}}"' +
        '——{{summary}}这和你的问题并不相同，所以在采信结果之前，先判断它是否真的回答了你。',
      RunNearest: '就跑这个',
      NearestFollowUp: '如果这不是你的意思，用你在晶圆上看到的现象再说一遍，我重新找。',
    },

    auth: {
      Title: '登录后即可使用助手',
      Why:
        '登录的作用是给这次会话署名——记录你提出的问题、它们变成的实验，以及下次可以接着做的' +
        '工作。厂里其余部分不需要账号：设备、晶圆和求解器对每一个站在车间里的人都开放。',
      Google: '使用 Google 继续',
      Guest: '以访客身份继续',
      SignIn: '登录',
      AsMe: '实名登录',
      Anonymously: '匿名登录',
      MenuWhy: '账号的作用，是让你的工作能延续到下一次。',
      GuestScope: '匿名——仅限本浏览器',
      ConfirmGuestTitle: '确定匿名登录吗？',
      ConfirmGuestBody:
        '匿名账号在退出的那一刻就消失，且无法恢复——在它名下保存的一切都会一并失去。它也不' +
        '存在于任何其他浏览器或电脑上。请只在这台电脑上、只为一次使用而选择它。',
      ConfirmSignOutTitle: '确定退出这个匿名账号吗？',
      ConfirmSignOutBody: '退出后此账号无法恢复，在它名下保存的工作都会丢失。实名登录才能让工作长久保留。',
      Yes: '是',
      No: '否',
      GuestNote:
        '访客账号不带姓名和邮箱。它的用法和已登录账号完全一样，有效期取决于这个浏览器保留多久。',
      Checking: '正在确认当前登录用户……',
      Retry: '重试',
      SignedInAs: '已登录：{{name}}',
      SignedInAsGuest: '已以访客身份登录',
      SignOut: '退出登录',
      GuestName: '访客',
      SignedIn: '已登录',
      ErrNetwork: '没有网络。登录需要联网——厂区和求解器不需要。',
      ErrNotEnabled: '该登录方式尚未在此项目中启用。',
      ErrDomain: '{{host}} 未获授权用于登录。请将它加入 Firebase 的授权域名列表。',
      ErrTooMany: '这台机器尝试次数过多。请等一分钟后再试。',
      ErrPopupBlocked: '浏览器拦截了登录窗口。请为本站点允许弹出窗口，或改走访客入口。',
      ErrUnavailable: '登录不可用——应用无法连接 Firebase。',
    },

    recipe: {
      // Preset display labels. Code-like ones keep their code everywhere.
      presetLabel: {
        // Etch (ETCH-01) recipe codes: rendered without a namespace.
        baseline: 'BASELINE-SI-01',
        lowPressure: '低气压 欠饱和',
        highPressure: '高气压 扩散受限',
        highBias: '高偏压 各向异性',
        smallOpenArea: '低负载产品',
        starvedFlow: '低流量异常',
        facilities: {
          vacNominal: '健康腔体',
          vacThrottled: '流导受限',
          vacLeak: '真实漏气',
          vacDirty: '潮湿腔体',
          vacBiggerPump: '更大的泵，同一根管',
        },
        ale: {
          baseline: '基准-ALE-硅',
          tooEnergetic: '离子能量过高',
          tooGentle: '离子能量过低',
          starvedDose: '剂量低于饱和',
          noPurge: '无吹扫',
          highAspect: '高深宽比',
          nitride: '氮化物',
        },
        bonding: {
          baseline: '基准-金铝键合',
          coldBond: '键合不足',
          overDriven: '过度施压',
          longCure: '延长固化',
          highPinCount: '高引脚数',
        },
        dicing: {
          baseline: '基准-200UM',
          thinStack: '堆叠减薄',
          noRelief: '不做应力释放',
          fastFeed: '快速进给',
          narrowStreet: '窄切割道',
        },
        packaging: {
          baseline: '基准-BGA',
          thinMould: '薄模封盖',
          bigBody: '大封装体',
          floorLifeBlown: '干燥包装外放置',
          hotReflow: '高温回流',
        },
        finaltest: {
          baseline: '基准-商规',
          noBurnIn: '不做老化',
          automotive: '车规',
          noGuardBand: '无保护带',
          shortTest: '短测试',
        },
        clean: {
          baseline: '基准-RCA',
          noHf: '不做 HF 收尾',
          queueBlown: '排队超时',
          aggressive: '激进 SC-1',
          shortClean: '短清洗',
        },
        gowning: {
          baseline: '基准-ISO5-洁净区',
          noHood: '没戴头罩',
          wrongOrder: '穿衣顺序错误',
          crowded: '人员拥挤',
          straightThrough: '径直穿过气闸',
          lowFlow: '换气减少',
          longExposure: '晶圆敞置',
        },
        cmp: {
          baseline: '基准-铜大马士革',
          underPolished: '无过抛',
          overPolished: '重度过抛',
          mismatchedSpeeds: '转速失配',
          hardPress: '高下压力',
        },
        crystal: {
          baseline: '基准-CZ300-硼',
          perfectSilicon: '完美硅',
          antimony: '锑衬底',
          pulledTooFast: '提拉过快',
          tightSpec: '窄电阻率规格',
          magneticCz: '磁控直拉',
        },
        epi: {
          epiDevice: '器件层',
          epiSlow: '动力学探针',
          epiThin: '薄层快长',
        },
        gas: {
          baseline: '基准-砷化氢面板',
          lazyPurge: '偷懒吹扫',
          silaneRelease: '硅烷泄漏',
          chlorinePool: '氯气，探测器在天花板',
          dilutedArsine: '稀释砷化氢',
          slowDetector: '响应迟缓的探测器',
          ammoniaHabit: '氨——坏习惯',
        },
        implant: {
          baseline: '基准-硼源漏',
          zeroTilt: '零倾角',
          shallow: '浅结延伸',
          deepWell: '深阱',
          hotImplant: '热注入',
        },
        patterning: {
          baseline: '基准-SAQP-40',
          pitchWalk: '节距漂移',
          overTrimmed: '芯轴过度修整',
          thickSpacer: '侧墙过厚',
          doubleOnly: 'SADP（一遍）',
          aggressive: '激进——32 nm 节距',
        },
        plating: {
          baseline: '基准-铜大马士革',
          noAccelerator: '无加速剂',
          noSuppressor: '无抑制剂',
          thinSeed: '薄种子层',
          hotPlate: '高电流',
          overPlated: '过度电镀',
          highAspect: '高深宽比',
        },
        inspection: {
          baseline: '基准-暗场',
          hotScan: '热扫描',
          coldScan: '冷扫描',
          shortWave: '更短波长',
          sampled: '抽样扫描',
        },
        probe: {
          baseline: '基准-分选',
          bigDie: '大裸片',
          poissonForecast: '泊松预测',
          heavyCluster: '重度成簇',
          wideEdge: '宽边缘排除',
        },
        thermalBaseline: '栅氧 + RTA',
        thermalWetField: '湿法场氧',
        thermalFurnaceAnneal: '炉管退火',
        thermalShallowJunction: '浅结',
        thermalArsenicSd: '砷源漏',
        waferprep: {
          baseline: '基准-300MM',
          thinWire: '细锯线',
          singleSided: '单面抛光',
          shortEtch: '短腐蚀',
          shortPolish: '短抛光',
          coarseSaw: '粗锯',
          thinWafer: '薄晶圆',
        },
      },
      knob: {
        bedLoadFraction: '混床已用寿命',
        uvIntensity: 'UV 氧化灯输出',
        pouTempC: '取样温度',
        inletTocPpb: 'RO 来水 TOC',
        modifySeconds: '氯吸附剂量',
        modifyPressureMtorr: '改性步压力',
        purgeSeconds: '吹扫',
        ionEnergyEv: '离子能量',
        removeSeconds: '移除步时间',
        ionFlux: '离子通量',
        forceGf: '键合压力',
        ultrasonicMw: '超声功率',
        bondMs: '键合时间',
        stageTempC: '载台温度',
        cureMinutes: '环氧固化时间',
        cureTempC: '固化温度',
        wiresPerDie: '每颗裸片打线数',
        targetThicknessUm: '晶圆目标厚度',
        finalGrit: '精磨轮粒度',
        feedRateMmS: '切割进给速度',
        streetWidthUm: '切割道宽度',
        diePerWafer: '每片晶圆裸片数',
        mouldThicknessUm: '模封盖厚',
        substrateThicknessUm: '基板厚度',
        mouldTempC: '模封温度',
        postCureMinutes: '模后固化',
        floorLifeHours: '回流前车间寿命',
        humidityPct: '环境湿度',
        reflowPeakC: '回流峰值温度',
        bodySizeMm: '封装体尺寸',
        useTempC: '规定使用温度',
        burnInTempC: '老化温度',
        burnInHours: '老化时长',
        hotTestC: '高温测试',
        coldTestC: '低温测试',
        guardBandFraction: '保护带',
        testSeconds: '单颗测试时间',
        lotSize: '批量',
        sc1Minutes: 'SC-1 时间',
        sc1TempC: 'SC-1 温度',
        megasonicW: '兆声功率',
        sc2Minutes: 'SC-2 时间',
        hfSeconds: 'HF 浸蚀时间',
        hfDilution: 'HF 稀释比',
        queueMinutes: 'HF 后等待时间',
        incomingParticlesCm2: '来料颗粒',
        incomingMetal: '来料金属沾污',
        occupants: '区内人数',
        activity: '活动强度',
        airChangesPerHour: '换气次数',
        filterCoverage: '滤网覆盖率',
        filterEfficiency: '滤网效率',
        bayVolumeM3: '房间容积',
        downflowMs: '下吹流速',
        airlockMinutes: '气闸停留时间',
        waferExposureMinutes: '晶圆暴露时间',
        particleSizeUm: '规定粒径',
        downForceKPa: '下压力',
        platenRpm: '抛光盘转速',
        carrierRpm: '载具转速',
        slurryFlowMlMin: '研磨液流量',
        overpolishPct: '过抛量',
        overburdenNm: '来料余量厚度',
        incomingStepNm: '来料台阶高度',
        meltDopingCm3: '熔体掺杂',
        chargeKg: '装料量',
        diameterMm: '晶棒直径',
        pullRateMmMin: '提拉速率',
        gradientKCm: '轴向温度梯度',
        crucibleRpm: '坩埚转速',
        crystalRpm: '晶体转速',
        magneticFieldT: '磁场强度',
        resistivityTolerance: '电阻率容差',
        targetResistivity: '目标电阻率',
        cylinderPercent: '钢瓶浓度',
        purgeCycles: '吹扫循环数',
        purgeRatio: '单循环稀释比',
        leakRateSccm: '泄漏率',
        detectorThreshold: '报警阈值',
        detectorResponseS: '探测器响应时间',
        energyKeV: '注入能量',
        dose: '注入剂量',
        tiltDeg: '倾角',
        twistDeg: '扭转角',
        beamCurrentMa: '束流',
        waferTempC: '晶圆温度',
        mandrelPitchNm: '芯轴节距',
        mandrelCdNm: '芯轴线宽（光刻后）',
        mandrelTrimNm: '芯轴修整量',
        spacer1Nm: '侧墙 1 厚度',
        spacer2Nm: '侧墙 2 厚度',
        spacerOveretch: '侧墙过刻量',
        currentDensityMaCm2: '电流密度',
        seconds: '电镀时间',
        seedThicknessNm: '种子层厚度',
        trenchWidthNm: '沟槽宽度',
        trenchDepthNm: '沟槽深度',
        suppressor: '抑制剂',
        accelerator: '加速剂',
        leveler: '整平剂',
        copperGL: '硫酸铜浓度',
        acidGL: '酸浓度',
        wavelengthNm: '波长',
        thresholdSigma: '检测阈值',
        pixelNm: '像素尺寸',
        scanCoverage: '晶圆扫描覆盖率',
        reviewSample: 'SEM 复检抽样',
        designRuleNm: '设计规则',
        incomingDensityCm2: '来料缺陷密度',
        dieAreaCm2: '裸片面积',
        edgeExclusionMm: '边缘排除区',
        clusterAlpha: '缺陷聚集度',
        touchdownsPerClean: '两次清针间触点数',
        parametricGuardBand: '参数保护带',
        wireDiameterUm: '焊线线径',
        sawGrit: '锯切粒度',
        lapGrit: '研磨粒度',
        lapRemovalUm: '单面研磨去除量',
        etchRemovalUm: '单面腐蚀去除量',
        polishPressureKPa: '抛光压力',
        polishRpm: '抛光转速',
        polishMinutes: '抛光时间',
        ingotLengthMm: '晶棒可用长度',
        bakeMinutes: 'H2 烘烤时间',
        epiTempC: '生长温度',
        tcsTorr: 'TCS 分压',
        epiMinutes: '生长时间',
        epiDopingCm3: '外延层掺杂',
        h2Slm: '氢气载气',
        pressureMtorr: '腔室压力',
        sourcePowerW: '源射频功率',
        biasPowerW: '偏压射频功率',
        flowSccm: '总气体流量',
        cf4Fraction: 'CF4 占比',
        wafertempC: '晶圆温度',
        openAreaFraction: '开口率',

        // 光刻。
        doseMjCm2: '曝光剂量',
        focusNm: '离焦量',
        pitchNm: '节距',
        pebBlurNm: 'PEB 扩散长度',
        resistContrast: '光刻胶对比度',
        developThreshold: '显影阈值',

        // 热处理工艺。
        oxideTempC: '氧化温度',
        oxideMinutes: '氧化时间',
        implantDose: '注入剂量',
        implantKeV: '注入能量',
        annealTempC: '退火温度',
        annealSeconds: '退火时间',

        // 厂务。
        volumeL: '腔室容积',
        pumpSpeedLs: '泵抽速',
        lineDiameterCm: '管路直径',
        lineLengthCm: '管路长度',
        leakThroughput: '漏气流量',
        outgassingInitial: 't=0 时的放气量',
        outgassingTau: '放气衰减时间',
      },
      preset: {
        facilities: {
          vacNominal: '短粗管线，密封良好。流导不是限制，也没有漏气。',
          vacThrottled: '同一台泵接在 5 厘米粗、两米长的管线后面。腔体永远看不到泵的抽速。',
          vacLeak: '已抽了一整夜的腔体上有一道被夹住的门封，腔壁已干，只剩漏气。本底压力卡住不动，压升曲线永远不弯。注意放气很低：今早才打开过的腔体是没法检漏的。',
          vacDirty: '开盖暴露大气后带着潮气重新装好。放气很重，但最终能抽走。',
          vacBiggerPump: '在受限管线上换三倍大的泵——昂贵的错误答案。',
        },
        ale: {
          baseline:
            '认证的 ALE：氯剂量饱和，氩离子 28 eV——窗口正中。每循环约 0.3 nm，比同一腔室里的 RIE 配方慢两个数量级。',
          tooEnergetic:
            '把能量提到 60 eV 来提速。已越过衬底溅射阈值，未改性的硅也在被连续移除。更快了，但不再是 ALE——协同度数字会比刻蚀轮廓更早说明这一点。',
          tooGentle:
            '12 eV。低于改性层的阈值，氯吸附上去了，却什么也下不来。腔室看起来一切正常。',
          starvedDose:
            '氯只给半秒。覆盖不完整，于是每循环刻蚀量取决于剂量到达晶圆各处的多少——而这正是当初改用 ALE 想摆脱的均匀性问题。',
          noPurge:
            '两个半循环重叠，部分刻蚀变成连续进行。仍在刻，但已不是逐层刻。',
          highAspect:
            '同一配方用在 50:1 的结构上。移除半步没问题——离子是定向的——但中性剂量必须到达底部，在这个深宽比下它到不了。',
          nitride:
            '同一配方，另一种膜。氯化学并不通用——氮化物更难改性，每循环刻蚀量下降，配方需要重新认证。',
        },
        bonding: {
          baseline:
            '认证的热超声金球键合，铝焊盘。',
          coldBond:
            '超声功率减半。氧化层活了下来，于是界面的大部分从未真正焊上。',
          overDriven:
            '功率和压力一起加大，把焊点硬压出来。焊上了，然后继续往硅里压。',
          longCure:
            '200 C 下固化四小时以求保险。金属间化合物全程在长，而且之后也不会变薄回去。',
          highPinCount:
            '一颗裸片上 480 根线。看起来很漂亮的单线良率，取到 480 次方就不漂亮了。',
        },
        dicing: {
          baseline:
            '认证流程：细磨轮、应力释放、适中进给的刀片切割。',
          thinStack:
            '为堆叠封装减薄到 50 um。下游的每一步都因此更脆。',
          noRelief:
            '磨完直接切。每片省 45 秒，把磨削损伤留在片内——这是一个强度问题，要到模压机那里才有人看见。',
          fastFeed:
            '为产能把进给翻倍。崩边随它的平方根增长。',
          narrowStreet:
            '切割道缩到 40 um 好多排裸片。锯缝和崩边可没跟着变小。',
        },
        packaging: {
          baseline:
            '认证的键合线 BGA：模封与基板应力平衡，干燥包装，标准回流。',
          thinMould:
            '为限高槽位把模封盖减到 250 um。叠层严重失衡，随之翘曲。',
          bigBody:
            '40 mm 的封装体。翘曲随跨度的平方增长，所以这不是一个小改动。',
          floorLifeBlown:
            '回流前在 70% 湿度的车间放了三周。袋子上印的时钟不是装饰。',
          hotReflow:
            '峰值推到 268 C 保证润湿。所有与吸湿相关的问题都会加重。',
        },
        finaltest: {
          baseline:
            '125 C 老化 48 小时，高低温测试，10% 保护带。',
          noBurnIn:
            '为产能整个跳过。便宜、快，而且每一颗早夭品都会发到客户手里。',
          automotive:
            '150 C 下 168 小时，宽保护带。在这里漏检就是召回，宁枉勿纵反而便宜。',
          noGuardBand:
            '量在限内就出货。良率最高，而每一次漏检都是一次退货。',
          shortTest:
            '为产能把测试时间砍到一秒。覆盖率跟着掉。',
        },
        clean: {
          baseline:
            '完整 RCA，短 HF 收尾，20 分钟内进炉。',
          noHf:
            '把化学氧化层留在片上。放在想要它的沉积之前是对的；放在栅氧之前是错的。',
          queueBlown:
            'HF 收尾之后，晶圆搁了四个小时。自然氧化层长回来了，这次清洗等于白做。',
          aggressive:
            '80 C 三十分钟，兆声开满。颗粒几乎清零，换来一个粗糙度可以测出来的表面。',
          shortClean:
            '为产能只做两分钟 SC-1。大部分颗粒还在。',
        },
        gowning: {
          baseline:
            '四个人，全套洁净服，每小时 500 次换气。规程照本执行，也是下游每台设备默认的数字。',
          noHood:
            '少穿一件。丢掉的不是五分之一的防护——是头罩贡献的整个倍数，因为洁净服的作用是相乘的。',
          wrongOrder:
            '每件都穿了，顺序全不对。衣服是齐的，但在穿的过程中已经被污染了。',
          crowded:
            '装机进来十二个人。发尘量随人数线性增长，而通风什么都没变。',
          straightThrough:
            '不停留。气闸存在的意义就是吹掉新洁净服正在脱落的东西；径直走过去等于跳过它唯一的作用。',
          lowFlow:
            '为省电把风速降到 150 ACH。浓度等于产生除以通风，所以这和把人数增至三倍效果完全一样。',
          longExposure:
            '一批晶圆在台面上敞开放了半小时。浓度没变，完全在级别之内；只是晶圆收集了三十分钟而不是两分钟。',
        },
        cmp: {
          baseline:
            '认证的铜抛光：盘与载具转速匹配，20% 过抛。',
          underPolished:
            '端点信号一动就停。在完美均匀的设备上勉强撑得住；在任何真实设备上，抛得慢的地方仍有金属把线路桥接起来——那是短路，也就是一颗死片。',
          overPolished:
            '把过抛翻倍以确保清干净。每条宽线都因此下凹。',
          mismatchedSpeeds:
            '载具转速只有盘的一半。相对速度在晶圆上不再均匀，出现一个压力修正消不掉的径向特征。',
          hardPress:
            '为产能把压力加到 40 kPa。更快，而每一种缺陷机制都随它同步放大。',
        },
        crystal: {
          baseline:
            '硼掺杂，150 kg，300 mm。以 1.0 mm/min 对着 30 K/cm 的梯度提拉——Voronkov 窗口的空位一侧，量产硅大多住在这里。',
          perfectSilicon:
            '把拉速降到正好坐在 Voronkov 比值上：空位和自间隙原子谁也不占优。牺牲的是生长速度、也就是钱，这是栅氧客户付钱买的东西。',
          antimony:
            '重掺 n+ 衬底。k = 0.023，熔体富集近四十倍，晶棒大部分够不着任何窄电阻率窗口。这是偏析最残酷的样子。',
          pulledTooFast:
            '梯度不变，拉到每分钟两毫米半。越过潜热上限：界面保持不了平面，晶体失去单晶结构。',
          tightSpec:
            '同一根晶体，容差收紧到百分之五。生长什么都没变，而晶棒的大部分卖不出去了——规格就是良率。',
          magneticCz:
            '0.3 T 磁场压制熔体对流，到达界面的坩埚氧大幅减少。低氧硅——同时也失去了内吸杂这条退路。',
        },
        epi: {
          epiDevice:
            '生产点：深入传输平台区，鼓泡器决定速率，灯的误差被原谅。在 CZ-07 长出的任何衬底上长四微米轻掺硅。',
          epiSlow:
            '降到 950 °C 后表面反应成为瓶颈，生长速率就是一支温度计——这是把气路问题和温度问题分开的那个配方。',
          epiThin:
            'TCS 开满，一分钟不到长一微米。有意贴着喷溅极限跑——盯着颗粒数。',
        },
        gas: {
          baseline:
            '砷化氢面板，开箱前十个循环、每循环十比一。认证规程，以及它背后的算术。',
          lazyPurge:
            '同样十个循环，但每次只抽到一半压力。十个 0.5 的循环是一千倍；十个 0.1 的循环是一百亿倍。同样的规程，同样的时间，差七个数量级。',
          silaneRelease:
            '纯硅烷漏进气瓶间。自燃气体，所以火是眼前的问题；如果没点着，要紧的才是暴露限值。',
          chlorinePool:
            '氯气密度是空气的两倍，会在地面积聚。下面这个完全混匀的数字偏乐观，而装在天花板上的探测器量错了地方。',
          dilutedArsine:
            '1% 砷化氢氢气稀释——通常就是这么买的。少吹两个数量级，最后的暴露限值一分不少。',
          slowDetector:
            '报警点设在暴露限值两倍、响应两分钟的探测器。它会报警。这个工具要问的是：报得来得及吗。',
          ammoniaHabit:
            '氨在远低于限值时你就闻到了，于是它教会你鼻子是个探测器。砷化氢在任何危险浓度下都没有气味警告。',
        },
        implant: {
          baseline:
            '源漏硼注入：30 keV，3 × 10¹⁵，七度倾角避开沟道效应。',
          zeroTilt:
            '正对沟道打下去。峰值落在该落的地方，而相当一部分剂量钻到了三倍深的地方。',
          shallow:
            '5 keV 的 BF2。分子重、能量低、非常浅——每个离子造成的损伤也大得多。',
          deepWell:
            '200 keV 的磷。够深，也不可避免地弥散——离散度是射程的 38%。',
          hotImplant:
            '晶圆保持 300 C，晶格边注入边自修复。留给后面退火的损伤更少，也就是热预算花得更少。',
        },
        patterning: {
          baseline:
            '160 nm 芯轴节距两次减半到 40 nm——任何 193 nm 光刻机都解析不了的节距。修整量已调到两种间隔正好相等。',
          pitchWalk:
            '芯轴修整偏了 4 nm——该是 10 修成了 14。平均节距仍然正好 40 nm，平均间隔仍然正好 20 nm，CD-SEM ' +
            '的所有汇总数字都在目标上。而每隔一条线就长错了地方。',
          overTrimmed:
            '修到没了。侧墙还没沉积芯轴就已消失，没有可依附的侧壁。',
          thickSpacer:
            '160 nm 节距上沉 30 nm 的 ALD。侧墙相接，图形连成一整块——这是几何，不是工艺。',
          doubleOnly:
            '第一遍就停：80 nm 节距，只有一种漂移方式而不是两种。密度减半，能出错的地方也少得多。',
          aggressive:
            '128 nm 芯轴节距配 16 nm 侧墙，做 32 nm 的最终节距。处处更紧，修整窗口随之收窄。',
        },
        plating: {
          baseline:
            '认证镀液，20 mA/cm² 一分钟，填 60 nm 沟槽。自底向上填满，留下余量交给 CMP-11 去除。',
          noAccelerator:
            '去掉加速剂就没有 CEAC 正反馈：沟槽变成保形沉积，槽口先合拢，结构里留下空洞。这个添加剂不是锦上添花。',
          noSuppressor:
            '没有什么再压制平场和槽口，增益塌向一，侧壁赢下这场赛跑。',
          thinSeed:
            '10 nm 种子层给沟槽腾了地方——也让方块电阻翻了四倍，端子效应把晶圆中心饿着了。',
          hotPlate:
            '50 mA/cm² 把周期时间砍半。越过极限电流后镀液供铜不及，镀层烧焦。',
          overPlated:
            '镀三分钟而不是一分钟。沟槽早就满了；之后长的全是余量，而每一纳米余量都是 CMP-11 的下凹时间。',
          highAspect:
            '20 nm 宽、300 深的沟槽。超填充在这里反而更强——底面积收缩更猛——但侧壁要合拢的距离也近得多。',
        },
        inspection: {
          baseline:
            '认证的量产扫描：355 nm 暗场，4 sigma，全片。',
          hotScan:
            '阈值降到 2 sigma，什么都要找出来。确实找到更多——也把复检队列埋进了表面粗糙度里。',
          coldScan:
            '阈值提到 9 sigma，报告干净。报告是干净了；晶圆没有。',
          shortWave:
            '266 nm 换下 355 nm。灵敏度随波长的三分之二次幂变化，所以这是 20% 的收益，不是 25%。',
          sampled:
            '为产能只扫五分之一的晶圆。一切按比例缩水，包括置信度。',
        },
        probe: {
          baseline:
            '认证分选：0.59 cm² 裸片，3 mm 边缘排除，负二项分布 alpha 3。',
          bigDie:
            '同一条线上 2 cm² 的裸片。良率随面积指数下降，这就是为什么裸片尺寸是设计者做的最贵的决定。',
          poissonForecast:
            '同一片晶圆改用泊松预测。系统性偏悲观，因为真实缺陷成簇，而成簇的缺陷把自己浪费在本来就死了的裸片上。',
          heavyCluster:
            '一道划伤或一次不当搬运：alpha 降到 0.8。缺陷数没变，良率反而更好。',
          wideEdge:
            '排除 8 mm 以避开边缘失效。顺带也扔掉了好裸片。',
        },
        waferprep: {
          baseline:
            '认证产线：120 µm 线锯，双面研磨与抛光，成品 775 µm。晶棒仍有五分之一以磨浆的形式离开。',
          thinWire:
            '70 µm 细线省锯缝。确实省了——它也会游走，因为同样张力下更细的线切得更松。片数更多，平整度更差。',
          singleSided:
            '只加工正面。工艺时间省一半，晶圆却会弓弯，因为背面损伤应力再没有东西平衡它。',
          shortEtch:
            '化学腐蚀砍到一微米。它是唯一只除损伤、不添损伤的一步，砍掉它就把研磨损伤留在了晶圆里——而后面的抛光够不到那么深。',
          shortPolish:
            '抛光砍到两分钟。损伤早就除净了——受损的是表面光洁度，而一片发雾的晶圆是光刻机对不上焦的晶圆。',
          coarseSaw:
            '#250 的线锯切得更快，留下的损伤深过研磨所能触及。后面的工步是按更细的锯配的，现在对不上了。',
          thinWafer:
            '成品 400 µm，为堆叠器件准备。每根晶棒出更多片，用少得多的硅扛同样的弓弯。',
        },
        baseline:
          '已认证的窗口中心配方：204 nm/min，不均匀度 4.3%，选择比 3.7:1。从这里开始。',
        lowPressure:
          '压力降到 6 mTorr：116 nm/min，不均匀度 10.9%。腔室里能用来产生氟的气体太少，' +
          '需求超过供给（Da 1.15），最先接触气体的边缘因此快了 19%。',
        highPressure:
          '压力升到 90 mTorr：201 nm/min，不均匀度 8.6%。氟很充足（Da 0.20），但扩散系数随 1/p 变化，' +
          '来不及送到晶圆中心。又是边缘偏快，13%——原因相反，特征相同。',
        highBias:
          '偏压加到 350 W：227 nm/min，但不均匀度 20%，选择比掉到 1.3:1。多出来的需求把刻蚀推进了' +
          '输运受限区（Da 1.22）。这是追速率最不该走的路。',
        smallOpenArea:
          '开口率只有 4%：308 nm/min，配方没动却快了一半，因为争夺同样这些氟的硅少了很多。' +
          '均匀性出于同样的原因改善到 1.8%。这就是负载效应，也是同一份配方换个产品就会漂移的原因。',
        starvedFlow:
          '质量流量控制器从 200 sccm 降到 20：崩到 72 nm/min，不均匀度 28%，78% 的进气被解离，' +
          'Da 超过 2。氟的总量由 MFC 放进来多少决定，此时它已经成了约束条件。',

        // 光刻。
        lithoBaseline: '在 180 nm 节距下调剂量到目标尺寸，最佳焦距。对比度 0.97，NILS 7.1，LER 2.4 nm。',
        lithoDefocused: '离焦 90 nm。对比度跌到 0.42，线宽严重偏离目标。',
        lithoUnderdosed: '20 mJ/cm²。线印得太宽——光少，去掉的胶就少。',
        lithoTightPitch: '已经超出常规照明能好好应付的范围：对比度 0.57，窗口随之收窄。',
        lithoEuvFine: '把光源也换成 EUV。印得出来，但只靠一小部分光子。',

        // 热处理工艺。
        thermalBaseline: '1000 °C 干氧栅氧，再用 30 s 快速退火激活一次浅硼注入。',
        thermalWetField: '1050 °C 水汽通两小时。厚得多，也快得多——代价是花掉大量热预算。',
        thermalFurnaceAnneal: '同一次注入，但改成炉管退火 30 分钟，而不是 RTP 里的 30 秒。',
        thermalShallowJunction: '低能量注入配上短促的高温尖峰退火——这是做浅结的现代配方。',
        thermalArsenicSd: '用砷代替硼。原子重得多，所以几乎不动——而这正是重点。',

        // 厂务：真空系统配置。
        vacNominal: '短而粗的管路，密封良好。流导不是瓶颈，也没有漏。',
        vacThrottled: '同一台泵，接在 5 cm 粗、两米长的管路后面。腔室根本感受不到泵的抽速。',
        vacLeak:
          '腔门密封圈被夹伤，而这台腔已经抽了一整夜，所以腔壁是干的，剩下的只有漏。' +
          '本底压力卡住不动，升压率曲线永远不弯。注意放气量很低：今天早上刚开过的腔，是查不了漏的。',
        vacDirty: '开腔见了大气，回装时还带着潮气。放气很重，但最终还是能抽掉。',
        vacBiggerPump: '在那条被卡住的管路上换一台三倍大的泵——这是又贵又错的答案。',
      },
    },

    chamber: {
      maint: {
        chamberClean: {
          Label: '腔室清洗与预处理',
          Detail:
            '湿法清洗、重新装配，再跑预处理片。重置腔壁状态，连带解决沉积物造成的匹配器失调。' +
            '代价是数小时的设备可用时间。',
        },
        pumpService: {
          Label: '维护泵组',
          Detail: '恢复本底真空度和升压率。设备停机一个班次。',
        },
        replaceFocusRing: {
          Label: '更换聚焦环',
          Detail: '恢复边缘电场整形。通常在计划内清洗时一并完成。',
        },
        matchTune: {
          Label: '重新调谐射频匹配器',
          Detail:
            '不开腔就能恢复输入功率——但如果根因是沉积物，它很快就会再次漂移。这是治标不治本。',
        },
      },
    },

    tool: {
      hmi: {
        Qubit: '量子比特',
        WafersPerHour: '片/小时',
        Yield: '良率',
        WorkZone: '工作区',
        Wires: '每颗线数',
        Welded: '焊合面积',
        WarpUm: 'µm 翘曲',
        Usable: '可用长度',
        Uniformity: '不均匀性',
        Thickness: '厚度',
        Surface: '表面',
        Stack: '排气口',
        Species: '注入离子',
        Screened: '筛除率',
        Repeatability: '重复性',
        Removal: '去除率',
        Regime: '控制区',
        Recovery: '自净时间',
        Purge: '吹扫',
        PumpSpeed: '抽速',
        PitchWalk: '节距漂移',
        PctYield: '% 良率',
        OxideCapped: '氧化层封盖',
        Overkill: '误杀率',
        Nuisance: '误报率',
        NmProbe: 'nm 探针',
        NmPitch: 'nm 节距',
        NmOxide: 'nm 氧化层',
        Moisture: '含湿量',
        LotHere: '批次在机',
        Landing: '着陆能量',
        HfLast: 'HF-last',
        Growth: '生长时间',
        GoodWafers: '合格晶圆',
        GoodDie: '合格芯片',
        Gain: '自底向上增益',
        EscapesPpm: 'ppm 漏放',
        Dose: '剂量',
        Cycles: '循环数',
        Contrast: '对比度',
        Channelled: '沟道比例',
        Capture: '捕获率',
        Budget: '热预算',
        Bow: '弯曲度',
        AcrossDie: '芯片内起伏',
      },
      history: {
        word: {
          map_clustered: '晶圆图呈团簇分布',
          map_radial: '晶圆图呈径向分布',
          map_centre: '晶圆图呈中心失效',
          map_edge: '晶圆图呈边缘失效',
          map_uniform: '晶圆图呈均匀分布',
          attach_void: '贴片空洞',
          intermetallic: '金属间化合物',
          cratered_pad: '焊盘弹坑',
          lifted_ball: '焊球脱起',
          trench_filled: '沟槽填满',
          trench_seam: '沟槽有缝隙',
          trench_void: '沟槽有空洞',
          lattice_damaged_but_crystalline: '晶格受损但仍为晶态',
          layer_amorphised: '表层已非晶化',
          transport_limited: '输运控制',
          transition_limited: '过渡区',
          reaction_limited: '反应控制',
          interstitial_rich: '富间隙原子',
          perfect: '完美晶体',
          vacancy_rich: '富空位',
          arsenic: '砷',
          antimony: '锑',
          phosphorus: '磷',
          boron: '硼',
        },
        EtchedCleared: '，晶圆 {{pct}}% 已刻穿至停止层',
        Etched: '刻蚀 {{s}} s，速率 {{rate}} nm/min，不均匀性 {{nu}}%，侧壁 {{angle}}°：膜内 CD {{cd}} nm',
        LithoFailed: '——部分曝光场未成像',
        LithoExposedEuv: '以 13.5 nm 曝光，{{dose}} mJ/cm²，节距 {{pitch}} nm：CD {{cd}} nm，LER {{ler}} nm ' +
          '3σ，场内变化 {{range}} nm',
        LithoExposed: '以 193 nm 曝光，{{dose}} mJ/cm²，节距 {{pitch}} nm：CD {{cd}} nm，LER {{ler}} nm ' +
          '3σ，场内变化 {{range}} nm',
        Cooled: '冷却至 10 mK：结 R·A {{ra}} Ω·µm²，E_J {{ej}} GHz，电容 {{cap}} fF，transmon f₀₁ ' +
          '{{f01}} GHz',
        SemMeasured: '显影后 CD {{cd}} nm，节距 {{pitch}} nm，着陆能量 {{ev}} eV，探针 {{probe}} nm',
        EuvExposedPlain: '以 13.5 nm 曝光',
        EuvExposed: '以 {{dose}} mJ/cm² 曝光，节距 {{pitch}} nm：CD {{cd}} nm，LER {{ler}} nm 3σ，随机失效 ' +
          '{{failures}} 处',
        Tested: '在 {{temp}} °C 下老化 {{hours}} h 并测试：出货 {{shipped}}，误杀良品 {{overkill}}，漏放不良品 ' +
          '{{escaped}}',
        MouldedCracked: '（{{pct}}% 因减薄损伤开裂）',
        MouldedPopcorned: '，爆米花开裂',
        Moulded: '塑封并回流：翘曲 {{um}} µm，进 {{die}} 颗芯片，出 {{packages}} 个封装',
        BondedFailure: '（{{mode}}）',
        Bonded: '每颗 {{wires}} 根线，剪切力 {{gf}} gf，焊合 {{welded}}%——进 {{before}} 颗，出 {{after}} 颗',
        DicedChipping: '，崩边越过切割道',
        Diced: '减薄至 {{um}} µm 并切割：{{die}} 颗芯片，{{mpa}} MPa，m = {{m}}',
        SaqpWalk: '——平均间距 {{nm}} nm，每隔一条线就错位',
        Saqp: 'SAQP {{mult}}×：节距 {{from}} → {{to}} nm，线宽 {{cd}} nm，节距漂移 {{walk}} nm',
        SaqpCollapsed: 'SAQP 坍塌：{{reason}}',
        Sorted: '已分选：良率 {{yield}}%，探测 {{probed}} 颗中 {{good}} 颗合格，{{signature}}',
        Scanned: '以 {{nm}} nm 扫描：报告 {{density}}/cm²（误报 {{nuisance}}%），捕获真实缺陷的 {{capture}}%',
        RcaOxideCapped: '，氧化层封盖',
        RcaHfLast: '，HF-last，排队 {{min}} min（收益已回吐 {{pct}}%）',
        Rca: 'RCA 清洗：去除 {{pct}}% 颗粒，剩余 {{left}}/cm²',
        Exposure: '在 ISO {{iso}} 工作区敞开 {{min}} min（{{zone}}/m³，房间读数 {{room}}/m³）：+{{adders}} ' +
          '颗粒/cm²',
        PlatedOverburden: '，过镀层 {{over}} nm，留给 CMP-11 的台阶 {{step}} nm，不均匀性 {{nu}}%',
        PlatedVoid: '（空洞 {{pct}}%）',
        Plated: '电镀 {{nm}} nm，增益 {{gain}}×：{{fill}}',
        PolishedWorst: '，最差处为 {{region}}（碟形凹陷 {{dishing}} nm，侵蚀 {{erosion}} nm）',
        PolishedBridging: '，金属仍在桥连',
        Polished: '抛光 {{s}} s，芯片内起伏 {{nm}} nm',
        AldParasitic: '，其中 {{pct}}% 为寄生 CVD',
        Ald: '{{cycles}} 个 ALD 循环，{{nm}} nm，不均匀性 {{nu}}%',
        AnnealOxide: '，生长氧化层 {{nm}} nm',
        AnnealFresh: '（新片应为 {{xj}} nm）',
        Anneal: '退火循环：xj {{xj}} nm',
        ImplantChannelled: '，{{pct}}% 发生沟道效应，深达 {{nm}} nm',
        Implant: '{{species}}，{{kev}} keV，{{dose}}/nm²：Rp {{rp}} nm，{{lattice}}',
        EpiSpitting: '，带入气相颗粒',
        EpiSlip: '，边缘出现滑移',
        EpiSeam: '，{{um}} µm 自掺杂过渡区',
        Epi: '外延层：{{um}} µm，掺杂 {{doping}}/nm³，{{regime}}',
        Sliced: '切片、研磨、腐蚀并抛光：{{um}} µm，TTV {{ttv}} µm，弯曲 {{bow}} µm，残余损伤 {{damage}} ' +
          'µm（{{mpa}} MPa）。该晶锭得到 {{good}} 片合格晶圆。',
        IngotStructureLost: '——结构已丢失',
        Ingot: '晶锭已生长：{{mm}} mm 掺{{dopant}}硅，籽晶端 {{rho}} Ω·cm，氧 {{oxygen}}/nm³，{{regime}}',
        StockBlank: '库存空白片——锯上没有来自 CZ-07 的晶锭。',
        BareWafer: '抛光裸片，无薄膜',
      },
      quantum: {
        Dd3Note: '这个面板上面的每一个数字都把两个量子点当作排在一条线上。其实不是：电子是从栅极之间的缝隙里穿过去的，而缝隙是有宽度的。所以这里把一个电子放在三维势场的左量子点里然后松手，对整个波积分薛定谔方程，用的就是 ' +
          'THRM-04 的隧穿体积和 MET-05 ' +
          '的衍射所用的同一个传播器。没有任何东西告诉它该穿得多快。图表数的是波有多少还在左边，耦合强度从第一次折返读出：h/4t。把挤压保持为零，灰色虚线（二能级，用一维求解器的 ' +
          't）就压在蓝线下面——两个除了势场之外毫无共同之处的计算，得到了一致的结果。现在把缝隙挤窄。缝隙越窄，横向上把电子束缚得越紧，这要付出零点能，电子在穿过时必须凑够这份能量：它就是换了名字的势垒高度，每单位挤压相当于半个 ' +
          'ħω₀。橙色曲线正是把这一项加到一维势垒上，它与波的真实行为相差不到十分之一。势垒栅比图纸窄几个纳米，对量子比特速度的影响比它的电压还大，这让 t ' +
          '成了一个光刻参数。量子阱按它真实的十纳米画出；在网格上它厚了 2.2 ' +
          '倍，因为硅中的电子沿生长方向重五倍，换一个变量就能把两种质量放进同一个网格。一个电子，一个能谷，没有自旋，没有噪声，没有弛豫。',
        Dd3KnobDetuning: '失谐',
        Dd3KnobPinchValue: '缝隙内 ħω = {{hw}} meV',
        Dd3KnobPinch: '栅极把缝隙挤窄多少',
        Dd3KnobBarrier: '势垒栅',
        Dd3Idle: '按“运行”：电子在左量子点被释放，并在工作线程上传播，每次运行十到二十秒。',
        Dd3Status: '{{ps}} / {{run}} ps · {{left}} % 在左量子点 · Σ|ψ|² = {{norm}}',
        Dd3TileNoPinch: '缝隙与量子点一样宽：无需修正',
        Dd3TileAdiabaticNote: '势垒增加 {{mev}} meV：缝隙以 ħω = {{hw}} meV 束缚电子',
        Dd3TileAdiabatic: '计入缝隙的零点能',
        Dd3Tile1dNote: '应在 {{ps}} ps 内穿越',
        Dd3Tile1d: '一维求解器，同样的栅极',
        Dd3TileWaiting: '在电子第一次折返时读取',
        Dd3TileMeasuredNote: '第一次折返在 {{ps}} ps，{{pct}} % 到了另一侧',
        Dd3TileMeasured: '隧穿耦合，由运行结果测得',
        Dd3Crossed: '第一次折返',
        Dd3ChartY: 'P(左)',
        Dd3ChartX: '时间 (ps)',
        Dd3ChartTitle: '电子处于左量子点的概率',
        Dd3LineAdiabatic: '二能级，把缝隙的零点能加到势垒上',
        Dd3Line1d: '二能级，用一维求解器的 t',
        Dd3Measured: '三维传播',
        Dd3About: '这里展示了什么，模型又略去了什么',
        SectionDd3: '三维传播下的穿越——以及缝隙变窄的代价（薛定谔方程，分裂 Cayley 步进）',
        NextFrequency: 'EPI-20 量子阱里的自旋量子比特靠磁场而不是约瑟夫森结来劈裂，约每特斯拉 28 GHz。多大的磁场能让它在这台制冷机里和 5 GHz 的 ' +
          'transmon 一样“冷”？你预计最先出问题的会是什么？',
        TitleFrequency: '|1⟩ 布居随量子比特频率的变化',
        LabelP1High: '最高频率下处于 |1⟩',
        LabelP1Low: '最低频率下处于 |1⟩',
        HypFrequency: '当两能级之间的间隔远大于周围的热能时，二能级系统就处在基态：要紧的是 hf 比 kT，而不是 T ' +
          '本身。如果是这样，同一台制冷机、同一套线路，应该让低频量子比特混得很厉害，而高频的很干净，两者之间制冷机上什么都没变。',
        AskedFrequency: '量子比特的频率如何决定制冷机够不够冷？',
        SumFrequency: '量子比特频率对热布居：冷不冷看的是 hf 对 kT，而不是多少毫开。',
        NextAttenuation: '上个月测得很好的一颗芯片，今天在 |1⟩ 的读数是百分之十，而制冷机上所有温度计都正常。在有人提议回温之前，趁制冷机还冷着，你会先量什么？',
        TitleAttenuation: '量子比特温度随线路衰减的变化',
        LabelPlate: '冷盘始终为',
        LabelQubitCold: '衰减最多时的量子比特',
        LabelQubitWarm: '衰减最少时的量子比特',
        HypAttenuation: '传送驱动脉冲的同轴线同时也带着室温噪声，在量子比特的模式里约有一千个光子。每 10 dB ' +
          '的低温衰减会去掉其中的十分之九。如果量子比特是和它“听到”的东西热平衡，而不是和它所在的冷盘热平衡，那么每加一个衰减器它的温度都应该下降，直到只剩冷盘自身的辐射——而冷盘上的温度计应该纹丝不动。',
        AskedAttenuation: '量子比特的驱动线需要多少衰减？',
        SumAttenuation: '驱动线衰减对量子比特温度：室温噪声，每 10 dB 减为十分之一。',
        knob: {
          circulation: '氦-3 循环量',
          attenuationDb: '驱动线衰减',
        },
        AssistantBlurb: '问问驱动线需要多少衰减，或者为什么量子比特要做在 5 GHz。每个回答都是用这份配方把制冷机跑一遍。',
        AssistantLead: 'QDT-24 探究助手',
        TileExcitedNote: '针对上面设计的 {{f}} GHz 量子比特',
        TileExcited: '无驱动时处于 |1⟩ 的比例',
        TileQubitNote: '其模式中有 {{n}} 个热光子',
        TileQubit: '量子比特温度',
        TilePlateNote: '从 4 K 到基础温度需 {{h}} h',
        TilePlate: '混合室冷盘',
        SectionTool: '本班次的制冷机：冷盘有多冷，量子比特有多冷',
        fault: {
          missingAttenuator: {
            Evidence: [
              '无驱动时量子比特在 |1⟩ 的读数是百分之十，而限值是百分之一，同时冷盘读数为 10 mK。',
              '流量、4 K 级和真空罐压力都正常：制冷机是健康的。',
              '网络分析仪在一条规格为 60 dB 的线上只测到 40 dB。',
            ],
            Truth: '重做线路时，驱动线混合室一端的 20 dB ' +
              '衰减器被漏装了。现在这条线放过的室温噪声是应有值的一百倍：量子比特模式里有八分之一个光子，而不是千分之一个，这相当于 10 mK 的冷盘上坐着一个 ' +
              '109 mK ' +
              '的量子比特。每十次测量就有一次从错误的初态开始。所有温度计都是对的。量子比特的温度不是它拧在上面的那块金属的温度，而是它能“听到”的东西的温度。',
            Complaint: '制冷机状态完美：10 mK，按时到达。量子比特却不行。读出保真度很差而 T1 看上去正常，而且上个月这颗芯片在另一台制冷机里测得很好。',
            Label: '驱动线上少了一个衰减器',
          },
          canLeak: {
            Evidence: [
              '真空罐压力是合格值的八倍。',
              '蒸馏室流量正常，说明制冷功率完好；4 K 级正常，说明多出来的热量不是沿支撑件传下来的。',
              '检漏仪在真空罐法兰处检到了氦。',
            ],
            Truth: '真空罐上的一处铟密封漏了，本该是真空的空间里有了氦气。气体会导热：它把热量从 4 K ' +
              '壁直接带到冷盘，是合格漏热的八倍，冷盘就停在根号八倍的基础温度上。这是最贵的一种——必须把制冷机升到室温才能打开——也正因为如此，要先把那两个便宜的相似故障排除掉。',
            Complaint: '这次降温冷盘卡在 28 mK 不动了。流量正常，4 K 级也正常。',
            Label: '真空罐里进了氦气',
          },
          warmCompressor: {
            Evidence: [
              '4 K 级读数为 5.2 K。冷阱堵塞和真空罐漏气都不会让 4 K 级变热。',
              '蒸馏室流量和真空罐压力都正常。',
              '压缩机进水 31 °C，限值是 25 °C。',
            ],
            Truth: '驱动脉管的压缩机拿到的冷却水有 31 °C，正在降额运行，于是 4 K 级停在了 5.2 ' +
              'K。所有更冷的部件都挂在这一级上，而沿支撑件和线缆传下来的热量大致与它温度的平方成正比：漏热是原来的 2.6 倍，基础温度就是原来的根号 2.6 ' +
              '倍。下午更糟，是因为大楼的冷却水，而不是制冷机。',
            Complaint: '一周内基础温度从 10 mK 慢慢爬到了 16 mK，而且下午更糟。循环量完全是设定值。',
            Label: '脉管压缩机运行偏热',
          },
          pluggedTrap: {
            Evidence: [
              '蒸馏室流量只有设定值的 20%。真空罐漏气只会增加热量，不会减少流量。',
              '真空罐压力和 4 K 级都正常。',
              '冷阱两端压降是正常值的五倍。',
            ],
            Truth: '用来净化回流氦-3 的液氮冷阱被冻住的空气堵满了，把循环量卡到了设定值的五分之一。制冷功率与循环量乘以 T ' +
              '的平方成正比，所以五分之一的流量在同样的漏热下只能维持根号五倍的温度：22 ' +
              'mK。制冷机内部什么问题也没有。要修的地方在气体处理柜上，一个下午就够，而且不用回温。',
            Complaint: '冷盘降不到 22 mK 以下。上一次降温它到了 10。有人说是真空罐漏了，想回温把所有密封都重做一遍。',
            Label: '冷阱正在堵塞',
          },
          cryoHealthy: {
            Evidence: [
              '冷盘在 10 mK，量子比特在 |1⟩ 的读数是八分之一个百分点。',
              '所有台面读数都正常。',
            ],
            Truth: '没有任何问题。正确的判断是开始量测。',
            Complaint: '例行降温。冷盘一夜之间到了基础温度。',
            Label: '一切正常',
          },
        },
        reading: {
          lineInsertionLoss: {
            Label: '用网络分析仪测驱动线插入损耗 (2.0 h)',
          },
          leakCheck: {
            Limit: '无信号',
            Label: '制冷机低温状态下对真空罐做氦检漏 (3.0 h)',
          },
          compressorWater: {
            Limit: '进水 < 25 °C',
            Label: '脉管压缩机处的冷却水 (30 min)',
          },
          trapPressure: {
            Label: '液氮冷阱两端压降 (30 min)',
          },
          qubitPopulation: {
            Label: '无驱动时量子比特在 |1⟩ 的布居',
          },
          canPressure: {
            Label: '真空罐压力',
          },
          fourKelvin: {
            Label: '4 K 级温度',
          },
          stillFlow: {
            Label: '蒸馏室的氦-3 流量',
          },
        },
        aspect: {
          wiring: {
            Action: '回温、打开制冷机，把驱动线上缺的衰减器装回去',
            Label: '微波线路',
          },
          vacuum: {
            Action: '回温、找到漏点、重做铟密封，再重新降温',
            Label: '真空罐',
          },
          pulseTube: {
            Action: '恢复压缩机的冷却水，让 4 K 级恢复',
            Label: '脉管制冷机及其压缩机',
          },
          circulation: {
            Action: '给液氮冷阱回温并抽空，然后把循环量恢复上去',
            Label: '氦-3 循环',
          },
        },
        SecondaryLabel: '量子比特在 |1⟩ 的布居',
        ToolLabel: 'QDT-24 低温量子器件测试',
        GuardWafer: '这批还是整片晶圆。请先在 DICE-15 切割：制冷机放的是样品座上的芯片，它的冷盘比晶圆还小。',
        GuardPackaged: '这些器件已经塑封成封装了。量子比特芯片是裸片进制冷机的，用引线键合到样品座上。',
        CoolLot: '降温到 10 mK 并表征量子比特',
        NoteNoLot: '制冷机里没有芯片。把完工的量子芯片带到这里来降温并测量。',
        SectionSpin: 'EPI-20 量子阱里的自旋量子比特：能谷劈裂（紧束缚）',
        SectionTransmon: 'Transmon：求解哈密顿量（Sturm 二分法）',
        DriveRabiTitle: '共振驱动：布居数来回摆动',
        DriveRabiX: '时间 (ns)',
        DriveRabiY: '布居数',
        DriveP0: '|0⟩',
        DriveP1: '|1⟩',
        DriveP2: '|2⟩（泄漏）',
        DriveLeakTitle: 'π 脉冲太短就会泄漏出量子比特',
        DriveLeakX: '门时间 (ns)',
        DriveLeakY: '留在 |2⟩ 及以上的布居数（log₁₀）',
        DriveLeakSeries: '校准 π 脉冲后的泄漏',
        DriveSpeedLimit: '1/|α| = {{nsec}} ns',
        DriveThisGate: '当前门',
        DriveRamseyTitle: 'Ramsey：被电荷噪声带走的相位',
        DriveRamseyX: '两个 π/2 脉冲之间的等待时间 ({{unit}})',
        DriveRamseyY: '第二个脉冲后处于 |0⟩ 的概率',
        DriveFringe: '条纹',
        DriveEnvelope: '包络',
        DriveT2: 'T₂*',
        DriveStatF01: '量子比特频率 f₀₁',
        DriveStatRatio: 'E_J/E_C = {{ratio}}',
        DriveStatAlpha: '非谐性 α',
        DriveStatLimit: '速度极限 1/|α| = {{nsec}} ns',
        DriveStatRabi: 'Rabi 频率',
        DriveStatPi: '方形 π 脉冲需要 {{nsec}} ns',
        DriveStatLeak: '{{nsec}} ns π 脉冲后的泄漏',
        DriveStatP1: 'P(|1⟩) = {{p}}',
        DriveFrameRwa: '旋转波近似',
        DriveFrameLab: '实验室坐标系，保留反旋转项',
        DriveStatT2: '电荷噪声决定的 T₂*',
        DriveStatAsymptotic: 'E_J/E_C 已超出电荷基所能分辨的范围；此处 f₀₁ 的摆幅取 Koch 的渐近式，电荷噪声不再限制这个量子比特',
        DriveStatSigma: '偏置电荷噪声 σ = {{sigma}} e',
        DriveTransmon: 'Transmon（E_J 来自约瑟夫森结）',
        DriveBox: '库珀对盒（E_J = E_C）',
        DriveShowLeak: '泄漏与门时间',
        DriveShowRamsey: 'Ramsey 条纹',
        DriveKnobCap: '并联电容',
        DriveKnobCapValue: '{{ff}} fF · E_C = {{ec}} MHz',
        DriveKnobAmp: '驱动幅度',
        DriveKnobGate: 'π 脉冲门时间',
        DriveKnobSigma: '偏置电荷噪声 σ',
        DriveKnobSigmaValue: '{{sigma}} e',
        SectionDrive: '驱动下的 transmon：Rabi、泄漏与退相位（薛定谔方程，RK4）',
        DriveNote: '把上面的能谱拿来做点事。微波信号耦合到超导岛上的电荷，而在能级自身的基底中，电荷算符只连接相邻能级，所以频率为 f₀₁ 的信号会让量子比特以左图的 Rabi 频率在 |0⟩ 和 |1⟩ 之间来回摆动。麻烦在于 |2⟩：它离等间距下一级的位置只差非谐性 α，而短于几个 1/|α| 的脉冲带宽足以把它激发出来。泄漏曲线是在每个时长下校准过的 π 脉冲——这是每一台超导量子处理器都绕不开的速度极限。切换到 Ramsey 看的是另一只时钟：衬底中的杂散电荷会漂移，能级对它的依赖使 f₀₁ 随之漂移，一批测量就会在 T₂* 内失去相位。切换到库珀对盒，相位在几纳秒内就消失了；而在面板默认参数下，transmon 随噪声滑块不同能保持几十到几百微秒，约为它的两万倍，这正是它存在的理由。四个求解出的能级在旋转波近似成立时按该近似驱动，不成立时（库珀对盒，或相对量子比特频率很强的脉冲）则在实验室坐标系中计算，保留反旋转项。噪声按准静态处理，能量弛豫、1/f 噪声谱以及 DRAG 之类的脉冲整形都没有包括在内。',
        SectionChargeQubit: '一个电子，两个量子点：运动中的电荷量子比特（薛定谔方程，幺正步进）',
        ChargeQubitNote: '上一节止于一个数字：几十微电子伏的隧穿耦合 t。这一节告诉你那个数字究竟是什么。把电子放进左边的量子点再松手，它不会待着不动——它会在 h/(4t)（这里约五十皮秒）内越到右边的点，然后再回来，如此往复不休，因为这个模型里没有任何东西能让它停下。\n\n左边画的是 |c_L φ_L + c_R φ_R|²，也就是交换耦合求解器早已算出的两个轨道，其振幅是一步步积分出来的，而不是照搬教科书公式——那个公式只用来在测试中核对结果。把两点倾斜一下，会同时发生两件事：越过去的比例按 4t²/(4t² + ε²) 变小，而越过去的那部分反而更快。\n\n斜坡切换展示的是真实逻辑门的驱动方式——让失谐扫过零点，扫得慢电子就沿反交叉进入另一个点，扫得快则径直穿过，其概率由朗道–齐纳公式给出。\n\n最后一个旋钮说明了电荷量子比特为何只是教学装置而非产品：杂散电荷会推动失谐，频率随之漂移，振荡在几纳秒内就模糊殆尽——只有几百次往返，而 transmon 能做到几十万次。在零失谐处，频率对噪声只有二阶依赖，这正是所谓的甜点。模型只有两个能级与准静态噪声：没有能谷、没有声子、没有弛豫，电子也从未被测量。',
        CqDensityTitle: '电子在哪里：跨越两个量子点的 |ψ|²',
        CqDensityX: '沿量子点连线的位置（nm）',
        CqDensityY: '能量（meV）',
        CqPotential: '栅极势',
        CqDensitySeries: '电子密度 |ψ|²',
        CqTraceTitle: '电子来回摆荡：P(左) 随时间变化',
        CqTraceX: '时间（ns）',
        CqTraceY: '电子位于左侧量子点的概率',
        CqTraceSeries: 'P(左)，积分所得',
        CqEnvelopeSeries: '电荷噪声留下的可见度',
        CqNow: '此刻',
        CqRampTitle: '扫过反交叉的失谐斜坡',
        CqRampX: 'log₁₀ 扫描速率（µeV/ns）',
        CqRampY: '电子留在原量子点的概率',
        CqRampSeries: '积分所得',
        CqRampFormula: '朗道–齐纳公式',
        CqRampHere: '此斜坡',
        CqCrossover: '转折点',
        CqToggleOscillation: '让它来回摆荡',
        CqToggleRamp: '扫过反交叉',
        Cq3dCrossing: '穿越用时',
        Cq3dSweep: '扫描历时',
        Dot3dInLeft: '在左量子点',
        Dot3dInRight: '在右量子点',
        CqAbout: '这里展示了什么，模型又省略了什么',
        CqBenchKnobs: '旋钮',
        CqBenchReadouts: '读数',
        Dot3dAbout: '关于这幅图',
        Dot3dScaleTailsShort: '尾部（对数）',
        Dot3dScaleDensityShort: '密度（线性）',
        Dot3dAnimCaption: '一个电子，在器件之中。这团云是两个量子点轨道按电子不断变化的振幅相加的结果，|c_L φ_L + c_R φ_R|²，干涉也包含在内——所以它是从一个点流向另一个点，而不是两团云互换亮度。沿两点方向的形状是解出来的；垂直于该方向以及穿过 10 nm 量子阱的部分是假设的基态。',
        CqStatCrossover: '临界扫描速率',
        CqStatCrossoverNote: '2πt²/ħ：比它慢的斜坡把电子带过去，比它快的则把电子留在原地',
        CqStatStays: '这次斜坡留在原地的比例',
        CqStatStaysNote: '朗道–齐纳公式给出 {{formula}}%',
        YieldShort: '{{y}}% 的芯片没有频率碰撞 · 每个量子比特落在目标约 {{sigma}} MHz 以内',
        CqRampAveraged: '远离交叉点时，两条能支之间的拍频快得一帧画面无法显示，那里画的是拍频的平均；在交叉点附近，画的就是拍频本身。',
        CqStatT: '隧穿耦合 t',
        CqStatTNote: '与交换耦合小节在同一设计上读到的 {{freq}} 相同',
        CqStatPeriod: '一次往返',
        CqStatPeriodNote: '把电子送到隔壁需 {{half}}：半个周期',
        CqStatAmplitude: '有多少真正越过去',
        CqStatAmplitudeNote: 'ε = {{eps}} µeV 时的 4t²/(4t² + ε²)',
        CqStatT2: '相干时间 T₂*',
        CqStatT2Note: '振荡模糊之前可往返 {{periods}} 次',
        CqStatT2Coherent: '无噪声：完全相干',
        CqStatT2CoherentNote: '把电荷噪声调上去：这正是电荷量子比特唯一挺不过去的东西',
        CqSlowdown: '放慢 {{factor}} 倍：这里的一秒相当于制冷机中的 {{nsec}} ns',
        CqRun: '运行',
        CqPause: '暂停',
        CqReset: '回到左侧量子点',
        CqKnobBarrier: '势垒栅极',
        CqKnobDetuning: '两点之间的失谐',
        CqKnobNoise: '作用在失谐上的电荷噪声',
        CqNoiseNone: '无',
        CqKnobRate: '扫描速率',
        CqKnobRateValue: '{{rate}} µeV/ns · {{stays}}% 留在原处',
        ExchOff: '关闭',
        ExchPotentialTitle: '两个量子点，一个电子：局域轨道',
        ExchPotentialX: '沿量子点连线的位置 (nm)',
        ExchPotentialY: '能量 (meV)',
        ExchPotentialSeries: '栅极势',
        ExchLeft: '左量子点 |φ_L|²',
        ExchRight: '右量子点 |φ_R|²',
        ExchJTitleBarrier: '交换能 J 随势垒栅的变化',
        ExchJTitleDetuning: '交换能 J 随失谐的变化',
        ExchJXBarrier: '势垒栅高度 (meV)',
        ExchJXDetuning: '失谐 ε (meV)',
        ExchJY: 'log₁₀ J (µeV)',
        ExchJSeries: 'J，Hubbard 模型',
        ExchJHere: '当前设计',
        ExchJGate: '10 ns √SWAP',
        ExchStatT: '隧穿耦合 t',
        ExchStatTNote: '局域轨道之间的非对角元；鞍点比量子点底部高 {{saddle}} meV',
        ExchStatU: '共用一个量子点的代价 U',
        ExchStatUNote: '两个电子同处一个 {{size}} nm 量子点时的库仑能',
        ExchStatJ: '交换能 J',
        ExchStatJNote: '{{mhz}} MHz；弱隧穿公式 4t²U/(U² − ε²) 给出 {{formula}}',
        ExchStatGate: '√SWAP 门时间',
        ExchStatGateNote: 'h/(4J)：交换相位转过四分之一圈',
        ExchStatMerged: '电子已不再被束缚在两个量子点中——把基态能级降到鞍点以下，或升高势垒',
        ExchStatTilted: '倾斜已把两个轨道都拉进同一个量子点——隔壁没有电子可以交换；把失谐调回零附近',
        ExchSweepBarrier: 'J 随势垒栅',
        ExchSweepDetuning: 'J 随失谐',
        ExchKnobBarrier: '势垒栅',
        ExchKnobDetuning: '两个量子点之间的失谐',
        ExchKnobSep: '量子点间距（半个节距）',
        ExchKnobHw: '量子点约束能 ħω₀',
        SectionExchange: '两个自旋量子比特及其之间的门：交换耦合（薛定谔方程，Hubbard 模型）',
        ExchangeNote: '一个量子点容纳一个量子比特，而计算机需要两个量子比特相互作用。在硅中，这种相互作用就是交换作用，没有人需要为它布线。相邻量子点中的两个电子可以短暂地共用一个量子点来降低能量，但泡利原理只允许自旋相反时这样做，因此单态比三重态低一个能量 J；让 J 保持开启 h/(4J) 的时间，两个自旋就完成一个 √SWAP 门。这里的每一步都是解出来的：双量子点势中的单电子给出局域轨道之间的隧穿耦合 t，对左轨道的库仑积分给出共用一个量子点的代价 U，双电子 Hubbard 模型给出 J，在弱隧穿时 J 为 4t²U/(U² − ε²)。升高势垒栅，t 按指数下降，J 随其平方下降：一个栅上十毫电子伏的变化可使 J 改变近五个数量级，芯片正是这样开关一个门的。模型沿量子点连线为一维，用量子点尺寸来软化库仑核，不含能谷、自旋轨道耦合或电荷噪声。',
        Dot3dT: '隧穿耦合 t',
        Dot3dJ: '交换 J',
        Dot3dGate: '√SWAP',
        Dot3dCaption: '沿两个量子点的方向：求解所得。横跨量子点及穿过 10 nm 量子阱的方向：假定为基态。每个量子点上方是一个柱塞栅，两者之间是势垒栅——颜色越暖表示势垒越高。白色处是两个轨道相互重叠的地方。',
        Dot3dScaleTails: '显示尾部（对数，四个数量级）',
        Dot3dScaleDensity: '真实密度（线性）',
        SectionJunction: '约瑟夫森结：一纳米的氧化层（传递矩阵、Landauer 公式）',
        SpinNote: '硅导带的最低能量位于动量空间的六个点上；应变量子阱只留下其中两个——即两个能谷——而且几乎等高。这里的原子链是最小的、能有两个能谷的模型——每个原子面一个格点，跃迁参数选成让能带最低点落在硅的位置——所以陡峭的 ' +
          'Si/SiGe 台阶能把两个能谷劈开几百 ' +
          'µeV。当生长温度偏高、锗扩散把这个台阶抹开半个纳米时，平滑模型里的劈裂就崩塌了；但合金是逐个原子随机的，电子碰到的随机锗又会碰巧把能谷劈开。一座晶圆厂的自旋量子比特良率，就是那些在这场抽奖中赢得足够多、使自旋在工作磁场下不跑进上能谷的量子点所占的比例。',
        KnobB: '磁场',
        KnobDot: '量子点半径',
        KnobField: '栅极电场',
        KnobGe: '势垒中的锗含量',
        KnobWell: '硅量子阱厚度',
        KnobInterface: '界面宽度（EPI-20 生长）',
        DotNext: '换一个量子点看看',
        DotSmooth: '平滑合金，无无序',
        DotAlloy: '真实量子点：随机合金',
        StatThermalNote: '能谷劈裂必须是它的许多倍，否则上能谷会被占据',
        StatThermal: '100 mK 下的热能',
        StatHotSpotNote: '自旋劈裂等于能谷劈裂、自旋弛豫最快的地方——要远离它',
        StatHotSpot: '热点',
        StatQubitNote: '在 {{b}} T：塞曼劈裂 {{ez}} µeV',
        StatQubit: '自旋量子比特频率',
        StatWaferNote: '的量子点超过 100 µeV；中位数 {{median}}，10-90%：{{lo}}-{{hi}} µeV',
        StatWafer: '五十个量子点中',
        StatThisDotNote: '所画量子点的能谷劈裂；无无序时为 {{smooth}} µeV',
        StatThisDot: '这个量子点',
        LevelsHot: '热点',
        LevelsHere: '工作磁场',
        LevelsY: '能量 (µeV)',
        LevelsX: '磁场 (T)',
        LevelsTitle: '自旋与能谷能级随磁场的变化',
        LevelValley1Up: '上能谷，自旋向上',
        LevelValley1Down: '上能谷，自旋向下',
        LevelValley0Up: '下能谷，自旋向上',
        LevelValley0Down: '下能谷，自旋向下',
        ValleyTarget: '100 µeV',
        ValleyHere: '当前生长',
        ValleyBand: '10% 和 90% 分位的量子点',
        ValleyMedian: '真实合金，中位数量子点',
        ValleySmooth: '平滑合金（无无序）',
        ValleyY: 'log₁₀ 能谷劈裂 (µeV)',
        ValleyX: 'Si/SiGe 界面宽度 (nm)',
        ValleyTitle: '能谷劈裂与界面宽度的关系',
        WellDensity: '电子',
        WellSmooth: '平滑分布',
        WellAlloy: '这个量子点的合金',
        WellY: '导带底 (meV)',
        WellX: '膜层中的高度 (nm)',
        WellTitle: '逐个原子面看量子阱，以及电子在哪里',
        YieldNote: '在这个离散下，上面的量子比特落在目标频率约 {{sigma}} MHz 以内（一个标准差）。当两个相邻比特中任一个落在对方 0-1 或 1-2 ' +
          '跃迁线 {{window}} MHz 以内时就发生碰撞，此时双比特门会驱动错误的跃迁——只有 {{y}}% ' +
          '的芯片完全没有碰撞。在室温下测量每个结，再用激光退火微调，是晶圆厂压低离散的一种做法。',
        KnobSpread: '整片晶圆上的结电阻离散',
        TransmonNote: '这个电路的哈密顿量是 4E_C(n − n_g)² − E_J cos φ，其中 n 数的是穿过结的库珀对，φ ' +
          '是相位差，二者像位置和动量一样共轭。写在库珀对数的基底里，它是一个三对角矩阵，它的本征值就是能级阶梯。把电容缩小到 E_J 接近 ' +
          'E_C，能带就会随每一个杂散电荷摆动；把电容做大，能带按指数变平，而非谐性——第一级和第二级台阶之间的差，正是它让脉冲只驱动量子比特——只按幂次缩小。这个取舍就是 ' +
          'transmon。',
        KnobNg: '偏置电荷',
        KnobEj: '约瑟夫森能 E_J / h',
        KnobCapValue: '{{ff}} fF · E_C/h {{ec}} MHz',
        KnobCap: '并联电容',
        StatShiftNote: '多 0.01 nm 的 AlOx 会让这个量子比特偏移多少',
        StatShift: '十分之一埃的氧化层',
        StatDispersionNote: 'Koch 等人的渐近公式：{{koch}}',
        StatDispersion: 'f₀₁ 的电荷色散',
        StatRatioNote: '大于约 20 是 transmon；接近 1 则是库珀对盒',
        StatRatio: 'E_J / E_C',
        StatAnharmNote: '约为 −E_C，即 −{{ec}} MHz：正因如此脉冲才能只驱动 0→1',
        StatAnharm: '非谐性',
        StatF01Note: '教科书公式 √(8E_J E_C) − E_C 给出 {{asym}} GHz',
        StatF01: '量子比特频率 f₀₁',
        YieldHere: '当前工艺',
        YieldSeries: '对芯片做蒙特卡罗',
        YieldY: '无频率碰撞的芯片 (%)',
        YieldX: '结电阻离散，一个标准差 (%)',
        YieldTitle: '无频率碰撞的芯片，{{n}} 个量子比特排成一链',
        BandsHere: 'n_g',
        BandsY: '高于基态平均值的能量 / h (GHz)',
        BandsX: '偏置电荷 n_g（库珀对）',
        BandsTitle: '能级阶梯与岛上杂散电荷的关系',
        PotentialExcited: '|1⟩',
        PotentialGround: '|0⟩',
        PotentialSeries: '约瑟夫森势',
        PotentialY: '能量 / h (GHz)',
        PotentialX: '结两端的相位 φ (rad)',
        PotentialTitle: '量子摆：−E_J cos φ 及其两个最低能态',
        JunctionNote: '这个结是在两次铝蒸镀之间往蒸镀腔里通几分钟氧气长出来的，留下的是一纳米左右的非晶 AlOx。电子靠隧穿穿过它，所以电阻随厚度指数增长——这正是 ' +
          'THRM-04 栅氧化层的那一课，只是目标反过来：这里晶圆厂需要的是精确数量的隧穿，而不是完全没有。电阻在室温下逐个结测量，因为 ' +
          'Ambegaokar 和 Baratoff 证明了：芯片一旦变冷，它就确定了临界电流。势垒高度和质量是拟合真实结得到的；曲线有多陡与它们无关。',
        KnobMass: '氧化层中的有效质量',
        KnobBarrier: '势垒高度',
        KnobArea: '结面积',
        KnobOxide: '氧化层厚度',
        StatEjNote: 'E_J/h——下面的 transmon 就建立在它上面',
        StatEj: '约瑟夫森能',
        StatIcNote: 'Ambegaokar-Baratoff：铝进入超导态后 I_c R_n = πΔ/2e',
        StatIc: '临界电流',
        StatRnNote: '{{a}} µm² 的结，在室温下测得',
        StatRn: '正常态电阻',
        StatRaNote: '氧化层每厚一埃，×{{f}}',
        StatRa: 'R·A',
        RaBand: '量子比特结',
        RaHere: '当前氧化层',
        RaSeries: 'Landauer 公式，经由传递矩阵',
        RaY: 'log₁₀ R·A (Ω·µm²)',
        RaX: 'AlOx 厚度 (nm)',
        RaTitle: '电阻与氧化层厚度的关系',
        JunctionWave: '电子波穿过氧化层：只剩 {{f}}',
        JunctionAl: 'Al',
      },
      ebeam: {
        NextCurrent: '量具研究没通过，而单帧图像看上去很清晰。在电流、帧数和机器下面的地面这三者里，你会先查哪一个？什么读数能告诉你不是另外两个？',
        TitlePrecisionCurrent: '重复性和束斑随探针电流的变化',
        LabelSpotThere: '该电流下的束斑',
        LabelAtCurrent: '对应电流',
        LabelBestPrecision: '最佳重复性',
        HypCurrent: '边缘是在由计数电子构成的轮廓里定位的，所以它的离散应该与电流的平方根成反比。但电流来自亮度固定的源：想要更多，就得把针尖上更大的一块成像到晶圆上。如果两者都成立，重复性一开始应该改善得很快，然后停下来——要么因为束斑变大，要么因为有些噪声电流压不下去。',
        AskedCurrent: '更大的束流能让 CD 重复得更好吗？',
        SumCurrent: '探针电流对重复性：计数噪声一路下降，直到只剩下电流碰不到的那部分噪声。',
        NextLanding: '上面的产额部分显示了光刻胶在哪里不再充电。如果最锐的束斑在 2000 eV，而电荷中性点在 500 附近，你会放弃哪一个？选错了图像会是什么样子？',
        TitleSpotEnergy: '束斑尺寸随着陆能量的变化',
        LabelChromaticShare: '低能端的色差模糊',
        LabelSpotHigh: '最高能量下的束斑',
        LabelSpotLow: '最低能量下的束斑',
        HypLanding: '有两种模糊取决于束能量。衍射与电子波长成正比，而波长与能量的平方根成反比。色差模糊与电子枪的能量展宽除以能量成正比，下降得更快。如果两者都对，束斑应该随着束变硬而稳定缩小，其中色差那一份最先塌下去。',
        AskedLanding: '着陆能量对束斑有什么影响？',
        SumLanding: '着陆能量对束斑尺寸：束越硬，衍射和色差模糊都越小。',
        knob: {
          alphaMrad: '会聚半角',
          frames: '平均帧数',
          currentPa: '探针电流',
          landingEv: '着陆能量',
        },
        AssistantBlurb: '问问着陆能量对束斑有什么影响，或者更大的束流能不能让 CD 重复得更好。每个回答都是用这份配方把镜筒跑一遍。',
        AssistantLead: 'SEM-23 探究助手',
        TileSignalNote: '电流 × 帧数 × 探测器，相对合格值',
        TileSignal: '每次量测的信号',
        TilePrecisionNote: '同一条线，反复量测',
        TilePrecision: '重复性',
        TileSpotNote: '衍射 {{d}} nm，色差 {{c}} nm',
        TileSpot: '晶圆上的束斑',
        SectionTool: '本班次的 CD-SEM：看得多细，重复得多好',
        fault: {
          tiredScintillator: {
            Evidence: [
              '参考样品上的探测器信号只有合格值的 25%。',
              '噪声在每一帧之内，而不在帧与帧之间：配准离散正常。',
              '束斑尺寸和图像功率谱都正常。',
            ],
            Truth: '闪烁体被电子轰击了多年，发光量只剩原来的四分之一。每个像素计到的电子少了，每个像素的噪声就大了，而在有噪声的轮廓里找到的边缘会游走：精度与信号的平方根成反比，所以四分之一的信号就是两倍的离散。束斑不受影响。多平均几帧确实能补回来，代价是光刻胶要吃四倍的剂量，而它在电子束下会收缩。',
            Complaint: '一个月来重复性慢慢超出了限值。图像看上去有颗粒感，操作员开始多平均几帧来得到一张干净的图，结果产能减半。',
            Label: '探测器闪烁体老化',
          },
          groundedMount: {
            Evidence: [
              '帧间配准离散是限值的三倍，而单帧束斑正常。',
              '探测器信号正常，所以不是计数噪声。',
              '加速度计显示机架在跟着地面动。',
            ],
            Truth: '一个气浮支座掉了压，机架落在了限位块上，于是镜筒现在跟着地面一起动：束与晶圆之间有 1.2 nm rms ' +
              '的相对运动。单帧里几乎看不出来，因为一帧很快。它出现在帧与帧之间：每一帧落在稍微不同的位置，平均后的边缘就跟着游走。SEM-23 ' +
              '的操作台与基座分开放置，防的正是这个。',
            Complaint: '量具研究没通过：同一条线量十次，离散是应有值的三倍。单张图像看上去却非常清晰。',
            Label: '隔振支座没有浮起',
          },
          stigmatorDrift: {
            Evidence: [
              '图像功率谱呈椭圆形。色差模糊是圆的。',
              '发射噪声正常，给针尖做闪烁处理也毫无变化。',
              '过焦时，模糊被拉长并转过九十度。',
            ],
            Truth: '消像散器漂了，束斑里有 3.5 nm ' +
              '的像散。它看上去像是枪的问题，因为两者都让图像发软、线条变宽；但它不是枪的问题，因为这种模糊有方向：焦点以下束斑朝一个方向拉长，焦点以上朝另一个方向拉长。顺着拉长方向的线条看上去仍然很锐，所以一个操作员说机器没问题，下一个却说有问题。',
            Complaint: '图像发软，线条读数偏宽。今天早上给电子枪做了闪烁处理，一点用也没有。',
            Label: '像散未校正',
          },
          gassyTip: {
            Evidence: [
              '发射电流噪声随模糊一起升高。像散不会让电子枪闪烁。',
              '模糊是圆的：功率谱没有被拉长。',
              '离焦系列测得展宽 0.7 eV。',
            ],
            Truth: '冷场发射针尖已经好几天没做闪烁处理了。吸附在上面的气体一块一块地改变功函数，能量展宽从 0.25 eV 涨到了 0.7 eV。在 500 eV ' +
              '着陆能量下，这个展宽就占了束斑的大头：色差模糊与 ΔE/E 成正比，所以低电压镜筒恰恰最怕它。线并没有变宽，变宽的是尺子。',
            Complaint: '从周末起图像就发软，每条线都宽了约一纳米。光刻那边已经调高过一次剂量，想把 CD 拉回目标。',
            Label: '枪尖吸附了气体',
          },
          semHealthy: {
            Evidence: [
              '束斑和重复性都正常。',
              '所有台面读数都在限值内。',
            ],
            Truth: '没有任何问题。正确的判断是继续量测。',
            Complaint: '例行班次。每日标样的测量值正常。',
            Label: '一切正常',
          },
        },
        reading: {
          grainCheck: {
            Label: '平坦样品单帧内的噪声 (30 min)',
          },
          accelerometer: {
            Limit: '隔振有效',
            Label: '镜筒机架与地面的加速度计对比 (1.5 h)',
          },
          throughFocus: {
            Limit: '圆形',
            Label: '碳上金标样的过焦系列 (1.0 h)',
          },
          energySpread: {
            Label: '由色差离焦系列测得的能量展宽 (2.0 h)',
          },
          detectorSignal: {
            Label: '参考样品上的探测器信号',
          },
          frameJitter: {
            Label: '帧间配准离散',
          },
          fftEllipticity: {
            Label: '图像功率谱的椭圆度',
          },
          emissionNoise: {
            Label: '发射电流噪声',
          },
        },
        aspect: {
          detector: {
            Action: '更换闪烁体并重新设定光电倍增管增益',
            Label: '二次电子探测器',
          },
          isolation: {
            Action: '让气浮支座重新浮起并调平，找出是什么碰到了机架',
            Label: '隔振系统',
          },
          column: {
            Action: '重新对中镜筒，并在碳上金标样上消除像散',
            Label: '镜筒对中与消像散器',
          },
          gun: {
            Action: '对针尖做闪烁处理赶走吸附气体，并等待发射稳定',
            Label: '场发射电子枪',
          },
        },
        SecondaryLabel: '重复性',
        ToolLabel: 'SEM-23 CD-SEM 与电子束检测',
        GuardEtched: '这个图形已经刻进薄膜了。显影后检查是在刻蚀之前看的一眼，那时印坏了还能去胶返工。',
        GuardNoPattern: '这批晶圆上没有图形。请先在 EUV-22 或 LITH-03 曝光：CD-SEM 量的是图形，而这片晶圆上没有。',
        MeasureLot: '测量图形并放行去刻蚀',
        NoteNoLot: '载台上没有批次。把曝过光的晶圆带到这里来测量。',
        SectionInspection: '电子束检测：看得更细，覆盖更少',
        SectionLinescan: '测量一条线（蒙特卡罗）',
        WaveNote: 'MET-05 的模拟箱让几电子伏的电子穿过相距一纳米的狭缝，电子束按波长决定的角度散开。上面的电子光学柱工作在 {{e}} eV，波长是 ' +
          '{{pm}} pm，而透镜只接受几个毫弧度——所以聚焦的束斑被展宽 0.54 λ/α，也就是几纳米，原因和狭缝把电子束散开完全一样。下面就是 ' +
          'MET-05 自己的求解器。',
        SectionWave: '衍射项背后的波（MET-05 的模拟箱）',
        SectionProbe: '束斑：衍射与像差之争',
        YieldBalance: 'σ = 1：离开的与到达的一样多',
        YieldLandingMark: '着陆 {{e}}',
        YieldChartTitle: '每个着陆电子从样品中带出了什么',
        YieldChartX: '着陆能量，log₁₀ eV（2 = 100 eV，3 = 1 keV）',
        YieldChartY: '每个入射电子对应的出射电子数',
        YieldSeriesDelta: 'δ 二次电子（< 50 eV）',
        YieldSeriesEta: 'η 背散射电子（≥ 50 eV）',
        YieldSeriesTotal: 'σ = δ + η',
        YieldChargeBalanced: '平衡：绝缘体在这里保持不动',
        YieldChargePositive: '带正电：离开的电子多于着陆的电子',
        YieldChargePending: '扫描中……束流还没有到达这个能量',
        YieldChargeNegative: '带负电：电子在表面堆积',
        YieldStatMax: '二次电子最多处',
        YieldStatMaxNote: '着陆能量为 {{e}}',
        YieldStatCross: 'σ 穿过 1 的位置',
        YieldStatCrossNote: 'E₁ 和 E₂：绝缘体可以成像而不带电的着陆能量',
        YieldStatLanding: '在 {{e}}',
        YieldProgress: '扫描中… {{pct}}%（每个能量 {{n}} 个束流电子）',
        YieldDone: '{{n}} 个束流电子，用时 {{s}} s',
        YieldIdle: '按“运行”，让电子束打到薄膜上。',
        YieldMaterial: '电子束下的样品',
        YieldCar: '化学放大光刻胶',
        YieldMor: '金属氧化物光刻胶',
        YieldSi: '硅',
        YieldPause: '暂停',
        YieldResume: '继续',
        YieldRun: '运行扫描',
        YieldKnobLanding: '要检查的着陆能量',
        SectionYield: '图像为什么有衬度——二次电子产额与着陆能量（蒙特卡罗）',
        YieldNote: 'SEM 拍下的每一幅图像，都由离开样品的电子构成，而这些电子分两类。背散射电子是在固体内部转向后高速返回的束流电子；二次电子是被电子束撞出的慢电子，只有产生在距表面几纳米以内的那些才能逸出。这里的扫描用上方线扫描所用的同一个蒙特卡罗级联，把束流电子打到一块厚而平的薄膜上，并统计这两类电子。δ 和 η 从 100 eV 起才分开画：在此之下，直接弹回的束流电子能量已低于 50 eV 的界限，所以那里只画两者之和。看 δ 先上升，在几百 eV 处达到峰值，然后随着更快的电子束把能量沉积到太深、任何二次电子都到不了表面的地方而下降。总产额 σ = δ + η 大于一时，表面失去电荷；小于一时，表面获得电荷。绝缘的光刻胶只有在两个交叉点上才保持不动，而上面的那个 E₂，正是 CD-SEM 以几百伏着陆的原因。未包含：电荷没有反馈到电子束上，薄膜是平的，模型中的硅峰值偏低、背散射偏多，与实测的硅不同。',
        SectionGun: '电子枪：从针尖隧穿出来的电子（THRM-04 的传递矩阵）',
        KnobSnr: '所需信噪比',
        KnobContrast: '缺陷对比度',
        KnobPixel: '像素尺寸',
        InspNote: '电子束能看到任何光学机台都标不出来的开路接触孔或埋藏的空洞，因为它不依赖于光在比波长小得多的东西上的散射。但只有当缺陷从计数噪声中凸显出来时才看得见，这就确定了每个像素需要的电子数。像素越小，束斑就要越小，束斑越小承载的电流越少，而超过一百纳安电子束就会自己把自己弄模糊。所以电子束检测只去设计或光学扫描认为值得花时间的少数地方。',
        StatWaferTimeNote: 'INSP-13 扫一片只要 {{min}} 分钟，但看不到 {{nm}} nm 以下',
        Hours: '{{h}} 小时',
        Years: '{{y}} 年',
        StatWaferTime: '整片晶圆',
        StatRate: '每小时面积',
        StatDwellNote: '不低于 {{min}} ns——探测器需要稳定时间',
        StatDwell: '每像素驻留时间',
        StatInspCurrentNote: '{{probe}} nm 束斑能承载的电流，上限 {{cap}} nA',
        StatInspCurrent: '束流',
        StatElectronsPxNote: '以信噪比 {{snr}} 看到对比度 {{c}}% 的缺陷',
        StatElectronsPx: '每个像素需要的电子数',
        InspWafer: '每小时一整片晶圆',
        InspHere: '当前像素',
        InspOptical: 'INSP-13 光学扫描（最小能看到 {{nm}} nm）',
        InspEbeam: '电子束',
        InspY: 'log₁₀ 每小时 mm²',
        InspX: '像素尺寸 (nm)',
        InspTitle: '每小时检测面积与像素尺寸的关系',
        ScanNote: 'CD-SEM ' +
          '从来看不到“宽度”。电子束逐点扫过时，它数的是从样品逃逸出来的慢电子；靠近侧壁时电子既能从顶面也能从侧壁逃出，所以边缘发亮。它报出的数字，是信号在“间隙基线到峰值”之间某个选定比例处的穿越点——移动阈值，同一条线就测得更宽或更窄；侧壁倾斜，答案就在顶部宽度和底部宽度之间移动。这个偏差要对照截面来校准。校准不掉的是计数：平均更多帧，重复测量的离散按平方根缩小，而每多一个电子，被测的光刻胶就会多收缩一点。',
        KnobSidewall: '侧壁偏离竖直的角度',
        KnobHeight: '光刻胶高度',
        KnobLineCd: '线条底部宽度',
        KnobFrames: '平均帧数',
        KnobThreshold: '边缘阈值',
        ScanRepeat: '重复四十次',
        ScanAgain: '再测一次',
        ScanRun: '扫描线条',
        ScanStale: '样品或电子束变了——按“扫描线条”重新测量。',
        ScanStatus: '用上面电子光学柱的 {{probe}} nm 束斑、{{e}} eV 扫描。',
        ScanProgress: '正在扫描线条……{{pct}}%',
        StatPrecisionNote: '平均 {{mean}} nm',
        StatPrecision: '精密度，重复 40 次',
        StatDoseNote: '{{pa}} pA，每像素 {{dwell}} ns，{{frames}} 帧，平均 {{lines}} 条扫描线',
        StatDose: '每像素电子数',
        StatBiasNote: '机台要对照截面校准掉的部分',
        StatBias: '相对半高处线宽的偏差',
        StatCdNote: '阈值 {{threshold}}%；真实线宽顶部 {{top}} nm，底部 {{bottom}} nm',
        StatCd: '测得线宽',
        SignalCd: '边缘 · {{cd}} nm',
        SignalMeasured: '实测，{{frames}} 帧',
        SignalExpected: '期望值（蒙特卡罗）',
        SignalY: '每个入射电子的二次电子数',
        SignalX: '束斑位置 (nm)',
        SignalTitle: '横跨线条的二次电子信号',
        ScanSilicon: '硅',
        ScanResist: '光刻胶',
        KnobAlpha: '会聚半角',
        KnobCurrent: '束流',
        KnobLanding: '着陆能量',
        ProbeNote: '把透镜开大，衍射模糊就会缩小——电子是波，更宽的锥角聚得更细——但离轴远的光线焦点偏短，能量不同的电子聚焦在不同深度，这两者都随角度增大。最佳束斑在它们交叉的地方。在几百电子伏时通常是色差项占上风，所以上一节电子枪的能量展宽决定了分辨率；要更大的电流会放大光源的像，用分辨率换信号。',
        StatGunUsedNote: '能量展宽 {{spread}} eV，来自上面的电子枪',
        GunSchottky: '肖特基',
        GunCold: '冷场发射',
        StatGunUsed: '电子枪',
        StatBestNote: '在 {{a}} mrad',
        StatBest: '最佳可达',
        StatProbeNote: '衍射 {{d}}，色差 {{c}}，球差 {{s}}，源像 {{i}} nm',
        StatProbe: '此角度下的束斑',
        StatLambdaNote: '在 {{e}} eV——远小于一个原子，却仍然限制着束斑',
        StatLambda: '电子波长',
        ProbeHere: '设定',
        ProbeBest: '最佳角',
        ProbeSpherical: '球差',
        ProbeChromatic: '色差，来自电子枪的能量展宽',
        ProbeDiffraction: '衍射，0.54 λ/α',
        ProbeTotal: '束斑',
        ProbeY: '包含一半电流的直径 (nm)',
        ProbeX: '会聚半角 (mrad)',
        ProbeTitle: '束斑尺寸与会聚角的关系',
        GunNote: '在每纳米几伏的电场下，钨针尖外面的真空变成一道不到一纳米宽的势垒，电子隧穿过去——这就是 Fowler-Nordheim 隧穿，和 THRM-04 ' +
          '的栅氧被偏压推过能带偏移时进入的是同一个区间。电流来自同一个传递矩阵，只是把氧化层换成了真空，再对金属中的电子积分；没有人告诉它 ' +
          'Murphy-Good ' +
          '方程，它却落在了方程上。把针尖加热并镀膜以降低功函数，同样的计算就变成肖特基发射体：电子翻越被电场拉低的势垒。它更稳定，能量展宽也更大——下一节会把这变成更大的束斑。',
        KnobField: '针尖处的引出电场',
        EmitterSchottky: '肖特基，ZrO/W，1800 K',
        EmitterCold: '冷场发射，W，300 K',
        KnobEmitter: '发射体',
        StatBrightnessNote: 'A/(m²·sr·V)——一个小而准直的束斑能携带多少电流',
        StatBrightness: '约化亮度',
        StatTopNote: '高于费米能级，已被电场和镜像电荷拉低',
        StatTop: '势垒顶',
        StatSpreadNote: '出射电子的半高全宽——决定下面的色差模糊',
        StatSpread: '能量展宽',
        StatJNote: '{{r}} nm 针尖发出 {{i}} µA',
        StatJ: '电流密度',
        FnSeries: '隧穿处是一条直线',
        FnY: 'ln(J / F²)',
        FnX: '1 / 电场 (nm/V)',
        FnTitle: 'Fowler-Nordheim 图',
        JfHere: '当前电场',
        JfMg: 'Murphy-Good 方程',
        JfTmm: '传递矩阵（THRM-04 引擎）',
        JfY: 'log₁₀ 电流密度 (A/m²)',
        JfX: '针尖处电场 (V/nm)',
        JfTitle: '电流密度与电场的关系',
        GunWidth: '要隧穿 {{w}} nm',
        GunX: '进入真空的距离 (nm)',
        GunOut: '出射',
        GunMetal: '针尖',
      },
      euv: {
        NextPower: '这周机器慢了四分之一，而光源已经满功率。你会先把功率计放在等离子体和晶圆之间的哪个位置？每个读数又能排除什么？',
        TitlePowerWph: '每小时晶圆数随光源功率的变化',
        LabelMirrorHeat: '进入镜子的热量',
        LabelWphHigh: '最高功率下的产出',
        LabelWphLow: '最低功率下的产出',
        HypPower: '曝光时间等于剂量除以晶圆处的功率，所以光源翻倍应该让它减半。但晶圆还要花时间装载、对准和步进，这些时间里根本没有光。如果这份固定开销是真的，产出的增长就应该比功率慢，并趋向一个任何光源都抬不高的上限。',
        AskedPower: '光源功率能换来多少产出？',
        SumPower: '光源功率对产出：收益递减，代价是镜子上的热。',
        NextDose: '金属氧化物光刻胶对到达它的光子的吸收是原来的三倍。你会把这份好处花在更低的剂量上，还是更平滑的边缘上？厂里谁会替哪一边说话？',
        AxisWph: '每小时晶圆数',
        TitleDoseWph: '每小时晶圆数随剂量的变化',
        AxisLer: 'LER 3σ (nm)',
        TitleDoseTrade: '粗糙度和产出随剂量的变化',
        LabelWphLost: '让出的产出',
        LabelLerHigh: '最高剂量下的粗糙度',
        LabelLerLow: '最低剂量下的粗糙度',
        HypDose: '边缘之所以粗糙，是因为它是由数得清的光子画出来的，而计数噪声与计数的平方根成反比。如果事情就这么简单，剂量翻两番应该让粗糙度大约减半——而曝光要用固定的光束给出这份剂量，时间就要四倍。',
        AskedDose: '剂量换来什么，又花掉什么？',
        SumDose: '剂量用产出换平滑的边缘：散粒噪声对曝光时间。',
        knob: {
          sourceW: '中间焦点处的光源功率',
          doseMjCm2: '曝光剂量',
        },
        AssistantBlurb: '问问剂量要花多少每小时晶圆数，或者更高的光源功率到底能换来什么。每个回答都是用这份配方把从等离子体到晶圆的整条链跑一遍。',
        AssistantLead: 'EUV-22 探究助手',
        TileIfNote: '其中 {{heat}} W 最终用来加热了镜子',
        TileIf: '中间焦点功率',
        TileLerNote: '实际给出的剂量为 {{dose}} mJ/cm²',
        TileLer: '线边缘粗糙度',
        TileWphNote: '到达晶圆的功率 {{w}} W',
        TileWph: '产出',
        SectionTool: '本班次的光刻机：光子进，晶圆出',
        fault: {
          doseSensorOverReads: {
            Evidence: [
              '监控片粗糙度超限，而整条光路上的功率都正常。任何光损失都不会造成这种情况。',
              '产出高于额定值。这台机器上其他任何故障都只会让它变慢。',
              '工件台上的参考光电二极管读到的只有剂量传感器报告值的 80%。',
            ],
            Truth: '狭缝能量传感器漂了，它报告的剂量比实际给出的多五分之一，于是机器每次曝光都提前结束。配方要 40 mJ/cm²，晶圆只拿到 ' +
              '32。粗糙度就是光子散粒噪声，与剂量的平方根成反比，所以边缘粗糙了约十分之一，而机器变快也正是出于同一个原因。好消息和坏消息是同一个故障。',
            Complaint: '没人抱怨这台机器。它的产出比额定值高了百分之十，班长把这写进周报当作好消息。SEM-23 却把最近三批都标记为边缘粗糙。',
            Label: '剂量传感器读数偏高',
          },
          carbonOnOptics: {
            Evidence: [
              '中间焦点功率达标；晶圆处功率相对它低了四分之一。损失在光刻机内部。',
              '逐镜检查读到 68%，本应是 70%。',
              '等离子体处的脉冲能量正常。',
            ],
            Truth: '每面投影镜都因为光束自己从残余碳氢化合物里裂解出的碳而损失了两个点的反射率。两个点听起来不算什么。但从中间焦点到晶圆有十一次反射，0.68 ' +
              '的十一次方只有 0.70 的十一次方的四分之三。多层膜没有任何余量可让。',
            Complaint: '产出下降了约六分之一。中间焦点处的光源功率完全达标，所以光源组已经关闭了他们的工单。',
            Label: '投影镜上长碳',
          },
          tinOnCollector: {
            Evidence: [
              '等离子体处的脉冲能量正常，而中间焦点功率低了 40%。损失发生在这两者之间，而两者之间只有收集镜。',
              '晶圆处功率与中间焦点功率成比例：投影光学系统把收到的光都传了下去。',
              '远场成像显示收集镜只有干净时的 60%。',
            ],
            Truth: '锡屑在收集镜上越积越多，现在它的反射率只有干净时的约 ' +
              '60%。光源组说得对：等离子体发出的光一点不少。光是丢在它遇到的第一面镜子上的，而那正是整台机器里唯一一面直接对着每秒五万次锡爆炸的镜子。',
            Complaint: '产出已经连续下滑三周，现在低了约四分之一。光源组说他们的脉冲能量稳如磐石。',
            Label: '收集镜上有锡',
          },
          dropletTimingDrift: {
            Evidence: [
              '等离子体处的脉冲能量降到了合格值的 60%。脏的收集镜做不到这一点：它在等离子体之后。',
              '中间焦点功率下降了同样的比例，所以在收集镜上没有额外损失。',
              '时序扫描发现主脉冲滞后。',
            ],
            Truth: '主脉冲到得晚了，预脉冲压扁的液滴已经漂出焦点，所以每一发只产生应有带内光的约 ' +
              '60%。光是在等离子体处就缺了——它根本没被产生出来——而收集镜对收到的光反射得很好。换收集镜要花三十小时，却什么也改变不了。',
            Complaint: '自从更换液滴发生器喷嘴后，产出下降了约四分之一。夜班认为收集镜终于被锡糊满了，想预约更换。',
            Label: '激光脉冲没打中液滴',
          },
          euvHealthy: {
            Evidence: [
              '等离子体、中间焦点和晶圆处的功率都正常。',
              '监控片上的粗糙度正常。',
            ],
            Truth: '没有任何问题。正确的判断是继续曝光。',
            Complaint: '例行班次。产出符合计划，监控片在规格内。',
            Label: '一切正常',
          },
        },
        reading: {
          doseCrossCheck: {
            Label: '用工件台上的参考光电二极管核对剂量 (2.0 h)',
          },
          opticsTransmission: {
            Label: '开机检修时逐镜透过率检查 (6.0 h)',
          },
          collectorImage: {
            Label: '收集镜远场成像 (2.0 h)',
          },
          dropletTiming: {
            Limit: '命中',
            Label: '光源液滴-脉冲时序扫描 (1.5 h)',
          },
          lerMonitor: {
            Label: '监控片上的线边缘粗糙度',
          },
          waferPower: {
            Label: '晶圆处功率与中间焦点功率之比',
          },
          ifPower: {
            Label: '中间焦点功率',
          },
          plasmaEnergy: {
            Label: '等离子体处的带内脉冲能量',
          },
        },
        aspect: {
          doseControl: {
            Action: '用参考光电二极管重新校准狭缝能量传感器',
            Label: '剂量传感器',
          },
          optics: {
            Action: '对投影镜执行原位氢自由基清洗',
            Label: '投影光学系统',
          },
          collector: {
            Action: '换上一块干净的收集镜，把这块送去除锡',
            Label: '收集镜',
          },
          source: {
            Action: '重新校准预脉冲和主脉冲与液滴的时序，并重新对准液滴流',
            Label: '液滴发生器与激光时序',
          },
        },
        SecondaryLabel: '线边缘粗糙度',
        ToolLabel: 'EUV-22 锡光源 EUV 光刻机',
        GuardNoPrint: '还没有打印任何图形。先让上面的打印部分算完，这样批次才会按有人看过的配方曝光。',
        NoteWouldExpose: '这个批次将以 {{dose}} mJ/cm² 曝光，边缘粗糙度 {{ler}} nm 3σ。',
        ExposeLot: '曝光晶圆并送往 CD-SEM',
        NoteNoLot: '吸盘上没有批次。把涂好胶的晶圆带到这里来曝光。',
        SectionPrint: '用数得清的光来印图',
        SectionCascade: '一个光子，一簇电子（蒙特卡罗）',
        SectionThroughput: '从光源的瓦数到每小时晶圆数',
        AbsorbCar: '聚合物 CAR',
        AbsorbCarF: '含氟 CAR',
        AbsorbMorSn: '锡氧 MOR',
        AbsorbMorHf: '铪氧 MOR',
        AbsorbSi: '硅（衬底）',
        AbsorbBar: '{{el}}：以 {{mass}}% 的质量贡献 {{share}}% 的 μ',
        AbsorbElementsTitle: '谁在吸收：按元素分解的 μ',
        AbsorbElementsX: '膜中的元素',
        AbsorbElementsY: 'μ 的分量（每 µm）',
        AbsorbWithout: '不加该原子',
        AbsorbCurveTitle: '膜吸收了多少光',
        AbsorbCurveX: '膜厚（nm）',
        AbsorbCurveY: '吸收（%）',
        AbsorbShort: 'μ = {{mu}} 每 µm · 吸收了 {{pct}}% 的光子',
        AbsorbHelp: '完整的光子收支',
        AbsorbStatus: 'μ = 每 µm {{mu}} · 衰减长度 {{length}} nm · 每 nm² {{incident}} 个光子中吸收 {{absorbed}} 个（{{pct}}%）· 顶部 5 nm 得到的是底部 5 nm 的 {{ratio}} 倍',
        AbsorbGain: '按质量加入 {{pct}}% 的 {{el}}，在密度不变时 μ 提高到 {{factor}} 倍。金属氧化物光刻胶还同时带来了更高的密度。',
        AbsorbResist: '光刻胶',
        AbsorbThickness: '膜厚',
        AbsorbDose: '剂量',
        AbsorbAdd: '加入一种原子',
        AbsorbAddNone: '不加',
        AbsorbAddFraction: '其质量分数',
        SectionAbsorb: '光刻胶留住多少光子——逐个原子的吸收（光电截面）',
        AbsorbNote: '上面的级联从一个被吸收的光子开始，下面的印制在数光子，但剂量把光子送到的是膜表面，而不是膜内。在 92 eV，吸收就是光电效应，别无其他，因此膜的衰减是对其原子的求和：μ = ρ Σ wᵢ(μ/ρ)ᵢ，每种元素的截面由其内层电子决定。按原子计，锡的吸收约为碳的三十倍，氧约为三倍半，氢几乎为零。按质量计差距小得多，所以把聚合物质量的十分之一换成锡，吸收只增加约六分之一；金属氧化物光刻胶靠的是含量和密度两者兼得。看柱状图：在聚合物中氧的吸收超过其质量占比，在锡簇中锡承担了大部分 μ。35 nm 的聚合物膜大约每七个光子留住一个，而更暗、更厚的膜顶部得到的剂量多于底部，于是印出倾斜的侧壁。截面取自 Henke 表在 91.84 eV 处的数值，锡和铪分别处在 4d 和 4f 共振上，最不确定；被吸收的光子接下来做什么，是级联部分的事。',
        SectionMirror: '反射镜：四十对钼和硅（传递矩阵）',
        SectionSource: '光源：在 13.5 nm 发光的锡离子',
        PrintNote: '每平方纳米得到的光子数是围绕空间像所要求值的泊松随机数，每个光子把它的酸抛出几纳米远，烘烤把它们模糊，酸够多的地方光刻胶被显影掉。这里没有人告诉模型粗糙度与剂量的平方根成反比——是计数本身做到了这一点。提高剂量，边缘变平滑而线条变细；为了产能降低剂量，边缘就会变粗糙，直到越过一个悬崖，某处的间隙被桥连或线条断开。更长的酸扩散能掩盖噪声，但也一起模糊了图像。额定剂量是光刻胶化学家手里的旋钮：它决定半节距线条在哪个剂量下印出来。',
        PrintSweep: '扫描剂量',
        PrintAgain: '再曝一片晶圆',
        Na055: '高 NA 0.55',
        Na033: 'NA 0.33',
        KnobSizeDose: '光刻胶额定剂量',
        KnobDiffusion: '烘烤中的酸扩散',
        KnobPitch: '线条节距',
        KnobPrintDose: '剂量',
        PrintScale: '沿线方向 {{w}} nm · 横向 {{h}} nm',
        PrintWorking: '正在逐个光子曝光……',
        StatNilsNote: '空间像对比度 {{contrast}}%',
        StatNils: '图像对数斜率',
        StatFailuresNote: '{{rows}} 行线条中断线 {{breaks}} 行、桥连 {{bridges}} 行',
        StatFailures: '随机失效',
        StatLerNote: '沿边缘的 3σ；线宽粗糙度 {{lwr}} nm',
        StatLer: '线边缘粗糙度',
        StatCdNote: '目标 {{half}} nm，即半个节距',
        StatCd: '印出的线宽',
        StatPhotonsNote: '其中 {{abs}} 个被光刻胶吸收',
        StatPhotons: '每 nm² 光子数',
        SweepHere: '当前曝光',
        SweepFail: '断线或桥连的行数 ÷ 20',
        SweepLer: '线边缘粗糙度',
        SweepY: 'LER 3σ (nm) · 失效行数 ÷ 20',
        SweepX: '剂量 (mJ/cm²)',
        SweepTitle: '粗糙度与失效随剂量的变化（每个剂量换用对应额定的光刻胶）',
        CascadeNote: '一个 92 eV ' +
          '的光子被一个原子吸收，原子射出一个带走大部分能量的光电子。这个电子再撞出别的电子，每次电离花掉大约十电子伏，直到没有一个快到还能电离；慢电子停下的地方，光致产酸剂可以把它们俘获并释放出酸。所以图形并不写在光落下的地方，而是写在它周围几纳米的范围里，而且每个光子产生多少酸本身就是随机的。金属氧化物胶的吸收强四倍——又是锡的 ' +
          '4d 电子，和光源里发光的是同一批——而且它的膜更致密，电子停得更早。',
        CascadeFire: '再吸收七个光子',
        ResistMor: '金属氧化物胶（锡）',
        ResistCar: '化学放大胶（聚合物）',
        KnobResist: '光刻胶',
        StatAbsorbedNote: '到达 {{t}} nm 光刻胶的光子中',
        StatAbsorbed: '膜层吸收',
        StatBlurNote: '相对光子落点的横向均方根距离',
        StatBlur: '电子模糊',
        StatElectronsNote: '光电子以及它释放出的每个二次电子',
        StatElectrons: '每个光子的电子',
        StatEventsNote: '{{blank}}% 的光子一个都没产生',
        StatEvents: '每个光子的酸',
        HistSeries: '1500 个光子',
        HistY: '光子比例',
        HistX: '一个光子引发的化学事件数',
        HistTitle: '每个被吸收光子产生的酸',
        CascadeEvent: '化学事件',
        CascadeAcid: '生成的酸',
        CascadeSecondary: '二次电子',
        CascadePhotoelectron: '光电子',
        CascadePhoton: '被吸收的光子',
        CascadeSubstrate: '底层',
        KnobMirrors: '光源之后的反射镜数',
        KnobMirrorR: '每面反射镜的反射率',
        KnobDose: '光刻胶剂量',
        KnobSource: '中间焦点处的光源功率',
        WphNote: '每一环都是相乘。十几次 70% 的反射只剩不到 2%，整形出均匀的狭缝又去掉一半，所以光源处的 250 W ' +
          '到晶圆上只有两瓦半左右。把光刻胶所需剂量减半，机台产出几乎翻倍——这就是光刻胶化学家和光源工程师在争同一件事的原因，也是下一节要展示降低剂量代价的原因。',
        StatWph: '产能',
        StatExposureNote: '另加每片 {{overhead}} s 不是曝光的时间：换片、对准、步进',
        StatExposure: '每片晶圆的曝光时间',
        StatWaferNote: '而反射镜一路吸收了 {{heat}} W',
        StatWafer: '到达晶圆的功率',
        StatTransmissionNote: '{{n}} 次反射，照明系统在整形狭缝时只保留 {{keep}}%',
        StatTransmission: '光学系统透过率',
        WphHere: '当前配方',
        WphSeries: '光源 {{w}} W',
        WphY: '每小时晶圆数',
        WphX: '剂量 (mJ/cm²)',
        WphTitle: '每小时晶圆数与光刻胶所需剂量的关系',
        MirrorNote: '任何材料对 13.5 nm ' +
          '光的折射都只差几个百分点，所以单个表面反射不了多少。把一种重金属和一种轻的间隔层交替叠起来，每对厚半个波长，八十个界面上微弱的反射就会同相叠加。内部的驻波把暗的波节放在钼里——正是光会被吸收的地方。粗糙的界面让反射镜赖以反射的台阶变得模糊；光本身从真空里裂解出来的碳则在表面上吸收。一台光刻机里这样的反射连着有十几次，所以一面镜子损失一个百分点，就要损失十二次。',
        KnobAngle: '入射角',
        KnobCarbon: '镜面上沉积的碳',
        KnobRough: '界面粗糙度',
        KnobPairs: '膜对数',
        KnobGamma: '钼占周期的比例',
        KnobPeriod: '周期',
        MirrorSolving: '正在求解膜系……',
        StatBestNote: '当前设定：{{now}} nm',
        StatBest: '13.5 nm 的最佳周期',
        StatChainNote: '连续 {{n}} 次反射，在 13.5 nm',
        StatChain: '穿过整台光刻机',
        StatFwhmNote: '峰值一半处的全宽',
        StatFwhm: '带宽',
        StatRNote: '峰值 {{peak}}%，位于 {{at}} nm',
        StatR: '13.5 nm 处的反射率',
        PairsHere: '当前膜系',
        PairsSeries: '反射率',
        PairsY: '13.5 nm 处的反射率 (%)',
        PairsX: 'Mo/Si 膜对数',
        PairsTitle: '再多加膜对也没用',
        MirrorEdge: 'Si L 吸收边',
        MirrorChain: '经过整条反射镜链之后，×20',
        MirrorSeries: '当前膜系',
        MirrorY: '反射率 (%)',
        MirrorX: '波长 (nm)',
        MirrorTitle: '单面反射镜随波长的变化',
        FieldX: '距表面的深度 (nm)',
        FieldTitle: '反射镜顶部内部的 |E|²',
        SourceNote: '这条阶梯是算出来的：每一级都是该离子薛定谔方程的自洽解，凡是 NIST ' +
          '有数据的地方都与之吻合。电荷态分布也是在它上面算出来的——电子碰撞电离与复合之间的平衡。离子在哪里发光则是测出来的，不是算出来的：单电子计算把 ' +
          '4d→4f 跃迁放在 17 nm 附近，是 4d 与 4f 电子之间的交换作用——一种多电子效应——把 Sn⁸⁺ 到 Sn¹³⁺ 的光一起拉到了 ' +
          '13.5 nm。正是这种叠加，让锡等离子体在关键带宽里比黑体强五十倍。',
        LaserSolid: '固体激光，1 µm',
        LaserCo2: 'CO₂，10.6 µm',
        KnobLaser: '驱动激光',
        KnobTeValue: '{{ev}} eV · {{kk}} 千 K',
        KnobTe: '电子温度',
        StatCeNote: '激光能量中进入带内的比例，在真实光源上测得',
        StatCe: '量产转换效率',
        StatPhotonNote: '光是从 Sn¹⁰⁺ 再剥一个电子就要 {{ev}} eV——是一个离子发回的光子能量的两倍多',
        StatPhoton: '一个光子',
        StatBlackNote: '同一带宽里——所以要谱线光源，所以要锡',
        StatBlack: '黑体只能放进',
        StatInBandNote: '这些离子的发射中落在 13.5 nm ± 1% 以内的部分',
        StatInBand: '带内',
        StatMeanNote: '最多的离子是 Sn{{q}}+',
        StatMean: '平均电荷',
        ScanHere: '当前等离子体',
        ScanSolid: '1 µm 激光，10²¹ 电子/cm³',
        ScanCo2: 'CO₂ 激光，10¹⁹ 电子/cm³',
        ScanY: '带内光的比例 (%)',
        ScanX: '电子温度 (eV)',
        ScanTitle: '激光应当把等离子体打到什么温度',
        SpectrumBand: '2% 带宽',
        SpectrumBlack: '同温度的黑体',
        SpectrumTin: '此温度下的锡离子',
        SpectrumY: '发射功率，峰值 = 1',
        SpectrumX: '波长 (nm)',
        SpectrumTitle: '发射出来的光',
        ChargeBand: '这些离子在带内发光',
        ChargeSeries: '离子',
        ChargeY: '占锡的比例',
        ChargeX: '电荷态 q（锡离子 Sn q+）',
        ChargeTitle: '{{te}} eV 的等离子体里有哪些离子',
        LadderPhoton: '一个 13.5 nm 光子',
        LadderNist: '实测值（NIST）',
        LadderSolved: '求解值（Hartree-Fock-Slater）',
        LadderY: '电离能 (eV)',
        LadderX: '被电离离子的电荷态 q',
        LadderTitle: '阶梯：再剥掉一个电子要多少能量',
      },
      panel: {
        HelpMore: '了解更多',
        AboutSim: '这里展示了什么，模型又省略了什么',
        BenchKnobs: '旋钮',
        BenchReadouts: '读数',
        BenchRun: '运行',
        DownForPm: '在车间里，这台设备正因预防性维护而上锁挂牌：外壳已拆下，里面有一名技术员，所以它的屏幕和工作区看板都显示停机。你在这里操作的是它的合格工艺，也就是维护结束后它要恢复运行的那套工艺。从这个面板运行的批次，就是按那套工艺跑的，和摘牌之后一样。',
        ValueOnTarget: '在目标上',
        SolverHeadMeasured: '实测',
        SolverHeadClosedForm: '解析式',
        SolverHeadSolved: '数值解',
        LabelClosed: '解析解',
        LabelSolved: '数值解',
        TravelCard: '流转卡',
        ThermalBudgetLine: '热预算：相当于 1000 °C 下 {{min}} 分钟，共 {{steps}} 个高温工步——足以让硼移动约 {{nm}} nm',
        SectionStoredRecipes: '已存配方',
        SectionWafer: '晶圆',
        SectionLot: '批次',
        SectionProcessState: '工艺状态',
        SectionRecipe: '配方',
        SectionShift: '班次',
      },
      cleanroom: {
        NextThoseAddersBecomeCln09: '这些增量会变成 CLN-09 的来料颗粒数，再变成 INSP-13 的缺陷密度，再变成 PRB-14 ' +
          '的良率数字。沿产线追踪其中一个，看看晶圆盒敞开一分钟折合多少裸片。',
        AxisXAirChangesPerHour: '每小时换气次数',
        TitleRecoveryTimeAgainstAirChanges: '恢复时间随换气次数的变化',
        LabelDepositionVelocity: '沉降速度',
        NoteAfterFilterLoading: '滤网积尘之后',
        LabelEffectiveAch: '有效换气次数',
        LabelRecovery: '恢复',
        HypSteadyStateConcentrationShouldGo: '稳态浓度应等于产生量除以通风量，所以换气次数加倍应使浓度大约减半——绝对收益逐步递减。恢复时间应以同样方式下降。如果是这样，通风就是一根价格陡涨的杠杆，而且只要房间里有人，它就有一个跌不破的下限。',
        AskedWhatDoesVentilationBuyAnd: '通风买到了什么？一次扰动的影响会持续多久？',
        SumAirChangesRecoveryTimeAnd: '换气次数、恢复时间，以及敞开晶圆盒上的暴露时钟。',
        NextEveryNumberHereIsPer: '这里的每个数字都是按每分钟暴露计的。问我暴露时间本身值多少——它是这张单子里唯一由设备排程而不是由人控制的变量。',
        LabelWrongOrder: '顺序错误',
        LabelStreetClothes: '便服',
        LabelPenetrationGowned: '穿透率，全套穿戴',
        HypEachGarmentIsAFilter: '每件洁净服都是一个过滤器，串联的过滤器是相乘而不是相加——所以整套组合应远好于任何单件，而去掉一件的代价应远大于那件看上去单独贡献的量。顺序重要也是同一个道理：在更脏的一件之后穿上的衣服，还没开始工作就已被污染。',
        AskedHowMuchDoesTheGowning: '更衣规程实际买到了多少？',
        SumGarmentPenetrationAsAProduct: '洁净服穿透率是一个乘积，以及为什么穿戴顺序是一条规则。',
        NextTheDownflowCarriesParticlesFrom: '下行气流把颗粒从人站的位置带向他下方和下游的一切。既然如此，装载口敞开时你该站在过道的哪里——墙上的监测器能告诉你你站错了吗？',
        LabelRoomAverageTheMonitor: '全室平均（监测器）',
        LabelWorkZone: '工作区',
        AxisYParticlesM: '颗粒/m³',
        AxisXPeopleInTheBay: '区间内人数',
        TitleRoomAverageAndWorkZone: '全室平均与工作区浓度随人数的变化',
        LabelZoneRoom: '工作区 / 全室',
        LabelWaferSees: '晶圆感受到',
        LabelMonitorReads: '监测器读数',
        HypACeilingMonitorMeasuresA: '天花板监测器测的是全室平均，而平均假定污染源均匀分布在房间里。站在设备前的人不是均匀分布的——他是距离敞开产品一米的点源。如果这一点重要，工作区浓度就应明显高于全室平均，而且每多一个人差距就该拉大。',
        AskedIsTheNumberOnThe: '墙上的数字是晶圆感受到的数字吗？',
        SumRoomAverageAgainstWorkZone: '全室平均对工作区，以及为什么墙上的监测器可以既正确又没用。',
      },
      intents: {
        NextHoldingIsTheEasyHalf: '扣住是简单的那一半。已经放在装载口的那批货现在怎么办？在你把它判为报废而不是等待之前，还需要知道什么？',
        NextAQualPassesOnTheDay: '资格认定只对做它的那一天有效。下一批要看到什么，你才会认定这次只是运气好而不是设备真的健康？在你相信任何一种结论之前，又想看多少片？',
        LabelBenchReadings: '台面读数',
        LabelNonUniformity: '不均匀度',
        LabelMonitorRate: '监控片速率',
        VerdictWithAWatchItem: '放行，但有一项要盯着',
        VerdictHeld: '扣住',
        VerdictQualified: '已认定',
        LabelVerdict: '判定',
        HypAQualificationIsADecision: '资格认定是一个决定，不是一段解释。只要监控片落在规格窗口内，而且台面上每一项读数都在各自限值内，设备就放行；任何一项不合格就扣住。再用标称腔体跑一次、用已认定配方跑一次，就足以指出是哪一边的责任，不必做完整诊断。',
        AskedIsThisToolFitToRun: '这台设备现在能不能跑产品，如果不能该怎么办？',
        SumRunTheMonitorWafer: '跑一片监控片，把台面读数逐项对照限值，然后做出放行判断。',
        LabelAsFound: '发现时状态',
        LabelNoEffect: '无效果',
        AxisYRecovered: '找回比例 %',
        AxisXCandidateRanked: '候选项（按排名）',
        TitleHowMuchEachSingleRepair: '每项单独修复各能找回多少',
        LabelDeviationAsFound: '发现时的偏差',
        LabelSeparation: '区分度',
        LabelNextBest: '次佳候选',
        LabelMostLikelyCause: '最可能的原因',
        HypIfASingleChamberAspect: '如果罪魁是腔体的某一项，那么把恰好那一项——且只有那一项——恢复到标称值就应能找回结果，而恢复其他任何一项都不行。如果没有任何单项恢复有效，原因就不在腔体。',
        AskedTheRecipeHasNotChanged: '配方没变，结果却变了。腔体里是什么动了？',
        SumIsolateAChamberFaultBy: '每次只恢复一个嫌疑项，以此隔离腔体故障。',
        AxisYNonUniformity: '非均匀性 %',
        LabelBestUniformity: '最佳均匀性',
        LabelRateChange: '速率变化',
        LabelAtYourSetting: '你的设置下',
        SumSweepAnySingleRecipeKnob: '扫描配方中任意一个旋钮，同时看到所有后果。',
        LabelHeavyLoad: '重负载',
        TitleRadialRateTheSpreadThat: '径向速率——决定过刻蚀窗口的分散',
        LabelMeanRate: '平均速率',
        LabelNuHeavyLoad: '重负载 NU',
        LabelNuAsRunning: '当前 NU',
        HypOpticalEmissionFromTheEtch: '刻蚀产物的光学发射在薄膜清除时下降，所以曲线标出了终点。但晶圆不是一下子全部清干净的——它在由均匀性决定的窗口内逐渐清除——所以所需的过刻蚀，以及期间损失的氧化层，都应随非均匀性放大。',
        AskedHowIsTheEndOf: '刻蚀的终点是怎么检测的？需要多少过刻蚀？',
        SumEndpointDetectionOveretchAndWhat: '终点检测、过刻蚀，以及非均匀性在下游的代价。',
        NextTheSheathIsThinnerThan: '在这个气压下鞘层比离子平均自由程还薄，所以离子不经碰撞穿过鞘层，以窄能量带到达。气压升到一定程度，这就不再成立。当离子开始以各种角度到达时，剖面会怎样？',
        AxisXSourcePowerW: '源功率 (W)',
        TitleEtchRateVersusSourcePower: '固定偏压下刻蚀速率随源功率的变化',
        NoteImprovesWithSofterIons: '离子更柔和时反而改善',
        LabelRate2001600W: '速率，200 -> 1600 W',
        NoteScale: '尺度',
        DesignSourcePowerSwept200To: '源功率从 200 扫到 1600 W，偏压功率恒定。在每个点求解放电，得到等离子体密度、玻姆通量、由功率平衡得到的鞘层电压，以及由 Child ' +
          '定律得到的鞘层厚度。',
        HypTheBiasSupplyDeliversA: '偏压电源向穿越鞘层的离子输送固定的瓦数。因此每个离子的能量就是瓦数除以离子电流。如果这是对的，提高源功率——制造更多离子——应让每个离子的能量更低，尽管偏压设定从未动过。',
        AskedWhereDoesIonBombardmentEnergy: '离子轰击的能量从哪里来？哪个旋钮控制它？',
        NextBiasRaisesIonEnergySource: '偏压提高的是离子能量。源功率提高的是离子数量。如果你需要更高速率又不想丢选择比，该伸手去动哪一个——把源功率调高时，鞘层又会对离子能量做什么？',
        AxisXBiasPowerW: '偏压功率 (W)',
        TitleRateAndSelectivityBothRespond: '速率和选择比都响应偏压——方向相反',
        LabelProfileAt200W: '200 W 时的剖面',
        LabelProfileAt0W: '0 W 时的剖面',
        LabelSelectivityAt500W: '500 W 时选择比',
        LabelRateAt500W: '500 W 时速率',
        LabelOutOfReach: '无法企及',
        DesignBiasSweptFrom0To: '偏压从 0 扫到 500 W，源功率、气压和流量固定。测量刻蚀速率、Si:SiO2 选择比、侧壁角度，以及相应的离子能量。',
        HypBiasPowerSetsIonEnergy: '偏压功率决定离子能量，不决定离子数量。硅有自发的氟反应通道和很低的离子阈值；氧化物两样都没有。所以提高偏压应让两者都变快，但相对而言氧化物快得更多——也就是说，速率是拿选择比买来的。',
        SumTradeRateAgainstSelectivityAnd: '在速率与选择比、氧化层击穿之间做取舍。',
        LabelLoadTheLowLoadProduct: '载入低负载产品',
        NextARecipeQualifiedOnA: '一个在开口率 15% 产品上认证过的配方，现在要跑开口率 65% ' +
          '的产品。你是重新定时，还是重新认证配方？问我：每种选择在晶圆边缘的氧化层损失上各要付出什么。',
        TitleRadialProfileAtEachLoad: '各负载下的径向分布',
        LabelMeanRateL305: '平均速率',
        AxisXOpenAreaFraction: '开口面积比',
        TitleEtchRateVersusOpenArea: '刻蚀速率随开口率的变化',
        LabelNuAtHeavyLoad: '重负载下的 NU',
        LabelLoadingRatio: '负载比',
        LabelRateAt90Open: '开口率 90% 时速率',
        LabelRateAt4Open: '开口率 4% 时速率',
        HypEveryExposedSquareMillimetreOf: '每一平方毫米裸露的硅都在争夺同一份有限的氟供应。如果确实如此，速率应随开口率上升而下降，同时晶圆的均匀性应变差——因为更重的负载把刻蚀推向输运控制。',
        AskedWhyDoesAnIdenticalRecipe: '为什么完全相同的配方在不同产品上给出不同的刻蚀速率？',
        LabelLoadARecipeThatRuns: '载入一个边缘偏快的配方（开口率 90%）',
        LabelAsRunning: '当前状态',
        AxisYFAtomsM3: 'F 原子 m⁻³',
        TitleEtchantDensityAtTheSurface: '表面刻蚀剂密度——速率背后的供应',
        AxisXRR0Centre: 'r / R (0 = 中心)',
        TitleEtchRateAcrossTheWafer: '沿晶圆半径的刻蚀速率',
        LabelBestCondition: '最佳条件',
        LabelSignature: '特征',
        HypAFastEdgeMeansThe: '边缘快意味着晶圆边缘比中心得到更充足的刻蚀剂供应。如果这是对的，任何提高供需比的做法——更多气体、更小偏压、更快扩散——都应把分布拉平。如果全都无效，原因就根本不在供应。',
        AskedWhyDoesTheEtchRate: '为什么刻蚀速率从中心到边缘有倾斜？这里的原因是什么？',
        SumDiagnoseACentreToEdge: '诊断中心到边缘的特征，找出是哪种机制造成的。',
      },
      metro: {
        NextYouHaveOneAlarmAnd: '你手上有一次报警和一个被扣住的批次。扣批要花钱，把坏品发出去也要花钱。你接下来会测什么来做决定？还要多少次额外读数，答案才不再是抛硬币？',
        LabelNoisyGauge: '偏吵的量具',
        LabelStepAt30: '第 30 批处的阶跃',
        TitleTheDriftingRunMeasuredAnd: '漂移中的运行——实测值，以及背后的真相',
        LabelCpkNoisyGauge: 'Cpk，量具偏吵',
        LabelStepCaughtAfter: '阶跃在此之后被抓住',
        LabelDriftCaughtAtLot: '漂移在此批次被抓住',
        NoteIn60LotsOfA: '来自从未移动过的工艺的 60 个批次',
        LabelFalseAlarmsStable: '误报警，稳定工艺',
        HypControlRulesFireOnWhatever: '控制规则只对摆在它面前的东西报警，而摆在它面前的是工艺加量具。所以完全稳定的工艺也会产生报警；真正的漂移应先触发游程规则、后触发三西格玛规则；更吵的量具会毁掉能力指数，而工艺本身丝毫未变。',
        AskedTheChartFiredIsThe: '控制图报警了。是工艺在动，还是噪声？',
        SumSeparateARealProcessDrift: '把真实的工艺漂移与测量噪声、与运气区分开。',
        NextIfTheGaugeIsNot: '如果量具能力不足，你可以对多次读数取平均来压低它的噪声。这要付出什么代价？到什么程度不如干脆买台更好的设备？',
        LabelTightProcess: '严苛工艺',
        LabelLooseProcess: '宽松工艺',
        LabelThisProcess: '当前工艺',
        NoteRepeatability: '重复性',
        DesignTwentyFiveRepeatMeasurementsOf: '对同一片不变的晶圆重复测量二十五次，再把这一重复性与三种不同的工艺波动做比较。',
        HypAGaugeHasOneRepeatability: '量具有一个固定的重复性，但能力不是量具的属性。它是测量分散与总分散之比。所以同一台设备，对宽松的工艺应判为有能力，对严苛的工艺应判为没能力——而设备本身什么都没变。',
        AskedIsThisMeasurementToolCapable: '这台测量设备有能力看清它所监控的工艺吗？',
        SumCompareMeasurementSpreadWithProcess: '把测量分散与工艺分散做比较——能力是一个比值。',
        NextIfTheResidualCannotTell: '如果残差不能告诉你模型错了，什么能？想想第二种独立的测量必须对什么敏感，才能抓住这个问题。',
        LabelRecipeIndex: '配方折射率',
        LabelCorrectIndex: '正确折射率',
        TitleMeasuredSpectrumAndTheFitted: '实测光谱与拟合模型',
        LabelResidualRecipe: '残差，配方模型',
        LabelResidualCorrect: '残差，正确模型',
        LabelRecipeModel: '配方模型',
        LabelCorrectModel: '正确模型',
        HypTheToolMeasuresReflectedLight: '设备测的是反射光，不是厚度。它找的是模型光谱与所见最匹配的那个薄膜厚度。模型对，答案就该对；模型的折射率错了，拟合仍会收敛，仍会报出一个自信的数字，但它是错的——而残差几乎不会抱怨。',
        AskedTheToolReportedAThickness: '设备报出了一个厚度。它是对的吗？',
        SumShowThatAMeasurementIs: '说明一次测量其实是一次模型拟合，因此可以精确地错。',
      },
      dep: {
        TitleThicknessAcrossTheRadiusUnder: '各条件下沿半径的厚度分布',
        LabelBestCondition: '最佳条件',
        LabelParasitic: '寄生',
        LabelAsRunning: '当前状态',
        HypAProperlySaturatedProperlyPurged: '饱和充分、吹扫充分的 ALD ' +
          '薄膜应几乎完全均匀，因为无论流过多少气体，每个位点都恰好接收一个分子。所以非均匀性意味着生长在某处跟随了气体输送——要么剂量从未饱和，要么寄生 ' +
          'CVD 在 ALD 之上额外镀膜。两者的解决办法不同，分别测试就能区分它们。',
        AskedIsThisThicknessMapA: '这张厚度分布图是剂量问题还是吹扫问题？',
        SumDecideWhetherAThicknessMap: '判断一张厚度分布图是剂量问题还是吹扫问题。',
        TitleFlatWaferGrowthOverThe: '同一扫描范围内平坦晶圆的生长——纹丝不动',
        LabelStepCoverage: '台阶覆盖率',
        AxisYCoverageAtTheBottom: '底部覆盖率 %',
        TitleStepCoverageVersusDoseExposure: '台阶覆盖率随剂量的变化——曝置才是杠杆',
        NoteAlreadySaturatedUpTop: '顶部早已饱和',
        LabelFlatGrowthUnchanged: '平面生长不变',
        LabelOnAFlatWafer: '平坦晶圆上',
        HypInsideADeepFeatureThe: '在深结构内部，前驱体靠在壁间反弹前进，每次反弹都是它在到达底部之前发生反应的机会。如果限制保形性的正是这一点，那么解决办法就是曝置——更长的剂量——它应能改善底部，而对平坦晶圆的生长毫无影响，因为平坦表面早已饱和。',
        SumShowWhyConformalityIsA: '说明为什么保形性是剂量问题，而不是化学问题。',
        NextThePurgeHasToClear: '吹扫必须清空腔体，而腔体按驻留时间清空。如果提高载气流量，能不能进一步缩短吹扫——还会改变什么？',
        LabelCycleTime: '循环时间',
        AxisYSecondsPerCycle: '每循环秒数',
        TitleCycleTimeVersusPurgeTime: '循环时间随吹扫时间的变化——你买到的产能',
        AxisXPurgeMs: '吹扫 (ms)',
        TitleParasiticCvdVersusPurgeTime: '寄生 CVD 随吹扫时间的变化',
        LabelCycleTimeSaved: '节省的循环时间',
        LabelShortestSafePurge: '最短安全吹扫',
        LabelResidenceTime: '驻留时间',
        HypPurgingEmptiesTheChamberExponentially: '吹扫按气体驻留时间以指数方式清空腔体。共反应物到达时仍残留的前驱体会在气相中直接反应，而不是等待空位——那就是 ' +
          'CVD。所以短吹扫应该买到循环时间，代价是寄生生长和均匀性，而不是生长速率。',
        AskedHowShortCanThePurge: '吹扫最短能到多短，工艺才不至于不再是 ALD？',
        SumTradeCycleTimeAgainstParasitic: '在循环时间与寄生 CVD 和均匀性之间做取舍。',
        NextBothEndsOfTheWindow: '窗口的两端都比中间长得更快。如果有人只给你看一个厚度数字，并告诉你设备温度偏高，你如何把它与温度偏低区分开？',
        LabelGrowthHere: '此处生长量',
        LabelParasiticHere: '此处寄生生长',
        LabelYourSetpoint: '你的设定点',
        HypAdsorptionNeedsHeatToComplete: '吸附需要热量才能完成，但热量过多会让前驱体自行脱附或分解。如果这是对的，中间会有一个区间，生长平坦且基本全是自限制的，而两端的寄生生长各因不同的原因上升。',
        AskedWhereIsTheTemperatureBand: '这个工艺保持 ALD 特性的温度区间在哪里？',
        SumMapTheAldWindowAnd: '描绘 ALD 窗口，找出生长在哪里不再自限制。',
        NextASaturatedDoseOnA: '在平坦晶圆上已饱和的剂量，仍可能让深结构的底部得不到前驱体，因为前驱体必须在反应之前先到达底部。你的产品深宽比是多少？这个剂量在它的底部还饱和吗？',
        AxisYNonUniformity: '非均匀性 %',
        TitleNonUniformityFollowsSaturation: '非均匀性跟随饱和程度',
        AxisXDoseMs: '剂量 (ms)',
        LabelUniformityAtYourDose: '你的剂量下的均匀性',
        LabelDoseNeeded: '所需剂量',
        LabelSaturatedGrowth: '饱和生长量',
        LabelSelfLimiting: '自限制',
        HypIfTheSurfaceSaturatesGrowth: '如果表面会饱和，每循环生长量会随剂量上升然后走平，拐点之后的一切都完全相同。如果它继续上升，表面就不是限制因素，这个工艺就是披着 ALD 配方外衣的 ' +
          'CVD。',
        AskedDoesGrowthStopRespondingTo: '生长会不会不再随剂量变化——这个工艺真的是自限制的吗？',
        NextDoseCost: '延长剂量脉冲能修好沟槽，但也会拉长你全部 {{cycles}} 个循环中的每一个。算一算这在运行时间上的代价，再决定提高前驱体分压是否能更便宜地买到同样的暴露量。',
        SumSweepDoseTimeAndFind: '扫描剂量时间，找出生长在哪里不再响应。',
      },
      config: {
        LabelDeepseek: 'DeepSeek',
      },
      route: {
        step: { wellDriveIn: '阱区推进', gateOxidation: '栅氧化', spacerDensify: '侧墙致密化', spikeAnneal: '尖峰退火' },
        stepPurpose: {
          'a-day-per-cooldown-the-test-is-the-bottleneck': '每次降温要一天：测试就是瓶颈',
          'aluminium-wire-which-goes-superconducting-with-the-chip': '用铝线，它会和芯片一起进入超导态',
          'a-fridge-takes-a-chip-not-a-wafer': '制冷机放的是芯片，不是晶圆',
          'area-sets-the-frequency-and-ten-percent-wide-is-a-collision': '面积决定频率，宽百分之十就会发生频率碰撞',
          'a-nanometre-of-aluminium-oxide-counted-in-cycles': '一纳米的氧化铝，按循环数来数',
          'transfer-it-into-the-superconducting-film': '把图形转移到超导薄膜里',
          'capacitor-pads-resonators-and-the-junction-leads': '电容极板、谐振器和结的引线',
          'a-lossy-surface-is-a-lossy-qubit': '表面有损耗，量子比特就有损耗',
          'bin-it-and-decide-what-escapes': '分档，并决定什么会漏出去',
          'and-the-die-is-under-183-mpa-for-the-rest-of-its-life': '裸片此后终生承受 183 MPa',
          'onto-a-substrate': '装到基板上',
          'and-the-wafer-stops-being-a-wafer': '晶圆从此不再是晶圆',
          'the-first-honest-yield-number-anyone-has-had': '所有人拿到的第一个真实良率数字',
          'last-look-before-the-wafer-is-cut-up': '晶圆被切开前的最后一眼',
          'a-defect-free-doped-to-order-layer-over-the-bulk': '在体硅之上按需掺杂、无缺陷的一层',
          'before-it-is-allowed-into-the-front-end': '进入前段产线之前的一道关',
          'one-crystal-becomes-a-thousand-wafers': '一根晶体变成上千片晶圆',
          '72-hours-for-one-ingot-and-one-ingot-is-1000-wafers': '一根晶棒要 72 小时，而一根晶棒约合 1000 片晶圆',
          'the-next-layer-prints-on-this-surface': '下一层就印在这个表面上',
          'slurry-is-a-defect-if-it-is-left-on': '浆料留在上面就是缺陷',
          'take-off-everything-above-the-trench': '把沟槽以上的一切磨掉',
          'bottom-up-fill-and-an-overburden-for-cmp': '自底向上填充，并留给 CMP 一层余量',
          'copper-poisons-silicon-so-it-is-fenced-in': '铜会毒化硅，所以要把它圈起来',
          'polymer-off-before-anything-is-deposited-on-it': '去掉聚合物，然后才能在上面沉积',
          'ash-the-resist-off-before-anything-is-deposited': '在沉积任何东西之前先把胶灰化去除',
          'and-the-wire-that-sits-on-top-of-the-via': '以及骑在通孔上方的那条导线',
          'down-to-the-layer-below': '一直打到下一层',
          'where-the-wires-go': '导线要走的地方',
          'the-insulator-the-trenches-go-into': '沟槽所在的绝缘层',
          'because-everything-after-this-assumes-it-worked': '因为后面的一切都默认这一步成功了',
          'activate-the-dopant-and-move-it-as-little-as-possible': '激活掺杂并尽量少让它移动',
          'self-aligned-to-the-gate-that-was-just-etched': '与刚刻出的栅自对准',
          'the-gate-electrode': '栅电极',
          'the-thinnest-most-consequential-film-in-the-device': '器件中最薄、影响最深远的一层膜',
          'deep-and-expensive-in-budget': '很深，而且很耗热预算',
          'the-tub-the-device-sits-in': '器件栖身的那口阱',
          'a-furnace-drives-contamination-in-it-does-not-remove-it': '炉管会把污染往里驱入，而不是去除它',
          'print-one-line-in-four': '每四条线曝印一条',
          'four-lines-where-there-was-one': '一条线变成四条',
          'anisotropic-again': '再来一次各向异性刻蚀',
          'the-first-spacers-are-now-the-mandrels': '第一层侧墙现在成了芯轴',
          'two-lines-where-there-was-one': '一条线变成两条',
          'anisotropic-clears-the-horizontals': '各向异性：清掉水平面',
          'ald-this-thickness-sets-the-half-pitch': 'ALD——这层厚度决定半节距',
          'the-knob-that-centres-the-pattern': '把图形置中的那个旋钮',
          'the-only-exposure-in-the-whole-module': '整个模组唯一的一次曝光',
          'transfer-that-too': '再把这些也转移下去',
          'print-the-lines-in-between': '在中间曝印另一半线条',
          'transfer-it': '把它转移下去',
          'print-every-other-line': '曝印每隔一条的线条',
          'transfer-resist-into-the-film': '把光刻胶图形转移进薄膜',
          'print-the-pattern-directly-at-13-5-nm': '以 13.5 nm 直接曝印图形',
          'print-the-pattern-directly': '直接曝印图形',
        },
        stepLabel: {
          'cool-down-and-characterise': '降温并表征',
          'wire-bond-to-the-sample-holder': '引线键合到样品座',
          'dice-into-chips': '切割成芯片',
          'measure-every-junction': '量测每一个结',
          'grow-the-tunnel-barrier': '生长隧穿势垒',
          'etch-the-circuit': '刻蚀电路',
          'expose-the-circuit': '曝光电路图形',
          'substrate-clean': '衬底清洗',
          'final-test-and-burn-in': '终测与老化',
          'mould-and-cure': '塑封与固化',
          'die-attach-and-wire-bond': '贴片与引线键合',
          'grind-and-dice': '减薄与划片',
          'wafer-sort': '晶圆测试分选',
          'final-inspection': '最终检查',
          'epitaxial-device-layer': '外延器件层',
          'incoming-clean': '来料清洗',
          'slice-lap-etch-polish': '切片、研磨、腐蚀、抛光',
          'crystal-growth': '晶体生长',
          'thickness-and-dishing-check': '厚度与碟形凹陷检查',
          'post-cmp-clean': 'CMP 后清洗',
          'copper-cmp': '铜 CMP',
          'copper-plate': '铜电镀',
          'barrier-and-seed': '阻挡层与种子层',
          'post-etch-clean': '刻蚀后清洗',
          'resist-strip': '去胶',
          'trench-etch': '沟槽刻蚀',
          'via-etch': '通孔刻蚀',
          'trench-lithography': '沟槽光刻',
          'dielectric-deposition': '介质沉积',
          'post-gate-metrology': '栅后量测',
          'spike-anneal': '尖峰退火',
          'source-drain-implant': '源漏注入',
          'polysilicon-deposition': '多晶硅沉积',
          'gate-oxidation': '栅氧化',
          'well-drive-in': '阱推进',
          'well-implant': '阱注入',
          'pre-gate-clean': '栅前清洗',
          'pull-the-first-spacers': '拔除第一道侧墙',
          'spacer-2-etch': '刻蚀第二层侧墙',
          'deposit-spacer-2': '沉积第二层侧墙',
          'pull-the-mandrel': '拔除芯轴',
          'spacer-1-etch': '刻蚀第一层侧墙',
          'deposit-spacer-1': '沉积第一层侧墙',
          'trim-the-mandrel': '修整芯轴',
          'print-the-mandrel': '曝印芯轴',
          'etch-population-4': '刻蚀，第 4 组',
          'etch-population-3': '刻蚀，第 3 组',
          'etch-population-2': '刻蚀，第 2 组',
          'etch-population-1': '刻蚀，第 1 组',
          'expose-population-4': '曝光，第 4 组',
          'expose-population-3': '曝光，第 3 组',
          'expose-population-2': '曝光，第 2 组',
          'expose-population-1': '曝光，第 1 组',
          'etch-second-population': '刻蚀，第二组线条',
          'expose-second-population': '曝光，第二组线条',
          'etch-first-population': '刻蚀，第一组线条',
          'expose-first-population': '曝光，第一组线条',
          'pattern-transfer-etch': '图形转移刻蚀',
          expose: '曝光',
        },
        NoteTakeTheCall: '四个候选原因，其中之一是：真的就只是某一台机台。站点报警是免费的，逐层曲线不是——这正是要做的决定：花一个半小时的机台时间去看趋势，还是凭一份怎么看都是绿色的汇总直接下结论。',
        FaultIntro: '本模拟器中其他所有故障都是机台故障：某个腔体出了问题，而且腔体自己会报出来。这三个不是。在每一个案例里，每一次过站都在各自的规格之内，而产品却超出了自己的规格——所以没有坏读数可找，没有报警可响应，也没有机台可修。要练的本事是别再盯着机台看，这也是最难教的习惯，因为一个好技术员的全部直觉都在喊：去查机台。',
        AxisChlorineDoseS: '氯剂量 (s)',
        AxisEpcNm: 'EPC (nm)',
        NoteTheSameInstrumentDep02: '与 DEP-02 上 ALD ' +
          '用的是同一件仪器，同一个测试：真正自限制的半循环会先上升然后走平。曲线一直往上爬，就说明更多前驱体仍在去除更多材料。注意仅凭这条曲线并不能证明 ' +
          'ALE——能量过高的配方也会饱和。协同度那个数字才是把它们区分开的测试。',
        NoteBelowSaturationDoseNow: '低于饱和——现在是剂量决定速率',
        NoteSaturated: '已饱和',
        ValueSputtering: '溅射',
        ValueStarved: '欠饱和',
        ValueInWindow: '在窗口内',
        NotePartOfThisEtchIs: '这次刻蚀有一部分是连续进行的',
        NoteNeitherHalfEtchesAlone: '两个半反应都不单独刻蚀——这才是 ALE',
        LabelEtchRate: '刻蚀速率',
        LabelSelectivity: '选择比',
        LabelRegime: '工作区',
        LabelSynergy: '协同度',
        mat: {
          'silicon-oxide': '氧化物',
          'silicon-nitride': '氮化物',
          silicon: '硅',
        },
        SectionFilm: '薄膜',
        SecondaryLabel: '最后一层的 CD 分散',
        ToolLabel: '工艺路线——每台设备，按顺序',
        fault: {
          justOneTool: {
            Evidence: [
              '有一个站点超出规格，而真正的流程故障从不出现这种情况。',
              '分层图走平，结深在目标上，没有触发任何控制规则。',
              '变化是突然的、有明确日期的——流程故障是慢慢到来的。',
            ],
            Truth: '这不是流程故障。CMP-11 ' +
              '正在为去除速率报警——一个普普通通的单站问题，配一个普普通通的诊断，它自己的班次报告二十分钟就会点出名字。大多数良率波动就是一台设备的事，一个学会条件反射式抄起流程工具的学生，学到的是这堂课错误的那一半。先读站点报警；它们是免费的。',
            Complaint: '良率从周二起掉了五个点。良率工程师开了流程复盘，想谈逐层累积。',
            Label: '就是一台设备的事，流程是干扰项',
          },
          sequenceDrift: {
            Evidence: [
              '控制图规则触发了，却没有任何一个点越限。',
              '黄金晶圆直接量出了累计偏移。',
              'CMP 分层图走平，结深在目标上。',
            ],
            Truth: '有一台设备每层漂约 0.18。十一层就是 ' +
              '1.8——单侧规格的六成——而没有一个读数越出过控制限，因为漂移相对判限很小、相对批间散布却很大。居中的过程连续八点落在中心线同一侧是 1/128 ' +
              '的事件，这就是这条规则存在的原因，也是只有跨访问的控制图才有它的原因。',
            Complaint: '参数良率每周滑一点，没有人指得出是哪一批出的错。什么都没报警。',
            Label: '十一次测量全部合格，而有什么在动',
          },
          budgetSpent: {
            Evidence: [
              '没有任何一次退火超过它自己的限值。',
              '结剖切超过 78 nm。',
              'CMP 的分层图走平，没有触发任何控制图规则。',
            ],
            Truth: '为了解决一个电阻率投诉，阱推进从 300 秒延长到了 880 秒。它仍是一次合法的退火——单步限值是 900 ' +
              '秒——但它花掉了流程末尾尖峰退火所依赖的预算。结深随总量的平方根变化，所以流程里最后一步被之前的每一步约束着，而这本账没有人管。',
            Complaint: '整个产品的参数分级都挪了位。每个炉管步骤都在自己的窗口内，THRM-04 每次诊断都干净。',
            Label: '结太深，而没有一次退火超时',
          },
          stackAccumulation: {
            Evidence: [
              'CMP 后台阶高度按限值原文十层全过。',
              '对层号作图后，它爬升并停在高处，而不是走平。',
              '没有站点报警，结深也在目标上。',
            ],
            Truth: 'CMP-11 的垫修整器磨损了，平坦化效率从 0.90 掉到 0.55。每次抛光留下的台阶仍低于 80 nm ' +
              '的单次限值——但它向上传递的形貌比以前多，十层复利下来是 70 nm。双方说的都是实话。单次限值从来不是从焦深预算推出来的，80 nm ' +
              '和光刻机真正需要的 55 nm 之间的缝隙，就是故障藏身的地方。',
            Complaint: '光刻在每一批的第 8 层金属上报废，怪焦距。CMP 说自己十层全部合格，还有数据为证。',
            Label: '第 8 层金属印不出来，而 CMP-11 每一层都合格',
          },
          routeHealthy: {
            Evidence: [
              '每一次单站检查都通过，没有站点在报警。',
              '分层图走平，结深在目标上。',
            ],
            Truth: '什么问题都没有。正确的判定就是继续跑。',
            Complaint: '良率例会。产线按预测在跑。',
            Label: '一切正常',
          },
        },
        NoteSCycleTheRieRecipe: '{{a}} 秒/循环——隔壁的 RIE 配方约跑 200',
        NoteEvAgainstAEvWindow: '{{a}} eV，窗口为 {{b}}–{{c}} eV',
        NoteNmOverCycles: '{{b}} 个循环共 {{a}} nm',
        NoteSameChamberSameGasesSame: '同一个腔室、同样的气体、同样的电源。让它成为 ALE ' +
          '的，是化学和离子在时间上被分开了，而且离子能量被压到它自己什么都刻不动的程度。比上面那份配方慢两个数量级，而当出错不可接受时，最后两纳米就是这么去除的。',
        SectionEnergyWindowIsTheRemoval: '能量窗口——移除半步是否自限制？',
        NoteTheModifiedLayerHasA: '改性层的溅射阈值低于其下的材料，两个阈值之间有一条带：改性层能被移除而衬底不会。低于这条带什么都去不掉；高于它就是在溅射，工艺就不再是任何意义上的原子级。跑在窗口之上，是配方名叫 ' +
          'ALE 而实际不是的最常见方式。',
        SectionSaturationCurveIsTheDose: '饱和曲线——剂量半步是否自限制？',
        SectionStoredAleRecipes: '已存 ALE 配方',
        KnobWafers: '{{n}}k 片',
        flow: {
          'quantum-chip': {
            Purpose: '八道工序，一个一纳米厚的结，以及一次要在 10 mK 下花一整天的测试。',
            Name: 'Transmon 测试芯片',
          },
          'product-saqp': {
            Purpose: '从硅料到分级成品，内连线回路每层金属跑一遍。',
            Name: '完整产品流程——SAQP',
          },
          'back-end': {
            Purpose: '一片完工晶圆变成分好级的成品。',
            Name: '组装与测试',
          },
          'gate-saqp': {
            Purpose: '阱、栅氧、多晶硅、图形化，以及随后的源漏。',
            Name: '栅层——SAQP',
          },
          substrate: {
            Purpose: '多晶硅和一颗籽晶变成一片抛光晶圆。',
            Name: '衬底',
          },
          'metal-layer': {
            Purpose: '刻蚀介质，填入铜，再把多余的磨掉。',
            Name: '大马士革金属层',
          },
          le4: {
            Purpose: '四张掩模，才到 SAQP 用一张就够到的节距。',
            Name: 'LE⁴ 四重图形化',
          },
          saqp: {
            step7: {
              Label: '拔除第一道侧墙',
            },
            step6: {
              Label: '刻蚀第二层侧墙',
            },
            step5: {
              Label: '沉积第二层侧墙',
            },
            step4: {
              Label: '拔除芯轴',
            },
            step3: {
              Label: '刻蚀第一层侧墙',
            },
            step2: {
              Label: '沉积第一层侧墙',
            },
            step1: {
              Label: '修整芯轴',
            },
            step0: {
              Label: '曝印芯轴',
            },
            Purpose: '一张掩模、八步操作，最终节距由一层薄膜的厚度决定。',
            Name: 'SAQP',
          },
          lele: {
            Purpose: '两张掩模、四步操作。两次曝光间的套刻误差会变成 CD 误差。',
            Name: 'LELE 双重图形化',
          },
          'single-euv': {
            Purpose: '一张掩模、两步操作，达到别人要八步才够到的节距。',
            Name: '单次曝光，EUV',
          },
          'single-duv': {
            Purpose: '一张掩模、两步操作，以及 36 nm 半节距处的硬极限。',
            Name: '单次曝光，193i',
          },
        },
        LabelGateOxide: '栅氧',
        LabelWellDriveIn: '阱推进',
        TextTheThreeMechanisms: '三种机制，逐一拆解',
        TextTakeTheCall: '来做这个判定',
        TextHowYouWouldSeeIt: '怎样才能看见它',
        TextTheFault: '故障所在',
        TextWhatTheProductSays: '产品怎么说',
        TextWhatTheToolSays: '设备怎么说',
        TextMostExpensive: '花费最多',
        TextMostVisited: '到访最多',
        TextShowWhatWasActuallyWrong: '看看究竟是哪里坏了',
        TextRouteFaults: '流程级故障',
        TextComparePatterning: '图形化方案对比',
        LabelEtchPerCycle: '每循环刻蚀量',
        LabelSurfaceCoverage: '表面覆盖率',
        NoteTheMaskThresholdIsFar: '掩模的阈值远高于改性层',
        SectionAleRecipe: 'ALE 配方',
        case: {
          'slow-drift': {
            HowToSeeIt: '西电规则。居中的过程连续八点落在中心线同一侧是 1/128 的事件，所以这不是巧合。没有任何点越限；『模式』本身才是信号，而只有跨访问的控制图才有模式。',
            ProductSays: '参数良率在下滑，却没有人指得出是哪一批开始出的错。',
            ToolSays: '每个读数都在规格和控制限之内。没有报过一次警。',
            Truth: '一只质量流量控制器每层漂移约合 0.4 nm。十一层下来就是 4 nm 的偏移——每一次单看都在规格内，而过程已经不再居中。',
            Label: '十一次测量全部合格，而设备是坏的',
          },
          'thermal-account': {
            HowToSeeIt: '沿流程把热预算加总，而不是逐步对照各自的限值。流程里最后一次退火被之前的每一次退火约束着，而这本账没有人管。',
            ProductSays: '栅模块结束时结深超出规格。',
            ToolSays: '每个炉管步骤都在自己的时间和温度窗口内。THRM-04 每次诊断都是干净的。',
            Truth: '为了解决一个电阻率投诉，阱推进被延长了。它单独看仍是一次合法的退火。但它花掉了流程末尾尖峰退火所依赖的预算，最后出来的结深超了 30%。',
            Label: '结太深，而没有一次退火超时',
          },
          'planarisation-drift': {
            HowToSeeIt: '把 CMP 后极差对『层号』作图，而不是对规格作图。健康的叠层会收敛成一条平线；这一组一路爬升不停，因为公比变小的几何级数会收敛在更高的地方。',
            ProductSays: '到第 8 层金属时，光刻机上的 CD 散布已超出焦深预算，光刻组在怪光刻机。',
            ToolSays: '每一层的每次 CMP 都在自己的规格内。去除速率、均匀性、抛后极差全部正常。',
            Truth:
              '抛光垫修整器磨损了，平坦化效率从 0.90 掉到 0.55。每次抛光仍然去除配方要求的量，仍然把台阶留在 45 nm ' +
              '的单次限值以内——但它向上传递的形貌比以前多，而十层这样传下来是会复利的。',
            Label: '第 8 层金属印不出来，而 CMP-11 每一层都合格',
          },
        },
        AsideDrift:
          '十一层累计漂移 {{total}}——单侧规格的六成，花掉时没有一个读数越出窗口。居中的过程连续八点落在中心线同一侧的概率约为 ' +
          '1/128，所以它是一条规则，不是一种直觉。',
        AsidePlanarisation:
          '健康的叠层收敛在 {{healthy}} nm，这一组收敛在 {{worn}} nm。两者都是几何级数——R(n+1) = ' +
          '(R(n)+F)(1−η)——收敛点是 F(1−η)/η，由效率决定，跟任何单独一次抛光无关。单次限值写成 {{visit}} ' +
          'nm，只是因为设备能守住这个数；焦深预算真正需要的是 {{route}} nm。故障就住在这两个数字的缝隙里，而这道缝才是真正的缺陷。',
        VerdictNoRule: '未触发任何规则',
        VerdictNoReadingOut: '没有任何读数超出规格或控制限',
        VerdictJunction: '结深 {{junction}} nm，限值 {{limit}} nm',
        VerdictAnnealsOk: '每次退火都在各自窗口内',
        VerdictCdSpread: 'CD 散布 {{spread}} nm，预算 {{budget}} nm',
        VerdictInSpecAll: '10 层全部合格',
        LabelSpike: '尖峰退火',
        LabelDensify: '致密化',
        LimitRouteNeeds: '流程真正需要的 {{limit}} {{unit}}',
        LimitPerVisit: '单次限值 {{limit}} {{unit}}',
        LimitControl: '控制限',
        LimitSpec: '规格线',
        StepListMore: '……还有 {{more}} 步，多数是金属层的又一轮',
        StepListHead: '操作步 — 共 {{total}} 步，显示第 1 至 {{shown}} 步',
        TileGoodDieNote: '{{gross}} 颗中合格 {{good}} 颗',
        TilePerGoodDie: '每合格裸片',
        TileDieYieldNote: '按 {{area}} cm² 裸片',
        TileDieYield: '裸片良率',
        TileLineYieldNote: '存活下来的整片晶圆',
        TileLineYield: '线良率',
        TileThermalNote: '折合 1000 °C 下的秒数',
        TileThermal: '热预算',
        TileDefectsNote: '每碰一次都有代价',
        TileDefects: '新增缺陷',
        TileLithoNote: '的成本，出自 {{steps}} 步中的 {{masks}} 步',
        TileLithoShare: '光刻占比',
        TileCostNote: '为光罩成本',
        PerWaferSuffix: '/片',
        TileCost: '成本',
        TileMasksNote: '每张都是一块光罩，买一次',
        TileOperationsNote: '晶圆要停这么多次',
        TileOperations: '操作步数',
        ThPerGoodDie: '$/合格裸片',
        ThYield: '综合良率',
        ThLitho: '光刻占比',
        ThPerWafer: '$/片',
        ThMasks: '掩模',
        ThSteps: '步数',
        ThRoute: '流程',
        CompareIntro:
          '把图形放上晶圆的五种方法。后三种达到的节距是任何单次 193 nm ' +
          '曝光都解析不了的，它们靠拿掩模换操作步数来实现——掩模买一次，工步却每片晶圆都要付。改一改下面的裸片面积或生产量，排名就会变，这才是真正的答案：没有最好的流程，只有对某个产品最好的流程。',
        KnobVolume: '生产量',
        KnobDieArea: '裸片面积',
        group: {
          quantum: '量子器件',
          product: '产品',
          layers: '层模块',
          patterning: '图形化模块',
        },
        SideNote: '晶圆厂不是一排设备。它是一个循环，每做一层就跑一圈，而产品就是晶圆绕这个圈的次序。',
        reading: {
          goldenWafer: {
            Label: '在黄金晶圆上复测最近 11 层（6.0 小时）',
          },
          junction: {
            Label: '结剖切——破坏性（4.0 小时）',
          },
          layerTrend: {
            Limit: '走平，且低于 55 nm',
            Label: 'CMP 后极差对层号作图（1.5 小时）',
          },
          stationAlarms: {
            ValueNone: '无',
            ValueCmp: 'CMP-11 去除速率偏低',
            Limit: '无',
            Label: '当前超规格的站点',
          },
          spcRules: {
            ValueNone: '无',
            Limit: '无',
            Label: '层序中触发的控制图规则',
          },
          thermalTotal: {
            Limit: '无单步超过 900 秒',
            Label: '本流程已花热预算',
          },
          cmpVisits: {
            Value: '{{b}} 层中 {{a}} 层在 {{c}} nm 之内',
            Limit: '10 层全部',
            Label: '每层 CMP 后台阶高度',
          },
        },
        aspect: {
          singleStation: {
            Action: '去报警的那台设备跑它自己的班次报告——这不是流程问题',
            Label: '单一站点，按常规诊断处理',
          },
          toolDrift: {
            Action: '从分层图上找出漂移的设备并重新对中',
            Label: '某台设备在层序中漂移',
          },
          thermalBudget: {
            Action: '把阱推进退回 300 秒，另想办法达到电阻率目标',
            Label: '最后一次退火前已花掉的热预算',
          },
          planarisation: {
            Action: '修锐 CMP-11 的垫修整器，并把单次台阶限值改由焦深预算推导，而不是看设备能撑多少',
            Label: '金属叠层上的逐层累积',
          },
        },
      },
      etch: {
        GuardNotRun: '腔体还没有运行。请先在上面启动工艺：批次记下的是等离子体实际做了什么，而不是配方要求了什么。',
        GuardEtched: '这个图形已经在薄膜里了。要再曝光一次，才有新的东西可转移。',
        GuardNoPattern: '这批晶圆上没有图形。刻蚀机是把光刻胶图形转移到下面的薄膜里：请先在 LITH-03 或 EUV-22 曝光。',
        TransferLot: '转移图形并把批次送去清洗',
        NoteNoLot: '装载腔里没有批次。带一片有图形的晶圆来，把图形转移到薄膜里。',
        SecondaryLabel: '非均匀性',
        ToolLabel: 'ETCH-01 · ICP 多晶硅刻蚀',
        SectionAtomicLayerMode: '原子层模式——同一个腔室，换一种跑法',
        SectionSheath: '离子的方向从哪来——晶圆边缘的鞘层（电场）',
        SheathNote:
          '一张穿过晶圆边缘的剖面：顶部是作为平坦边界的等离子体，底部是晶圆和它的聚焦环，两者之间就是鞘层——' +
          '而这正是画面所呈现的东西。明暗就是电位，等离子体一侧亮、晶圆一侧暗；黄线是它的等势线；' +
          '箭头是离子的去向——沿着电场往下走，而在平坦表面之上，这就意味着' +
          '笔直向下。等离子刻蚀之所以能切出垂直侧壁，全部原因就在这里：不是离子会瞄准，是电场替它瞄准了。' +
          '现在看聚焦环。全新时，它的顶面与晶圆齐平，电场平直地掠过边缘，所以最外圈的芯片看到的与中心' +
          '完全相同。让腔室继续运行，环被逐渐刻蚀掉：等势线在更低的表面上方下陷，于是在同一高度上，环上方' +
          '的电位高于晶圆上方，晶圆边缘正上方的电场便多出一个由环指向晶圆的横向分量。两件事同时发生——' +
          '边缘的离子向内倾斜，落在晶圆裸露的棱角上，最外圈的芯片因此刻出倾斜的侧壁；而原本铺散在环上的' +
          '电场线，现在像汇聚到避雷针那样汇聚到那个棱角上，边缘挨打更重、跑得更快。这就是"环磨损"故障里' +
          '那张边缘偏快的分布图，被看见了。把偏压调高，每一支箭头都变长，却没有一支转向：这是拉普拉斯的' +
          '场，而拉普拉斯是线性的，所以倾斜属于几何，只有换新环才治得了。',
        SheathCaveat:
          '诚实的边界。真实的鞘层是一层几毫米厚的空间电荷区，其边界就是等离子体本身；这里则是固定平板下的' +
          '一段导电间隙。形状——平面之上是平的、台阶之上会弯——是一样的，而那个弯正是这一节要讲的。' +
          '这里没有"运行"可按，也没有时钟：这个场里不含时间，所以只要旋钮一动，求解器立刻把它安顿好；' +
          '在这里变化的是环，而不是分钟。环紧贴晶圆，' +
          '真实的环则隔着零点几毫米；而且它被固定在晶圆电位上：真实的环通过自身电容与射频耦合，随着变薄而' +
          '改变，甚至可能把环上方的鞘层往相反方向推——这正是现代腔室要给聚焦环单独加射频来操纵倾斜的原因。' +
          '在这里，唯一会动的只有几何。',
        SheathTilt: '晶圆边缘离子向内倾斜',
        SheathTiltTitle: '离子到达的方向，沿晶圆分布',
        SheathTiltX: '沿晶圆的位置（mm）—— 边缘在右侧',
        SheathTiltY: '偏离垂直的倾角（°，向内为正）',
        SheathTiltSeries: '离子入射角',
        SheathTiltStatus: '边缘 {{a}}°，晶圆中部 {{b}}° · 弯曲这条线的是聚焦环，偏压不会让它移动',
        SheathEdgeFlux: '边缘 / 中心电流',
        SheathWear: '环磨损',
        SheathDirected: '漂移/扩散',
        SectionTrench: '自由基进入沟槽——窄图形为何刻得慢（深宽比相关刻蚀）',
        TrenchNote:
          '上面那张晶圆图是一种"供不应求"——晶圆上每一处开口都在抢同一份供给，也就是负载效应。这里是同一种' +
          '供不应求，只是尺度小了一千倍。同一片等离子体下的四条沟槽，宽度从 90 nm 到 720 nm，深度都是' +
          '900 nm，因此深宽比分别是 10、5、2.5 和 1.25。自由基自上方扩散进来，凡是遇到硅就被消耗：槽底，' +
          '以及一路下去的每一面侧壁。按运行，然后在图上读那四个槽底。10:1 沟槽的底部所见，只是 1.25:1 ' +
          '所见的一小部分——同一片等离子体、同一时刻——所以它刻得更慢，而同时带有这两种图形的版图，总是宽的' +
          '那条先到底。这就是深宽比相关刻蚀，也叫 RIE 滞后，也正是刻蚀时间要按最窄图形来定、其余一律过刻的' +
          '原因。现在让表面消耗得更猛：把晶圆加热，或者把偏压调高让离子打得更狠。窄沟槽饿得更厉害，滞后' +
          '反而更严重。那个在速率上取胜的激进配方，输掉了图形之间的均匀性。',
        TrenchCaveat:
          '这里被标定的是表面：活化能取的就是本面板自己的氟对硅的数值，离子增强也遵循本面板"阈值以上按平方根' +
          '增长"的规律，所以每一个方向性都是面板的。输运则是用连续扩散来代替 100 nm 沟槽在 20 mTorr 下真正' +
          '经历的分子流，而速率常数是特意选来让最窄的沟槽在合格配方下呈现明显的饥饿状态。所以四个槽底的先后' +
          '顺序、以及它们对旋钮的响应，都是挣来的；那些百分比不是可以拿回机台去引用的数字，时间轴也不是——' +
          '真实的沟槽在微秒量级就填满了。',
        TrenchWide: '1.25:1 槽底',
        TrenchNarrow: '10:1 槽底',
        TrenchLag: '滞后',
        reading: {
          houseutilities: {
            Limit: '总管不漏，PCW 在供应温度，干燥气 < 50 ppm',
            Label: '会同厂务检查公用设施 (2 h)',
          },
          seasoning: {
            Label: '腔壁挂片 / OES 预处理检查 (1 h)',
          },
          sealcheck: {
            Label: '本腔室氦检漏 (6 h)',
          },
          ringgauge: {
            Label: '聚焦环厚度量规 (30 min)',
          },
          base: {
            Label: '本底真空度',
          },
          leak: {
            Label: '升压率',
          },
          power: {
            Label: '实际输入源功率',
          },
          pump: {
            Label: '泵运行小时',
          },
          rf: {
            Label: '聚焦环射频小时',
          },
          flowverify: {
            Label: '流量校验，升压率法 (45 min)',
          },
          tempverify: {
            Label: '仪表化晶圆，卡盘温度 (2 h)',
          },
        },
        aspect: {
          power: {
            Label: '射频匹配 / 输入功率',
            Action: '重新调谐匹配器',
          },
          flow: {
            Label: '供气系统（MFC）',
            Action: '校准或更换 MFC',
          },
          vacuum: {
            Label: '真空完整性（泄漏 / 泵）',
            Action: '查漏并维护泵组',
          },
          walls: {
            Label: '腔壁状态',
            Action: '腔室清洗与预处理',
          },
          chuck: {
            Label: '静电卡盘温控',
            Action: '维护冷水机',
          },
          ring: {
            Label: '聚焦环',
            Action: '更换聚焦环',
          },
          house: {
            Label: '问题不在这台设备——上报厂务',
            Action: '停止在这台设备上排查，交由厂务处理',
          },
        },
        fault: {
          none: {
            Label: '无故障——腔室健康',
            Complaint: '设备已认证，运行在规格之内。',
            Truth: '一切正常。',
            Evidence: [
              '所有腔室读数都在标称值上。',
            ],
          },
          mfcDrift: {
            Label: '质量流量控制器供气不足',
            Complaint: '刻蚀速率偏低，晶圆均匀性变差。没有人改过配方。',
            Truth: 'CF4 质量流量控制器显示的是设定值，实际只放过去约 42%。氟的总量由真正进入腔室的气体决定，而不是由显示屏上的数字决定。',
            Evidence: [
              '流量校验（升压率法）与 MFC 回读值不一致。',
              '实际输入射频功率正常，排除匹配器问题。',
              '升压率正常，排除泄漏。',
              '做一次腔室清洗也解决不了。',
            ],
          },
          chamberLeak: {
            Label: '真空泄漏',
            Complaint: '速率下降，选择比也变得反常。上次保养之后开始的。',
            Truth: '重新装配时门封被夹住了。空气正在漏入，氧在气相中夺走氟，因此即使流量和功率都正确，刻蚀剂也到不了晶圆。',
            Evidence: [
              '升压率远高于 1 mTorr/min 的限值——这是决定性的测试。',
              '本底真空度同样偏高。',
              '流量校验和实际输入功率都正常。',
            ],
          },
          matchDetuned: {
            Label: '射频匹配网络失调',
            Complaint: '速率偏低。反射功率报警自己消失了，所以那个班就让它继续跑。',
            Truth: '匹配网络只把设定值的约 70% 送进等离子体。吸收功率变少意味着等离子体变稀、离子变少——而偏压功率被分摊到更少的离子上，每一个反而打得更狠。',
            Evidence: [
              '实际输入功率明显低于设定值——这是决定性读数。',
              '离子能量比标称值更高而不是更低，这正是把它与供气问题区分开的关键。',
              '升压率和流量校验都正常。',
            ],
          },
          pumpDegraded: {
            Label: '泵组性能退化',
            Complaint: '交班时本底真空度报警。工艺还能跑。',
            Truth: '泵组已经抽不到本底真空度。残余气体把腔室的有效压力抬到设定值以上，从而拖慢了向晶圆中心的扩散。',
            Evidence: [
              '本底真空度是正常值的好几倍。',
              '升压率略有升高，但远达不到真正泄漏的程度。',
              '流量和实际输入功率都正常。',
            ],
          },
          heavySeasoning: {
            Label: '腔室清洗已经超期',
            Complaint: '速率整周都在慢慢往下走。每一批都比前一批稍慢一点。',
            Truth: '经过许多片之后腔壁上积起了沉积物。它们在氟到达晶圆之前就把氟复合掉，同时又让匹配器失调——一个根因产生了两个看起来像是两种故障的症状。',
            Evidence: [
              '距上次清洗的片数已经远超保养限值。',
              '实际输入功率偏低，同时自由基供给也偏低——两者同时出现。',
              '升压率和流量校验都正常。',
              '腔室清洗能恢复；只调匹配器则只能管一小阵。',
            ],
          },
          chillerFault: {
            Label: '卡盘冷水机守不住设定值',
            Complaint: '最近两批的剖面看着有钻蚀。速率略微偏高。',
            Truth: '卡盘实际比设定值高约 85 C。自发氟反应这一支随温度呈阿伦尼乌斯关系，因此被放大——而自发化学没有方向性，于是侧壁被钻蚀。',
            Evidence: [
              '卡盘温度回读与独立的晶圆测量结果不符。',
              '速率是升高而不是降低，这一下就排除了所有供给侧的故障。',
              '侧壁角变小，而离子能量没有变化。',
            ],
          },
          wornFocusRing: {
            Label: '聚焦环磨损超限',
            Complaint: '晶圆边缘远早于中心就刻通了。平均速率看起来还行。',
            Truth: '聚焦环已经磨蚀，无法再整形晶圆边缘的电场，边缘离子通量因此偏高。平均速率几乎不动，所以这个问题躲得过速率图，只会在均匀性图上现形。',
            Evidence: [
              '聚焦环上的射频小时数已过更换限值。',
              '平均刻蚀速率接近标称——速率控制图抓不到它。',
              '均匀性图呈现明显的边缘偏快，而且分布是一圈环形，不是单侧倾斜。',
            ],
          },
          houseVacuumLeak: {
            Label: '共用真空总管泄漏',
            Complaint: '速率下降，选择比也变得古怪——和上次保养后腔门密封圈被夹伤时一模一样。那个密封圈我们已经换过了。DEP-02 也在报同样的问题。',
            Truth: '上周末次厂房施工时动到了共用工艺真空总管上的一处法兰，现在正在漏。挂在这条总管上的每一个腔室都会把它当成自己的漏点——因为从腔室内部看，它就是。唯一不成立的是"这是本腔室的漏"，而花在拆开这台设备上的每一个小时，总管都在继续向其他每一台设备漏。',
            Evidence: [
              '升压率远超限值，本底压力也跟着上去了——典型的漏气特征。',
              '对"本"腔室做氦检结果干净。正是这一条读数，把它从维修变成了上报。',
              '流量验证与输入功率都正常。',
              '同一条总管上的其他设备也各自报着同一件事——这条线索不花一分钱，却几乎总是被忽略。',
            ],
          },
          warmPcw: {
            Label: '冷却水温度偏高',
            Complaint: '速率低了大约 15%，输入功率读数也偏低。上一班已经重调过一次匹配器，结果照旧。',
            Truth:
              '冷冻水站送来的供水比标称高了约 22 C。这台设备本身没有任何故障。影响从两条路进来：卡盘温度上浮约 19 ' +
              'C，因为冷水机没有足够冷的介质可以排热；更重要的是，射频发生器与匹配器接在同一回路上，正在降额运行，输入功率因此下降。这就是它为什么看起来像匹配失谐，也是为什么调匹配器解决不了。',
            Evidence: [
              '输入功率偏低，看上去和匹配网络失谐一模一样。',
              '重调匹配器几乎没有恢复。真的失谐是会恢复的。',
              '每一片晶圆都还在规格内——这正是陷阱：没有任何东西逼你去查，设备就这样安静地慢着15% 一直跑，直到有人注意到产能。',
              '厂务公用设施检查显示冷却水远高于其供水温度。',
            ],
          },
        },
        SectionProcessState: '工艺状态',
        SectionWaferMap: '晶圆图 — {{sites}} 点取样',
        SectionRadialProfile: '径向分布',
        OesGround: '基态',
        OesElectron: '电子进入',
        OesPhoton: '光子发出',
        OesSpectrumTitle: '光谱仪所见',
        OesSpectrumX: '波长 (nm)',
        OesSpectrumY: '每 nm 强度（谱线面积即其强度；Ar 750 在 3 eV、2.1×10¹⁷ m⁻³ 时 = 1）',
        OesSpectrumSeries: '发射',
        OesTraceTitle: '贯穿整个工艺的两条谱线——终点',
        OesTraceX: '工艺时间 (s)',
        OesTraceY: '谱线强度（Ar 750 在 3 eV、2.1×10¹⁷ m⁻³ 时 = 1）',
        OesTraceSiF: 'SiF 440 nm（产物）',
        OesTraceF: 'F 703.7 nm',
        OesClearing: '薄膜刻穿',
        OesActinometry: '光强比测定 F 703.7 / Ar 750.4：{{ratio}}，为运行开始时的 ×{{fold}}',
        OesActinometryNow: '光强比测定 F 703.7 / Ar 750.4：{{ratio}}',
        OesActinometryWatching: '光强比测定 F 703.7 / Ar 750.4：{{ratio}}，为本面板开始观察时（运行已在进行中）的 ×{{fold}}',
        OesUnwatched: '这次运行有一段发生在本面板关闭期间。那一段的产物谱线来自腔室自己的记录；氟谱线留空，因为当时没有任何东西在读取腔室的氟，事后无法重建。',
        OesActinometryNone: '光强比测定需要进气中含有氩，而这个配方没有。',
        OesFailed: '发射模型加载失败。',
        OesStatus: 'T_e {{te}} eV · n_e {{ne}} m⁻³ · {{line}} nm：k = {{k}} m³/s，强度 {{rel}}',
        OesLoading: '正在加载发射模型…',
        OesTeSource: '电子温度',
        OesFollow: '跟随腔室',
        OesManual: '手动设定',
        OesTe: 'T_e',
        OesPause: '暂停',
        OesRun: '运行',
        OesReset: '重置',
        SectionEmission: '终点信号从何而来——电子激发原子，原子发射谱线（能级与麦克斯韦分布）',
        EmissionNote: '上方的终点曲线，是光谱仪在看等离子体的辉光，而这辉光就是每个班次都在用的量子力学。腔室里的原子有分立的能级。等离子体中的电子只有带着不低于该能级的能量到达，才能把原子抬上去；原子落回时发出一个光子，其波长由两个能级决定：λ = hc/(E上 − E下)。氩的 750 nm 谱线需要 13.5 eV 的电子；而电子是几 eV 的麦克斯韦分布，所以光来自分布的尾部，尾部按 exp(−13.5/T_e) 变化——这就是为什么 T_e 的微小变化会让氩线大幅改变，而阈值仅 2.9 eV 的产物谱带几乎不动。每条谱线的速率都是算出来的：从阈值起的 Bethe 截面，对分布求平均。氟由解离产生，只要还有硅可刻就被消耗；SiF 是产物。所以到达终点时两件事同时发生：SiF 谱带熄灭，氟线上升，因为再没有什么在消耗氟。氩是平衡气体，没有什么消耗它，所以晶圆厂真正监看的终点是氟与氩之比（光强比测定）：等离子体本身的漂移会同时移动两条谱线而相互抵消。能级取自 NIST。省略了级联、猝灭以及谱带的振动结构，谱带画成一个宽峰。',
        SectionEndpoint: '光学发射终点曲线',
        SectionIedf: '离子能量分布 — 微观尺度',
        IedfNote: '上面这条分布是从放电求解出来的，不是查表得到的：玻姆通量决定有多少离子到达，偏压功率除以该电流决定它们打得多重，低能拖尾则是穿越鞘层时发生电荷交换的那一部分。',
        SectionRunControl: '运行控制',
        RunControlNote: '这层膜的真实刻蚀大约需要两分半钟。倍速只缩放工艺时间——物理在每一步都照样完整求解。',
        SectionChamber: '腔室状态 — 配方管不到的部分',
        SectionRecipe: '配方 — {{code}}',
        SectionStoredRecipes: '已存配方',
        StoredRecipesNote: '载入已存配方时会同时装入一片新晶圆。',
        ModeThickness: '剩余膜厚',
        ModeRate: '刻蚀速率',
        ModeDeviation: '相对均值的偏差',
        TileEtchRate: '刻蚀速率',
        TileNonUniformity: '不均匀度',
        TileSelectivity: '选择比',
        TileRegime: '限制机制',
        TileIonEnergy: '离子能量',
        TileIonFlux: '离子通量',
        TileSidewall: '侧壁',
        TileProcessTime: '工艺时间',
        TileCleared: '刻通比例',
        FastEdge: '边缘偏快',
        FastCentre: '中心偏快',
        Flat: '平坦',
        RegimeTransport: '输运受限',
        RegimeMixed: '混合',
        RegimeReaction: '反应受限',
        FromSheathSolve: '来自鞘层求解',
        Anisotropic: '各向异性',
        Undercut: '钻蚀',
        FilmIntact: '氧化层完好',
        OxideLost: '氧化层损失 {{nm}} nm',
        UnitNmRemaining: 'nm 剩余',
        CaptionDeviation: '蓝 = 慢于均值，红 = 快于均值',
        CaptionNonUniformity: '不均匀度 {{percent}}%',
        ChartProfileTitle: '沿半径的刻蚀速率与刻蚀剂供给',
        ChartProfileX: 'r / R（0 = 中心）',
        ChartProfileSeries: '刻蚀速率',
        ChartEndpointTitle: '刻蚀产物的发射强度 — 膜刻通时下降',
        ChartEndpointX: '工艺时间（s）',
        ChartEndpointY: '信号',
        ChartIedfTitle: '离子能量分布 — 平均 {{ev}} eV',
        ChartIedfX: '离子能量（eV）',
        ChartIedfY: '占比',
        ChartMean: '平均',
        PreventiveMaintenance: '预防性维护',
        MaintButton: '{{label}} — 停机 {{hours}} 小时',
        AssistantBlurb: '提出问题，我会设计一个实验，用这台求解器把它跑出来，并报告真正得到的结果——然后再还你一个更锋利的问题。',
        AssistantLead: '先启动工艺、看着晶圆图，然后问我它为什么长这样。',
      },
      patterning: {
        NoteMeanIsStillOnTarget: '平均值 {{a}} nm，仍在目标上',
        NoteCentred: '已居中',
        NoteSpacersMeet: '侧墙相遇：{{a}} nm 的间隔不算间隔。薄膜对这个周期太厚了，或者芯轴修整过头了。',
        ResultFourLines: '一根变四根——周期 {{a}} nm',
        ResultAnisotropicAgain: '再来一次各向异性刻蚀',
        ResultAldOverFirstPass: '在第一道侧墙上再镀 {{a}} nm ALD——它们现在成了芯轴',
        ResultTwoLines: '一根变两根——周期 {{a}} nm',
        ResultAnisotropicClears: '各向异性：清除水平面，留下侧壁',
        ResultAldConformalSidewall: '{{a}} nm ALD，保形度 {{b}}% → 侧壁上 {{c}} nm',
        ResultTrimBothSides: '{{a}} → {{b}} nm（两侧）',
        ResultNmLinesOnPitch: '{{b}} nm 周期上的 {{a}} nm 线条',
        NoteMeanSpacingWalks: '平均间距 {{a}} nm——正中目标——但四个间隔之间有 {{b}} nm 的分散。周期在游走。把芯轴修整量设为 {{c}} nm ' +
          '就能居中，而那一步在你测到问题的地方上游六步。',
        NoteAllFourSpacingsAre: '四个间隔都是 {{a}} nm。图形已居中。',
        SectionPasses: '道次',
        SectionRoute: '流程',
        SecondaryLabel: '线条 CD 均匀性',
        ToolLabel: 'PAT-21 自对准四重图形化单元',
        NextTheRouteSurvivedOnPaper: '这条路线在纸面上活了下来。现在问问：2% 的 ALD 厚度波动和不垂直的芯轴侧壁会对同一配方做什么——这台设备的现状两样都占。',
        AxisXSpacer1ThicknessNm: '第一层侧墙厚度 (nm)',
        TitleFinalPitchAgainstFirstSpacer: '最终周期随第一层侧墙厚度的变化',
        LabelTrimmedMandrel: '修整后的芯轴',
        HypPitchDivisionIsSubtractionEach: '周期分割是减法。每层侧墙都从它沉积其中的间隙里拿走宽度，到某个点，剩下的间隙就再也装不下下一层薄膜了。如果是这样，失效应是突然的而不是渐进的——配方一直好使、好使、好使，然后就没地方了。',
        AskedWhereDoesThisRouteStop: '这条路线到哪里就在几何上走不通了？',
        SumTheGeometricLimitsOfPitch: '周期分割的几何极限，以及什么最先耗尽。',
        LabelFinalCd: '最终 CD',
        AxisXSpacer2ThicknessNm: '第二层侧墙厚度 (nm)',
        TitleFinalCdFollowsTheSpacer: '最终 CD 跟随侧墙薄膜',
        NoteToPrintThisDirectly: '若要直接印出此周期',
        LabelScannerNeeded: '所需光刻机',
        LabelMultiplication: '倍增系数',
        HypIfSaqpWorksTheWay: '如果 SAQP ' +
          '按设计运作，光刻机只定芯轴周期，之后的一切都是除法。最终周期应跟随侧墙厚度——一个由具有亚纳米控制精度的沉积设备决定的数字——而对光刻机的分辨率完全不敏感。',
        AskedWhichKnobDecidesThePitch: '最终出货的周期由哪个旋钮决定？',
        SumADepositedFilmThicknessNot: '决定权在沉积薄膜的厚度，不在光刻机——以及这买到了什么。',
        NextEveryOtherLineInThis: '这个图形里每隔一根线就来自不同的来源。如果其中一半系统性地偏窄，这会对 PRB-14 的良率图做什么——它看起来会像缺陷，还是像参数失效？',
        LabelMeanSpacing: '平均间距',
        AxisYMeanSpacingNm: '平均间距 (nm)',
        TitleAndTheMeanWhichBarely: '而平均值几乎毫无察觉',
        LabelIdealTrim: '理想修整量',
        AxisYWalkNm: '游走 (nm)',
        AxisXMandrelTrimNm: '芯轴修整量 (nm)',
        TitlePitchWalkAgainstMandrelTrim: '周期游走随芯轴修整量的变化',
        LabelIdealTrimL126: '理想修整量',
        NoteLooksFineEitherWay: '怎么看都正常',
        LabelMeanSpacingL122: '平均间距',
        AskedWhyAreTheSpacesIn: '为什么最终图形里的间隙不是全都一样宽？',
        SumPitchWalkTheTrimThat: '周期游走、造成它的修整量，以及能修好它的修整量。',
        fault: {
          slopedMandrel: {
            Evidence: [
              '芯轴侧壁角 4.5°，限值 1°。',
              '最终 CD 均匀性超限，节距仍然居中。',
              'ALD 的台阶覆盖率和厚度散布都正常。',
            ],
            Truth: '芯轴刻蚀留下了 4.5° 的侧壁。沉在斜壁上的侧墙下厚上薄，于是刻出来的线不再等于膜厚——和 ALD 漂移造成的 CD ' +
              '均匀性损失一模一样，只是出自刻蚀机。能把两者分开的是 ALD 的读数，而它们是干净的。',
            Complaint: '还是那个变差的 CD 均匀性，而 ALD 昨天刚重新认证过。',
            Label: '芯轴侧壁不垂直',
          },
          aldNonUniform: {
            Evidence: [
              'ALD 厚度散布 4.2%，限值 1.5%。',
              '最终 CD 均匀性超限，而节距漂移为零。',
              '台阶覆盖率和芯轴轮廓都正常。',
            ],
            Truth: 'ALD 喷淋头漂了，片内厚度散布到了 ' +
              '4.2%。最终的线就是这层膜，膜的均匀性就是线的均匀性——而因为它对每个侧墙一视同仁，节距一点都不会漂。均匀性超差而节距居中，就是它的特征。',
            Complaint: '全片的线宽均匀性变差了。节距完全居中。',
            Label: '侧墙厚度全片波动',
          },
          thinSidewallFilm: {
            Evidence: [
              '台阶覆盖率 86%，底线是 97%。',
              'CD-SEM 显示间隔在漂移。',
              'ALD 厚度分布图和光刻机的 CD 控制都正常。',
            ],
            Truth: 'ALD 的台阶覆盖率只有 ' +
              '86%，于是侧壁上的膜——唯一会变成线条的那部分——比厚度监控在平面上量到的更薄。节距由那个侧壁厚度决定，于是它漂了，而沉积在任何人量过的每一块平面上都完美合格。光刻拒绝得有理。',
            Complaint: '节距开始漂了。有人要求光刻收紧芯轴 CD 控制，光刻拒绝了，理由是他们的控制全厂最好。',
            Label: '侧墙膜比顶面薄',
          },
          patHealthy: {
            Evidence: [
              '台上的每个读数都正常。',
              '节距居中，CD 散布很小。',
            ],
            Truth: '什么问题都没有。正确的判定就是放行这一批。',
            Complaint: '例行批次。最终节距和 CD 都在目标上。',
            Label: '一切正常',
          },
        },
        NoteNmHalfPitchDuvLimit: '半节距 {{a}} nm，DUV 极限 {{b}} nm',
        NoteSetToNmThisIs: '设为 {{a}} nm——这是把图形调正的那个旋钮',
        NoteTheScannerAloneWouldGive: '光刻机单干只能给 {{a}} nm',
        NoteNmPrintedDivided: '曝印 {{a}} nm，再除以 {{b}}',
        SectionThePitchStopsBeingThe: '节距不再归光刻机管',
        NoteA193NmScannerCannot: '193 nm 光刻机印不了约 36 nm 半节距以下的图形，2007 ' +
          '年以来一直如此。更细的一切都是先印粗的再分割出来的：在印好的线上沉一层保形薄膜，各向异性地刻蚀让它只留在侧壁上，再把中间的线抽掉。一条线变两条。做两遍就是四条。最终 ' +
          'CD 是一层膜厚——DEP-02 能把它控制到百分之一，而 LITH-03 只能控制到百分之几。',
        TextPatternCollapsed: '图形塌陷',
        SectionCellState: '单元状态',
        SectionThePatternThatComesOut: '做出来的图形',
        TextRunTheCellOnThis: '在这批晶圆上跑一遍图形化单元',
        LabelSaqpTwoPasses: 'SAQP——两遍',
        LabelSadpOnePass: 'SADP——一遍',
        LabelFinalPitch: '最终节距',
        LabelPitchWalk: '节距漂移',
        LabelFinalLineCd: '最终线宽',
        NoteThisIsAFilmThickness: '这是一层膜厚，不是曝印出来的图形',
        LabelCdUniformity: 'CD 均匀性',
        LabelIdealMandrelTrim: '理想芯轴修整量',
        LabelAgainstAScannerAlone: '对比单用光刻机',
        AssistantLead: 'PAT-21 探究助手',
        AssistantBlurb: '可以问：间距为什么一宽一窄、最终节距到底由什么决定、这条工艺路线在哪一步没地方了。每个回答都是用这套配方真实跑完的八步流程。',
        reading: {
          cdUniformity: {
            Label: '最终线宽均匀性，49 点（2.0 小时）',
          },
          pitchWalk: {
            Limit: '散布 < 1 nm',
            Label: '对一个周期内每个间隔做 CD-SEM，200 点（3.0 小时）',
          },
          scannerCd: {
            Label: '光刻机芯轴 CD 控制',
          },
          sidewallAngle: {
            Label: '芯轴侧壁偏离垂直角',
          },
          aldMap: {
            Label: 'ALD 片内厚度散布',
          },
          conformalityCheck: {
            Label: '监控结构上的 ALD 台阶覆盖率',
          },
        },
        aspect: {
          scanner: {
            Action: '这里无须维修——芯轴会被耗掉，它的 CD 到不了成品',
            Label: 'LITH-03 光刻机 CD 控制',
          },
          sidewall: {
            Action: '重调芯轴刻蚀以获得垂直轮廓',
            Label: 'ETCH-01 芯轴侧壁角',
          },
          aldUniformity: {
            Action: '重调 ALD 喷淋头并重测全片厚度分布',
            Label: 'DEP-02 侧墙厚度均匀性',
          },
          conformality: {
            Action: '重新认证 ALD 饱和窗口——侧壁比顶部薄',
            Label: 'DEP-02 侧墙保形性',
          },
        },
      },
      cmp: {
        NextHoldingIsTheEasyHalf: '扣住是简单的那一半。这一班已经抛完出去的片子，用的是同一张单子——你要量什么，才能判断其中有没有该收回来的？',
        NextTheSheetPassedToday: '今天这张单子过了。上面哪一行离限值最近？在它变成扣住设备的那一行之前，你觉得还剩几批？',
        BridgingClear: '无',
        BridgingShorted: '短路',
        LabelBridging: '金属搭桥',
        LabelPlanarity: '平整度',
        VerdictWithAWatchItem: '放行，但有一项要盯着',
        VerdictHeld: '扣住',
        VerdictQualified: '已认定',
        LabelVerdict: '判定',
        HypAQualificationIsADecision: '资格认定是一个决定，不是一段解释。只要监控片抛完后平整度够下一层曝光、抛光时间在产线能吸收的范围内、没有任何金属搭桥，而且台面每一项读数都在限值内，设备就放行；否则扣住。再用同一台机器跑已认定配方，就能看出该负责的是配方还是设备。',
        AskedIsThisPolisherFit: '这台抛光机现在能不能跑产品，如果不能该怎么办？',
        SumPolishTheMonitorWafer: '抛一片监控片，把台面读数逐项对照限值，然后做出放行判断。',
        UnitNmAcrossDie: 'nm（跨裸片）',
        NoteTheRegionsAboveAre: '上面的区域是一张列表：每个区域按自己的公式清除、按自己的规律碟形磨损，谁也不知道邻居的存在。下面，同一颗裸片是一张按时间步进的图——研磨垫是毫米尺度的卷积核，哪里凸起就往哪里压得最重，所以平坦化是一种反馈而不是一个系数，一块版图挨着什么，决定它怎么被抛。下方的剖线横穿裸片中部：焊盘、致密阵列、孤立线条、其间的开阔区。',
        NoteTheTwoModelsDisagree: '两个模型在这里对时钟意见不一，这通常意味着配方已经离开了区域列表拟合时所在的区间——来料台阶太重，或者过抛光太极端。仍在做物理的是网格：压力跟随它实际压着的那个表面。',
        NoteOnThisRecipeTheTwo: '在这个配方上，两者在时钟和失效次序上是一致的——网格本身就包含区域列表的规律，所以在区域真正独立的地方它能复现列表。列表永远无法包含的是研磨垫的作用长度：硬垫平坦化，软垫保形抛光，侵蚀取决于邻域，而过抛光会把裸片重新磨得不平——台阶高度在终点时降到最低，随后侵蚀又把它买了回来。',
        ValueNoSuchTerm: '无此项',
        ValueInTheKernel: '在卷积核里：版图的抛光取决于它的邻居',
        MetricNeighbourhood: '邻域效应',
        ValuePerRegionTimes: '只有各区域时间——没有场，没有图',
        ValueSOfSequence: '先后相差 {{a}} s，开阔区最后清除',
        MetricClearing: '清除',
        MetricDieSurfaceRange: '裸片表面高差',
        MetricWorstErosion: '最严重侵蚀',
        MetricWorstDishing: '最严重碟形磨损',
        MetricEndpoint: '终点',
        LegendTheRegionList: '区域列表',
        LegendTheDieStepped: '裸片，按时间步进（{{a}}×{{a}} 格）',
        ValueClears: '清除时间',
        LabelRegion: '区域',
        LabelRemoval: '去除量',
        LabelRemovalNonUniformity: '去除非均匀性',
        LabelPolishTime: '抛光时间',
        SecondaryLabel: '抛光时间',
        ToolLabel: 'CMP-11 · 铜大马士革抛光机',
        NextIfTheLayoutBuildsThe: '如果失效是版图内建的，那么解决办法就不在这台设备上。你会对设计做什么修改让它能被抛平——又需要谁来同意？',
        AxisYDishingNm: '碟形磨损 (nm)',
        AxisXLineWidthUm: '线宽 (um)',
        TitleDishingAgainstFeatureWidthAt: '碟形磨损随图形宽度的变化，图形密度固定',
        NoteSamePolishSameWafer: '同一次抛光，同一片晶圆',
        LabelFineArrayDishes: '细密阵列碟形磨损',
        LabelWidestFeatureDishes: '最宽图形碟形磨损',
        LabelClearsLast: '最后清除',
        LabelClearsFirst: '最先清除',
        HypTwoSeparateLayoutPropertiesAre: '是两个互不相同、又容易混淆的版图属性在起作用。一个区域有多大比例是凸起的，决定它何时被清除，因为只有凸起部分接触研磨垫并承担全部载荷。一个图形有多宽，决定它是否碟形磨损，因为研磨垫必须弯进凹陷才能碰到底部的金属，而它的弯曲能力有限。密度决定时机；宽度决定损伤。',
        AskedYouWantToKnowWhy: '你想知道为什么同一次抛光下，裸片上有些图形完好而有些被毁掉。',
        SumShowThatTheLayoutNot: '证明是版图而不是抛光机决定哪些图形失效。',
        NextIfMatchedSpindlesMakeThe: '如果主轴匹配使速度均匀，那么这台设备上残留的任何径向特征都是压力问题。载具上有两样东西能造成径向特征——而且它们的作用方向相反。哪个是哪个？',
        LabelMatchedToPlaten: '与压盘匹配',
        AxisYRemovalNonUniformity: '去除非均匀性 (%)',
        AxisXCarrierSpeedRpm: '载具转速 (rpm)',
        TitleRemovalUniformityAgainstCarrierSpeed: '去除均匀性随载具转速的变化，压盘转速固定',
        LabelEdgeSpeedThen: '此时边缘速度',
        LabelCarrierAtHalf: '载具转速减半',
        LabelNonUniformityMatched: '非均匀性，匹配时',
        NoteIdenticalAtEveryRadius: '每个半径处完全相同',
        LabelMatchedCentreToEdge: '匹配时，中心到边缘',
        HypAPointOnTheWafer: '晶圆上一点相对研磨垫的速度为 w_p x d + (w_p - w_c) x r，其中 d 是两个中心的偏移量，r ' +
          '是该点在晶圆上的位置。当两个转速相等时第二项恰好为零，所以每个点无论在哪里都看到相同的速度。如果这是对的，主轴匹配应给出完全均匀的去除，而任何失配都会把径向特征直接印在晶圆上。',
        AskedYouWantToKnowWhyL193: '你想知道为什么两个主轴要以相同转速运转，以及不相同时晶圆会发生什么。',
        SumShowThatMatchingTheTwo: '证明让两个主轴转速匹配能使研磨垫速度完全均匀。',
        NextOverPolishBuysInsuranceAgainst: '过抛光是为防短路买的保险。是什么决定了你需要多少保险——这台设备上哪个旋钮改变的是这一点，而不是改变你抛光多久？',
        LabelYourRecipe: '你的配方',
        LabelDishingWidestFeature: '碟形磨损，最宽图形',
        LabelMetalStillBridging: '金属仍在桥接',
        AxisXOverPolish: '过抛光 (%)',
        NoteOfTheScannerBudget: '占光刻机预算',
        LabelCdCostAt20: '20% 时的 CD 代价',
        NoteSameRecipeMoreTime: '同一配方，更长时间',
        LabelDishingAt120: '120% 时的碟形磨损',
        NoteWidestFeature: '最宽的图形',
        LabelDishingAt20: '20% 时的碟形磨损',
        NoteMetalStillBridging: '金属仍在桥接',
        LabelShortsBelow: '短路阈值以下',
        HypTheseTwoFailuresPullIn: '这两种失效朝相反方向拉扯，不存在同时避免两者的设置，因为终点检测器靠整体摩擦变化触发，看不到最后仍残留金属的那几个百分点的面积。过抛光就是为这个盲区付出的代价，而过抛光的每一秒同时也在把最先清除干净的区域磨出碟形。',
        SumFindTheTradeBetweenMetal: '找出金属残留桥接与金属过度碟形磨损之间的取舍。',
        fault: {
          zoneDrift: {
            Evidence: [
              '去除是中心快型——和保持环磨损符号相反，这也是分开两者的唯一依据。',
              '两项规格照样都过。没有报废，没有什么会逼出一个决定。',
              '压力标测晶圆和分区调压阀报的数对不上。',
              '护环量出来没问题，排除了另一个压力类故障。',
            ],
            Truth: '载具中心分区的一只调压阀漂了，实际给出的压力比它报的高约 ' +
              '40%。每片晶圆的中心都抛得快、先清完，然后在边缘收尾时被过抛。平坦度和抛光时间照样合格，这正是陷阱：设备已经有 16% ' +
              '的不均匀而每片晶圆都能出货，所以在漂移大到开始报废之前，没有什么会逼人做决定。',
            Complaint: '晶圆图变成了中心快。一切照样合格，班组想接着跑。',
            Label: '载具中心分区压力虚高',
          },
          wornRing: {
            Evidence: [
              '去除是边快型——边缘减中心的读数强烈为正。',
              '平坦度超差，而且边缘处最差。',
              '载具分区漂移给出的是相反的径向特征。方向就是全部诊断，一张晶圆图一眼看穿。',
              '护环厚度量出来低于更换限值。',
            ],
            Truth: '保持环是消耗件，这只已经超限。它突出的高度不够，压不住晶圆前方的垫子，垫子越过晶圆边缘时回弹，最后那几毫米受到的压力超过配方要求。边缘先清完，然后在中心赶上来的剩余时间里一直被过抛，这就是它既不均匀又下凹、而不只是不均匀的原因。',
            Complaint: '边缘测点过抛，晶圆边缘的下凹超差。晶圆中心没问题。',
            Label: '保持环磨损超限',
          },
          agedSlurry: {
            Evidence: [
              '平坦度严重超差，而速率和均匀性都接近正常。',
              '损伤在全片是均匀的，这一下排除了两个压力类故障——载具或护环造成的缺陷不可能没有径向特征。',
              '研磨液滴定显示缓蚀剂浓度远低于限值。',
              '监控片轮廓仪显示宽结构下凹、阵列被侵蚀。',
            ],
            Truth: '料桶在设备上挂太久，缓蚀剂分解了。磨料几乎不受影响，所以去除速率纹丝不动——但缓蚀剂干的是两份活。它钝化介质层，工艺的选择比就是从这来的；它还保护接触点之间低洼处的铜。它一没，每条宽线都下凹，每个密集阵列都被侵蚀，而且全片均匀地发生。',
            Complaint: '焊盘上的下凹翻了三倍。速率看着正常，均匀性也正常。',
            Label: '研磨液过期',
          },
          glazedPad: {
            Evidence: [
              '抛光时间远超，而平坦度和均匀性恰好正常。晶圆本身什么都没变。',
              '去除速率掉了一半还多。',
              '垫切削率检查表明修整器没有在削去垫材。',
              '这是质量体系看不见的故障。只有设备日志抓得住它。',
            ],
            Truth: '金刚石修整器切不动了，垫面封釉，再也带不动研磨液进入接触区。同样的压力和转速，去除量掉了下来。它之所以容易被忽略，是因为设备是端点控制的：它就多抛一会儿，产出一片一模一样的晶圆。缺陷根本不在晶圆上，它在产线的产能里，而且会一直隐形，直到有人去看产能图而不是质量图。',
            Complaint: '抛光时间涨了一整周——原来九十秒的配方现在快三分钟了。每片晶圆量出来还是完美的。没人看得出问题。',
            Label: '抛光垫釉化，修整器切不动',
          },
          healthy: {
            Evidence: [
              '平坦度、均匀性、抛光时间都在各自的认证值上。',
            ],
            Truth: '什么问题都没有。正确的判定就是放行这一批。',
            Complaint: '抛光垫周一换的，设备通过了认证。没有任何报告。',
            Label: '抛光机已认证',
          },
        },
        TextArrivedWithNmOfSurface: '来料带着 {{a}} nm 的表面起伏。',
        NoteNmTotal: '共 {{a}} nm',
        TextNm: '{{a}} / {{b}} nm',
        region: { pad: '焊盘，80 um', array: '密集阵列，0.5 um', iso: '孤立线条，2 um' },
        TextMetal: '{{a}}——金属占 {{b}}%',
        NoteRecessedMetalAtOfBlanket: '凹陷处金属按全片速率的 {{a}}%',
        NoteNm: '< {{a}} nm',
        NoteEndpointAtS: '端点在 {{a}} 秒',
        NoteMSPadSpeed: '垫速 {{a}} m/s',
        SectionRemovalAcrossTheWafer: '全片去除分布',
        NoteTurnThePlatenAndThe: '让抛光盘和载具同速旋转，晶圆上每一点看到的垫速完全相同——是严格相同，不是近似。让它们不同速，就会出现一个任何压力修正都消不掉的径向特征。',
        SectionTheDieRegionByRegion: '裸片，一个区域一个区域看',
        NoteEndpointFiresWhenTheBulk: '端点在晶圆的大部分发生转变时触发，而不是在最后一个区域完成时。先清完的地方只能等——而且边等边被过抛。',
        SectionWhatTheNextLayerInherits: '下一层继承到什么',
        NoteAScannerHasOneFocal: '光刻机只有一个焦平面，而这个表面有一个高低范围。这里剩下的任何起伏，两步之后都要从光刻的焦深预算里扣——扣在一台工作完全正常的光刻机上。',
        SectionSolvedBesideTheFormulaIt: '数值解与它取代的公式并排',
        TextDishingErosion: '下凹 / 侵蚀',
        AxisXPositionOnTheDieMm: '裸片上的位置（mm）',
        AxisYNmAboveTheLowestPoint: '高出最低点（nm）',
        LabelRemovalRate: '去除速率',
        LabelPlanarityAcrossDie: '裸片内平坦度',
        LabelMetalBridging: '金属桥接',
        LabelSlurrySelectivity: '研磨液选择比',
        AxisXNormalisedRadius: '归一化半径',
        AxisYMetalRemovedNm: '去除金属（nm）',
        LabelSurfaceHandedOn: '交下去的表面',
        NoteAcrossTheDie: '跨裸片范围',
        LabelCdSpreadItCauses: '它造成的 CD 散布',
        NoteAtLith03FromDefocus: '在 LITH-03，仅离焦一项',
        LabelOfTheCdBudget: '占 CD 预算',
        NoteNoLotOnThePlaten: '抛光盘上没有批次。从上游设备送一片晶圆过来抛光。',
        NoteThisLotIsStillAn: '这批货还是一根晶棒。得先由 WPR-08 切片才能抛光。',
        TextPolishThisWaferAndPass: '抛光这片晶圆并传下去',
        reading: {
          profilometry: {
            Label: '监控片轮廓仪测量（4 小时）',
          },
          zonemap: {
            Label: '压力标测晶圆，全部分区（3 小时）',
          },
          ringgauge: {
            Label: '保持环厚度测量（1 小时）',
          },
          inhibitor: {
            Label: '研磨液滴定，抑制剂含量（1 小时）',
          },
          padcut: {
            Label: '修整器切削率检查（2 小时）',
          },
          short: {
            Limit: '无桥接',
            Label: '抛后金属桥接',
          },
          edge: {
            Label: '边缘减中心去除量',
          },
          nu: {
            Label: '去除不均匀度',
          },
          rate: {
            Label: '全片去除速率',
          },
          cycletime: {
            Label: '抛至端点的时间',
          },
        },
        aspect: {
          carrier: {
            Action: '用压力标测晶圆重新校准各区调压阀',
            Label: '载具分区压力',
          },
          slurry: {
            Action: '排空回路，换新桶，冲洗并重新认证',
            Label: '研磨液供给',
          },
          ring: {
            Action: '更换保持环并重新归零载具',
            Label: '保持环',
          },
          pad: {
            Action: '磨合新垫并重新认证修整器扫掠',
            Label: '抛光垫',
          },
        },
        AssistantBlurb: '可以问过抛、主轴转速，或者为什么同一片芯片上这个图形失效、旁边那个却在同一次抛光里安然无恙。每一个回答都是拿这台抛光机在它当下的真实状态下跑出来的实验。',
        AssistantLead: 'CMP-11 探究助手',
      },
      plating: {
        TransportSurface: '晶圆表面 Cu(II)',
        TransportOfLimit: '占极限',
        TransportBurning: '表面耗尽 — 烧焦',
        SectionTransport: '极限电流是怎么来的（质量传输）',
        TransportNote: '晶圆上的电流把槽液中的铜消耗掉，槽液再通过表面的一层扩散把它补回来。颜色表示实际有多少 Cu(II)。把电流调大，表面就被抽空；调得足够大它归零，这就是"烧焦镀层"的含义 — ' +
          '铜只能以扩散允许的速度到达，于是落下的是粉末而不是薄膜。真正精妙的是改变槽液浓度时会发生什么：无论槽液起点多高，同样的电流在表面之下留下的缺口总是一样大，所以表面被抽空时的电流恰好正比于其中的铜含量。这就是上面面板用作上限的那条直线 ' +
          '— 斜率不再是选来的，而是被解释了。一点诚实的局限：这里解的是欧姆场，所以电流越过极限后仍会继续上升，而不像真实电解槽那样趋于平台。请读表面浓度，不要读电流。',
        NoteBothModelsAgreeOn: '两个模型在这个结构上意见一致，而这与它们的分歧同样有价值——一个在公式适用之处却复现不出公式的求解器只能是错的。把深宽比推过六左右，或者让加速剂枯竭，就能看着它们分道扬镳。',
        NoteTheTwoModelsReturn: '两个模型对这个结构给出的是不同的判定，而不只是不同的数字。赛跑判据靠一条拟合直线把一个比值映射成空洞比例；网格则要么最后剩下未填的格子，要么没有。两者中只有一个能对着横截面去核对——这正是关键所在。',
        NoteAboveValidatedDomain: '这个加速剂浓度已超过认证槽液的 {{a}} ' +
          '倍，而那正是这个求解器不再可信的地方。在那个区间它的覆盖度平衡存在一种未经实测检验的失控解，所以请把这幅图当作示意，而不是结果。',
        ValueAtTheBottomByDefinition: '{{a}}，按定义位于槽底',
        ValueAtOfTheDepth: '{{a}}，位于 {{b}} % 深度处',
        MetricPeakAcceleratorCoverage: '加速剂峰值覆盖度',
        ValueXAssumed: '{{a}}×（假定）',
        ValueXMeasured: '{{a}}×（实测）',
        MetricBottomUpGain: '自底向上增益',
        ValueATimeNotAnEvent: '只是一个时间，不是一个事件',
        ValueNever: '从不',
        ValueSteps: '第 {{a}} 步',
        MetricMouthClosesAt: '槽口封闭于',
        ValueNoSuchQuantity: '无此量',
        ValueNone: '无',
        ValueNmWideNmDown: '宽 {{a}} nm，深 {{b}} nm 处',
        MetricVoidAsAPlace: '空洞，按位置',
        MetricVoidAsAFraction: '空洞，按比例',
        ValueFilled: '填满',
        MetricVerdict: '判定',
        NoteTheCellAboveDecides: '上面的电镀槽用两个数字赛跑来决定结构的命运：底部长到顶要多久，对侧壁合拢要多久。下面则改为在网格上推进铜前沿。其中没有任何环节去判断沟槽是否留洞——侧壁要么在槽底到达之前合拢，要么没有，剩下的就是空洞。于是这里有一幅可以端详的横截面，而两个数字的赛跑永远给不出这个。',
        LabelNonUniformity: '非均匀性',
        ValueVoid: '空洞',
        ValueSeam: '缝隙',
        ValueClean: '完好',
        LabelFill: '填充',
        LabelVoidLegend: '空洞',
        LabelDielectric: '介质',
        SecondaryLabel: '自底向上增益',
        ToolLabel: 'ECD-12 铜电镀槽',
        NextEveryNanometreOfOverburdenHere: '这里的每一纳米过镀层都是 CMP-11 的抛光时间，而抛光时间就是碟形磨损。仍能完全填满沟槽的最薄镀层是多少？',
        AxisXCurrentDensityMaCm: '电流密度 (mA/cm²)',
        TitleFieldThicknessAgainstCurrentDensity: '平场厚度随电流密度的变化',
        LabelFieldThickness: '平场厚度',
        AskedWhatSetsPlatedThicknessAnd: '是什么决定电镀厚度？镀得更快要付出什么？',
        NextTryTheSameSweepAgainst: '把同样的扫描换成对填充质量做一遍。哪个种子层厚度既厚到能镀平、又薄到还能填满沟槽——这里存在这样的厚度吗？',
        AxisYEdgeCentre: '边缘 − 中心 (%)',
        AxisXSeedThicknessNm: '种子层厚度 (nm)',
        TitleTerminalEffectAgainstSeedThickness: '终端效应随种子层厚度的变化',
        NoteOverburden: '过镀层',
        NoteEdgeOverCentre: '边缘高于中心',
        LabelSeedSheetR: '种子层方阻',
        HypCurrentEntersTheWaferThrough: '电流从晶圆边缘的触点进入，必须穿过种子层才能到达中心。种子层是薄金属，所以它是个电阻，中心的电位比边缘低。如果机制是这个，更厚的种子层就应把分布拉平——而且完全不用碰槽液。',
        AskedWhyDoesPlatedThicknessLean: '为什么电镀厚度从边缘到中心有倾斜？',
        NextTheAdditivesBreakDownAs: '添加剂在工作中不断分解，所以早上还能填满的槽液，下午未必还能填满。这种失效在 INSP-13 的检测扫描上会是什么样子——它到底会不会显出任何样子？',
        AxisYFractionOfTrench: '占沟槽比例',
        TitleVoidFraction: '空洞比例',
        LabelConformalNoAdvantage: '保形——无增益',
        AxisXAcceleratorQualifiedBath: '加速剂（× 认证槽液）',
        TitleBottomUpGainAgainstAccelerator: '自底向上增益随加速剂的变化',
        LabelAspectRatio: '深宽比',
        HypIfPlatingWereUniformA: '如果电镀是均匀的，沟槽处处以相同速率镀膜，槽口会在底部填满之前先封死，每个结构里都会留一个空洞。通常没有空洞，说明底部的生长必须快于槽口。如果做到这一点的是加速剂，去掉它就应重新引入这个工艺存在的意义所要避免的空洞。',
        AskedWhyDoesTheTrenchEnd: '为什么沟槽最后是个空洞，而不是实心的铜？',
        SumBottomUpSuperfillAndWhat: '自底向上的超级填充，以及添加剂配比失衡时会发生什么。',
        fault: {
          starvedBath: {
            Evidence: [
              '镀液铜含量 14 g/L，低于 34 g/L 的下限。',
              '每片晶圆厚度偏低，并带有烧焦镀层特有的不均匀。',
              '阳极电位、接触电阻、镀液寿命和温度全部正常。',
              '厂务的公用设施面板显示 ECD-12 化学品管线泄漏，输送已被联锁关停。',
            ],
            Truth: '地下层的电镀化学品管线在双套管内泄漏，柜下托盘传感器报警，泄漏联锁关停了输送泵——这正是它的用途。在楼上这意味着镀液整个班次都没有补液：每片晶圆都从镀液里带走铜，如今只剩配方铜量的三分之一。极限电流随之下降，同样的 20 mA/cm² 已超出镀液的供铜能力，镀层烧焦——变薄，并在电流集中的边缘变粗糙。这个镀槽上做什么都补不了一个供液在两层楼下被联锁切断的镀液。',
            Complaint: '整片晶圆的铜都偏薄，镀层在边缘变得暗淡起粉。阳极、接触环和镀液温度都正常；镀液按计划加过药。',
            Label: '镀液缺铜——输送管线泄漏',
          },
          dirtyContact: {
            Evidence: [
              '接触电阻 70 mΩ，限值 15 mΩ。',
              '厚度分布又低又不均。',
              '阳极电位正常。',
            ],
            Truth: '接触环脏了。它像钝化的阳极一样饿着晶圆的电流，还添了一样阳极永远做不到的事：每根触指上的损耗不一样，于是晶圆镀得又薄又不均。薄加不均，就是它的特征。',
            Complaint: '铜偏薄，而且分布图在一个扇区上边缘偏重。',
            Label: '接触环电阻偏高',
          },
          passivatedAnode: {
            Evidence: [
              '阳极电位高出标称 1.4 V，限值 0.4 V。',
              '厚度分布偏低。',
              '接触环电阻正常。',
            ],
            Truth: '阳极上长了一层膜，吃掉了驱动电位，晶圆看到的电流比电源以为的少。电流少就是铜少，法拉第定律说得明明白白。接触环是干净的，这是它和另一种饿着晶圆的方式的分界。',
            Complaint: '铜镀出来偏薄，CMP 在靠过抛找齐。',
            Label: '阳极钝化',
          },
          exhaustedAdditives: {
            Evidence: [
              '镀液时龄 32 Ah/L，限值 12 Ah/L。',
              '剖面显示填充带空洞。',
              '厚度和均匀性都完美在目标上，这正是不毁一片晶圆就看不见这个故障的原因。',
            ],
            Truth: '镀液已到 32 Ah/L，加速剂分解了。法拉第定律不管添加剂——铜的总量一点没少——但让沟槽自底向上填满的 CEAC ' +
              '正反馈没了，于是每个结构中间都藏着一条空洞。它通过全厂拥有的每一种非破坏性测量，然后在客户手里失效。',
            Complaint: '可靠性部门退回了一批通孔开路的器件，而那批货在分选时每项测试都过了。电镀那边说晶圆是完美的：厚度对、均匀性对、节拍也对。',
            Label: '添加剂体系耗竭',
          },
          ecdHealthy: {
            Evidence: [
              '台上的每个读数都正常。',
              '剖面显示干净的自底向上填充。',
            ],
            Truth: '什么问题都没有。正确的判定就是放行这一批。',
            Complaint: '例行批次。厚度和均匀性都在目标上。',
            Label: '一切正常',
          },
        },
        NoteSPerWafer: '每片 {{a}} 秒',
        NoteSeedSqTheCentreIs: '种子层 {{a}} Ω/sq——晶圆中心离接触环最远',
        NoteAcceleratorCoverageAtTheTrench: '槽底加速剂覆盖度 {{a}}',
        NoteGainAgainstNeededAtAr: '增益 {{a}} 倍，深宽比 {{c}} 需要 {{b}} 倍',
        SectionAPositiveFeedbackMadeOf: '一个由几何构成的正反馈',
        NoteTheAcceleratorIsConservedOn: '加速剂在它附着的表面上是守恒的。沟槽底部往上填的时候，那个表面在收缩——同样多的分子占了更小的面积，覆盖度升高，底部长得更快，底面积于是缩得更快。这就是自底向上填充的全部机理，也是超填充恰好在最需要它的结构上出力最猛的原因。增益必须超过深宽比的两倍，否则侧壁先合拢，沟槽就空了。',
        SectionSolvedBesideTheFormulaIt: '数值解与它取代的公式并排',
        LabelCopperLate: '铜，后期',
        LabelCopperEarly: '铜，早期',
        SectionCellState: '镀槽状态',
        SectionBathAndRecipe: '镀液与配方',
        TextPlateTheWaferAndPass: '镀完这片晶圆，传给 CMP-11',
        LabelBottomUpGain: '自底向上增益',
        LabelOverburden: '余量厚度',
        NoteEveryNanometreIsCmp11: '每一纳米都是 CMP-11 的下凹时间',
        LabelTerminalEffect: '端子效应',
        Note4AcrossTheWafer: '全片 < 4%',
        LabelChargePassed: '已通过电量',
        AssistantLead: 'ECD-12 探究助手',
        AssistantBlurb: '可以问：沟槽为什么留了空洞、边缘为什么镀得更厚、要镀多久。每个回答都是在这个镀液里真实跑出来的一次填充。',
        reading: {
          bathCopper: {
            Limit: '> 34 g/L',
            Label: '镀液铜含量与补液目标',
          },
          houseutilities: {
            Limit: '所有系统正常',
            Label: '致电厂务查看公用设施面板（0.5 小时）',
          },
          crossSection: {
            Limit: '填满',
            Label: '沟槽 FIB 剖面——破坏性（3.0 小时）',
          },
          thickness: {
            Label: '四探针厚度标测，49 点（1.0 小时）',
          },
          bathTemp: {
            Label: '镀液温度',
          },
          contactOhms: {
            Label: '接触环电阻',
          },
          anodeVolts: {
            Label: '阳极电位高出标称',
          },
          bathAge: {
            Label: '距上次放旧的镀液时龄',
          },
        },
        aspect: {
          house: {
            Action: '停止本机台作业，上报厂务',
            Label: '本机台无故障——升级到厂务',
          },
          temperature: {
            Action: '重新校准镀液热电偶并重调加热回路',
            Label: '镀液温度',
          },
          contact: {
            Action: '清洁接触环并逐指复测电阻',
            Label: '接触环',
          },
          anode: {
            Action: '剥除阳极膜并重新活化，或更换阳极',
            Label: '阳极',
          },
          bath: {
            Action: '对镀液放旧补新，并按 CVS 分析重设加药量',
            Label: '添加剂体系',
          },
        },
      },
      thermal: {
        TunnelDrawnMag: '墙后的图像比实际放大了 ×{{m}} 倍亮度',
        NoteOnTheMonitor: '监控片上',
        LabelJunction: '结深',
        NextHoldingIsTheEasyHalf: '扣住是简单的那一半。早些时候在这根炉管跑过的批次，用的是同一张台面单——你要量什么、量哪几片，才能判断其中有没有该收回来的？',
        NextOneMonitorInAFiftyWafer: '一片监控片放行了五十片一炉。你会把这片监控片放在舟的什么位置？在装载的另一处再放一片，能告诉你这一片说不了的什么？',
        LabelActivation: '剂量激活比例',
        LabelSheetResistance: '方块电阻',
        LabelGateOxide: '栅氧化层',
        VerdictWithAWatchItem: '放行，但有一项要盯着',
        VerdictHeld: '扣住',
        VerdictQualified: '已认定',
        LabelVerdict: '判定',
        HypAQualificationIsADecision: '资格认定是一个决定，不是一段解释。只要监控片回来时氧化层厚度落在窗口内、方块电阻在目标容差之内，而且台面每一项读数都在限值内，炉子就放行；否则扣住。再用同一根炉管跑已认定配方，就能看出该负责的是配方还是设备。',
        AskedIsThisFurnaceFit: '这台炉子现在能不能跑产品，如果不能该怎么办？',
        SumRunTheMonitorWafer: '跑一片监控片，把台面读数逐项对照限值，然后做出放行判断。',
        NotePerNm3: '硅本身约为每 nm³ 50 个原子',
        BudgetSpikeCaveat: '图中下面两个面板是同一实验按流传的两种“热预算”定义分别打分。∫T dt —— 温度乘时间 —— 认为这两个流程只差约 ' +
          '4%：它的两条曲线几乎重合，而且它的单位 °C·s 根本算不出深度。∫D dt（单位 ' +
          'nm²）则认为它们相差上百倍，而它才是能预测硼去了哪里的那个。实验写在场景脚本里 —— 没有旋钮，因为除了尖峰之外两片晶圆必须共享一切 —— ' +
          '流程结束时它会自己停下；按重置就把九十分钟重新跑一遍。',
        BudgetSpikeNote: '两片晶圆，同样的硼注入剂量，同样的目标：900 °C 下 90 分钟。恒温那片得到的正是这些，正中目标。有尖峰那片除了在第二十分钟起经历十分钟 ' +
          '1200 °C 外完全相同 —— ' +
          '九十分钟流程里的一步，探针处却深了好几倍，而且几乎所有损害都发生在那十分钟里。这就是预算的含义：炉子不会对流程取平均，它对 D 做积分，而 D ' +
          '随温度呈指数变化。',
        SectionBudgetSpike: '热预算 —— 九十分钟里的十分钟',
        BudgetSteady: '恒温',
        BudgetSpiked: '有尖峰',
        BudgetSpiking: '尖峰 —— 左侧晶圆 1200 °C',
        BudgetShared: '同一流程 —— 两片晶圆完全相同',
        BudgetLabelSpiked: '尖峰 · 其中 10 分钟 1200 °C',
        BudgetLabelSteady: '恒温 · 全程 900 °C',
        BudgetNowTemp: '当前 {{t}} °C',
        BudgetDtNote: '每一步各自的 ∫D·dt，单位 nm² —— 这才是结深真正积分的量，也是上面两种单位的换算关系：热预算磁贴把同一块面积除以 1000 °C 下的 ' +
          'D，折合成当量秒数。按步画出来，指数关系一目了然：又短又热的一步可能比又长又冷的一步花得更多。',
        SeriesStepDt: '每步 D·t',
        ChartDtAxis: 'D·t (nm²)',
        ChartBudgetDtTitle: '每一步的 D·t —— 花掉预算的是温度',
        HopBuilding: '正在构建晶体 —— 放入间隙原子并让 217 个原子在力场上弛豫……',
        HopGlowNote: '黄色原子就是间隙原子。它是实时找出来的：离理想晶格所有格点最远的那个原子 —— ' +
          '正因如此，每次踢出交换把间隙身份交给另一个原子时，标记都能跟上。在高温下缺陷有时会摊在共享一个格点的两个原子上（哑铃构型）；这时两个都会发光，因为它们俩确实都是缺陷。',
        HopMsdNote: '晶体旁边的图有两条曲线，二者之间的差距正是这一课的重点。实线是测量值：填隙原子实际走到了哪' +
          '里，连续采样，所以它随晶格颤动，跳跃发生时上升，下一次踢出反向进行时又落回去。几次跳跃的单' +
          '条轨迹不是任何东西的平均——同一温度下三次运行分别达到 9.5、51 和 3.4 Å²，这' +
          '只是随机行走小样本的常态，所以不要从它上面读斜率。虚线则把跳距的平方累加起来：如果这些跳跃' +
          '没有一次折返，它们会走出的那条路。它只会上升，D 就是从这条线读出来的——斜率除以六，单位' +
          '与推进扩散所用的相同。当实线落到虚线下方时，你看到的是关联：填隙原子又回到了原处，这正是实' +
          '测扩散系数带有关联因子的原因。也要读那些平段：跳跃是泊松过程，所以即使在 1650 K，两' +
          '皮秒内一次跳跃都没有也完全正常，一次运行要跑满十皮秒再读数。对照组让两条线永远平直——没有' +
          '缺陷，就没有什么可跟踪，也没有什么可计数，D = 0。',
        HopMsdStatus: '填隙原子已离开出发点 {{a}} Å² · 跳跃求和 Σλ² = {{b}} Å² → D ≈ {{c}} Å²/ps（6·D·t）',
        HopChartY: '位移² (Å²)',
        HopMeasured: '填隙原子目前走到哪里',
        HopJumpSum: '由跳跃推得的随机行走（Σλ²）',
        HopChartX: '模拟时间 (ps)',
        HopArrTitle: '阿伦尼乌斯图 —— 每次 {{a}} ps 运行一个点，斜率 = −Eₐ/k',
        HopArrX: '1000 / T（K⁻¹）',
        HopArrY: 'ln(每 ps 跳跃次数)',
        HopArrPerPs: '次/ps',
        HopArrPoints: '已完成的运行',
        HopArrFit: '最小二乘直线',
        HopArrEmpty: '在某个炉温下让间隙原子运行 {{a}} ps：运行会自行停止，并在这里落成一个点。两个炉温就能画出直线。',
        HopArrOne: '目前一次运行 —— 换一个炉温再跑一次就能画出直线',
        HopArrStatus: '{{n}} 次运行 · 由斜率得 Eₐ ≈ {{ea}} eV · 每个点带有 ±1/√跳跃数 的泊松计数误差',
        HopArrSkipped: '{{n}} 次无跳跃的运行落在对数轴之外',
        HopArrClear: '清除各点',
        HopArrNote: '激活能就是这样测的，在炉子里和在这里都一样。跳跃速率按 exp(−Eₐ/kT) 变化，所以其对数对 1/T 是一条直线，斜率就是 −Eₐ/k。间隙原子每运行 {{a}} ps 就是一次测量：在几个炉温下各跑一次，点就会排成线，并带着泊松计数应有的离散——六次事件的一次运行，其速率大约只有 40% 的准确度。同一炉温下的重复直接显示了这种离散，因此直线是对全部点拟合而不是在两点之间连线。完美晶体不画在图上：它在任何温度下速率都是零，而零没有对数。',
        SectionTunnel: '栅氧化层为什么有下限——隧穿',
        TunnelNote: 'THRM-04 生长这层氧化层，上面的配方想做多薄都可以。可晶体管用不了。薄到两纳米以下，沟道里的电子就不再被氧化层挡住，因为电子是一列波，而波能够伸进一堵它没有足够能量翻越的墙。这里是一维薛定谔方程的解：一个波包撞上一个高度等于硅–SiO₂ 能带偏移的矩形势垒。穿过去的比例每增厚十分之一纳米就按一个固定倍数下降，所以把氧化层从 2 nm 减到 1.2 nm，漏电会放大几千倍——这正是业界转向更厚的氧化铪薄膜而不是更薄的二氧化硅的原因。势垒两侧和势垒内部统一使用 0.5 m₀ 的有效质量；这堂课依赖的是氧化层内部的衰减，而这个质量对它是正确的。 两侧的硅只是画出它的晶格作为参照：模型中的电子以有效质量的自由粒子形式穿过它——晶体对这些能量的电子正是这样起作用的——所以并没有针对那些原子求解。 「按相位着色」按 ψ 的相位给波上色：条纹就是它的波长，随波包移动，在走得快的地方变密；「蓝与绿」只画强度，墙后为绿色。',
        TunnelChartTitle: '越过氧化层的概率',
        TunnelChartX: '时间 (fs)',
        TunnelChartY: 'log₁₀ 比例',
        TunnelMeasured: '传播的波包',
        TunnelFormula: '此波包的公式值',
        TunnelFailed: '薛定谔求解器加载失败。',
        TunnelLoading: '正在加载薛定谔求解器…',
        TunnelStatus: '{{a}} fs · 越过氧化层 {{b}}（{{c}}）· 反射 {{d}}%',
        TunnelOneIn: '每 {{n}} 个电子中有一个',
        TunnelNone: '低于十亿分之一',
        TunnelSum: 'Σ|ψ|² = {{s}}（反射 + 墙内 + 穿过，含吸收边界）',
        TunnelColourLabel: '相位着色',
        TunnelChartShort: '转移矩阵：透过 {{a}} · 每纳米氧化层 {{b}} 个数量级',
        TunnelChartHelp: '这两个数是什么',
        TunnelChartStatus: '在这堵墙上用传递矩阵求解，并对波包的能量分布取平均：{{a}}（仅取平均能量则为 {{c}}）· 在此能量下每纳米氧化层 {{b}} 个数量级',
        TunnelThickTitle: '透射率随氧化层厚度的变化',
        TunnelThickX: '氧化层厚度 (nm)',
        TunnelRuns: '已完成的运行',
        TunnelLineAt: '{{e}} eV、{{v}} eV 势垒下的公式',
        TunnelThickEmpty: '每次完成的运行都会在此按其厚度落成一个点。改变厚度再运行，直到把指数曲线画出来。',
        TunnelThickStatus: '图上的点：{{n}} · 在当前能量和势垒下，虚线每纳米下降 {{b}} 个数量级',
        TunnelClear: '清除各点',
        TunnelThickNote: '上面那张图一次只总结一次运行，这里是它们合起来的实验。每次完成的运行就是其厚度处的一个点；在三四个厚度下各跑一次，点会在这个对数轴上排成一条直线——对数轴上的直线就是指数。斜率是每纳米 2κ/ln 10 个数量级，κ 由电子能量低于势垒多少决定，所以势垒越矮直线越平缓：这就是 high-k 的全部理由，一张图说完。在别的能量或势垒下做出的点仍留在图上并偏离当前的直线，这对它们来说是诚实的位置。',
        TunnelReset: '重置',
        TunnelSio2: 'SiO₂ · 3.1 eV',
        TunnelHfo2: 'HfO₂ · 1.5 eV',
        TunnelWidth: '氧化层厚度',
        TunnelEnergy: '电子能量',
        TunnelBarrier: '势垒高度',
        TunnelPastLabel: '越过氧化层',
        BoxNote: '同一层氧化层，从上往下看，并且由原子构成。这是 Quantum Modeler 的二维盒子，墙由密排的排斥性原子核组成，间距 0.32 nm——大约是二氧化硅中相邻原子的间距——层数由厚度决定：1.2 nm 的氧化层是四层。波包一边前进一边变宽，在原子前堆成驻波条纹——入射波与反射波在那里交叠——一个微弱的副本穿过去继续前进，用绿色放大画出。原子核的高度使其在一层内的平均值等于能带偏移，所以上面的平板正是它们的平均，虚线是这个厚度下平板公式的值。在旋钮允许的每个厚度下，原子的透射率与该平均值相差不过百分之几十，这一点经过校验；曲线与虚线之间的差距是原子造成的，不是误差。「氧化层作为平板」把原子换成它们的平均。 细线是 |ψ|² 的等值线，取波包初始峰值的 2、9、20、36、56 和 81%；墙后按放大后的刻度绘制。',
        BoxChartTitle: '越过墙的概率',
        BoxChartStatus: '在这堵墙上用传递矩阵求解，并对波包的能量分布取平均：{{a}} · 在此能量下每纳米墙 {{b}} 个数量级',
        BoxPastLabel: '越过墙',
        BoxStatus: '{{a}} fs · 越过墙 {{b}}（{{c}}）· 反射 {{d}}%',
        BoxLayers: '{{n}} 层',
        TunnelTab1d: '一维 · 线',
        TunnelTab2d: '二维 · 俯视',
        TunnelTab3d: '三维 · 体积',
        TunnelTabLeak: '栅极漏电',
        LeakNote: '氧化层到底漏多少。上面几个视图回答的是一个电子有多大比例穿过去；而晶体管要问的是另一个问题——每平方厘米多少安培，对照规格——这一页回答的正是它。这里的透射率用传递矩阵求解，而不是传播波包：对这个势形它是精确的，只要几微秒，而且是对波包的独立校验，测试脚本把三者放在一起比对。然后对真正到达的电子积分，也就是 Tsu–Esaki 供给函数；它在零偏压时为零：平的势垒无论多薄都不导电流，所以这一页也是势垒开始倾斜的地方。把偏压推过能带偏移，氧化层远端的角就落到电子以下，势垒变成三角形，直接隧穿让位给 Fowler–Nordheim。模型并没有被告知这个转变；传递矩阵只看得到势形。这里有两个不同的质量：氧化层内衰减用 0.5 m₀，硅中态密度计数用 0.19 m₀。没有包含的、引用数字前该知道的：镜像力势垒降低、反型层子带、任何陷阱辅助路径，以及温度（固定在 300 K）。结果是直接隧穿的估计，与实测氧化层相差约一个数量级，并且算在上面那些原子所平均成的平板上。',
        LeakStatus: '{{v}} V 下 {{j}} A/cm² · 电场 {{f}} MV/cm · {{r}}',
        LeakDirect: '直接隧穿',
        LeakFowler: 'Fowler–Nordheim',
        LeakBreakdown: ' · 已超过真实氧化层的击穿场强',
        LeakOver: '超出 1 A/cm² 规格 {{n}} 倍',
        LeakUnder: '低于 1 A/cm² 规格 {{n}} 倍',
        LeakNone: '没有偏压就没有电流：从栅极回来的电子和过去的一样多。',
        LeakJvTitle: '栅极电流随偏压的变化',
        LeakJvX: '栅极偏压 (V)',
        LeakJvY: 'log₁₀ A/cm²',
        LeakSeries: '{{w}} nm 氧化层',
        LeakBarrierSeries: '{{v}} eV 势垒',
        LeakSpec: '规格，1 A/cm²',
        LeakHere: '当前设定',
        LeakThickTitle: '{{v}} V 下栅极电流随氧化层厚度的变化',
        LeakJvCaption: '在当前偏压处，曲线每伏特上升 {{d}} 个数量级。',
        LeakThickCaption: '在此偏压下，曲线每纳米氧化层下降 {{d}} 个数量级。每微缩十分之一纳米，漏电就要付出一个固定的倍数，正是这一点终结了二氧化硅。',
        LeakBias: '栅极偏压',
        LeakCathode: '硅沟道',
        LeakOxide: '氧化层，{{w}} nm',
        LeakAnode: '栅极',
        LeakSpectrum: '电流来自哪里',
        LeakThisWall: '当前这堵墙',
        LeakShort: '{{v}} V 下漏电 {{j}} A/cm²',
        LeakRunShort: '这次运行已确认 {{p}}%',
        LeakHelp: '这个电流是怎么算出来的',
        LeakBeside: '这堵墙在 {{v}} V 下漏 {{j}} A/cm²。这是用传递矩阵在同一层氧化层上解出来的，而不是从左边那个波包测出来的——电流需要沟道所供给的每一个能量处的透射率，那要跑上几百次——两者相差不过几个百分点。',
        LeakRunCheck: '左边这次运行到目前为止测到穿过这堵墙的是 {{a}}，而上面的电流所依据的是 {{b}}：已经走了 {{p}}%。电流是这堵墙的属性，不是这次运行的属性——运行所做的，是印证它。',
        VolumeNote: '再看三维。这是 Quantum Explorer 的三维模型——同样的 Cayley 传播子在 208 × 48 × 48 网格上按 x、y、z 拆分，同样的密排原子核现在堆成真正的球层，同样的吸收边界和同样的概率核算——在工作线程上求解，每次运行几秒，并以体绘制画出：波包所在处为蓝色，墙后按自己的放大刻度为绿色。拖动可旋转，右键拖动可平移，滚轮可缩放。透射比例与二维盒子和一维模型测得的是同一个量，平板公式也是同一条虚线；两者互相校验。',
        VolumeIdle: '按 Run：波包在工作线程上传播，每次运行几秒。',
        VolumeRunning: '在工作线程上求解，每次几秒',
        VolumeStop: '停止',
        VolumeUnavailable: '三维求解器需要工作线程，这里无法启动。',
        BoxAtoms: '氧化层作为原子',
        BoxSlab: '氧化层作为平板',
        BoxWallAtoms: '{{n}} 层原子 · {{w}} nm · 平均 {{v}} eV',
        BoxFormulaSlab: '同样平均高度的平板',
        HopChartTitle: '填隙原子实际走了多远，对比其跳跃所隐含的随机行走',
        BudgetCaveat: '这个实验是写进场景脚本里的，所以这个视图没有任何旋钮，并且会在流程结束时自行停止：只有两片晶圆拿到完全相同的预沉积，这个对比才有意义，而任何能单独改动其中一片的旋钮都会毁掉它。点重置会把整条流程重跑一遍。图旁边那两条曲线就是热预算本身 ' +
          '—— 每片晶圆的 ∫D ' +
          'dt，由场景自己的脚本实时累加，而不是这个面板算出来的。注意看它们分开的幅度比掺杂分布小得多：左边晶圆多出大约三分之一的预算，却在探针深度处造成三倍的浓度差，因为结深按 ' +
          '√(Dt) 变化。预算上的小变化，并不是器件上的小变化。',
        HopControlNote: '现在切换到完美晶体，把温度推到滑块的顶端。什么都不动。不是变慢 —— ' +
          '是完全不动：即使把炉温设到距熔点只差四十度，无缺陷晶格记录到的跳跃次数也恰好是零；而同样这个晶胞里只要放进一个填隙原子，每隔两三皮秒就会跳一次。这正是为什么注入损伤、以及修复它的退火，和掺杂属于同一个主题：掺杂原子是搭着缺陷走的，而隔壁那台离子注入机刚刚造出了大量缺陷。但请不要把这块静止的晶体读成"没有损伤的晶圆就不会扩散"。真实晶格在高温下会自己产生缺陷——空位与填隙原子在热平衡中不断生成又复合，浓度由它们的形成能决定——1100 °C 的炉子里多得很。这个晶胞做不到的是"等到"一个：216 个原子、皮秒量级的观察窗，对这样稀有的事件来说实在太小也太短。所以这个对照说明的是：缺陷是**载具**，而不是说高温晶体里没有缺陷。',
        BudgetABNote: '两片晶圆，相同的注入、相同的预沉积。预沉积结束时流程分岔：左边这片继续走完剩下的步骤 —— 先 1000 °C，再 900 °C —— ' +
          '右边这片则被淬冷到室温，此后再没有受过热。淬冷的那片是对照组，它显示的是你刚做完时这个结原本的样子。看着左边那片继续跑。到流程结束时，它的掺杂原子已经扩散出好几倍的距离，而这全都来自那些跟形成这个结毫无关系的步骤。上面那块热预算读数统计的就是这件事，这也是为什么一个结要针对整条流程来设计，而不是只针对它自己那步退火。',
        SectionBudgetAB: '热预算 —— 后续步骤会对已经做好的结做什么',
        HopReset: '重置计数',
        HopPause: '暂停',
        HopRun: '运行',
        HopTemp: '炉温',
        HopPerfect: '完美晶体（对照）',
        HopWithDefect: '一个填隙原子',
        HopLoading: '正在加载分子动力学引擎…',
        HopFailed: '分子动力学引擎加载失败。',
        HopStatus: '{{a}} 次跳跃 · {{b}}/ps · 实测 {{c}} K · {{d}} ps',
        HopCaveat: '这里刻意没有展示的，是一个硼原子的跳跃。硼的激活能是 3.46 eV —— 正是上面那个扩散场景的阿伦尼乌斯关系所用的数字 —— 换算下来在 ' +
          '1100 °C ' +
          '大约每秒跳两次。而这个视图只能跑到皮秒量级，也就是说差了大约八个数量级，再快的计算机也补不上这个差距。填隙原子之所以看得见，仅仅因为它的势垒低得多。正是这个差距，决定了晶圆尺度的扩散只能是偏微分方程里一个拟合出来的系数，而不是对原子的模拟 ' +
          '—— 那个基本事件是真实的，只是稀有得根本无法观看。',
        HopNote: '上面那个场景用一个随温度按阿伦尼乌斯关系变化的扩散系数来搬运掺杂原子。这个数字就是由眼前这件事构成的：一个硅原子不在晶格格点上，而是卡在格点之间，把邻居挤开、占据它们的位置。按下运行，看着计数往上走 ' +
          '—— 每一次跳跃都是 D 里的一项。然后把炉温调高，看着跳跃速率跟着升上去，这就是阿伦尼乌斯因子在真实发生，而不是被断言出来。',
        SectionHop: 'D(T) 是由什么构成的 —— 一个原子换了位置',
        BudgetFlowEnded: '流程结束',
        BudgetQuenched: '淬冷',
        BudgetFullFlow: '走完整条流程',
        BudgetPredep: '共同的预沉积 —— 两片晶圆完全相同',
        NoteTheSolverPutsThe: '求解器给出的结比公式深 {{a}} 倍。原因就一句话：D 取决于那里已有多少掺杂，所以重注入会自己往下挖，而这个分布跨越了 {{b}} ' +
          '倍的扩散系数范围，单独一个阿伦尼乌斯数表示不了。',
        NoteOnThisRecipeTheTwo: '在这个配方上两者一致到 ' +
          '{{a}}%。对一个短周期来说这是诚实的结果：几乎没有扩散，分布还是注入机留下的那个，用公式算完全没问题。把退火加长，或者在下面加一步，就能看着它们分开。',
        ValueAllOfIt: '全部',
        ValueSolubilityCeiling: '{{a}} %（固溶度上限）',
        MetricElectricallyActive: '电激活比例',
        Value100ByConstruction: '构造上就是 100 %',
        MetricDoseLeftInThe: '留在晶圆内的剂量',
        ValueNoInterface: '没有界面',
        ValueNmInterfaceMoving: '{{a}} nm，界面在移动',
        MetricSiliconConsumed: '消耗的硅',
        ValueOneNumberEverywhere: '处处同一个数',
        ValueSpreadNodeToNode: '节点间相差 {{a}} 倍',
        MetricDiffusivityAcrossThe: '分布内的扩散系数',
        MetricSurfaceConcentration: '表面浓度',
        MetricJunctionDepth: '结深',
        LegendTheFittedGaussian: '拟合高斯',
        LegendTheEquationSolvedOn: '方程，在 {{a}} 个时间步上求解',
        LabelAnneal: '退火',
        LabelOxidation: '氧化',
        SecondaryLabel: '方块电阻误差',
        ToolLabel: 'THRM-04 · 立式炉与快速热处理',
        LabelTryTheSpikeAnneal1100: '试试尖峰退火（1100 °C，2 秒）',
        NextASpikeAnnealSeparatesActivation: '尖峰退火靠又热又短把激活和扩散分开。是什么限制了它能有多短——升温那么快，晶圆又会发生什么？',
        AxisYSheetResistanceSq: '方块电阻 (Ω/sq)',
        AxisXJunctionDepthNm: '结深 (nm)',
        TitleJunctionDepthAgainstSheetResistance: '结深对方块电阻——把取舍画出来',
        NoteColdAndShortActivatesNothing: '低温短时什么都激活不了',
        LabelShortAnnealActivation: '短退火激活率',
        LabelSpikeAnnealActivation: '尖峰退火激活率',
        LabelItsSheetResistance: '它的方块电阻',
        LabelShallowestRoute: '最浅的路线',
        HypJunctionDepthIsSetBy: '结深由注入的起点加上热把它推了多远决定，方块电阻由这段深度里有多少激活的掺杂决定。任何让结变浅的做法，都是把同样的掺杂塞进更少的硅里，所以电阻应该上升——除非这个改动同时也改善了激活。',
        AskedHowDoIMakeThe: '怎么把结做浅？每条路各要付出什么代价？',
        SumTheJunctionDepthAndResistance: '结深与电阻的取舍，以及它为什么没有免费的一边。',
        NextAGateOxideHasTo: '栅氧化层只有几纳米厚，还必须复现到其中的零头。根据你刚看到的，你会用高温短时还是低温长时来长它——哪种对最终数字的控制更强？',
        TitleOxideThicknessAgainstTimeDry: '氧化层厚度随时间的变化，干氧与湿氧',
        NoteSqrt42WouldBe: 'sqrt(4) = 2 对应无线性项',
        LabelGrowthRatio: '生长比',
        LabelWet120Min: '湿氧，120 分钟',
        LabelAt120Min: '120 分钟时',
        LabelAt5Min: '5 分钟时',
        AskedWhyDoesOxideGrowthSlow: '为什么氧化层越厚，生长越慢？',
        SumTheLinearAndParabolicRegimes: '线性区与抛物线区，以及它们为什么需要不同的配方。',
        LateralUnderstood: '推进退火是否只把掺杂剂向下推，还是也会把它横向推到掩模之下。',
        LateralHypothesis: '扩散没有偏好的方向。掩模决定的是掺杂剂从哪里进入硅，而不是它最终到哪里；一旦进入，它沿表面扩展应当和向体内扩展一样容易——横向侵入的距离应当跟随结深，而不是随退火进行越落越远。',
        NextLateralBudget: '横向侵入由与结深相同的 D·t 决定，所以收窄它的唯一办法是少花热预算。问我下游每个高温工步会让这个结付出什么代价——那个实验在一维里跑，而你现在知道要把它的答案在横向上再算一遍。',
        NextIfADownstreamStepIs: '如果下游步骤是固定的、去不掉的，剩下的唯一杠杆就是注入。问我：如果把结做得浅到能挺过那个步骤，方块电阻会怎么样。',
        LabelAfter1050C: '1050 °C 之后',
        LabelJunctionDepth: '结深',
        AxisYJunctionDepthNm: '结深 (nm)',
        AxisXStep0None: '步骤 (0 = 无)',
        TitleJunctionDepthAfterEachAdded: '每增加一个下游步骤后的结深',
        NoteEquivalentAt1000C: '折合 1000 °C 当量',
        LabelBudgetWorstCase: '预算，最坏情况',
        NoteAlmostFree: '几乎免费',
        LabelAfterA450CStep: '经 450 °C 步骤后',
        LabelAfterA1050CStep: '经 1050 °C 步骤后',
        LabelJunctionAlone: '结，单独',
        HypDiffusionDependsOnlyOnThe: '扩散只取决于扩散系数与时间的乘积，对晶圆经历过的每个步骤求和。如果这是对的，那么下游新增的一个步骤——哪怕与这个结毫无关系——照样会移动它；而更热的步骤会比更长的步骤移动得多得多，因为扩散系数对温度是指数关系，对时间只是线性关系。',
        AskedTheImplantDidNotChange: '注入没有变，结却变了。热量是从哪里来的？',
        SumShowThatEveryHotStep: '说明每个高温步骤都在从同一个共享账户里花钱。',
        fault: {
          wetHouseHeader: {
            Evidence: [
              '氧化层厚而结没动——水的特征，不是炉管偏热。',
              '管路露点偏高，而且新换的阱没有解决。这两件事合在一起，指向上游，别无他处。',
              '公用系统检查发现总管带水。',
              'ALD 那台设备在同一时间抱怨。它的症状听起来和这边毫无相似，因为水在那边干的是完全不同的事——但那是同一股水。',
            ],
            Truth: '厂里的干燥气干燥器周末失效带水了，总管里带着约 2% 的水——是液态携带，不是干燥器饱和后 ppm ' +
              '级的缓慢爬升。它到达每一台呼吸厂务氮气或氧气的设备，到了之后干什么取决于那台设备拿这股气做什么用。注意两个阈值差得有多远：个位数 ppm ' +
              '的水就能毁掉 ' +
              'ALD，因为水是它的共反应物；而用蒸汽长氧化层的炉管低于约百分之一什么都察觉不到。要两台设备同时叫起来得是一次粗暴的失效，这正是两个组几乎从不把问题联系起来的原因。在这台设备上它看起来和水汽阱饱和一模一样，因为就是同一股水换了条路来——在阱的上游，而不是在阱里。这就是换阱毫无用处的原因，也是换完之后管路露点仍然读湿的原因。',
            Complaint: '栅氧又厚了，像上季度水汽阱那次。阱周一刚换过，没有任何用。DEP-02 说他们的生长量也高了。',
            Label: '厂务氧气总管带水',
          },
          lowDose: {
            Evidence: [
              '方块电阻远高，而氧化层、结和热预算全都正常。热的方面完全不涉及。',
              '峰值浓度掉了一半，任何退火错误都做不到这一点——退火只会摊开掺杂剂，不会拿走它。',
              '剂量监控片和注入机的日志对不上。',
            ],
            Truth: '束流积分出了错，晶圆实收剂量只有注入机日志记的一半左右。掺杂剂减半就是载流子减半，方块电阻陡然上升。热工步全都完全正常，所以结几乎不动——只是同样的扩散分布里坐着更少的掺杂剂。',
            Complaint: '方块电阻高上天了。栅氧没问题。',
            Label: '注入机剂量不足',
          },
          hotAnneal: {
            Evidence: [
              '方块电阻低、结深，而栅氧恰好在目标上——所以不管发生了什么，都发生在氧化之后。',
              '热预算高了约 90%，结一动首先就该看这里。',
              '注入剂量偏低也会动方块电阻，但那是往上动，而且不会动结。这次它往下动，结还变深了。',
              '校准晶圆读数比高温计报的高约 50 C。',
            ],
            Truth: 'RTP 高温计漂了，控制器把灯推到约 1100 C，报出来的却是 1050 C。扩散系数服从阿伦尼乌斯律，五十度几乎让退火花掉的 Dt ' +
              '翻倍：掺杂剂铺得更远，结变深，方块电阻下降。',
            Complaint: '方块电阻偏低，结比模型算的深。',
            Label: 'RTP 高温计读数偏低',
          },
          wetLine: {
            Evidence: [
              '氧化层厚了而结没有动。这一条一锤定音——炉管偏热不可能放过结。',
              '方块电阻分毫不差，热预算什么都没变。',
              '干氧管路露点读数远超限值。',
            ],
            Truth: '干氧管路上的水汽阱饱和了，到达炉管的氧化剂约有 5% ' +
              '是水。湿氧化比干氧化快一个数量级，所以哪怕百分之几也会让氧化层严重超厚。工艺的其他部分毫无察觉：水不扩散掺杂剂，结和方块电阻都原封不动。',
            Complaint: '栅氧又厚了，跟上季度炉管那次一样。',
            Label: '干氧管路带水',
          },
          hotFurnace: {
            Evidence: [
              '氧化层厚了，结也变深了。只有温度能同时做到这两件事，因为只有温度是每一步都在花的。',
              '气路带水会长出一模一样的厚氧化层，却把结留在原地。这个对比本身就是诊断。',
              '测温晶圆读数比设定值高约 30 C。',
            ],
            Truth: '区域热电偶漂了，炉管实际温度约 930 C。氧化生长服从阿伦尼乌斯律，30 C 就能多买 40% 的氧化层——但晶圆也以偏高 30 C ' +
              '的温度待了二十分钟，而这些时间花的是退火同一本热预算。结也跟着动了。',
            Complaint: '栅氧厚了约 40%。配方没动，设定值显示 900 C。',
            Label: '氧化炉偏热',
          },
          healthy: {
            Evidence: [
              '栅氧和方块电阻都在各自的认证值上。',
            ],
            Truth: '什么问题都没有。正确的判定就是放行这一批。',
            Complaint: '今早的监控片都在目标上。没有任何报告。',
            Label: '炉管已认证',
          },
        },
        NoteXA: 'x/A = {{a}}',
        SectionSolvedBesideTheFormulaIt: '数值解与它取代的公式并排',
        NoteEverythingAboveWidensAGaussian: '上面的一切都是把高斯分布按根号 2Dt 展宽，D ' +
          '从退火温度下的阿伦尼乌斯直线上读出。下面则是把扩散方程在网格上『解』出来——几百个节点走完整个热循环，每个节点的 D ' +
          '按局部浓度取值，氧化界面边生长边推进晶圆。退火短时两者重合。退火长时两者分开，而那道差距正是要造求解器的理由。',
        AxisYLogCm: 'log₁₀ nm⁻³',
        reading: {
          implantlog: {
            Label: '剂量监控片对注入机日志（6 小时）',
          },
          pyrometer: {
            Label: 'RTP 高温计对校准晶圆（3 小时）',
          },
          houseutilities: {
            Limit: '总管不漏，PCW 在供应温度，干燥气 < 50 ppm',
            Label: '与厂务共同检查公用系统（2 小时）',
          },
          moisture: {
            Label: '干氧管路露点（2 小时）',
          },
          profilewafer: {
            Label: '全区炉温测温晶圆（4 小时）',
          },
          setpoints: {
            Limit: '按配方',
            Label: '炉温 / 退火设定值',
          },
          budget: {
            Label: '已花费热预算',
          },
          junction: {
            Label: '结深（SIMS）',
          },
          rs: {
            Label: '方块电阻（四探针）',
          },
          oxide: {
            Label: '栅氧（椭偏仪）',
          },
        },
        aspect: {
          house: {
            Action: '停止在本机上的操作，知会厂务',
            Label: '本机无事可修——上报厂务',
          },
          implanter: {
            Action: '重新校准束流积分，重跑剂量监控片',
            Label: '注入机剂量控制',
          },
          rtp: {
            Action: '用测温晶圆校准高温计',
            Label: 'RTP 高温计',
          },
          gasline: {
            Action: '更换水汽阱，吹扫并重新认证干氧管路',
            Label: '氧化气路',
          },
          furnace: {
            Action: '跑一片测温晶圆并重新校准各区热电偶',
            Label: '氧化炉温度',
          },
        },
        SectionLot: '炉中的批次',
        LotNote:
          '上面的面板退火的是旋钮所描述的东西。下面的炉子退火的是这片晶圆——它在 IMP-10 ' +
          '实际接受的注入,叠加在它已经历的每一步热过程之上。同一循环跑两次,结深仍会继续移动:晶圆记得,旋钮不记得。',
        LotEmpty: '炉管处没有批次。请从上游设备把晶圆送来退火。',
        LotIngot: '该批次还是晶锭。要先经 WPR-08 切片,炉子才能处理它。',
        LotCard: '流程卡',
        LotImplant: '携带注入:',
        LotNoImplant: '此晶圆没有注入记录——由配方旋钮代替。',
        LotPrior: '步热过程已在账上——本循环将叠加其上',
        LotFresh: '尚无热历史。这是它的第一个循环。',
        LotRun: '对批次运行本循环并传递下去',
        SectionResult: '结果',
        SectionProfile: '掺杂分布',
        SectionDriveIn: '推进退火 — 实时二维扩散',
        SumShowThatADriveIn: '说明推进退火在加深结的同时也同样地展宽每一个图形。',
        LabelJunctionDepth2D: '结深',
        LabelUnderTheMask: '掩膜下方',
        NoteEachSide: '每侧',
        LabelLateralOverDepth: '横向 / 纵向',
        NoteRoughlyConstant: '随时间基本不变',
        LabelDopedWidth: '掺杂区宽度',
        NoteFromAWindow: '源自 {{nm}} nm 的窗口',
        TitleDepthAndLateralReach: '一次推进退火中的纵向深度与横向扩展',
        AxisXAnnealMinutes: '退火时间（分钟）',
        AxisYNm: 'nm',
        LabelDownDepth: '纵向（结深）',
        LabelSidewaysUnderMask: '横向（掩膜下方）',
        TitleLateralAsAFraction: '横向扩展占纵向深度的比例',
        AxisYFraction: '横向 / 纵向',
        LabelRatio: '比值',
        DriveInNote:
          '实时运行的模拟：硼通过 SiO₂ 掩膜上 0.6 µm 的窗口向硅中扩散，真实尺度' +
          '（画面宽 2 µm）。硼的扩散系数按阿伦尼乌斯规律随退火温度变化 — 一百度就是 D 的一个' +
          '数量级 — 试试同样的时间分别在 1000 °C 和 1100 °C 下退火。再注意掩膜边缘：结在掩膜' +
          '下方横向扩展约为纵向深度的 0.8 倍，这是任何一维分布图都无法展示的。',
        DiLoading: '正在加载扩散引擎……',
        DiFailed: '扩散引擎加载失败。',
        DiPause: '暂停',
        DiRun: '运行',
        DiReset: '重置',
        DiAnnealLabel: '退火',
        DiAnneal: '退火 {{c}} °C',
        DiJunction: '结深 {{um}} µm',
        DiLateral: '掩膜下 {{um}} µm',
        DiClock: 't = {{min}} 分钟',
        DiOpenInEnergy2D: '在 Energy2D 中打开此场景 ↗',
        DiOpenInEnergy2DTitle: '在新标签页中用 Energy2D 编辑器打开此场景，可以修改它的几何结构与材料，也可以让它的 AI 助手重建它。',
        DiGraphTitle: '各探针处的浓度',
        DiGraphX: '退火时间（分钟）',
        ProfileNote: '掺杂原子最后落在哪里。注入把它放在某个深度，此后的每一道热工艺都在把它摊开。结深，就是分布曲线降到衬底掺杂浓度的那个位置。',
        SectionSteps: '下游热工艺步骤',
        StepsNote: '这些步骤发生在结形成之后，跟这个结本身毫无关系。加一道进去，看着结照样往下跑。扩散只认 D·t 的总和，硅并不知道哪一段热是谁的。',
        SectionOxide: '氧化层生长——先线性，后抛物线',
        OxideNote: '薄氧化层受限于硅界面处的反应，厚度随时间线性增长。厚氧化层受限于氧穿过已有氧化层的扩散，于是生长慢到与时间的平方根成正比。同一个方程，两种区制。',
        SectionBudget: '热预算都花在哪',
        BudgetNote: '把每一道步骤都折算成"在 1000 °C 下产生同样扩散所需的时间"。这样各步骤之间才能比较，而 cm² 的数字不行。',
        SectionAmbient: '氧化气氛',
        AmbientDry: '干氧 O₂',
        AmbientWet: '湿氧（水汽）',
        AmbientDryShort: '干氧',
        AmbientWetShort: '湿氧',
        AmbientNote: '水汽提供的氧化剂多得多，所以湿氧氧化要快上好几倍。',
        SectionDopant: '掺杂剂',
        DopantBoron: '硼',
        DopantPhosphorus: '磷',
        DopantArsenic: '砷',
        DopantNote: '同样的热，越重的原子扩散得越慢——所以要结浅的地方用砷，不要求浅的地方用硼。',
        SectionRecipe: '配方',
        SectionStored: '已存配方',
        SectionShift: '值班',
        NoSteps: '目前还没有下游步骤。下面这个结，就是注入和它自己那道退火做出来的。',
        Remove: '移除',
        StepContact: '接触退火，450 °C，30 min',
        StepInterlayer: '层间氧化，700 °C，60 min',
        StepReflow: '回流，900 °C，20 min',
        StepSecondGate: '第二道栅氧，1050 °C，30 min',
        StepOxidation: '氧化',
        StepAnneal: '退火',
        TileOxide: '氧化层厚度',
        TileRegime: '生长区制',
        RegimeLinear: '线性',
        RegimeMixed: '过渡',
        RegimeParabolic: '抛物线',
        TileJunction: '结深',
        NoteBefore: '下游步骤之前为 {{nm}} nm',
        NoteNoSteps: '没有下游步骤',
        TileSheetR: '方块电阻',
        TileActive: '激活比例',
        NoteLattice: '进入晶格位置的掺杂原子',
        TilePeak: '峰值浓度',
        TileBudget: '热预算',
        NoteEquivalent: '折合 1000 °C 下的秒数',
        TileDt: 'D·t 总和',
        TileDownstream: '下游步骤数',
        ChartProfileTitle: '掺杂浓度随深度的分布（对数坐标）',
        ChartDepth: '深度（nm）',
        SeriesBefore: '下游步骤之前',
        SeriesAsProcessed: '实际处理后',
        AnnSubstrate: '衬底掺杂',
        AnnJunction: '结',
        ChartOxideTitle: '{{temp}} °C（{{ambient}}）下氧化层厚度随时间的变化',
        ChartTime: '时间（min）',
        ChartThickness: '厚度（nm）',
        SeriesOxide: '氧化层',
        AnnYourTime: '你设的时间',
        ChartBudgetTitle: '各步骤时长——但真正花掉热预算的是温度',
        ChartStepIndex: '步骤序号',
        ChartSeconds: '秒',
        SeriesDuration: '时长',
        AssistantBlurb: '提出问题，我会设计一个实验，用这台求解器把它跑出来，并报告真正得到的结果——然后再还你一个更锋利的问题。',
        AssistantLead: '整条流程里每一道热工艺，花的都是同一个账户里的钱。问我某一步花了多少，或者为什么没人动注入，结却自己变深了。',
      },
      gowning: {
        AirflowWafer: '晶圆处',
        AirflowWall: '墙面监测器',
        AirflowRatio: '产品/墙面',
        SectionAirflow: '看见这两个"房间"（气流与扩散）',
        AirflowNote:
          ' ' +
          '这是洁净区的一个剖面：天花板上的过滤器以设计风速把空气向下推，一位穿着无尘服的操作员站在气流中，旁边机台上放着敞开的产品，架空地板把空气带走。颜色表示操作员正在散发的颗粒。看它往哪里去。这里没有热羽流，也不该有——温热的人体大约只能把空气抬升到每秒零点二五米，而过滤器的风速接近它的两倍。快到足以压制下面工作的人，正是 ' +
          '0.45 m/s 这个数字的用途。操作员真正起的作用是挡路：气流必须绕过身体，而这个障碍物的尾流才是把散发颗粒带到产品上的东西。 ' +
          '如果你看到这团云爬到头顶上方，那是尾流在翻转，而不是热气上升：下降气流中人体正上方的流动会随着涡的脱落不时反向——此处实测某一刻向上 0.28 m/s，下一刻向下 0.19 ' +
          'm/s。这个区域里没有任何地方比别处更热，也就没有什么能把它抬起来。现在把两个探头一起读。墙面监测器几乎什么都读不到，而操作员旁边的产品正在被污染——这就是本模块反对它的全部理由。而且把下降风速调大，产品的读数会更高而不是更低，因为正是气流把散发的颗粒往下送，再由机台把它横向偏折到敞开的晶圆上。解决办法是换个地方站，而不是加大风量。',
        SecondaryLabel: '扰动后恢复',
        ToolLabel: 'GOWN-00 更衣气闸与规程区',
        fault: {
          starvedMakeup: {
            Evidence: [
              '区域压力为负，低于 +5 Pa 的下限。',
              '工作区计数超出等级。',
              '过滤器、洁净服、互锁和恢复时间全部正常——空气在换，只是换进来的是走廊的空气。',
              '全层每个区域读数相同，厂务的公用设施面板显示新风为设计值的 55%，房间为负压。',
            ],
            Truth: '地下层的新风机组初效过滤器堵塞，只送出约 55% 的设计新风量。本区域仍以全速换气——空调机组、顶部过滤器和洁净服都正常——但排风机是以新风量来平衡的，平衡一失，整个洁净室都低于走廊压力。所有区域一起读到负压，走廊空气从每条缝隙倒灌进来。追查本区域自身的排风什么也找不到，因为本区域什么都没变；压差梯度属于建筑，也只有建筑能把它还回来。',
            Complaint: '所有区域的计数同时升高，而且每个区域相对走廊都读到负压。没有新设备投运，也没人动过排风。',
            Label: '整个洁净室转为负压——新风不足',
          },
          starvedAir: {
            Evidence: [
              '恢复时间远超 0.85 分钟限值。',
              '过滤器组压降远在限值以内——风不是被堵住，而是根本没送到。',
              '厂务公用系统面板显示送风量只有设计值的 55%。',
            ],
            Truth:
              '四台循环空调机组里有两台皮带打滑，这间洁净间只拿到约 55% 的设计风量。这间房里' +
              '没有任何东西坏了——过滤器在寿命中段，压差梯度正常，无尘服是新的。暴露它的数字是' +
              '恢复时间：洁净间消化一次扰动要多久只由换气次数决定，别的什么都动不了它，所以过' +
              '滤器干净而恢复缓慢，问题在大楼，不在房间。换天花板过滤器要花十二小时，几乎什么' +
              '都换不回来。',
            Complaint:
              '同样的人数下洁净间读数变脏，开门事件后恢复明显变慢。天花板过滤器上个月刚换过。',
            Label: '洁净间被厂务空调断了风',
          },
          defeatedInterlock: {
            Evidence: [
              '互锁读作『被绕过』。',
              '工作区计数超出等级。',
              '其余读数全部正常，而且计数是阵发的，不是持续的。',
            ],
            Truth: '有人用胶带封了气闸的互锁，好让两扇门能同时开——因为互锁拖慢了一次装机。他们每这么干一次，这个区就和走廊短暂连通一次。阵发本身就是特征：持续的故障不会来了又走，而一扇被人撑开的门，多少通风都治不了。',
            Complaint: '计数一天里一阵一阵地蹿，而不是一直高。两阵之间，能测的一切都在规格内。',
            Label: '气闸互锁被绕过',
          },
          wornGarments: {
            Evidence: [
              '洁净服洗涤次数超过 30 次的限值。',
              '工作区计数超出等级。',
              '滤网压降、区压、恢复时间都正常。',
            ],
            Truth: '这个区领用的洁净服批次已经洗到第 51 次了。织物越洗越松，松开了的洁净服就不再是过滤器——它放过去的是新衣服的好几倍。这个区机械上的一切都没有问题。',
            Complaint: '只有一个区的计数在涨，而通风检查完全正常。',
            Label: '洁净服批次洗涤超次',
          },
          negativePressure: {
            Evidence: [
              '区压 −14 Pa，下限 +5 Pa。',
              '工作区计数超出等级。',
              '滤网、洁净服、互锁全都正常——这不是一个区的问题，是一栋楼的问题。',
            ],
            Truth: '新排风把这个区抽到了 −14 ' +
              'Pa，于是它从每一条缝隙、每一个电缆穿孔往里吸走廊的空气，而不是往外推洁净空气。这个区在自己的边界上不再是最干净的房间，而这正是洁净室赖以成立的全部原理。',
            Complaint: '隔壁一台新设备的排风投用之后，全区的颗粒计数都上去了。',
            Label: '洁净区变负压',
          },
          loadedFilters: {
            Evidence: [
              '滤网压降超过 220 Pa 的限值。',
              '恢复时间远超 0.85 分钟的限值。',
              '正压、洁净服、互锁都没问题。',
            ],
            Truth: '滤网组积满了尘，风机推不动原来那么多空气，这个区的换气次数远低于设计值。稳态几乎不动——发尘除以通风在发尘低时并不敏感——而恢复时间动得厉害，因为恢复时间只由通风决定。恢复慢的区，就是每次扰动之后脏得更久的区。',
            Complaint: '开过一台设备之后，这个区要老半天才能落定。墙上监测点还在等级内，谁也指不出哪里不对。',
            Label: '滤网组积尘',
          },
          gownHealthy: {
            Evidence: [
              '台上的每个读数都正常。',
              '工作区计数在等级限值之内。',
            ],
            Truth: '什么问题都没有。这个区在产品处和在墙上一样守得住等级。',
            Complaint: '例行巡查。墙上监测点读数在等级之内。',
            Label: '一切正常',
          },
        },
        garment: {
          mask: '口罩',
          gloves: '手套',
          boots: '洁净靴',
          hood: '头罩',
          coverall: '连体服',
        },
        SectionGowning: '更衣',
        NoteOverMinOpenCln09: '敞开超过 {{a}} 分钟——这些得靠 CLN-09 去除',
        NoteEffectiveAirChangesH: '有效换气 {{a}} 次/小时',
        NotePeopleAtActivity: '{{a}} 人，活动强度 {{b}}%',
        NoteOf5Garments: '5 件洁净服穿了 {{a}} 件{{b}}',
        NoteIsoAtMLimit: 'ISO {{a}}（{{b}} µm 粒径）——限值 {{c}}',
        SectionAChainDoesNotAverage: '链条不取平均',
        NoteTheGarmentsMultiplyLeavingThe: '洁净服的作用是相乘的。不戴头罩丢掉的不是五分之一的防护，而是头罩贡献的整个倍数——墙上的房间监测点几乎不会察觉，因为晶圆看到的世界由离它最近的人主导，不由全区平均值主导。穿衣出了错，靠通风是补不回来的。',
        SectionStoredScenarios: '已存场景',
        SectionBayState: '洁净区状态',
        SectionBayAndProtocol: '洁净区与规程',
        TextExposeTheLotToThis: '让这批晶圆暴露在这个环境里',
        LabelCorrectOrder: '顺序正确',
        LabelOutOfOrder: '顺序错误',
        LabelAtTheWafer: '晶圆处',
        LabelRoomMonitor: '房间监测点',
        NoteWhatTheWallReadsAnd: '墙上读到的数——它不是产品看到的数',
        LabelGarmentPenetration: '洁净服穿透率',
        LabelShedIntoTheBay: '散入洁净区',
        LabelRecoveryAfterUpset: '扰动后恢复',
        LabelOntoTheWafer: '落到晶圆上',
        AssistantLead: 'GOWN-00 探究助手',
        AssistantBlurb: '可以问：监测读数在洁净等级内的区域，为什么仍然在污染晶圆；一件无尘服到底值多少颗粒。每个回答都是在这个区域当前状态下真实跑出来的。',
        reading: {
          houseutilities: {
            Label: '致电厂务查询公用系统面板（0.5 h）',
          },
          recovery: {
            Label: '烟雾试验与恢复时间，区内停产（2.0 小时）',
          },
          workZone: {
            Label: '工作区便携粒子计数，一小时（1.0 小时）',
          },
          interlock: {
            ValueBad: '已被旁路',
            ValueOk: '工作正常',
            Limit: '工作正常',
            Label: '气闸互锁',
          },
          garmentCycles: {
            Label: '洁净服批次洗涤次数',
          },
          pressure: {
            Label: '洁净区对走廊压差',
          },
          filterDp: {
            Label: '滤网组压降',
          },
        },
        aspect: {
          house: {
            Action: '停止在洁净间里换东西，上报厂务',
            Label: '问题不在这间房——上报厂务',
          },
          interlock: {
            Action: '修好互锁，并查明是谁绕过了它、为什么',
            Label: '气闸互锁',
          },
          garments: {
            Action: '撤下磨损的洁净服批次，换发新洗涤周期的',
            Label: '洁净服库存',
          },
          pressurisation: {
            Action: '找出是什么在抽走区内空气，恢复压差梯度',
            Label: '洁净区正压',
          },
          filters: {
            Action: '更换积尘滤网组并重新平衡顶棚送风',
            Label: '顶棚滤网',
          },
        },
      },
      gas: {
        ValueNotNeeded: '无需预警',
        NoteTheAlarmArrivesAfter: '报警到来时机台区已经处于危险之中',
        NoteThePurgeAsSpecifiedLeaves: '按规程吹扫仍残留 {{a}} ppm——在这个比率下需要 {{b}} 个循环，而不是 {{c}} 个。不要打开面板。',
        NoteThePurgeAsSpecifiedReaches: '按规程吹扫可达到 {{a}} ppm，低于限值。面板可以打开。',
        VerdictImmediatelyDangerous: '立即危及生命或健康',
        VerdictAboveTheExposureLimit: '高于暴露限值',
        VerdictBelowTheExposureLimit: '低于暴露限值',
        NoteAlarmsAtPpmToRespond: '在 {{a}} ppm 报警，响应需 {{b}} 秒',
        ValueNoWarning: '无',
        NoteStaysBelowTheDangerous: '始终低于危险水平',
        NoteImmediatelyDangerousAt: '{{a}} 秒后立即危及生命',
        NoteEveryNumberOnThis: '这块面板上的每个数字，都能在接上气瓶之前用纸笔算出来：一根管线需要多少个吹扫循环，一处泄漏在稳态达到多少，一台探测器实际买到多少时间。吹扫是几何级数，所以循环质量胜过循环次数——十次十比一和十次二比一花同样的时间，结果差七个数量级。',
        ValueNever: '永远不会',
        gasNote: {
          ammonia: '这张单子里唯一在伤到你之前很久就能闻到的——这会养成坏习惯。',
          chlorine: '比空气重，会在地面积聚，天花板上的探测器看不见它。',
          hydrogenFluoride: '氟离子追着钙一直进到骨头里。全身性效应几小时后才到。',
          phosphine: '既自燃又剧毒，所以一次泄漏既是火灾也是中毒。',
          arsine: '五个 ppb。破坏红细胞；症状出现之前伤害已经造成。',
          silane: '在空气中自燃，有时有延迟——这让泄漏有机会先积累。',
        },
        gasLabel: {
          ammonia: '氨气 (NH₃)',
          chlorine: '氯气 (Cl₂)',
          hydrogenFluoride: '氟化氢 (HF)',
          phosphine: '磷烷 (PH₃)',
          arsine: '砷烷 (AsH₃)',
          silane: '硅烷 (SiH₄)',
        },
        NotePyrophoricIgnites: '自燃性：在空气中无需点火源即可起火。',
        SectionSpecies: '气体种类',
        SecondaryLabel: '到达烟囱的量',
        ToolLabel: 'GAS-19 气体库与化学品分配',
        NextFac06SolvesTheSame: 'FAC-06 解的是同一套流导物理，只是用于真空而不是输送。方程完全相同，算错的后果却不同——问问它：一根受限的前级管道要让工艺腔付出什么。',
        TitleLineConductanceAgainstDiameter: '管线流导随直径的变化',
        LabelPyrophoric: '自燃性',
        LabelConductance: '流导',
        HypMolecularFlowConductanceGoesAs: '分子流流导与直径的三次方成正比、与长度成反比，所以管线几何对输送的支配远超直觉——直径的小改动就是流导的大改动。而到达烟囱的东西应只取决于洗涤器，与管线完全无关。',
        AskedWhatDoesTheDeliveryLine: '输送管线起什么作用？到达排放烟囱的是什么？',
        SumLineConductanceScrubberLoadingAnd: '管线流导、洗涤器负载，以及离开建筑物的是什么。',
        NextTheBayVentilationIsWhat: '机台区的通风决定稳态浓度。如果一台风机只跑半速而没人发现，这些数字里哪个先变——面板上有什么会在泄漏之前把它显出来吗？',
        LabelNoWarning: '无预警',
        AxisYSecondsFromAlarmToIdlh: '报警到 IDLH 的秒数',
        AxisXLeakRateSccm: '泄漏速率 (sccm)',
        TitleWarningTimeAgainstLeakRate: '预警时间随泄漏速率的变化',
        LabelSeverity: '严重度',
        LabelSteadyState: '稳态浓度',
        LabelAlarmsAt: '报警浓度',
        NoteAlarmToIdlh: '报警到 IDLH',
        HypADetectorAlarmsAtA: '探测器在某个浓度报警，而在它响应期间气体还在继续充满机台区。有用的量不是报警浓度，而是从报警到空气变得立即危险之间的秒数。泄漏够快时，这个间隔缩到零，探测器就从警报变成了事后记录。',
        AskedDoesTheDetectorGiveEnough: '探测器给出的预警够多吗？值得装它吗？',
        SumAlarmThresholdResponseTimeAnd: '报警阈值、响应时间，以及从报警到 IDLH 之间的秒数。',
        NextThePurgeNitrogenCarriesIts: '吹扫用的氮气自带水汽，湿吹扫会在管线里生成固体，而不是清洁管线。这会对上面的曲线做什么——规程里写的循环数还会是对的吗？',
        TitleResidualConcentrationAgainstPurgeCycles: '残余浓度随吹扫循环数的变化',
        LabelPurgeTime: '吹扫时间',
        LabelSetTo: '当前设置',
        LabelCyclesNeeded: '所需循环数',
        LabelResidual: '残余浓度',
        HypEachPurgeCycleReplacesA: '每个吹扫循环置换的是管线内容物的固定比例，不是固定数量。这使残余浓度随循环数呈指数下降——从 100% 到 1% 所需的循环数与从 1% 到 ' +
          '0.01% 差不多，而"吹几次就够了"的直觉会错上几个数量级。',
        AskedHowManyPurgeCyclesDoes: '这根管线实际需要多少个吹扫循环？',
        SumCyclePurgingIsExponentialAnd: '循环吹扫是指数式的——最后一个数量级的浓度才是最慢的。',
        fault: {
          weakExtract: {
            Evidence: [
              '排风流量 35%，下限 90%。',
              '模拟释放的稳态远高于设计值。',
              '吹扫、阀门、洗涤器全都正常。',
            ],
            Truth: '排风机只剩设计流量的 ' +
              '35%。稳态浓度等于释放速率除以通风，流量剩三分之一，任何一处泄漏的浓度就是三倍——它还拉长了清除泄漏的时间，而那是没有人去算的另一半。',
            Complaint: '天热的日子，气瓶间里有淡淡的溶剂味。',
            Label: '气瓶间排风低于设计',
          },
          spentScrubber: {
            Evidence: [
              '介质负载 95%，限值 60%。',
              '排放口采样超限。',
              '间内释放模型完全没有变化。',
            ],
            Truth: '洗涤介质已经吃到 ' +
              '95%。尾气处理不会『关死』——它是退化的：去除效率下降，没被摧毁的就从排放口上去了。气瓶间里的每个读数都没变，因为间里确实什么都没变：这个故障完全发生在它所保护的人的下游，只有排放口采样看得见它。',
            Complaint: '环保部门质疑了一个排放口读数。气瓶间内一切正常，而且一直正常。',
            Label: '尾气处理介质耗尽',
          },
          seizedEfv: {
            Evidence: [
              '阀门功能测试读作『未能关闭』。',
              '模拟释放直接冲过 IDLH。',
              '水分、介质、排风全都正常——气体和气瓶间本身都没有毛病。',
            ],
            Truth: '超流阀上次功能测试没过，却被签成了通过。它是那道把断管变成可控泄漏、而不是整瓶释放的机械关卡——所以今天什么都没坏，而这个气瓶间可能发生的事故规模已经放大了八倍。这是只会在要命的那一天才被发现的故障。',
            Complaint: '换瓶前的例行检查。什么都没坏，什么都没发生。',
            Label: '超流阀卡死在开位',
          },
          wetHeader: {
            Evidence: [
              '总管水分 24 ppm，限值 1 ppm。',
              '吹扫后的管路分析远超暴露限值。',
              '超流阀、洗涤器、排风都没问题——问题在吹扫，也只在吹扫。',
            ],
            Truth: '氮气总管的水分到了 24 ' +
              'ppm。水吸附在管壁上又缓慢脱附，于是每次抽空再也到不了干燥时能到的真空，单循环的有效稀释比垮了——而吹扫十次连乘的正是这个比。十个按设定完成的循环，在管路里留下的砷化氢比规程假设的多四个数量级。面板数的是循环，数循环和数分子不是一回事。',
            Complaint: '上次换砷化氢钢瓶时，一名技师报告闻到了点什么。面板记录了十个完成的吹扫循环——那正是认证规程。',
            Label: '吹扫氮气带水',
          },
          gasHealthy: {
            Evidence: [
              '台上的每个读数都正常。',
              '吹扫验证结果低于暴露限值。',
            ],
            Truth: '什么问题都没有。正确的判定就是继续换瓶。',
            Complaint: '换瓶前的例行检查。面板上一片绿。',
            Label: '一切正常',
          },
        },
        TextPpmAgainstAPpmLimit: '{{a}} ppm，限值 {{b}} ppm',
        NoteSccmIntoMAtAch: '{{a}} sccm 漏入 {{b}} m³，换气 {{c}} ACH',
        NoteCyclesAreNeeded: '做了 {{a}} 个循环——需要 {{b}} 个',
        NoteImmediatelyDangerousAtPpm: '{{a}} ppm 即危及生命',
        SectionArithmeticYouDoBeforeYou: '开阀之前先做的算术',
        SectionStoredScenarios: '已存场景',
        SectionBunkerState: '气瓶间状态',
        SectionAssessment: '评估',
        SectionPanelAndBay: '面板与气瓶间',
        LabelExposureLimit: '暴露限值',
        LabelResidualAfterPurge: '吹扫后残留',
        LabelLeakSteadyState: '泄漏稳态浓度',
        LabelToExposureLimit: '到暴露限值的时间',
        LabelWarningTheDetectorBuys: '探测器换来的预警时间',
        LabelReachingTheStack: '到达排放口',
        NoteAfterAbatementAndAbatementDegrades: '经尾气处理后——而尾气处理是悄悄退化的',
        TextSignOffThisBayAs: '签核：此气瓶间可安全开启',
        AssistantLead: 'GAS-19 探究助手',
        AssistantBlurb: '可以问：到底需要几次吹扫循环、检测器能不能及时报警、最后排到烟囱的是什么。每个回答都是在这个气柜面板上真实跑出来的一次评估。',
        reading: {
          stackSample: {
            Label: '排放口采样分析（5.0 小时）',
          },
          releaseModel: {
            Limit: '< 3 ppm，预警 > 0 秒',
            Label: '示踪气体释放试验，人员撤离（4.0 小时）',
          },
          purgeVerify: {
            Label: '吹扫后用便携分析仪测管路（3.0 小时）',
          },
          extractFlow: {
            Label: '气瓶间排风流量',
          },
          mediaHours: {
            Label: '洗涤介质负载',
          },
          efvTest: {
            ValueBad: '未能关闭',
            ValueOk: '通过',
            Limit: '通过',
            Label: '超流阀上次功能测试',
          },
          moisture: {
            Label: '吹扫氮气水分',
          },
        },
        aspect: {
          ventilation: {
            Action: '把排风恢复到设计流量',
            Label: '气瓶间排风',
          },
          scrubber: {
            Action: '更换洗涤器介质并在排放口重新认证去除效率',
            Label: '尾气处理介质',
          },
          excessFlow: {
            Action: '更换超流阀，装回钢瓶前先做功能测试',
            Label: '超流阀',
          },
          purgeGas: {
            Action: '更换总管干燥器，吹扫配气管网并按水分重新认证',
            Label: '吹扫氮气品质',
          },
        },
      },
      crystal: {
        LatticeStatus: '{{a}} 个原子 · 实测 {{b}} K · {{c}} ps',
        LatticeTemp: '晶体温度',
        TraceTitle: '离晶格位点的距离 —— 热振动',
        TraceX: '模拟时间（ps）',
        TraceY: '位移（Å）',
        TraceHost: '一个硅原子',
        TraceSiRef: 'Si 在 300 K 的均方根位移（X 射线 Debye–Waller）',
        TraceStatus: '最近 {{a}} ps 内离位点的均方根位移：{{b}} Å · 键长 2.35 Å',
        TraceIdle: '按下运行：离位点的均方根距离在运行的最近 {{a}} ps 内测得',
        LatticeReset: '重置',
        LatticePause: '暂停',
        LatticeRun: '运行',
        LatticeFailed: '分子动力学引擎加载失败。',
        LatticeLoading: '正在加载分子动力学引擎……',
        NoteLattice: '晶棒的原子级样貌：金刚石立方结构的硅晶格——每个原子与四个近邻共价键合——在 Stillinger–Weber ' +
          '力场下实时振动。没有任何约束把原子固定在原位；晶体保持形状，是因为这种排列正是该势函数的基态。拖动可旋转视角，滚轮可缩放。把温度升向熔点（1687 ' +
          'K），观察热振动如何模糊拉晶炉辛苦赢得的秩序。',
        NoteBands: '这样的原子排列对电子做了什么——来自 AIMS 的紧束缚模型，不是画出来的记忆曲线。阴影带就是禁带：这块晶体里任何电子都不可能具有的能量。硅的导带最低点并不在价带最高点的正上方，所以虚线是斜的——越过禁带不仅要改变能量，还要改变动量，而单个光子给不了动量。这就是为什么硅能做出极好的探测器，却做不出像样的激光器。',
        BandsAxisEnergy: '相对价带顶的能量（eV）',
        BandsLoading: '正在求解紧束缚模型……',
        BandsFailed: '该晶体没有可用的能带模型。',
        BandsGap: '带隙 {{ev}} eV',
        BandsDirect: '直接带隙，位于 {{at}}',
        BandsIndirect: '间接带隙，{{from}} → {{to}}',
        BandsMeasured: '实验值 {{ev}} eV',
        SectionLattice: '最终产品——硅晶体晶格（分子动力学）',
        NoteSpecificationBand: '规格带 {{a}} – {{b}} Ω·cm。{{d}} 片切片中有 {{c}} 片在带内。',
        LabelGrowthTime: '生长时间',
        LabelPullRate: '拉速',
        SecondaryLabel: '拉速对上限',
        ToolLabel: 'CZ-07 直拉单晶炉',
        NextTheFurnaceAtThrm04: 'THRM-04 的炉子会故意把这些氧沉淀到体内。问问它：一片带着太少氧到货的晶圆会怎么样。',
        AxisYOxygenCm: '氧 (nm⁻³)',
        AxisXCrucibleRotationRpm: '坩埚转速 (rpm)',
        TitleOxygenAgainstCrucibleRotation: '氧含量随坩埚转速的变化',
        NoteAcrossTheSweep: '整个扫描范围内',
        LabelRotationRange: '转速范围',
        LabelWith03TMagnet: '加 0.3 T 磁场',
        LabelOxygenAsSet: '当前设置的氧含量',
        HypTheMeltSitsInA: '熔体盛在石英坩埚里，所以坩埚一直在往熔体里溶解。如果这就是来源，那么任何把熔体搅得更猛的因素都会把更多的氧带到界面——任何抑制对流的因素都会带得更少。',
        AskedWhatPutsOxygenInThe: '是什么把氧带进晶体？什么能改变它？',
        SumCrucibleDissolutionMeltConvectionAnd: '坩埚溶解、熔体对流，以及为什么氧是被规格化而不是被去除的。',
        NextVGHasTwoHalves: 'v/G 有两半，你只动了一半。如果换成让热区改变梯度，窗口会怎样——这两个量里哪个更容易稳稳保持三十个小时？',
        LabelPerfectSiliconBoundary: '完美硅边界',
        AxisYVGCmMinK: 'v/G  cm²/(min·K)',
        AxisXPullRateMmMin: '拉速 (mm/min)',
        TitleVoronkovRatioAgainstPullRate: '沃龙科夫比随拉速的变化',
        NoteLatentHeat: '潜热',
        LabelRateCeiling: '拉速上限',
        LabelVGAsSet: '当前设置的 v/G',
        HypIfPullRateMatteredOnly: '如果拉速只关乎产量，这里的每个拉速都会或快或慢地给出同一根晶体。如果沃龙科夫是对的，v/G ' +
          '会在这个范围内的某处跨过一条边界，主导点缺陷就换了种类——那是晶体本质的改变，不是制造快慢的改变。',
        AskedWhatDoesPullRateActually: '拉速除了决定生长要多久，实际上还决定什么？',
        NextEveryWaferInABox: '一盒里的每片晶圆都来自这条曲线上的不同位置。如果下游某个工艺对衬底电阻率敏感，那"批次"还算什么？',
        LabelSpecLow: '规格下限',
        AxisXDistanceFromSeedMm: '距籽晶距离 (mm)',
        NoteSpread: '分散',
        DesignOneGrowthAsSetThen: '先按当前设置长一根晶体，再用三份不同的电阻率规格去评判同一根晶体。三者之间晶体本身毫无变化：变的只是客户愿意接受其中多少。',
        HypBoronIsMoreSolubleIn: '硼在液态硅中的溶解度比在固态中高，所以每凝固一毫米，熔体都比之前更富一点。如果确实如此，尾端的掺杂必然比籽晶端重——电阻率更低——而且差距应朝末端越拉越大，而不是随机漂移。',
        AskedWhyDoesResistivityChangeDown: '为什么电阻率沿晶锭变化？这要付出什么代价？',
        SumSegregationDownTheIngotAnd: '沿晶锭方向的分凝，以及一份规格允许你卖掉其中多少。',
        fault: {
          shakingLift: {
            Evidence: [
              '籽晶提升振动 55%，限值 10%。',
              '热区探测没问题——上限就在它该在的地方。',
              '加热器校准正常。',
            ],
            Truth: '提升柱里的一个轴承坏了，晶体在被提出时一直被摇晃。结构是在远低于热学上限的地方丢掉的，这就是热区探测干干净净、炉次照样失败的原因。',
            Complaint: '这个月两次失去结构，都在等径段，而热区是新的。',
            Label: '籽晶提升振动',
          },
          heaterDrift: {
            Evidence: [
              '加热器校准值 78%，窗口是 ±2%。',
              '热区探测得出的上限低于配方的拉速。',
              '籽晶振动正常，排除了机械那条失去结构的路。',
            ],
            Truth: '加热器只输出校准功率的 ' +
              '78%，界面处的轴向梯度远比配方假设的平缓。梯度是带走潜热的东西，于是最大拉速掉到了一个没变过的设定值之下——配方现在要的，比热区给得起的多。',
            Complaint: '最近四炉里有两炉在放肩附近失去单晶结构。配方一年没动过了。',
            Label: '加热器失准',
          },
          restrictedArgon: {
            Evidence: [
              '氩气流量 45%，下限 90%。',
              'FTIR 氧含量偏高。',
              '坩埚才第一炉——这不是耗材问题。',
            ],
            Truth: '氩气管路堵到只剩 45% 的流量。氩气的作用是在一氧化硅溶回去之前把它从熔体表面扫走；没有氩气，氧就回来了。它给出的 FTIR ' +
              '数字和用旧坩埚一模一样，能分开两者的是坩埚的读数。',
            Complaint: '还是氧偏高，而坩埚上周才换的。',
            Label: '氩气吹扫受阻',
          },
          spentCrucible: {
            Evidence: [
              '本坩埚的炉次已超限。',
              'FTIR 氧含量超出 12 ×10¹⁷ 的带宽。',
              '氩气流量正常，排除了氧上升的另一条路。',
            ],
            Truth: '坩埚已经是第五炉了。整个提拉过程中石英都在往熔体里溶解，用旧的坩埚溶出的量几乎是新坩埚的两倍——于是氧含量上去了，而设备上每个设定值都纹丝没动。什么都没改，而这恰恰是没人往这里看的原因。',
            Complaint: '最近两根晶棒出的晶圆在炉里析出氧了。器件那边在问长晶炉是不是变了。没有变。',
            Label: '坩埚炉次超限',
          },
          czHealthy: {
            Evidence: [
              '台上的每个读数都正常。',
              '上一根晶棒的氧含量在带内。',
            ],
            Truth: '什么问题都没有。正确的判定就是开炉。',
            Complaint: '三天长晶前的开炉检查。一切读数正常。',
            Label: '一切正常',
          },
        },
        TextCm: '{{a}} Ω·cm',
        NoteLatentHeatCeilingMmMin: '潜热上限 {{a}} mm/min',
        NoteVG: 'v/G = {{a}}',
        NoteCmSeedToTail: '{{a}} → {{b}} Ω·cm，从籽晶端到尾端',
        NoteOfMmGrown: '共长 {{a}} mm——{{b}}%',
        SectionWhyAnIngotIsNot: '为什么一根晶棒不是同一种材料',
        SegregationLead: '晶体只取走熔体中掺杂剂的一个比例 k，所以沿长度方向掺杂越来越重。',
        SegregationHelp: '为什么这只会让晶锭更短，而不是更好',
        NoteDopantDoesNotPartitionEqually: '掺杂剂在固液两相中的分配并不均等。晶体只取熔体中含量的 k ' +
          '分之一，其余推回熔体，于是晶棒越长熔体越富集，晶体沿长度方向掺杂越来越高。电阻率是一条规格，达标的晶棒长度就是这台设备的良率——这就是为什么把窗口收窄给不了客户更好的晶棒，只给他们更短的一根。',
        SectionPullerState: '长晶炉状态',
        SectionHotZone: '热区 — 实时传热与流动',
        NoteHotZone:
          '实时运行的传热模拟，展示长晶炉的剖面：坩埚保持在加热器温度，熔融硅在浮力驱动下对流，' +
          '晶棒把热量向上导到冷却的籽晶头。箭头是熔体的流动 — 把热量送到生长界面的环流。',
        NoteHotZoneMagnet:
          '磁场旋钮在这里同样起作用。熔融硅是导体，横加磁场后，任何运动的熔体微团都会被制动 — ' +
          '把磁场调大，就能看到箭头逐渐平息。这就是磁控直拉的全部道理：正是这股环流把氧从石英坩埚' +
          '带到生长界面，所以抑制环流就是长出低氧硅的办法。被制动的熔体温度也不再摆动，' +
          '而这正是晶体中不出现生长条纹的原因。',
        CzMagnet: '磁场',
        CzLoading: '正在加载传热引擎……',
        CzFailed: '传热引擎加载失败。',
        CzPause: '暂停',
        CzRun: '继续',
        CzReset: '重置',
        CzClock: 't = {{s}} 秒',
        CzProbeMelt: '熔体',
        CzProbeInterface: '界面',
        CzProbeBoule: '晶棒',
        SectionResistivityDownTheIngot: '沿晶棒的电阻率',
        TextGrowTheIngotAndSend: '长完这根晶棒，送去 WPR-08',
        BandsAbout: '旁边的能带结构：这种排列对电子意味着什么',
        LabelUsableIngot: '晶棒可用比例',
        LabelResistivitySpread: '电阻率散布',
        LabelDefectRegime: '缺陷区制',
        LabelInterstitialOxygen: '间隙氧',
        NoteStrengthAndGetteringOrPrecipitates: '强度与吸杂，或是析出物',
        NoteOneCrystalStartToFinish: '一根晶体，从头到尾',
        AssistantLead: 'CZ-07 探究助手',
        AssistantBlurb: '可以问：电阻率沿硅锭的偏析、这根晶体能拉多快、氧从哪里来。每个回答都是在这台单晶炉上真实跑出来的一次生长。',
        reading: {
          gradientProbe: {
            Label: '热区热电偶探测，热区打开（5.0 小时）',
          },
          oxygenCheck: {
            Label: '上一根晶棒的 FTIR 间隙氧（3.0 小时）',
          },
          vibration: {
            Label: '籽晶提升振动',
          },
          argonFlow: {
            Label: '氩气流量',
          },
          heaterCal: {
            Label: '加热器功率校准',
          },
          crucibleRuns: {
            Label: '本坩埚已用炉次',
          },
        },
        aspect: {
          seedLift: {
            Action: '更换提升柱轴承并重新平衡',
            Label: '籽晶提升机构',
          },
          argon: {
            Action: '清除氩气管路堵塞并重设流量',
            Label: '氩气吹扫',
          },
          heater: {
            Action: '对参考高温计重新校准加热器并重测热区',
            Label: '加热器校准',
          },
          crucible: {
            Action: '报废坩埚重新装料。三天产量，但无可避免',
            Label: '石英坩埚',
          },
        },
      },
      waferprep: {
        NoteSurfaceCaveats: '三点如实说明。这是洁净表面 —— 真空设备在 HF ' +
          '浸洗或原位烘烤之后看到的那个表面；放在空气中的晶圆几分钟内就会长满自然氧化层。重构也并不是晶圆要花上几皮秒去做的事，它就是洁净表面本来所处的状态；从理想切面出发，只是为了让你看清这中间换掉了什么。还有，真实的二聚体是倾斜的，每一对中一个原子高、一个原子低，因为电荷在两者之间发生了转移 ' +
          '—— 那属于电子结构，而经典力场里没有电子，所以这里的原子对保持对称。板片底部被固定住：那些原子代表盒子之外那 775 µm 厚的晶圆。',
        NoteSurfaceDimers: '理想的切面并不是真实表面的样子。把晶格切开，顶层每个原子都失去四条键中的两条 —— ' +
          '那些红色原子就是，每一条都是无处安放的断键。按下运行看看：相邻原子彼此倾靠、两两配对，各自用一条悬挂键换来它们之间的一条新键。配成的对叫二聚体，它们还排成一行行，这个 ' +
          '2×1 重构就是每一片洁净 Si(100) 晶圆真正的表面结构。这里没有任何脚本安排它 —— 是 Stillinger–Weber ' +
          '势告诉原子配对后的表面能量更低，原子就走了过去。它从来不会完全配完：配对是就近做出的决定，于是总有几个原子被落下 —— ' +
          '它们两侧本该成为搭档的原子都已经先跟别人配了对，真实的 Si(100) 表面也带着同样的缺陷。把晶圆冷到 150 K ' +
          '再重置，差得就更远了：原子已经没有能量去找到自己的搭档。',
        SurfaceReset: '回到理想切面',
        SurfacePause: '暂停',
        SurfaceRun: '运行',
        SurfaceTemp: '晶圆温度',
        SurfaceLoading: '正在加载分子动力学引擎…',
        SurfaceFailed: '分子动力学引擎加载失败。',
        SurfaceChartTitle: '二聚体数随时间变化',
        SurfaceChartX: '模拟时间 (ps)',
        SurfaceChartY: '二聚体数',
        SurfaceChartSeries: '二聚体',
        SurfaceChartAllSites: '全部位点配对',
        SurfaceStatus: '{{a}}/{{b}} 个二聚体 · {{c}} 个原子仍未配对 · 实测 {{d}} K · {{e}} ps',
        NoteSurface: '锯切方向垂直于籽晶，因此晶圆上的缺口其实是在声明表面属于哪个晶面 —— 对 CMOS 逻辑而言那就是 (100)，因为 (100)/SiO₂ ' +
          '界面的电学陷阱密度最低，反型层中的电子迁移率最高。这里显示的就是那个晶面，取自单晶炉拉出的同一块金刚石立方硅。左键拖动旋转，滚轮缩放，右键拖动平移。',
        SectionSurface: '它呈现的表面 —— Si(100) 晶面（分子动力学）',
        NoteFrontOnly: '只加工正面——背面还是锯切态',
        NoteBothFacesBalanced: '两面平衡',
        LabelBow: '弯曲度',
        SecondaryLabel: '划痕',
        ToolLabel: 'WPR-08 切片、研磨与抛光',
        NextCmp11FlattensEachLayer: 'CMP-11 每建好一层就压平一层，并把自己的聚焦预算交给同一台光刻机。到第十层金属时，两者哪个更要紧？',
        AxisYTtvM: 'TTV (µm)',
        AxisXPolishTimeMin: '抛光时间 (min)',
        TitleTtvAndFlatnessYieldAgainst: 'TTV 与平整度良率随抛光时间的变化',
        LabelRoughness: '粗糙度',
        LabelFlatnessYield: '平整度良率',
        HypTtvIsAThicknessVariation: 'TTV 是厚度差，抛光应能减小它。弯曲不是——它是一种形状，而形状来自两面之间的失衡。如果这是对的，延长抛光应能压平 TTV ' +
          '而对弯曲几乎无用；只加工一面则应让弯曲急剧恶化，而 TTV 几乎无感。',
        AskedWhatSetsTheFlatnessOf: '是什么决定成品晶圆的平整度？',
        SumTtvBowAndWarpAnd: 'TTV、弯曲与翘曲，以及晶圆交给光刻的聚焦预算。',
        NextDice15GrindsThisWafer: 'DICE-15 会在器件做完之后把这片晶圆磨到十分之一的厚度，物理相同，余量却一点没有。问问它：损伤在那里要付出什么。',
        AxisYDamageM: '损伤 (µm)',
        AxisXLapRemovalPerSideM: '每面研磨去除量 (µm)',
        TitleResidualDamageAndStrengthAgainst: '残余损伤与强度随研磨去除量的变化',
        NotePerWafer: '每片晶圆',
        LabelScratches: '划痕',
        LabelStrength: '强度',
        LabelResidual: '残余损伤',
        LabelSawDamage: '锯痕损伤',
        HypAnAbrasiveCutLeavesCracks: '磨料切割会在它切出的表面之下留下裂纹，深度大致与磨粒尺寸成正比。如果是这样，一片看起来完美的晶圆仍可能很脆弱——锯后的去除工序不是修饰性的，而是结构性的。',
        AskedHowDeepDoesTheSaw: '锯痕损伤有多深？去掉它要付出什么？',
        SumSubsurfaceDamageAndWhyThree: '亚表面损伤，以及为什么锯后要跟三道工序而不是一道。',
        NextTheGapBetweenCutAnd: '那张图上"切出"与"出货"之间的差距就是平整度和破片。问我是什么决定了它。',
        AxisXWireDiameterM: '钢线直径 (µm)',
        TitleWafersFromOneIngotAgainst: '单根晶锭出片数随钢线直径的变化',
        NoteAfterFlatnessAndBreakage: '扣除平整度与破片后',
        LabelIngotLost: '晶锭损失',
        HypEveryCutRemovesASlot: '每一刀都切掉一条与钢线加线上磨料同宽的槽，那些硅就没了。如果锯缝与晶圆厚度相当，损失就应大得惊人——不是舍入误差，而是那根花了三十个小时才长成的晶体的一大部分。',
        AskedHowMuchOfTheIngot: '晶锭有多少变成了晶圆？由什么决定？',
        SumKerfLossWaferCountAnd: '锯缝损失、晶圆数量，以及以浆料形式流走的硅。',
        fault: {
          oversizeSlurry: {
            Evidence: [
              '抛光液粒径是批次规格的 240%。',
              '划痕计数远超限值，而平整度分布干干净净。',
              '锯和垫的每个读数都正常。',
            ],
            Truth: '这批抛光液团聚了，粒径跑到规格的 2.4 ' +
              '倍。超大颗粒对平整度毫无影响——它们犁出一道道深划痕，而在一片其余完美的晶圆上，划痕就是致命缺陷。这是这台设备上最便宜的维修，而它在另外三个故障比拼的那条轴上完全隐形。',
            Complaint: '检测在成品晶圆上发现长划痕。每一片的平整度都完美。',
            Label: '抛光液批次粒径超差',
          },
          glazedPad: {
            Evidence: [
              '抛光垫小时数超过 45 小时的限值。',
              '监控片上的去除量低于 0.28 µm 的下限。',
              '锯缝和张力都正常。',
            ],
            Truth: '抛光垫用到 76 ' +
              '小时已经釉化。釉化的垫不再输送抛光液，去除速率崩掉——于是抛光既磨不掉厚度，也做不了工艺指望它做的整平。锯完全无辜，两个锯的读数都这么说。',
            Complaint: '平整度勉勉强强，晶圆出来还偏厚。',
            Label: '抛光垫釉化',
          },
          slackWire: {
            Evidence: [
              '张力 19 N，窗口是 26 – 30 N。',
              '平整度分布严重超差，而锯缝宽度完全正常。',
              '锯线公里数在限内——这不是耗材。',
            ],
            Truth: '一只张力臂读数偏低，锯线只有 19 N 而不是 28 ' +
              'N。松的线在切缝里游走，切槽就起伏——和磨钝的线造成的退货平整度一模一样，起因却完全不同。这里的锯缝是正常的，因为线还锋利；它只是没被绷直。',
            Complaint: '还是平整度退货，而锯线周一才换的。',
            Label: '线张力偏低',
          },
          wornWire: {
            Evidence: [
              '锯线公里数超过 450 km 的限值。',
              '锯缝宽度超过 185 µm。',
              '张力在目标上，抛光垫也在寿命内。',
            ],
            Truth: '锯线已经跑了 810 ' +
              '公里。磨钝的线丢了磨料，是在犁而不是在切，既加宽了锯缝，也留下更起伏的切槽——而这份起伏能挺过研磨和抛光，变成厚度波动。锯缝测量就是它和另外两种丢平整度的方式的分界。',
            Complaint: '最近三根晶棒平整度都跑了，客户退了两根。',
            Label: '锯线超程',
          },
          wprHealthy: {
            Evidence: [
              '每个耗材计数都在限值内。',
              '平整度分布远低于 1.5 µm。',
            ],
            Truth: '什么问题都没有。正确的判定就是加工这根晶棒。',
            Complaint: '例行晶棒。出片数和平整度都正常。',
            Label: '一切正常',
          },
        },
        NoteTtvTheScannerHasTo: 'TTV——光刻机就得在这上面曝光',
        NoteFromMTheSawLeft: '从锯留下的 {{a}} µm 算起',
        NoteMKerfKgOfCrystal: '{{a}} µm 锯缝——{{b}} kg 晶体',
        NotePassFlatnessAndHandling: '{{a}} 片通过平整度与操作损检验',
        SectionTheOneThatIsGenuinely: '真正违反直觉的那一条',
        BowLead: '只要损伤在两面都有，损伤更多的晶圆反而可能比更干净的晶圆更平。',
        BowHelp: '为什么翘曲不是损伤的度量',
        NoteADamagedLayerIsA: '有损伤的层就是有应力的层，而两面平衡的应力什么也弯不了。只加工正面，背面就留着锯下的全部损伤，晶圆于是弓弯——这意味着损伤更多的晶圆反而可能比更干净的更平，只要损伤是对称的。弓弯不是损伤的度量。这就是研磨和抛光都做双面的原因。',
        FacesBoth: '双面加工',
        FacesFront: '仅正面加工',
        HistoryBareWafer: '裸抛光晶圆，无膜层',
        HistoryStockBlank: '库存坯片——锯台上没有来自 CZ-07 的晶锭。',
        SectionDoubleSidedProcessing: '双面加工',
        SectionLineState: '产线状态',
        TextSliceTheIngotAndStart: '切开晶棒，让一片晶圆上线',
        LabelWafersFromTheIngot: '晶棒出片数',
        LabelLostAsSlurry: '损失为磨浆',
        LabelResidualDamage: '残余损伤',
        LabelBreakingStrength: '断裂强度',
        NoteSetByTheWorstFlaw: '由留在表面的最坏一处缺陷决定',
        LabelFlatness: '平整度',
        AssistantLead: 'WPR-08 探究助手',
        AssistantBlurb: '可以问：锯切损失了多少硅、损伤有多深、晶圆为什么会翘曲。每个回答都是在这条线上真实跑出来的。',
        reading: {
          scratchCount: {
            Label: '暗场扫查划伤，25 片（2.0 小时）',
          },
          removalRate: {
            Label: '监控片抛光去除量（1.0 小时）',
          },
          kerfCheck: {
            Label: '废段上的锯缝宽度（2.0 小时）',
          },
          ttvMap: {
            Label: '电容式平整度测绘，10 片（1.5 小时）',
          },
          slurrySize: {
            Label: '抛光液粒径，对批次规格',
          },
          padHours: {
            Label: '抛光垫已用小时',
          },
          tension: {
            Label: '线张力',
          },
          wireKm: {
            Label: '锯线已用公里数',
          },
        },
        aspect: {
          slurry: {
            Action: '更换抛光液批次并检查粒径分布',
            Label: '抛光液',
          },
          pad: {
            Action: '修整抛光垫；若釉化到修不动就更换',
            Label: '抛光垫',
          },
          tension: {
            Action: '重设张力臂并将载荷传感器归零',
            Label: '线张力',
          },
          wire: {
            Action: '换线轴，并在废晶棒段上重新认证锯缝',
            Label: '锯线',
          },
        },
      },
      inspection: {
        LeadTopOfDistribution: '这台设备报告的只是尺寸分布的顶端。它没有坏，这是光学规律。',
        NoteThisScanWouldReport: '这次扫描会报出 {{a}}/cm²。这与晶圆实际携带的接不接近，只有探针台能回答。',
        SecondaryLabel: '误报率',
        ToolLabel: 'INSP-13 图形晶圆检测',
        NextAYieldMapAtPrb: 'PRB-14 的良率图按定义覆盖每一颗裸片。这让晶圆测试成了什么——一个检测工步，还是产线上唯一的全覆盖检测？',
        AxisXWaferCoverage: '晶圆覆盖率',
        TitleScanTimeAgainstCoverage: '扫描时间随覆盖率的变化',
        LabelTimeSaved: '省下的时间',
        LabelAtFullCoverage: '全覆盖时',
        LabelAt10Coverage: '10% 覆盖时',
        HypADensityIsAPer: '密度是按面积计的数字，所以局部扫描对它的估计应几乎和全扫一样好——抽样对平均值有效。抽样做不到的是找出局部化的特征，因为落在未扫区域里的缺陷簇对估计值和分布图都毫无贡献。',
        AskedWhatDoesScanningLessOf: '少扫一部分晶圆要付出什么？',
        SumSampledCoverageScanTimeAnd: '抽样覆盖、扫描时间，以及局部扫描能告诉你什么、不能告诉你什么。',
        NextPrb14MeasuresTheYield: 'PRB-14 测的是这个缺陷密度所预测的良率。如果两者不一致，哪个错了——你会把一批晶圆押在哪个上？',
        LabelDesignRule: '设计规则',
        AxisYDetectionLimitNm: '检测极限 (nm)',
        AxisXPixelNm: '像素 (nm)',
        TitleDetectionLimitAndScanTime: '检测极限与扫描时间随像素尺寸的变化',
        HypScatteredIntensityFallsExtremelyFast: '散射强度随颗粒尺寸下降得极快，所以小于某个直径的缺陷返回的光比噪声底还少，看多久都没用。缺陷尺寸分布是幂律，意味着小的才是多数。如果两者都成立，看不见的群体应远大于看得见的——而其中一部分仍大到足以杀死一颗裸片。',
        AskedWhatCanThisRecipeNot: '这个配方看不见什么？其中有要紧的吗？',
        SumTheDetectionLimitAndWhy: '检测极限，以及为什么每个检测数字都是低估。',
        NextNeitherEndOfThatCurve: '那条曲线的哪一端都到不了 100% 捕获率。问我为什么——答案不在阈值，而它是两个极限里更重要的那个。',
        AxisXThreshold: '阈值 (σ)',
        TitleCaptureAndNuisanceAgainstThreshold: '捕获与误报随阈值的变化',
        NoteWhatIsActuallyThere: '实际存在的量',
        LabelReported: '报出数',
        LabelNuisance: '误报',
        HypTheThresholdIsALevel: '阈值是噪声底之上的一个水平，而真实缺陷和表面噪声在同一根轴上。调低它必然两者都抓到更多。如果是这样，就不存在单纯"更灵敏"的设置——只有哪种错误更贵的选择，而误报的代价是用 ' +
          'SEM 复检小时数付的。',
        AskedWhatDoesTurningTheDetection: '把检测阈值调低，实际买到了什么？',
        SumCaptureRateAgainstNuisanceRate: '捕获率对误报率，以及为此买单的复检时间。',
        fault: {
          roughIncoming: {
            Evidence: [
              '来料粗糙度 1.6 nm，限值 0.5 nm。',
              '雾度、激光功率、载台振动全都正常。',
              '现象只出在一个产品上——设备故障会出在所有产品上。',
            ],
            Truth: '检测机没有任何问题。晶圆到货就是 1.6 nm RMS 而不是 0.3——CLN-09 的 SC-1 ' +
              '下手太狠——而表面粗糙度正是误报群体生长的地基。给一台光学本来就干净的设备洗光学，要花四个小时，什么也治不了。',
            Complaint: '某个产品的误报数翻了三倍。检测机正在背锅。',
            Label: '晶圆本身粗糙，设备没有问题',
          },
          stageShake: {
            Evidence: [
              '载台振动 60%，限值 10%。',
              '捕获率下降，而激光功率和雾度都正常。',
            ],
            Truth: '一只气浮隔振垫漏了压，载台把地板的振动带进了每一次扫描。图像拖影，丢灵敏度的方式和弱光源一样——但振动读数直接点了它的名，而激光是好的。',
            Complaint: '扫描变慢了，图看起来沿扫描方向拖了影。',
            Label: '载台隔振退化',
          },
          hazedOptics: {
            Evidence: [
              '雾度读 45%，限值 10%。',
              '误报率远超 40% 的复检限值。',
              '激光功率监测完全正常。',
            ],
            Truth: '收光光路起雾了。它把杂光散进探测器，既抬高了噪声本底，又丢了灵敏度——设备同时变得更瞎、更吵。这个组合就是特征：弱激光只占前一样，不占后一样。',
            Complaint: '复检那边快淹死了。他们看的点一半什么都没有，队列排到两天开外。',
            Label: '收光光学被污染',
          },
          tiredLaser: {
            Evidence: [
              '功率监测读 55%，下限 85%。',
              '标准片上的捕获率崩了。',
              '误报率正常——弱光源只丢灵敏度、不添噪声，这是它和光学起雾的分界。',
            ],
            Truth: '激光只剩额定功率的 55%。散射信号随照明强度变化，检出下限于是抬高，最小的那批缺陷干脆不再被报出来。计数下跌是因为设备瞎了，不是因为厂子干净了。',
            Complaint: '所有产品的缺陷计数连跌了两个星期。产线以为是厂子变干净了。',
            Label: '照明光源衰退中',
          },
          inspHealthy: {
            Evidence: [
              '台上的每个读数都正常。',
              '捕获率就在标准片说它该在的地方。',
            ],
            Truth: '什么问题都没有。正确的判定就是放行这一批。',
            Complaint: '例行开班。这台设备一直安安静静。',
            Label: '一切正常',
          },
        },
        NoteOfTheWafer: '晶圆的 {{a}}%',
        NoteOfReviewsWasted: '{{b}} 次复检浪费了 {{a}} 次',
        NoteRealNuisance: '真缺陷 {{a}} + 误报 {{b}}',
        NoteNmLight: '{{a}} nm 光源，{{b}}σ',
        SectionWhyTheNumberIsAlways: '为什么报数总是偏低',
        NoteScatteringFromASmallParticle: '小颗粒的散射随直径的六次方变化，所以存在一个尺寸，低于它这台设备什么都看不见——而缺陷尺寸服从幂律，小的远多于大的。设备报出的是分布的顶端。它没坏；这是光学。',
        SectionScanResult: '扫描结果',
        LabelDetectionLimit: '检出下限',
        LabelCaptureRate: '捕获率',
        NoteOfTheDefectsActuallyPresent: '占实际存在缺陷的比例',
        LabelReportedDensity: '报出密度',
        LabelNuisanceRate: '误报率',
        LabelKillerDensity: '致命缺陷密度',
        NoteBigEnoughAndOnPattern: '足够大、且落在图形上',
        LabelScanTime: '扫描时间',
        NoteNoLotOnTheStage: '载台上没有批次。送一片晶圆过来扫描。',
        TextScanTheWaferAndPass: '扫描这片晶圆并传下去',
        AssistantLead: 'INSP-13 探究助手',
        AssistantBlurb: '可以问：降低阈值买到了什么、这套配方根本看不见什么、部分扫描够不够。每个回答都是在这台设备上真实跑出来的一次扫描。',
        reading: {
          nuisance: {
            Label: 'SEM 复检 50 个报出点（2.0 小时）',
          },
          sensitivity: {
            Limit: '> 8%（认证值：10%）',
            Label: '标准缺陷片捕获率（1.0 小时）',
          },
          roughness: {
            Label: '来料表面粗糙度',
          },
          vibration: {
            Label: '载台振动',
          },
          haze: {
            Label: '光学雾度',
          },
          laserPower: {
            Label: '激光功率监测',
          },
        },
        aspect: {
          incoming: {
            Action: '不是维修——晶圆本身粗糙，归上游清洗台管',
            Label: '来料表面',
          },
          stage: {
            Action: '重调气浮隔振并重整载台伺服',
            Label: '载台与隔振',
          },
          optics: {
            Action: '拆洗收光光路，再用标准片重新认证',
            Label: '收光光学',
          },
          laser: {
            Action: '更换激光头并重新校准功率监测',
            Label: '照明光源',
          },
        },
      },
      probe: {
        ValueDie: '{{a}} 颗裸片',
        signature: {
          clustered: '成簇',
          radial: '径向型',
          centre: '中心型',
          edge: '边缘型',
          uniform: '均匀',
        },
        LotDefectsShort: '本批次：{{a}} 个缺陷/cm²，其中致命缺陷 {{b}}/cm²',
        LeadMeanHidesTheMap: '同一个平均良率，可能是三个不同的问题，归三个不同的人负责。',
        NoteThisLotCarriesDefects: '这批晶圆携带 {{a}} 个缺陷/cm²，其中 {{b}}/cm² 是致命的。拿它与 INSP-13 ' +
          '写在流转卡上的数字比一比——两者不会一致，差值就是检测机看不见的那部分。',
        NoteNoLotOnTheProber: '探针台上没有批次。在晶圆到来之前，它按产线假定的 {{a}}/cm² 运行。',
        LabelEdgeExclusion: '边缘排除区',
        SecondaryLabel: '测量不确定度',
        ToolLabel: 'PRB-14 晶圆探针测试与分选',
        NextFt18AppliesAGuard: 'FT-18 会对封装后的产品再加一道自己的保护带，用的是带着自身量具误差的测量。问问它：两道保护带串联要付出什么——第二道买到了第一道没买到的东西吗？',
        LabelParametric: '参数良率',
        LabelSignature: '特征',
        HypSortYieldIsAProduct: '晶圆测试良率是三个独立因子的乘积，其中只有一个与晶圆有关。如果损失来自缺陷，清洗探针卡不会有任何变化；如果来自接触，变化会很大，而且分布图会长得像探针卡而不像工艺。保护带则完全是第三回事——它是一个决定，不是一种失效。',
        AskedWhereIsThisYieldActually: '这些良率究竟丢到哪里去了？',
        SumSeparatingAProcessSignatureFrom: '把工艺特征、接触问题和保护带三者区分开。',
        LabelNegativeBinomial: '负二项',
        AxisXClusteringLowHeavilyClustered: '聚簇 α（低 = 高度聚簇）',
        TitleTwoModelsOneWafer: '两个模型，一片晶圆',
        LabelImpliedDensity: '隐含缺陷密度',
        NotePoisson: '泊松',
        LabelNegativeBinomialL196: '负二项',
        HypPoissonAssumesEveryDefectLands: '泊松模型假设每个缺陷都独立落下。真实的缺陷是成划痕、成涂布条纹、成边缘环到来的——它们彼此重叠，好几个缺陷落在早已报废的裸片上。如果是这样，同样的缺陷总数下聚簇应把良率抬到泊松之上，而泊松恰恰在产线有特征签名时才悲观。',
        AskedWhyDoTheYieldModels: '为什么各个良率模型互不一致？产线该用哪个？',
        SumClusteringAndWhyTheSimplest: '聚簇，以及为什么最简单的良率模型总是悲观的。',
        NextThatCurveAssumedDefectsLand: '那条曲线假设缺陷是各自独立落下的。它们不是——它们成簇。问我聚簇会对同样的数字做什么，因为这是一个能预测你产线的模型和一个不能的模型之间的差别。',
        LabelGoodDie: '好裸片',
        TitleGoodDieOut: '产出好裸片数',
        AxisXDieAreaCm: '裸片面积 (cm²)',
        TitleDefectYieldAgainstDieArea: '缺陷良率随裸片面积的变化',
        LabelTestTime: '测试时间',
        LabelEdgeLoss: '边缘损失',
        HypADieFailsIfAny: '只要有一个致命缺陷落在裸片上它就报废，所以存活概率随面积指数下降。如果这是对的，裸片面积翻倍的代价远不止良率减半，而且任何现实规模的工艺改进都补偿不了一个在掩模开刻之前就定下的设计决定。',
        AskedHowMuchDoesDieArea: '与其他一切相比，裸片面积对良率有多重要？',
        SumYieldIsExponentialInDie: '良率是裸片面积的指数函数，所以面积是最大的杠杆，而它握在设计手里。',
        fault: {
          chuckStarved: {
            Evidence: [
              '卡盘真空 21 kPa，低于 60 kPa 的下限。',
              '晶圆图特征为边缘型，而探针卡平面度在 6 µm 限值以内。',
              '短路标准片上的接触损失远超 2%，探针磨损正常。',
              '厂务的公用设施面板显示 PRB-14 的干泵停机，其余各泵正常抽气。',
            ],
            Truth: '这台机台下方、地下层里的干泵卡死了：前级管路接近大气压，卡盘只用应有真空的五分之一吸着晶圆。没被吸平的晶圆边缘会翘起，翘起的边缘在探针卡下落针偏轻，和探针卡不平的表现完全一样——所以晶圆图说“边缘”，而平面度量规却说探针卡没问题。重新垫卡、清洁探针：这台机台上做什么都恢复不了它自己并不产生的真空。厂里其他机台都没变，因为没有别的机台共用这台泵。',
            Complaint: '晶圆图一夜之间变成边缘型，所有产品的接触损失都上升了。探针卡上周刚重新垫过，平面度检查合格。',
            Label: '干泵故障导致卡盘失去真空',
          },
          noisyGauge: {
            Evidence: [
              '量具 sigma 是规格的 14%，限值 5%。',
              '晶圆图形貌均匀——噪声没有形状。',
              '吸盘、平面度、针尖磨损全都正常。',
            ],
            Truth: '参数测量单元噪声大——量具 sigma 是规格宽度的 14%，限值是 ' +
              '5%。靠近判限的好裸片这次挂、下次过。平均良率几乎不动，所以一直没人发现；复测结果对不上，是唯一能抓住它的症状。',
            Complaint: '同一片晶圆复测，每次的分级都不一样。平均良率倒是大差不差。',
            Label: '参数测量链路噪声大',
          },
          chuckOffset: {
            Evidence: [
              '吸盘偏差 +9 °C，限值 ±2 °C。',
              '晶圆图形貌读作放射型，它跟着吸盘走，不跟工艺走。',
              '分选良率没有变化——这个故障不报废裸片，它挪的是判限落在群体上的位置。',
              '接触损失正常——这不是落针的问题。',
            ],
            Truth: '吸盘热电偶漂了，晶圆比日志报的温度高 9 °C。参数测试对温度敏感，于是整个群体相对于在正确温度下定的判限整体挪了位。裸片本身是好的。',
            Complaint: '参数分级挪位了。导通没有挂的，挂掉的裸片呈放射状散布。',
            Label: '吸盘温度偏高',
          },
          cardOutOfPlane: {
            Evidence: [
              '平面度 14 µm，限值 6 µm。',
              '晶圆图形貌读作边缘型。',
              '针尖磨损在限值内，这不是清针的问题。',
            ],
            Truth: '探针卡失平了 14 µm，一侧落针太轻。那些裸片根本没接触上，被判成了坏品。形貌就是告密者：失平的卡挂掉的是边缘，而边缘型的图不是缺陷群体该有的长相。',
            Complaint: '晶圆图变成了边缘偏重。所有人都在怪边缘排除区。',
            Label: '探针卡失平',
          },
          wornTips: {
            Evidence: [
              '距上次清针的触点数远超 150,000 的限值。',
              '短路标准片上的接触损失超过 2%。',
              '损失出现在所有产品上，晶圆缺陷不会这样。',
            ],
            Truth: '针尖已经超过触点寿命，扎不穿焊盘氧化层了。电学上完好的裸片在导通测试上挂掉——损失发生在接触上，不在晶圆上，而接触电阻标准片会直接把这句话说出来。',
            Complaint: '从周二起，这个测试单元上所有产品的良率都掉了四个点。',
            Label: '探针针尖磨损',
          },
          probeHealthy: {
            Evidence: [
              '台上的每个读数都正常。',
              '晶圆图形貌是均匀的。',
            ],
            Truth: '什么问题都没有。正确的判定就是放行这一批。',
            Complaint: '例行批次。良率就在模型说它该在的地方。',
            Label: '一切正常',
          },
        },
        NoteMmRingHToProbe: '{{a}} mm 排除环，{{b}} 小时测完',
        NotePoissonWouldSay: '泊松模型会说 {{a}}%',
        NoteCentreEdge: '中心 {{a}}%，边缘 {{b}}%',
        NoteGoodOfProbed: '{{b}} 颗中良品 {{a}} 颗',
        SectionReadTheMapNotThe: '读图，别只读数',
        NoteAMeanYieldOf78: '78% 的平均良率，可以是均匀的 78%，可以是中间 95% ' +
          '而边缘全死，也可以是一片完美晶圆上划了一道。三个不同的问题，归三个不同的人管——而平均值分不出它们。',
        InvBandEdge: '导带底',
        InvFermi: '费米能级',
        InvInterface: '氧化层 | 硅',
        InvLevel: '{{ladder}} 第 {{n}} 级',
        InvShape: '每个电子所在之处（|ψ|²）',
        InvBandTitle: '栅极挖出的势阱及其能级',
        InvBandX: '距氧化层的深度 (nm)',
        InvBandY: '电子能量（eV，体内导带底 = 0）',
        InvDensityTitle: '沟道电荷所在的位置',
        InvDensityX: '距氧化层的深度 (nm)',
        InvDensityY: '电子密度 (10¹⁹ cm⁻³)',
        InvDensityQ: '量子',
        InvDensityCl: '经典（费米–狄拉克）',
        InvCentroid: '形心',
        InvCvTitle: '探针卡测到的量：栅电容',
        InvCvX: '栅压 (V)',
        InvCvY: '电容 (µF/cm²)',
        InvCvQ: '量子',
        InvCvCl: '经典（费米–狄拉克）',
        InvCox: '仅氧化层',
        InvHere: '当前栅压',
        InvFailed: '沟道求解器加载失败。',
        InvStatus: 'φ_s = {{phis}} V · N_inv = {{ninv}} cm⁻² · 电荷形心距氧化层 {{zavg}} nm · 等效氧化层 +{{dt}} nm · 阈值偏移 +{{dvt}} mV',
        InvLoading: '正在加载沟道求解器…',
        InvStatusDepleted: 'φ_s = {{phis}} V · 尚未反型：栅极还没有把沟道拉到表面 · 阈值偏移 +{{dvt}} mV',
        InvComputing: '正在求解电容扫描和阈值…',
        InvSweepFailed: '电容扫描无法计算。',
        InvRight: '右图',
        InvRightCv: '电容—栅压',
        InvRightDensity: '电荷—深度',
        InvVg: '栅压',
        InvTox: '氧化层厚度',
        InvNa: '衬底掺杂',
        SectionInversion: '阈值为何不在公式所说之处——把沟道当作量子阱（薛定谔–泊松）',
        InversionNote: '这块探针卡测到的每一个阈值电压，都由氧化层之下一纳米处的一个事实决定，而经典的电荷薄层公式把它漏掉了：栅极拉到表面的电子并不能待在表面上。栅极电场把导带压弯成紧贴氧化层的三角形势阱，而这么窄的阱只允许分立的能级，每一级的波函数在氧化层处为零、在约一纳米深处达到峰值。这里是把这个阱真正解出来：泊松方程贯穿栅极、氧化层和衬底，薛定谔方程处理 (100) 硅的两组能谷阶梯，两者迭代到彼此自洽。左图是带有能级和每个电子形状的能带图；右图是探针卡看到的量——栅电容，量子解对比采用同样费米–狄拉克统计的经典解，所以两条曲线之间的差距纯粹来自量子化。两者都达不到氧化层电容，因为稠密的电子气本身就有电容；量子曲线更低，是因为每多加一伏所带来的电荷落在离氧化层更远几埃的地方，而这段间距给出的差距大小，与实际差距相差不超过三分之一。状态行里的阈值偏移，是两种解在同一薄层电荷下的差值。栅极按视作金属的 n⁺ 多晶硅处理；多晶硅耗尽、界面态和漏极偏压均未计入，空穴按经典处理，所以积累一侧在这里没有量子化。',
        SectionShortChannel: '栅极为什么不再缩小——源漏之间的势垒，以及隧穿而过的电子（传输矩阵，传播的波包）',
        ShortChannelNote: '晶体管靠在源极与沟道之间竖起一道势垒来关断，关态下漏掉的电流，就是那些热能足以翻过势垒的电子的尾巴：每一个数量级的电流要六十毫伏的栅压，任何热器件都做不得更好。长的鳍做得到这一点：它的体区完全耗尽，没有什么来分走栅压的摆动，模型里的长栅就在热极限上摆动。这里展示的是短栅对那道势垒做了什么。源极和漏极同样是电极，它们的影响会沿着由体厚与氧化层决定的特征长度伸进沟道，因此当栅长只有几个这样的长度时，栅极失去一部分控制，是漏极自己把势垒压了下去——亚阈值摆幅退化到六十以上，阈值随漏压下降。这个阈值是按一条扣除了漏极自身回注的判据读出的，所以长栅的 DIBL 就是它应有的每伏零点几毫伏。势垒同时变薄，而两纳米宽的势垒，对根本翻不过去的电子来说是透明的。两者都出自同一次计算：静电学用的是特征长度模型（Young；双栅形式出自 Yan、Ourmazd 和 Lee），透射率用的是 THRM-04 用在栅氧上的同一套传输矩阵——势垒之上接近一，之下按指数衰减，于是热电子发射与隧穿是同一条曲线，而不是两个模型。左边那道波，是把这条透射率传播出来而不是算出来的。波包有意取得很长：波包是一段能量的展宽，只有长波包才窄到能整个落在势垒顶之下，所以从另一侧出来的那部分——在栅极之后放大重画了一遍——才是隧穿，从 10 nm 的栅到 20 nm 的栅，它下降几百倍。即便如此它也不是单一能量，所以给出三个数：波包实际送过去的份额、传输矩阵对波包自身各能量的平均，以及只按其平均能量算的 T。前两个在栅长 20 nm 以内相差不到百分之一，40 nm 时不到百分之三；第三个可以小一百万倍。把栅长往下拉，看隧穿在漏电流中的占比越过一半，看开关比崩塌，也看到栅极再也关不断器件的那个长度。在这么薄的鳍里，掺杂只按其体电荷移动阈值，而不改变摆幅，这正是实际的鳍不掺杂的原因。散射没有计入，所以开态电流是弹道输运的上限；沿体厚方向的量子限制是上一节的内容；绝对电流依赖于假定的沟道厚度，所以要读的是各种比值，而不是安培数。',
        ScBandTitle: '沿沟道的势垒，以及迎面而来的一个电子',
        ScBandX: '沿沟道的位置（nm）',
        ScBandY: '电子能量（eV，源极带边 = 0）',
        ScBand: '导带边',
        ScWave: '电子在哪里（|ψ|²）',
        ScGate: '栅极',
        ScTop: '势垒顶',
        ScLaunch: '发射能量',
        ScTransferTitle: '探针台测到的：电流随栅压的变化',
        ScTransferX: '栅压（V）',
        ScTransferY: 'log₁₀ 漏极电流（µA/µm）',
        ScTransferHigh: '漏压 {{vd}} V',
        ScTransferLow: '漏压 {{vd}} V',
        ScCriterion: '阈值判据',
        ScHere: '当前栅压',
        ScVt: '阈值',
        ScScaleOffTitle: '缩小的代价：关态电流随栅长的变化',
        ScScaleTunTitle: '漏电流中有多少是隧穿',
        ScScaleX: '栅长（nm）',
        ScScaleOffY: 'log₁₀ 关态电流（µA/µm）',
        ScScaleTunY: '低于势垒顶的占比（%）',
        ScScaleOff: '栅极接地',
        ScScaleTun: '隧穿',
        ScThisGate: '当前栅长',
        ScStatusShort: '势垒 {{top}} meV · {{tun}}% 的电流从其下方隧穿 · {{current}} µA/µm',
        ScNeverOnShort: '始终未开启：阈值高于栅极所能达到的 {{max}} V · 摆幅 {{s}} mV/dec',
        ScPacketShort: '{{fs}} fs · 穿过栅极 {{through}} · 转移矩阵 {{matrix}}',
        ScStatus: '势垒 {{top}} meV，半高处宽 {{width}} nm · 特征长度 {{lambda}} nm · 栅压摆动的 {{grip}}% 传到势垒顶 · 本电流中 {{tun}}% 走在势垒顶之下 · 此处 {{current}} µA/µm',
        ScMeasured: '摆幅 {{s}} mV/dec · DIBL {{dibl}} mV/V · 阈值 {{vt}} V · 关态电流 {{off}} µA/µm · 开关比 {{ratio}}',
        ScPunch: '已穿通：栅极无法关断该器件。栅极接地时仍通过 {{off}} µA/µm，因此没有阈值、没有摆幅，也没有阈值漂移可报。',
        ScPacket: '{{fs}} fs · 穿过栅极 {{through}} · 传输矩阵对本波包各能量取平均得 {{matrix}}，只按其平均能量算为 {{single}} · 已反射 {{back}} · 尚在途中 {{coming}} · 概率合计 {{norm}}',
        ScComputing: '正在求解转移曲线、摆幅与阈值漂移…',
        ScSweepFailed: '无法计算转移曲线。',
        ScFailed: '短沟道求解器加载失败。',
        ScLoading: '正在加载短沟道求解器…',
        ScNone: '无',
        ScRun: '发射一个电子',
        ScPause: '暂停',
        ScReset: '重置',
        ScRight: '缩放图',
        ScRightOff: '关态电流',
        ScRightTun: '隧穿占比',
        ScLength: '栅长',
        ScVg: '栅压',
        ScVd: '漏压',
        ScTox: '栅氧',
        ScBody: '体厚',
        ScNa: '体掺杂',
        ScEnergy: '发射能量',
        ScEnergyValue: '{{ev}} ± {{spread}} meV，为势垒的 {{pct}}%',
        ScEnergyNone: '已无势垒可瞄准——以 {{ev}} meV 发射',
        ScWavePast: '栅极之后，放大 ×{{gain}}',
        ScStale: '旋钮动了：变暗的曲线属于上一个器件，本器件的曲线正在求解…',
        ScNeverOn: '始终未开启：阈值高于栅极所能达到的 {{max}} V。满栅压下仅通过 {{on}} µA/µm · 摆幅 {{s}} mV/dec，取自量程顶端 · 关态电流 {{off}} µA/µm',
        ScNaValue: '{{na}} cm⁻³ · 阈值 +{{shift}} mV',
        ScBenchDevice: '器件',
        ScBenchBias: '偏压与电子',
        SectionSortResult: '分选结果',
        LabelSortYield: '分选良率',
        LabelMapSignature: '晶圆图形貌',
        LabelDefectYield: '缺陷良率',
        LabelImpliedKillerDensity: '反推致命缺陷密度',
        NoteInvertedFromTheYield: '由良率反推',
        LabelContactYield: '接触良率',
        NoteLostToTheCardNot: '损失在探针卡，不在晶圆',
        TextProbeTheWaferAndPass: '针测这片晶圆，把合格裸片传下去',
        AssistantLead: 'PRB-14 探究助手',
        AssistantBlurb: '可以问：芯片面积大了良率为什么差这么多、这条线该用哪个良率模型、损失是晶圆的还是探针卡的。每个回答都是针对这批片子真实跑出来的一次分选。',
        reading: {
          chuckVacuum: {
            Limit: '> 60 kPa',
            Label: '卡盘真空',
          },
          houseutilities: {
            Limit: '所有系统正常',
            Label: '致电厂务查看公用设施面板（0.5 小时）',
          },
          contactResistance: {
            Label: '短路标准片接触电阻检查（0.5 小时）',
          },
          mapShape: {
            Limit: '均匀或成簇',
            Label: '全片晶圆图，读判形貌（1.5 小时）',
          },
          gauge: {
            Label: '参数量具 sigma',
          },
          chuckTemp: {
            Label: '吸盘温度偏差',
          },
          planarity: {
            Label: '探针卡平面度',
          },
          tipWear: {
            Label: '距上次清针的触点数',
          },
        },
        aspect: {
          house: {
            Action: '停止本机台作业，上报厂务',
            Label: '本机台无故障——升级到厂务',
          },
          gauge: {
            Action: '重新校准参数测量单元及其线缆',
            Label: '参数测量链路',
          },
          chuck: {
            Action: '重新校准吸盘热电偶并重调控温回路',
            Label: '吸盘温度',
          },
          planarity: {
            Action: '重新垫平探针卡并四象限复测平面度',
            Label: '探针卡平面度',
          },
          tips: {
            Action: '在研磨板上清针；磨损过度则换卡',
            Label: '探针针尖',
          },
        },
      },
      litho: {
        ExposeLot: '曝光该批次并送去刻蚀',
        NextHoldingIsTheEasyHalf: '扣住是简单的那一半。这一班早些时候曝的批次走的是同一套光学——什么量测能告诉你其中有没有该收回来的？显影之后那个量测还做得了吗？',
        NextASendAheadIsOneField: '先导片只是一片上的一个曝光场。你要曝什么、曝多少个，才会相信刚才据以放行设备的那个数字？',
        PatternResolved: '已成像',
        PatternUnresolved: '没有成像',
        LabelResolved: '图形',
        LabelRoughness: '粗糙度 3σ',
        LabelPrintedCd: '实测线宽',
        VerdictWithAWatchItem: '放行，但有一项要盯着',
        VerdictHeld: '扣住',
        VerdictQualified: '已认定',
        LabelVerdict: '判定',
        HypAQualificationIsADecision: '资格认定是一个决定，不是一段解释。只要先导片显影出图形、线宽落在窗口内、粗糙度低于限值，而且台面每一项读数都在限值内，扫描机就放行；否则扣住。再用同一套光学跑已认定配方，就能看出该负责的是配方还是设备。',
        AskedIsThisScannerFit: '这台扫描机现在能不能曝产品，如果不能该怎么办？',
        SumExposeTheSendAhead: '曝一片先导片，把台面读数逐项对照限值，然后做出放行判断。',
        UnitNmCd: 'nm CD',
        NoteResolutionImprovesLinearly: '分辨率随 NA 线性改善，而聚焦预算至少以 NA 的平方下降——一旦角度不再算小就下降得更快，' +
          '而在这些孔径上角度早就不小了。所以 High-NA 在半周期上买到 1.7 倍，却在焦深上还回去 2.9 ' +
          '倍——而这台设备一开始也只有 72 nm。这就是 CMP-11 存在的原因。而让 NA 0.55 ' +
          '成为可能的变形光学，把扫描方向的曝光场砍了一半，于是 26×32 mm ' +
          '的裸片现在要曝两次再拼接。光子能量没有变，所以本来就是瓶颈的随机效应一点没变好。',
        ValueFieldsStitched: '{{a}} 场——需拼接',
        ValueFields: '{{a}} 场',
        HeadPerDie: '每颗裸片',
        HeadField: '曝光场',
        HeadFocusBudget: '聚焦预算',
        HeadHalfPitch: '半周期',
        NotePresetEuvFine: '把光源也换成 EUV。能印出来，但光子只剩一个零头。',
        NotePresetTightPitch: '超出传统照明的舒适区：对比度 0.57，窗口收缩。',
        NotePresetUnderdosed: '20 mJ/cm²。线条印得太宽——光少了，去除的光刻胶就少。',
        NotePresetFocusExcursion: '90 nm 离焦。对比度跌到 0.42，线宽严重偏离目标。',
        NotePresetBaseline: '在 180 nm 周期、最佳焦点下按目标尺寸曝光。对比度 0.97，NILS 7.1，LER 2.4 nm。',
        LabelUnderdosed: '剂量不足',
        SecondaryLabel: 'LER 3σ',
        ToolLabel: 'LITH-03 · 浸没式光刻机与涂胶显影轨道',
        NextIfDoseCannotFixRoughness: '如果剂量本身治不了粗糙度，还剩下什么？想想曝光后烘烤对边缘做了什么，同时又让你付出了什么。',
        AxisYLerNm3: 'LER (nm, 3σ)',
        AxisXDoseMjCm: '剂量 (mJ/cm²)',
        TitleRoughnessAgainstDoseAndThe: '粗糙度随剂量的变化——以及它跨不过去的下限',
        LabelFailuresIn300: '300 条中的失效数',
        NoteDoseOnlyBuysBackThe: '剂量只能买回光子那一项',
        LabelAtTwiceTheDose: '剂量加倍时',
        LabelCdSpread3: 'CD 分散 (3σ)',
        HypDoseIsDeliveredAsA: '剂量是以有限个光子的形式送达的。如果边缘位置由数光子决定，那么计数会按其平方根涨落，边缘的移动量应随剂量升高而下降——但永远降不到零，因为光刻胶本身也是由有限个分子组成的。',
        AskedWhyDoesTheSameExposure: '为什么同一次曝光重复一遍，印不出两条一样的线？',
        SumPhotonStatisticsLineEdgeRoughness: '光子统计、线边缘粗糙度，以及 EUV 为什么改变了规则。',
        NextEverythingAboveUsedConventionalIllumination: '上面的一切都用的是传统照明。离轴照明舍弃光源中心，只保留在紧密周期下仍能成像的角度。这会对上面的数字产生什么影响？代价又是什么？',
        LabelProductionMinimum: '量产下限',
        AxisYDepthOfFocusNm: '焦深 (nm)',
        AxisXPitchNm: '周期 (nm)',
        TitleDepthOfFocusAgainstPitch: '焦深随周期的变化',
        LabelManufacturable: '可量产',
        LabelDoseLatitude: '剂量宽容度',
        LabelDepthOfFocus: '焦深',
        DesignFocusSwept140NmAgainst: '聚焦扫描 ±140 nm，剂量扫描 ±40%，在三个周期下进行。统计 CD 保持在目标 ±10% 以内的区域。',
        HypAProcessWindowIsThe: '工艺窗口是两个容差的重叠，而不是一个。窗口宽，图形就能挺过不够平整的晶圆和漂移的光源；窗口窄，图形可以在测试中印得很漂亮，却仍在量产中失效。',
        AskedHowMuchFocusAndDose: '这个图形在超出规格之前，能吸收多大的聚焦和剂量误差？',
        SumMeasureDepthOfFocusAnd: '测量焦深与剂量宽容度，并判断其是否可量产。',
        LabelReturnToBestFocus: '回到最佳焦点',
        TitleCdAgainstFocusAtThree: '三个剂量下 CD 随聚焦的变化',
        LabelNilsAsSet: '当前设置的 NILS',
        LabelDiagnosis: '诊断',
        LabelDoseSensitivity: '剂量灵敏度',
        HypDoseAndFocusLeaveDifferent: '剂量和聚焦留下的指纹不同。剂量单调地移动 CD——光越多线条越细；而聚焦关于最佳焦点对称，无论偏向哪一侧，CD 的变化方向都相同。如果 CD ' +
          '对聚焦的响应不对称，设备就不在最佳焦点；如果只对剂量响应，那就是剂量误差。',
        AskedTheCdIsNotOn: 'CD 不在目标值上。两个旋钮里是哪一个的责任？',
        SumSeparateADoseErrorFrom: '区分剂量误差与聚焦误差——它们在 CD 读数上看起来一样。',
        fault: {
          warmPcwLitho: {
            Evidence: [
              'CD 偏宽而对比度、NILS、LER 都正常——剂量的特征。',
              '剂量计交叉核对是干净的，光刻胶对比度曲线也是。两个惯常嫌疑人都被排除了，而这正是多数人开始瞎猜的时刻。',
              '公用系统检查显示冷却水远高于供水温度。',
              '刻蚀机在同一时间变慢，原因看起来完全是另一回事。它不是另一回事。',
            ],
            Truth: '冷冻水厂送出的供水比标称高了约 22 ' +
              'C。没有任何一台设备坏了；每台向那条回路排热的设备只是再也守不住自己的设定值了。光刻机通过它的激光器感受到这一点：准分子光源是水冷的，冷却水一热就降额，于是它给出的剂量只有剂量计以为的四分之三左右。剂量计没有撒谎——它量的是别人告诉它该预期的脉冲能量，而设备没有任何办法知道自己的水是热的。',
            Complaint: '线条印宽了约 7%。看着像春天那次光源问题，可剂量计交叉核对是干净的，光刻胶对比度曲线也干净。ETCH-01 同时在变慢，没有人觉得这两件事有关系。',
            Label: '冷却水偏热',
          },
          hotPlate: {
            Evidence: [
              'LER 低于它的带宽。这不是好消息，是信号——这台设备上没有别的东西能把边做得比认证工艺还光滑。',
              '像对比度和 NILS 没动，光学没问题。模糊发生在曝光之后，不在曝光之中。',
              '烘板热电偶巡检读数明显高于设定值。',
            ],
            Truth: '曝光后烘烤板偏热，酸在烘烤期间扩散出四倍远。模糊是双刃剑，这就是全部教训：它抹平了酸的分布，实实在在改善了线边粗糙度；它也冲淡了分布，让线变窄。设备正在超差的图形上做出更好看的边。',
            Complaint: 'CD 窄了一个星期了。怪的是：线条边缘看起来比平时还好。',
            Label: '烘烤板偏热',
          },
          focusDrift: {
            Evidence: [
              '像对比度和 NILS 双双大跌。剂量或光刻胶故障做不到这一点——它们改变阈值在哪里被越过，改变不了像越过阈值时有多锐。',
              'LER 升高了，这是 NILS 丢失的直接后果。',
              '穿焦曝光找到的最佳焦点，离设备以为的位置差了约 35 nm。',
            ],
            Truth: '调平传感器漂了，晶圆离焦平面约 35 nm，而设备以为自己在最佳焦点。离焦最先毁掉的是空间像：对比度从 0.97 掉到 0.80，NILS ' +
              '跟着掉，发软的像印出又窄又毛的线。',
            Complaint: 'CD 偏窄，SEM 图上线条边缘发软。',
            Label: '调平传感器偏移',
          },
          weakResist: {
            Evidence: [
              'CD 偏宽而对比度、NILS、LER 都正常——在设备上和光源衰退分不出来。',
              '剂量计交叉核对回来是干净的。就是这条读数拍的板，而它靠的是排除另一个，不是靠找到什么。',
              '监控片上的对比度曲线显示 gamma 只有认证值的一半左右。',
            ],
            Truth: '这批光刻胶丢了差不多一半的对比度——在货架上放太久了。低对比度的胶沿着酸梯度溶解得更平缓，边缘落点更靠外，线条就印宽了。像是完美的；材料不是。',
            Complaint: '线条又印宽了，跟春天那次光源问题一个样。剂量上周才重新校准过。',
            Label: '光刻胶批次对比度超差',
          },
          sourceDrift: {
            Evidence: [
              'CD 偏宽而设备上其余一切正常。对比度、NILS、LER 都没动，所以光学和烘烤都不涉及。',
              '劣化的光刻胶批次在设备可见的每一个读数上都长得一模一样。要分开它们只有剂量计交叉核对这一条路，别的什么都做不到。',
              '对参考探测器的交叉核对显示实际剂量远低于设定值。',
            ],
            Truth: '准分子光源老化了，现在给出的剂量只有剂量计以为的八成左右。产酸变少，显影阈值在像的更外侧被越过，线条就印宽了。『像』本身什么都没变——对比度、NILS、粗糙度全都恰好在标称上——这正是它难以察觉的原因。',
            Complaint: '全片的线条都印宽了约 6%。同样的配方、同样的光罩，而设备说它给的剂量分毫不差。',
            Label: '光源输出衰退',
          },
          healthy: {
            Evidence: [
              'CD 在目标上，对比度和粗糙度都在认证值上。',
            ],
            Truth: '什么问题都没有。正确的判定就是放行这一批。',
            Complaint: '设备通过了每日 CD 检查。没有任何报告。',
            Label: '光刻机已认证',
          },
        },
        TextNmOfIncomingTopographyCd: '{{a}} nm 来料形貌 → CD 散布 ',
        LabelBaseline193i: '基线-193i',
        LabelFocusExcursion: '焦距漂移',
        LabelTightPitch110Nm: '紧节距 110 nm',
        LabelEuv36NmPitch: 'EUV 36 nm 节距',
        LabelHighNaEuv: '高 NA EUV',
        reading: {
          resistlot: {
            Label: '监控片光刻胶对比度曲线（4 小时）',
          },
          plateprofile: {
            Label: '烘板热电偶巡检（90 分钟）',
          },
          focusprobe: {
            Label: '监控片穿焦曝光（3 小时）',
          },
          houseutilities: {
            Limit: '总管不漏，PCW 在供应温度，干燥气 < 50 ppm',
            Label: '与厂务共同检查公用系统（2 小时）',
          },
          dosemeter: {
            Label: '剂量计对参考探测器交叉核对（2 小时）',
          },
          setpoint: {
            Limit: '按配方',
            Label: '剂量设定值 / 焦距设定值',
          },
          nils: {
            Label: '归一化像对数斜率',
          },
          contrast: {
            Label: '空间像对比度',
          },
          ler: {
            Label: '线边粗糙度，3 sigma',
          },
          cd: {
            Label: '曝印 CD（SEM）',
          },
        },
        aspect: {
          house: {
            Action: '停止在本机上的操作，知会厂务',
            Label: '本机无事可修——上报厂务',
          },
          resist: {
            Action: '撤下该批次，测对比度曲线，换新瓶供胶',
            Label: '光刻胶批次',
          },
          bakeplate: {
            Action: '巡检烘板热电偶并重新调平近接销',
            Label: '曝光后烘烤板',
          },
          stage: {
            Action: '用穿焦晶圆重新认证调平传感器',
            Label: '调平与对焦',
          },
          source: {
            Action: '交叉核对剂量计，重新校准光源输出',
            Label: '曝光源与剂量控制',
          },
        },
        SectionLot: '吸盘上的批次',
        LotNote:
          '光刻机只有一个焦平面,而晶圆自带表面形貌。CMP-11 没磨平的部分在这里直接从 CD 预算中扣除——而光刻机本身毫无故障。CMP ' +
          '的问题往往隔了两台设备,在这里才第一次被发现。',
        LotEmpty: '吸盘上没有批次。上面的旋钮曝光的是一片理想平坦的晶圆。',
        LotIngot: '该批次还是晶锭。要先经 WPR-08 切片才能曝光。',
        LotCard: '流程卡',
        SectionPrinted: '曝显结果',
        SectionSource: '光源',
        SourceDuv: '193 nm 浸润式（NA 1.35）',
        SourceEuv: 'EUV 13.5 nm（NA 0.33）',
        SourceNote: '换光源，同时改变了两件事：能分辨到多细，以及同样一份剂量能送来多少光子。这两个后果的方向正好相反——关于 EUV 的全部争论，就在这里。',
        SectionAerial: '空间像与光阻响应',
        SectionAerialWave: '空间像从哪来——两个衍射级次的干涉（波动光学）',
        AerialWaveNote:
          '上面那张图是用公式算出来的。这里则是公式所描述的那件事本身。两束相干光——被物镜收进来的 +1 与 −1 ' +
          '衍射级次——在晶圆面上相交，明暗是波场平方的时间平均，也正是光阻所积分的量。这里没有任何东西在"计算"' +
          '一幅像：两条线光源在辐射，求解器在传播，条纹就是两束光相加与相消的地方。它们的间距，就是正在印出来的' +
          '节距。按运行，看两束光抵达、条纹成形；从它们相遇的那一刻起画面就是静止的，因为两列交叉的波本来就形成' +
          '驻波图样。然后切换上面的光源。更大的数值孔径无非就是两个级次之间更陡的夹角，而更陡的夹角就是更细的' +
          '条纹——这就是 λ / 2NA 的全部，是发生出来的，而不是被断言的。',
        AerialWaveImmersion:
          '而这正是水的用处。介质的折射率以波速的形式进入，频率保持不变，因为光进入液体时就是这样：频率保留，' +
          '波长变短。所以同样的角度在水下印出更细的线条。但真正的关键是算式本身泄露的那一点——NA 等于 n sin θ，' +
          '而正弦不可能大于一，所以干式物镜无论怎么造都永远到不了 NA 1。1.35 的浸润式扫描机并不是一台更好的' +
          '物镜，它只是把同一套几何放进了一个"这个角度确实存在"的介质里。',
        AerialWaveCaveat:
          '三条诚实的边界。无论波长是多少，这个方框都是六个波长宽，因为 193 nm 和 13.5 nm 无法共用一张网格——' +
          '所以画面以波长为单位，而读数则按你所选的光源换算成纳米。光源长六个波长而非无限长，所以光束会在自己的' +
          '边缘发生衍射，测得的周期落在约百分之三以内而不是严丝合缝；在最小的数值孔径下，方框里只容得下两三条' +
          '条纹可供测量。而且这里刻意只取"双光束"的情形——一个孔径能印出的最细节距，也就是本面板分辨率数字所' +
          '假定的 k₁ = 0.25 极限。实际生产中较宽松节距的成像还会带上零级，而那第三束光正是上面那张图所计算、' +
          '这里所略去的。',
        ImageWaferPlane: '晶圆',
        ImageAngle: '级次夹角',
        ImageHalfPitch: '半节距',
        ImageVersus: '对比',
        ImageContrast: '对比度',
        ImageAxisX: '沿晶圆方向（nm）',
        ImageAxisY: '相对强度',
        ImageLabelSolved: '波动求解器印出来的',
        ImageLabelIdeal: 'λ / 2NA，两束光该有的周期',
        ImageVerdict:
          '求解器仅凭一个角度就印出了 {{measured}} nm 的半节距。而这台机台的分辨率极限，0.25 λ / NA，' +
          '给出的是 {{limit}} nm。{{dry}}',
        ImageDryImpossible: '在空气中根本不存在能做到这件事的角度：sin θ 得大于一。水就是为此而来的。',
        ImageDryAngle: '在空气中，同样的孔径需要 ±{{angle}}°。',
        SectionStack: '光落到晶圆上之后——光刻胶中的驻波',
        StackNote:
          '晶圆不是屏幕。到达的光会进入光刻胶、穿过它、在下面的材料上反射，再穿回已经被自己曝光过的光刻胶。' +
          '下行波和上行波是同一束光的两半，因而是相干的，会发生干涉：光刻胶不是被一束均匀的光曝光，' +
          '而是被一列驻波曝光——明暗相间的平面沿深度方向层层堆叠。这些平面正是显影后光刻胶侧壁呈扇贝状' +
          '而非光滑的原因，在画布上也能看到：那一摞竖直排列的亮斑，单靠空间像本来会画成干净的竖条纹。',
        StackNoteSwing:
          '现在拖动厚度。光刻胶本身就是一个光学腔，往返一圈回来是否同相取决于它有多厚，' +
          '于是膜吸收的剂量随厚度上下摆动——线宽也跟着摆动。这就是摆动曲线，也是为什么光刻胶厚度是' +
          '控制到几纳米的规格而不是偏好，为什么涂胶轨道要把转速控制到几转每分。然后把抗反射涂层切进来。' +
          '驻波塌缩，摆动曲线变平，因为已经没有反射波可供入射波干涉了。这就是这层膜的全部工作，' +
          '和相机镜头上的镀膜是同一个把戏。',
        StackNoteCaveat:
          '三条如实说明的限制。本节固定在 DUV 浸没式而不跟随光源选择器，因为这个故事讲的是厚度只有' +
          '波长几分之一的光刻胶，而 193 nm 和 13.5 nm 不可能同时如此。衬底不是硅：标量波模型无法承载' +
          '硅在 193 nm 处 0.88 − 2.78i 这样的折射率，因此这里用一个高折射率、强吸收的替身，其反射率' +
          '相当；画在求解器旁边的精确叠层用的是同一个替身，好让两者回答同一个问题。另外，求解出的' +
          '图样与精确解给出的位置相差不到一个网格单元——约六纳米——这正是陡峭界面在百格网格上的代价；' +
          '比较的是周期、对比度和形状，所以精确曲线是对齐到实测曲线画出的。',
        StackResistTop: '光刻胶',
        StackBarc: '抗反射涂层',
        StackSubstrate: '衬底',
        StackResist: '光刻胶',
        StackRipple: '驻波周期',
        StackCoated: '已镀抗反射膜',
        StackAxisDepth: '进入光刻胶的深度 (nm)',
        StackLabelExact: '精确的薄膜叠层解',
        StackAxisThickness: '光刻胶厚度 (nm)',
        StackAxisDose: '光刻胶内平均剂量',
        StackLabelBare: '裸衬底上',
        StackLabelCoated: '带抗反射涂层',
        StackLabelHere: '求解器在该厚度的结果',
        StackThickness: '光刻胶厚度',
        StackBare: '裸衬底',
        StackWithBarc: '加抗反射涂层',
        StackVerdict:
          '求解器给出的明暗平面间距为 {{measured}} nm。两列反向穿过薄膜的波，其拍出的周期等于它们' +
          '在膜内波长的一半（沿深度方向量取）；对这层光刻胶、这个角度而言即 {{predicted}} nm。' +
          '注意它不是 {{normal}} nm：衍射级偏离垂直方向七十度入射，这个余弦占了答案的六成。' +
          '摆动曲线重复的正是同一个周期，因为二者是同一套物理——每增加半个膜内波长的光刻胶，' +
          '这个腔就重新回到同相。',
        StackThin:
          '这层膜太薄，容纳不下值得测量的驻波——里面几乎只有一个暗面。把厚度拖大，' +
          '周期才成为可以向求解器提问的量。',
        SectionFocus: '焦深从哪里来——第三束光',
        FocusNote:
          '上面第二节的场景让两束光相交，而两束光根本没有任何焦深依赖：沿光轴移动晶圆，两个衍射级获得的相位' +
          '完全相同，所以它们形成的图样处处一样。因此本面板上所有的 Bossung 曲线都另有来源，来源就是这里——' +
          '零级，即径直穿过、未被衍射的那部分光。它沿轴传播，而正负一级斜着传播，于是三者一路走一路失步，' +
          '它们合成的像先升起、跌到零，再带着明暗互换回来。注意看画布上的条纹在中途缩到原间距的一半：' +
          '那正是零级落后四分之一周期、场中只剩下两个一级的时刻。',
        FocusNoteDipole:
          '然后把零级去掉。曲线变平——不是变小，是变平——画面也一样。这就是离轴照明和偶极照明的全部理由：' +
          '用光瞳边缘的两束光构成的像没有焦深上限，因为没有第三束光可以与之失步。代价是普适性，' +
          '因为这两束光是为某一个周期摆放的，掩模上其余的图形都会印得更差。',
        FocusBest: '最佳焦面',
        FocusGone: '像已消失',
        FocusPitch: '周期',
        FocusThree: '三个衍射级',
        FocusTwo: '两个衍射级',
        FocusVanishes: '像消失于',
        FocusNoDof: '完全没有焦深依赖',
        FocusAxisDepth: '相对最佳焦面的深度 (nm)',
        FocusAxisImage: '印出周期上的像',
        FocusLabelExact: '精确离焦相位的余弦',
        FocusLabelParaxial: '教科书的近轴相位',
        FocusLabelHalf: '半周期分量',
        FocusAllThree: '三个衍射级全在',
        FocusJustTwo: '去掉零级（偶极）',
        FocusVerdictThree:
          '像在最佳焦面下方 {{null}} nm 处跌到零，再反相回来——原来是空的地方变成了线。这就是焦深：' +
          '不是变模糊，而是对比度在振荡，也正是上面面板用公式画出的 Bossung 曲线。注意虚线的近轴曲线' +
          '到盒子底部时已经和求解结果偏离多远；在水中七十度入射时，π z λ / P² 并不是离焦相位。',
        FocusVerdictTwo:
          '去掉零级后，曲线是一条压在零上的直线，下面的半周期分量也纹丝不动——盒子里每个深度上的像' +
          '都是同一个像。两束光完全没有焦深依赖，因为移动晶圆给它们两个添加的相位一模一样。' +
          '这就是偶极照明和其他离轴照明的全部论据：把传播方式不同的那一级丢掉，焦深就不再是约束，' +
          '代价是只有一个周期印得好，其余都印得差。',
        SectionMask: '线条终止的地方——二维的像',
        MaskNote:
          '上面所有的光学场景成的都是线条/间隔光栅的像，而那是一个一维物体：它沿横向变化，沿线条方向永远不变。' +
          '对分辨率极限来说这是对的对象，对掩模设计师每天真正面对的问题来说却是错的对象，' +
          '那个问题是：线条终止的地方会发生什么。线端会缩回。拐角拒绝印成方的。接触孔永远收不到' +
          '大开口能收到的光。这些在剖面里一个也看不到，因为它们都不是一维现象——所以这一节是一个曲面，而且可以转动。',
        MaskNoteWhy:
          '印出来的边缘，就是这个曲面在光刻胶显影剂量处的等高线，也就是横切它的那张薄片；' +
          '下面那个平的轮廓是掩模原本画的样子。两者之间的差距正是光学邻近效应修正花钱去弥合的东西，' +
          '而它无法用剂量弥合的原因就写在形状里：剂量只是把薄片抬高或压低，等于同时移动所有边缘，' +
          '可拐角和线端偏离的幅度不同、方向也不同。拖动离焦量，看曲面塌下去——线端最先失守，' +
          '因为它本来就是像里对比度余量最少的部分。',
        MaskDrag: '拖动可以转动它。平的轮廓是掩模画的样子；那张薄片是光刻胶显影的位置。',
        MaskEnds: '两个线端',
        MaskHoles: '接触孔',
        MaskElbow: '一个拐角',
        MaskDefocus: '离焦量',
        MaskSigma: '部分相干因子 σ',
        MaskReadEnds:
          '画出的间隙为 {{drawn}} nm；每个线端缩回 {{pull}} nm，于是间隙印成 {{printed}} nm——' +
          '比画的宽了 {{pct}}%。',
        MaskReadHoles:
          '画出的孔径为 110 nm；中心孔的峰值只有明场的 {{peak}}，印出 {{width}} nm。' +
          '孔永远收不到大开口能收到的光，这正是接触孔层要单独定剂量的原因。',
        MaskReadElbow:
          '臂部印出 {{width}} nm 宽，而画的是 100 nm；拐角则根本印不成方的：外角被磨圆，内角被填平。' +
          '改剂量解决不了，因为剂量是把所有边缘一起移动。',
        SectionPeb: '夹在中间的那一烘——一个模糊长度会对形状做什么（酸扩散）',
        PebNote:
          '上面的配方把曝光后烘烤压缩成了一个数字，一个扩散长度，而空间像模型也只用一行就把它卷积掉了。这里就是那个数字所代表的' +
          '烘烤：光照到哪里就在哪里生成的光酸，在热板加热期间在光刻胶里扩散开来。这幅图是**俯视图**——从上往下看晶圆，而不是' +
          '看它的剖面——因为只有这样才能看见一个模糊长度说不出口的东西。黄线是显影液将会停下的地方，它画在"长直边恰好印在' +
          '标称尺寸上"的那个水平。盯住那条直边：无论烘多久，它都不动。然后再看别的。肘形图案的外角向内变圆，内角向外被填平，' +
          '而那两个线端则彼此后退，直到它们之间的缝隙明显比画出来的更宽。',
        PebNoteWhy:
          '为什么是这三处而不是直边：一条被模糊掉的边，是"酸离开"与"酸补进来"之间的平衡，而沿着一条长直边，两者恰好相等。' +
          '在凸角处，曝光的一侧少了一个象限，于是走掉的比补进来的多，轮廓就往回缩。在凹角处正好相反，于是轮廓往外推。' +
          '在线端处，酸同时朝三个方向漏走，而一旦模糊长度可以与线宽相比，线端就撑不住而开始后退——这就是为什么这里的缝隙在' +
          '几个纳米的模糊下几乎不动，然后迅速张开。这些都不是剂量误差。剂量让所有的边一起移动，所以它能拿线宽去换间距，' +
          '却永远换不回一个角。这正是光学邻近效应修正存在的全部理由，也是它把衬线加在外角、把切口开在内角的原因。',
        PebNoteCaveats:
          '三条诚实的边界。这里的酸一开始就是掩模本身，边缘锐利得任何镜头都交付不出来，所以你看到的是烘烤自己的那份贡献，' +
          '而不是烘烤叠在光学之上——那一半由上面那一节负责。这里也没有显影：轮廓只是显影液将会停下的地方，而不是光刻胶正在被溶解。' +
          '还有，画面是 300 nm 铺在一百个格子上，所以低于大约三纳米的模糊比网格还细，画面就分辨不出来了——而那也差不多正是' +
          '真实烘烤的贡献在光学面前不再重要的地方。',
        PebBlur: '模糊',
        PebBake: '相当于',
        PebBakeAt: '秒、温度',
        PebCorner: '外角内缩',
        PebGap: '缝隙张开',
        PebOver: '已烘过头，超出配方',
        PebVerdict:
          '在 {{blur}} nm 的模糊下，直边一点也没动，而其他地方全动了：外角向内圆了 {{corner}} nm，内角向外填了 {{notch}} nm，' +
          '两个线端之间的缝隙张开了 {{gap}} nm。改剂量会让所有的边一起移动，其中任何一样都撤不回来。这就是光学邻近效应修正的' +
          '全部理由——而落到热板本身上，这也是烘烤要按零点几度来规定的原因：活化能接近一个电子伏时，一度大约相当于模糊量的 ' +
          '{{perDegree}}%。',
        AerialNote: '上面那条曲线是落在晶圆上的光，由镜头能接住的那几阶衍射级合成。下面那条是曝后烘烤之后剩下的酸。线条就是显影后活下来的部分，它的宽度就是 CD。',
        SectionBossung: 'Bossung 曲线——CD 随焦距变化，每条线一个剂量',
        BossungNote: '光刻工程师赖以为生的一张图。曲线平坦，说明焦距影响不大；曲线扇形张开，说明剂量与焦距互相牵扯，而可用窗口就是它们同时都还落在 CD 公差内的那一块。',
        MeasureBossung: '量测 Bossung 曲线',
        SectionWindow: '焦距—曝光窗口',
        WindowNote: '对每一个焦距，给出仍能印在规格内的剂量范围。晶圆从来不是绝对平的，扫描机也从来不是绝对稳的，所以一个制程只有在这块区域宽到能同时吸收这两者时才算可用。',
        MeasureWindow: '量测窗口',
        SectionStochastics: '随机效应——同一件事印 300 次',
        PhotonCurveEuv: 'EUV 光刻机，92 eV 光子',
        PhotonCurveArf: 'ArF 光刻机，6.4 eV 光子',
        PhotonCurveSame: '用 ArF 光子构成的 EUV 像',
        PhotonCurveHere: '这片晶圆',
        PhotonChartTitle: '边缘粗糙度随剂量的变化',
        PhotonChartX: '剂量 (mJ/cm²)',
        PhotonChartY: '线边缘粗糙度，3σ (nm)',
        PhotonStatus: '{{energy}} eV 光子 · 每 nm² {{perNm2}} 个，每像素 {{perPixel}} 个 · LER {{ler}} nm · 线宽 {{width}} nm · {{defects}} 行有缺陷',
        PhotonPicImage: '透镜成的像',
        PhotonPicCounts: '到达的光子',
        PhotonPicPrint: '显影结果，叠加设计边缘',
        PhotonSource: '用谁的光子',
        PhotonChoiceEuv: 'EUV 光刻机',
        PhotonChoiceArf: 'ArF 浸没式光刻机',
        PhotonChoiceSame: 'EUV 的像，ArF 的光子',
        PhotonDose: '剂量',
        PhotonBlur: '光刻胶作用距离（酸、二次电子）',
        PhotonReroll: '再曝光一片晶圆',
        SectionPhotons: '像是由什么构成的——被计数的光子（空间像上的泊松统计）',
        PhotonsNote: '这块面板上的每一幅像都是平均值。光刻胶从来得不到平均值：它得到的是有限个光子，每一个要么落在这里要么不在这里，而落在决定一条边缘的那几个纳米里的数目，是一次均值为 N、涨落为 √N 的泊松抽样。剂量是能量，一个 92 eV 的 EUV 光子携带的能量是 6.4 eV ArF 光子的十四倍，所以同样的 mJ/cm² 剂量到达时光子数少了十四倍。这里由面板自己的傅里叶成像器生成 120 nm 周期、60 nm 线条的空间像，再逐像素按带种子的泊松计数交付，计数经光刻胶自身的作用距离模糊后按常规阈值显影，最后逐行测量印出的边缘。粗糙度随剂量的平方根下降。光刻机对光刻机比较，在这个周期下 ArF 的边缘反而更粗糙，因为它的透镜给出的边缘更平缓，而斜率补回了光子损失的部分——所以用虚线把光子单独分离出来：同一幅 EUV 像若由 ArF 光子构成，会平滑 √14 倍。这里计数的是入射光子；吸收率和酸产额会进一步削减有效计数，因此真实的 EUV 情形比所示更糟。',
        StochasticsNote: '下面每一次曝光用的都是完全相同的设定。剂量是能量，但曝显本质上是在数数：落进每个图形的光子数是有限的，尺寸一小，这个计数的涨落就足以把边缘推动。这个散布就是良率的上限。',
        RunExposures: '跑 300 次曝光',
        SectionRecipe: '曝光配方',
        DoseToSize: '调剂量到目标尺寸',
        SectionStored: '已存配方',
        SectionResist: '光刻胶为什么响应这个波长——光子把电子抬起来（PPP-CIS）',
        ResistChartTitle: '分子的吸收，与曝光所用的谱线对照',
        ResistChartX: '波长 (nm)',
        ResistChartY: '吸光度，相对值',
        ResistStatus: '{{formula}}，{{bonds}} 个双键 · 最强吸收在 {{lambda}} nm · 响应 {{lines}}',
        ResistBlind: '都不响应——这个分子对每一条谱线都是透明的',
        ResistConjugation: '共轭长度',
        ResistBonds: '{{n}} 个双键 · {{formula}}',
        ResistFailed: '分子光谱引擎加载失败。',
        ResistLoading: '正在加载分子光谱引擎…',
        ResistNote: '上面的一切都是光学：收进的衍射级、形成的像、给出的剂量。而这一切都默认了唯一不属于光学的那一步——光子到达时，光刻胶要有反应。它的反应是把一个电子从分子最高占据轨道抬到最低空轨道，而分子只能吸收能量与该间隙相匹配的光子。波长太红，光就直接穿过一层与玻璃无异的膜。这个间隙由电子能扩展多远决定：共轭链中的 π 电子就是一个盒中粒子，盒长即共轭长度，盒子越长能级越密。把链拉长，看吸收带如何从深紫外一路走进可见光，依次越过每一条曝光谱线。这正是为 365 nm i 线设计的增感剂在 193 nm 完全透明、毫无用处的原因，也是每一次向更短波长迈进都意味着全新光刻胶化学而不只是换一盏灯的原因。',
        ResistWhatNote: '这些并不是光刻胶。它们是全反式多烯——单双键交替的直碳链——而真正的光刻胶是含重氮萘醌增感剂的酚醛树脂，或是含光酸产生剂的丙烯酸酯：都是本模型不涵盖其化学的大分子。多烯序列精确承载的，是本面板所需的那个关系：共轭长度与吸收波长，而且是以最干净的形式呈现。请注意没有任何一条链落在 193 nm 的 ArF 上：短链吸收在其下，长链在其上——这恰好说明了为什么 193 nm 需要另一族发色团，而不是把旧的做短一点。引擎是 AIMS 的 PPP-CIS，它把乙烯定在 161 nm，实测值为 165 nm。',
        SectionShift: '值班',
        TilePrintedCd: '曝显 CD',
        DidNotPrint: '没有印出来',
        NoteTarget: '目标 {{nm}} nm',
        TileCdError: 'CD 误差',
        NoteSpec: '规格 ±10%',
        K1BelowLimit: '低于物理极限',
        K1Aggressive: '偏激进',
        K1Comfortable: '较宽裕',
        TileContrast: '成像对比度',
        NoteNils: '边缘处的像对数斜率',
        TileLer: '线边缘粗糙度',
        TilePhotons: '送达光子数',
        TilePhotonEnergy: '光子能量',
        TileWavelengthNa: '波长 / NA',
        NotMeasured: '尚未量测',
        ChartAerialTitle: '落在晶圆上的光，以及它留下的酸',
        ChartPosition: '跨越一个节距的位置（nm）',
        ChartNormalised: '归一化',
        SeriesAerial: '空间像',
        SeriesAcid: '烘烤后的酸',
        AnnDevelop: '显影阈值',
        ChartBossungTitle: '不同剂量下 CD 随焦距的变化',
        ChartFocusOffset: '离焦量（nm）',
        ChartCd: 'CD（nm）',
        AnnTarget: '目标',
        ChartWindowTitle: '剂量宽容度随焦距变化——DOF {{dof}} nm',
        ChartDoseLatitude: '剂量宽容度（%）',
        SeriesDoseLatitude: '剂量宽容度',
        AnnNeeded: '量产至少需要 5%',
        ChartStochTitle: '300 次完全相同的曝光——sigma {{sigma}} nm',
        ChartStochFailures: '，其中 {{n}} 次彻底没印出来',
        ChartPrintedCd: '曝显 CD（nm）',
        ChartCount: '计数',
        SeriesExposures: '曝光次数',
        AssistantBlurb: '提出问题，我会设计一个实验，用这台求解器把它跑出来，并报告真正得到的结果——然后再还你一个更锋利的问题。',
        AssistantLead: '真正抵达晶圆的只有两个数：剂量和焦距。它们都不直接决定 CD。问我它们是怎么走到那一步的。',
      },
      clean: {
        TransportSurface: '晶圆表面',
        TransportStarved: '亏缺',
        SectionTransport: '一槽药液可能短缺的是什么——串联的两个阻力（质量传输）',
        TransportCaveat: '这是**一般情形，不是这一槽**，而这个区别值得说准确。SC-1 中过氧化氢与氢氧化物的浓度大约是摩尔量级，' +
          '而表面每分钟只消耗几个纳米，供给比需求高出好几个数量级，真实的 SC-1 槽并不会缺料。上面那个区间来自一个特意选取的速率常数，' +
          '它把反应与输运放在同一量级上，而课程正活在那里——并且这也是真实湿法工艺确实会进入的区间：稀释或用旧的槽液、快速刻蚀、' +
          '以及深结构的底部。它同样**不是**换能器存在的理由。兆声波是靠**机械**方式把颗粒取下来的：声流不断拖拽颗粒，' +
          '直到拖曳力胜过按住它的范德华力，空化在旁边帮忙。那是一种"脱附"机制，而不是"输送"机制。这样一来，上面配方里把热因子' +
          '乘以兆声因子的算法依旧是错的——热和声不是同一个旋钮的两档——只不过错的理由是这一条，而不是这幅图所展示的那条。',
        TransportNote: '化学品必须先到达晶圆才谈得上起作用，而这里晶圆消耗它的速度快于静止槽液的补给 — 于是表面之上形成一层供应不足的区域，这里的颜色就是实际存在的氧化剂 ' +
          '有多少。搅动槽液，这层就变薄，表面重新被补满。现在把两个旋钮对着比一比。在静止槽中，把温度从 70 升到 85 ' +
          '°C，反应速率涨了一倍多，但去除量只多了约四分之一，因为表面只是被榨得更干。让槽液流动起来再做同样一步，回报是三倍。而在冷槽中，搅动完全买不到任何东西 — ' +
          '瓶颈在化学反应，搅它是在修错地方。串联的两个阻力，值得攻的永远只有较大的那一个。',
        ActionRunTheCleanAnd: '运行清洗，把晶圆传下去',
        NoteNoLotInTheBench: '清洗台上没有批次。把一片晶圆拿到这里来清洗。',
        SecondaryLabel: '表面金属',
        ToolLabel: 'CLN-09 RCA 湿法清洗台',
        NextTheRinseWaterIsA: '漂洗水低于约 15 MΩ·cm 时本身也是污染源。如果槽液化学没问题而晶圆还是脏着出来，你会先查哪里？',
        AxisY10AtomsCm: '×10¹⁰ 原子/cm²',
        AxisXSc2TimeMin: 'SC-2 时间 (min)',
        TitleMetalsAndParticlesAgainstSc: '金属与颗粒随 SC-2 时间的变化',
        NoteSc1SJob: 'SC-1 的活',
        LabelMetalsAsSet: '金属，当前设置',
        LabelMetalsNoSc2: '金属，无 SC-2',
        HypTheTwoBathsHaveOpposite: '两个槽的化学性质相反，原因也相反。SC-1 是碱性的，靠底切把颗粒抬起来；SC-2 是酸性的，溶解碱性化学留在表面上的金属。如果这是对的，SC-2 ' +
          '时间应该移动金属数值而几乎不动颗粒数值，去掉 SC-1 则应相反。',
        AskedWhatDoesTheSecondBath: '第二个槽去除的是什么？第一个槽为什么去不掉？',
        SumMetalsVersusParticlesAndWhy: '金属对颗粒，以及为什么一个槽干不了两件事。',
        NextThrm04GrowsAGate: 'THRM-04 要在这个表面上长栅氧化层。在脏台面上、空气里长出来的额外半纳米自然氧化层，会对一个假定从裸硅出发的 Deal-Grove 生长做什么？',
        AxisYOxideNm: '氧化层 (nm)',
        AxisXMinutesAfterHf: 'HF 后分钟数',
        TitleNativeOxideRegrowthAgainstQueue: '自然氧化层再生长随排队时间的变化',
        LabelOxideImmediate: '氧化层，立即',
        HypHfStripsTheNativeOxide: 'HF ' +
          '剥掉自然氧化层，让表面以氢终结。空气立刻开始把氧化层放回去，而且是对数式的——表面活性高时很快，随后爬行。如果是这样，排队的代价大部分花在头半小时里，等四小时只比等一小时差一点点。',
        AskedWhatDoesTheWaitBetween: '从这个清洗台到下一台设备之间的等待要付出什么代价？',
        SumNativeOxideRegrowthAgainstThe: '自然氧化层随排队时钟的再生长，以及为什么 HF 会启动这个时钟。',
        NextTheFloorThisCurveFlattens: '这条曲线趋平的下限不是零——是漂洗和干燥重新放回去的量。问我 HF 浸之后会发生什么，那里的情况要糟糕得多。',
        AxisYNmRms: 'nm RMS',
        TitleAndTheRoughnessItCost: '以及它付出的粗糙度',
        AxisYParticlesCm: '颗粒/cm²',
        AxisXSc1TimeMin: 'SC-1 时间 (min)',
        TitleParticlesRemainingAgainstSc1: '残留颗粒随 SC-1 时间的变化',
        LabelRoughness: '粗糙度',
        LabelParticles: '颗粒',
        AskedDoesMoreBathTimeKeep: '更长的槽浸时间会让晶圆一直更干净吗？',
        SumDiminishingReturnsOnBathTime: '槽浸时间的收益递减，以及为此付出的粗糙度。',
        fault: {
          poorRinse: {
            Evidence: [
              '冲洗水电阻率 11 MΩ·cm，下限 17。',
              '颗粒检查和金属检查都超差。',
              '两个槽的时龄和换能器都正常。',
            ],
            Truth: 'DI 水厂的抛光柱需要再生，冲洗水只有 11 ' +
              'MΩ·cm。冲洗是晶圆看到的最后一道，它带着什么，就在两种化学都完美干完活之后又放回什么。颗粒和金属同时挂掉，是任何单个槽都做不出来的特征——而且维修根本不在这台设备上。',
            Complaint: '这张台子上颗粒和金属双双超差。两个槽都是新的，兆声也检查无误。',
            Label: '冲洗水超差',
          },
          deadTransducer: {
            Evidence: [
              '兆声实际输出 30%，下限是 85%。',
              '监控片上的去除率低于 98%。',
              '两个槽的时龄都正常——这不是化学的问题。',
            ],
            Truth: '兆声换能器只输出指令功率的 30%。兆声的作用是加速被掀起的颗粒离开表面，没有它，化学照常起作用，颗粒又落了回去。新槽液加上差去除，合在一起就是特征。',
            Complaint: '颗粒去除变差了，而两个槽今早刚重配过。',
            Label: '兆声换能器失效中',
          },
          spentSc2: {
            Evidence: [
              'SC-2 槽液时龄超寿命。',
              'VPD-ICPMS 表面金属远超 5 ×10¹⁰ 的限值。',
              '颗粒去除完全正常。',
            ],
            Truth: 'SC-2 ' +
              '槽液失效了，金属没有被去除。晶圆看上去是干净的——光学检测仪看不见单层的铜——它们就这样把铜直接带进炉管，铜在那里扩散开来，杀死氧化层。这是全厂每一种便宜的测量都看不见的失效。',
            Complaint: '某个产品的栅氧完整性掉下来了。颗粒计数完全正常，没有人找得到缺陷。',
            Label: 'SC-2 槽液失效',
          },
          spentSc1: {
            Evidence: [
              'SC-1 槽液时龄超寿命。',
              '监控片显示去除率远低于 98%。',
              '表面金属正常，SC-2 在干它的活——坏的是一种化学，不是两种。',
            ],
            Truth: 'SC-1 里的过氧化氢分解了——槽液已经 34 小时，寿命是 16 ' +
              '小时。活性槽里的去除是一阶过程，失效的槽几乎不干活，颗粒径直穿了过去。设备看不出任何异样，因为一槽透明液体从来看不出任何异样。',
            Complaint: '凡是过这张台子的货，到 INSP-13 缺陷密度都翻倍了。台子自己没报警，看起来跟平时一模一样。',
            Label: 'SC-1 槽液失效',
          },
          clnHealthy: {
            Evidence: [
              '两个槽都是新配的。',
              '颗粒去除和表面金属都在带内。',
            ],
            Truth: '什么问题都没有。正确的判定就是放行这一批。',
            Complaint: '例行班次。没有针对这张清洗台的任何报告。',
            Label: '一切正常',
          },
        },
        NoteMinAtC: '{{b}} °C 下 {{a}} 分钟',
        NoteFromCmIncoming: '来料 {{a}}/cm²',
        SectionTwoThingsThisBenchIs: '这张清洗台在交换的两样东西',
        NoteParticleRemovalIsFirstOrder: '颗粒去除是一阶过程，所以最后那几颗颗粒最费时间——而每多一分钟热 SC-1 都在腐蚀硅、让表面变粗糙。然后，如果你用 HF ' +
          '把氧化层剥掉，一只时钟就开始走了：自然氧化层在头二十分钟里长回来得最快，所以排队时间是一条规格，不是一句客套。',
        NoteThisLotIsStillAn: '这批货还是一根晶棒。得先由 WPR-08 切片，这台设备才碰得了它。',
        SectionBenchState: '清洗台状态',
        LabelParticlesLeft: '剩余颗粒',
        LabelRemovalEfficiency: '去除效率',
        LabelMetalRemaining: '残留金属',
        Note5BeforeAFurnace: '进炉前需 < 5',
        LabelSurfaceRoughness: '表面粗糙度',
        NoteSc1EtchesAndEtching: 'SC-1 会腐蚀，腐蚀就会变粗糙',
        LabelOxideAtTheNextStep: '到下一步时的氧化层',
        LabelHfBenefitLost: 'HF 效果损失',
        AssistantLead: 'CLN-09 探究助手',
        AssistantBlurb: '可以问：延长槽内时间有没有用、HF 之后的排队时间代价是什么、第二道槽是干什么的。每个回答都是在这台清洗台上真实跑出来的一次清洗。',
        reading: {
          houseutilities: {
            Label: '致电厂务查询公用系统面板（0.5 h）',
          },
          metalCheck: {
            Label: 'VPD-ICPMS 表面金属——破坏性（4.0 小时）',
          },
          particleCheck: {
            Limit: '去除 > 98%',
            Label: '监控片清洗前后颗粒计数（1.5 小时）',
          },
          resistivity: {
            Label: '冲洗水电阻率',
          },
          transducer: {
            Label: '兆声实际输出功率',
          },
          sc2Age: {
            Label: 'SC-2 槽液时龄',
          },
          sc1Age: {
            Label: 'SC-1 槽液时龄',
          },
        },
        aspect: {
          house: {
            Action: '上报厂务——超纯水厂的抛光混床需要再生',
            Label: '冲洗水供给——不在本机',
          },
          megasonic: {
            Action: '更换换能器并重测槽内声场分布',
            Label: '兆声换能器',
          },
          sc2: {
            Action: '倒槽重配 SC-2',
            Label: 'SC-2 槽',
          },
          sc1: {
            Action: '倒槽重配 SC-1，并缩短槽液寿命间隔',
            Label: 'SC-1 槽',
          },
        },
      },
      atomView: {
        Atom: '原子',
        Coordinates: '坐标',
        AtomicMass: '质量',
        AtomicRadius: '半径',
        ElectricCharge: '电荷',
        AutoRotate: '自动旋转',
        Axes: '显示坐标轴',
        Container: '显示容器',
        Fog: '有雾',
        ViewDirection: '观察方向',
        PositiveX: '正X轴方向',
        NegativeX: '负X轴方向',
        PositiveY: '正Y轴方向',
        NegativeY: '负Y轴方向',
        PositiveZ: '正Z轴方向',
        NegativeZ: '负Z轴方向',
        Mechanics: '力学',
        KineticEnergyColors: '动能着色',
        MomentumVectors: '显示动量矢量',
        ForceVectors: '显示力矢量',
        GlobalStyle: '全局风格',
        BallAndStick: '球和棍',
        Wireframe: '线框架',
        Stick: '棍',
        SpaceFilling: '空间填满',
        Material: '材质',
        Diffuse: '散射',
        Soft: '柔软',
        Glossy: '光泽',
        Metal: '金属',
        Translucent: '半透明',
        Glass: '玻璃',
        Toon: '卡通',
        Flat: '平均',
        BackgroundColor: '背景颜色',
        TakeScreenshot: '截屏',
      },
      e2d: {
        ViewProperties: '视图属性……',
        Properties: '属性……',
        ThisPart: '此部件',
        ModelProperties: '模型属性……',
        Loading: '正在加载仿真引擎……',
        Failed: '仿真引擎加载失败。',
        Run: '运行',
        Pause: '暂停',
        Reset: '重置',
      },
      implant: {
        BeamWafer: '晶圆',
        BeamSpot: '最高温',
        BeamClamp: '夹持散热',
        SectionBeamHeat: '束流把什么加热了 — 夹持台上的晶圆（传热）',
        BeamHeatNote: '束流功率就是电压乘电流：30 keV、10 mA 即 300 W ' +
          '全部落在一片晶圆上。这里展示的就是这份功率进入晶圆、带状束以每秒一米扫过，以及静电夹盘通过晶圆背面的氦气把热量抽走。有三点值得看。晶圆只比夹持台高出几度就稳住了 — ' +
          '决定温度的是夹持散热而不是束流；一旦背面氦气失效，同样的束流会把晶圆推过 300 °C，光刻胶随之龟裂。束流电流同时买来产能和热量。还有，扫描的带状束并不会留下一道灼痕：硅的导热太好了 ' +
          '— 而这正是上面的面板可以老老实实只用一个晶圆温度、而不必用一张温度分布图的原因。',
        DamageFurnace: '退火炉温',
        DamageReset: '重置',
        DamagePause: '暂停',
        DamageRun: '运行',
        DamageFailed: '分子动力学引擎加载失败。',
        DamageLoading: '正在加载分子动力学引擎……',
        DamageHighlight: '突出显示缺陷',
        DamageChartTitle: '残留缺陷随时间变化——退火',
        DamageChartX: '模拟时间 (ps)',
        DamageChartY: '缺陷数（空位 + 填隙）',
        DamageChartSeries: '残留缺陷',
        DamageChartAsImplanted: '注入后初始值',
        RdfTitle: '径向分布函数 g(r) —— 把晶体画成曲线',
        RdfX: '原子对间距 r（Å）',
        RdfY: 'g(r)',
        RdfSeries: '当前 g(r)',
        RdfUncorrelated: 'g = 1，无结构',
        RdfStatus: '峰位于 {{a}} Å 与 {{b}} Å（理想值 2.35 与 3.84）· 二者之间 g(3.0 Å) = {{c}} —— 晶体中为零，碎晶中被填满',
        RdfNote: '把同一批原子的所有原子对间距做成直方图，并归一化使无关联气体读数为 1。晶体是落在标出的配位壳层上的尖峰，壳层之间空无一物；加热会让峰变宽，而损伤做的是另一件事——它把间隙填满，让第二、第三壳层糊成一片。把上方的剂量从零拖到上限，看曲线从梳齿变成土包：这就是衍射仪眼中晶态与非晶态的区别。曲线实时更新，退火重新锐化某个峰时这里也能看到。',
        DamageStatus: '{{a}} 个空位 · {{b}} 个填隙原子 · 束流损伤为非晶化剂量的 {{c}}% · {{d}} K',
        DamageNote: '每一个停留在晶圆中的离子，在进入的路上都会把硅原子撞离晶格位。每个被撞出的原子留下一个空位——红色笼形标记，代表空缺的晶格位——自己则停在晶格间隙成为填隙原子，以黄色显示。这一对就是弗兰克尔缺陷对；数量足够多时就是所谓的"非晶化"：整齐的金刚石秩序变成一片废墟。这个晶格由上方配方驱动，因此提高剂量或换用更重的离子，就能看到损伤攀升；再提高晶圆温度，损伤又会下降，因为热注入会边注入边自我修复。晶胞只有 ' +
          '216 个原子，所以损伤是按比例放大以便看见，而非字面数值。',
        CarriersTitle: '载流子随温度的变化',
        CarriersX: '温度（K）',
        CarriersY: 'log₁₀ nm⁻³',
        CarriersSeries: '载流子',
        CarriersIntrinsic: '本征硅',
        CarriersKnee: '全部释放',
        CarriersCrossover: '硅本身占主导',
        CarriersFailed: '载流子模型不适用于该掺杂浓度。',
        CarriersLoading: '正在求解载流子统计……',
        CarriersAtRoom: '300 K 时有 {{pct}}% 在贡献载流子',
        CarriersKneeAt: '到 {{k}} K 全部释放',
        CarriersCrossAt: '{{k}} K 起硅本身占主导',
        CarriersDoping: '掺杂 1e{{exp}} nm⁻³',
        NoteCarriers: '掺杂原子占据了晶格位置，还不等于就有了载流子。晶格旁边这条曲线是 AIMS 在用费米–狄拉克统计占据杂质能级、并求解电荷中性得到的：温度低时载流子会重新落回杂质能级，浓度急剧塌陷——这就是为什么在液氮里量到的电阻率和室温规格完全不同。温度升高后每个杂质都释放载流子，平台高度就是你掺进去的浓度；正是这段平坦区间，才使掺杂成为可用的设计手段。温度再高，硅自身的电子被激发越过带隙，材料便不再理会你注入了什么——这就是结温的上限。滑块调的是阱区或衬底的掺杂，不是这次注入的峰值浓度——后者要高出大约两个数量级。超过 10¹⁸ cm⁻³ 之后，杂质能级已并入能带，模型的非简并统计不再成立，所以滑块就停在这里，而不去画一条自信而错误的曲线；而这块面板刚刚做出的那次注入，正落在这个范围之外。',
        SectionDamage: '离子束造成的破坏——晶格损伤（分子动力学）',
        LatticeHighlight: '突出显示掺杂原子',
        LatticeStatus: '{{a}}：{{c}} 个原子中有 {{b}} 个掺杂原子 · 实测 {{d}} K',
        LatticeConc: '掺杂浓度（已夸大）',
        TraceTitle: '离晶格位点的距离 —— 热振动',
        TraceX: '模拟时间（ps）',
        TraceY: '位移（Å）',
        TraceDopant: '位点上的 {{a}}',
        TraceHost: '基体 Si 原子',
        TraceSiRef: 'Si 在 300 K 的均方根位移（X 射线 Debye–Waller）',
        TraceStatus: '最近 {{a}} ps 内离位点的均方根位移：{{b}} {{c}} Å · Si {{d}} Å · 键长 2.35 Å',
        TraceIdle: '按下运行：离位点的均方根距离在运行的最近 {{a}} ps 内测得',
        LatticeReset: '重置',
        LatticePause: '暂停',
        LatticeRun: '运行',
        LatticeFailed: '分子动力学引擎加载失败。',
        LatticeLoading: '正在加载分子动力学引擎……',
        DopantBohr: 'a*',
        DopantMeanR: '⟨r⟩',
        DopantHalfSpacing: '到下一个掺杂原子的一半距离',
        DopantOrbitTitle: '多出来的那个电子在哪里',
        DopantOrbitX: '距掺杂原子的距离 (nm)',
        DopantOrbitY: '径向密度 4πr²|ψ|²，峰值 = 1',
        DopantOrbitSeries: '被束缚的电子',
        DopantOrbitNone: '没有束缚态——电子是自由的',
        DopantModelNc: '模型：原子消失',
        DopantMeasuredNc: '实测 Si:P 转变点',
        DopantMeasuredE: '{{dopant}} 的实测值',
        DopantBindingTitle: '束缚有多紧，随掺杂浓度的变化',
        DopantBindingX: '掺杂浓度的 log₁₀ (cm⁻³)',
        DopantBindingY: '束缚能 (meV)',
        DopantBindingSeries: '计算值，含屏蔽',
        DopantBindingHere: '当前配方',
        DopantFailed: '掺杂原子求解器加载失败。',
        DopantShort: 'a* = {{bohr}} nm · 此处束缚能 {{bind}} meV · 在 {{nc}} cm⁻³ 时失去束缚',
        DopantHelp: '这个原子的全部数值',
        DopantStatus: 'a* = {{bohr}} nm · Ry* = {{ry}} meV（{{dopant}} 实测：{{measured}}）· 此处束缚能 {{bind}} meV · 德拜长度 {{debye}} nm，下一个掺杂原子在 {{spacing}} nm 之外 · 在 {{nc}} cm⁻³ 处消失',
        DopantStatusGone: 'a* = {{bohr}} nm · 没有束缚态：德拜长度为 {{debye}} nm，下一个掺杂原子在 {{spacing}} nm 之外 · 原子在 {{nc}} cm⁻³ 处已消失',
        DopantLoading: '正在加载掺杂原子求解器…',
        DopantConc: '掺杂浓度',
        DopantHost: '基体与载流子',
        DopantHostSiDonor: '硅，施主 (P)',
        DopantHostSiAcceptor: '硅，受主 (B)',
        DopantHostGe: '锗，施主',
        DopantHostGaAs: '砷化镓，施主',
        SectionDopant: '掺杂原子是什么——硅里的一个氢原子（薛定谔方程，径向）',
        DopantNote: '上面硅位置上的那个磷原子比这个位置需要的多带一个电子，而这个电子并不自由：磷核比邻居多一个正电荷，电子绕着它运动，就像电子绕质子运动。这是一个氢原子，只是吸引力被晶体的介电常数除过，电子带的是有效质量——于是轨道扩大到约 2.4 nm，束缚能缩小到几十 meV，这正是掺杂原子在室温下电离、在液氮里冻结的原因。径向薛定谔方程在网格上求解，解析解只用来核对。右图没有解析解：每个电离的掺杂原子都留下一个自由载流子，自由载流子会屏蔽，超过几个 10¹⁸ 每立方厘米之后，势阱浅得根本容不下束缚态。此时硅成了金属——这就是莫特转变，与 Si:P 实测值相差约 10%。模型是各向同性的，所以硅中施主算出 26 meV，而实测为 45；其余部分由中心元胞修正补上。屏蔽采用德拜模型，300 K。',
        Orbital3dHostSi: '硅',
        Orbital3dHostGe: '锗',
        Orbital3dHostGaAs: '砷化镓',
        Orbital3dFailed: '轨道生成器加载失败。',
        Orbital3dLoading: '正在生成轨道…',
        Orbital3dLegend: '束缚电子的 |ψ|² · 亮度 ∝ √密度，以其自身中心为基准 · 屏蔽使中心密度降低时云随之变淡，但不低于 30% · 两个掺杂原子的电子重叠处显示为琥珀色',
        Orbital3dClipped: '电子只有 {{inside}}% 落在所画的最大盒子之内（±{{half}} nm；⟨r⟩ = {{mean}} nm），因此云在边缘处被截断显示。',
        DopantWallHeld: '勉强束缚：此时的束缚能已小于求解器自身 {{box}} a* 盒子的限制能，⟨r⟩ 已扩展到 {{mean}} a*。盒子的边界与掺杂原子一样在束缚这个电子，因此这个束缚能应视为模型已到极限，而不是原子本身的性质。',
        Orbital3dUnbound: '没有束缚态',
        Orbital3dCaption: 'a* = {{bohr}} nm（{{bohrCells}} 个晶胞）· ⟨r⟩ = {{mean}} nm（{{meanCells}} 个晶胞）· 掺杂原子平均间距 {{spacing}} nm（{{spacingCells}} 个晶胞）',
        Orbital3dCaptionGone: 'a* = {{bohr}} nm · 掺杂原子平均间距 {{spacing}} nm（{{spacingCells}} 个晶胞）· 没有束缚态：电子不再属于任何一个掺杂原子',
        Orbital3dLattice: '{{host}}晶体画成穿过掺杂原子的两层相邻原子面，共 {{count}} 个原子，范围到 ±{{half}} nm：这是一片薄层，而不是整块晶体；整个盒子里的晶体会有 {{full}} 个原子。底边上的绿色标尺长一个 a*。',
        Orbital3dNeighbours: '盒子里有 {{k}} 个相邻掺杂原子，位于平均间距 {{spacing}} nm 处。它们的电子云与中心那团相遇的地方显示为琥珀色：电子在那里可以从一个掺杂原子跳到另一个，这种重叠就是莫特转变。',
        Orbital3dNoNeighbours: '下一个掺杂原子在 {{spacing}} nm 之外，不在这个盒子里：在这个掺杂浓度下，每个电子都独占自己的掺杂原子。',
        LatticeNote: '离子注入后的晶圆在原子尺度的样子：金刚石立方硅晶格中，配方所选的掺杂剂以替位方式占据硅位，在 ' +
          'Stillinger–Weber 力场下实时振动。"n ' +
          '型"并不神秘，就是这些原子：一个施主占据一个晶格位，多出一个价电子。在上方配方中更换注入种类，晶格会重新掺杂（BF₂ ' +
          '注入的是硼，氟在退火时离开）。浓度滑块被刻意夸大——真实掺杂是百万分之一量级，在这么小的晶胞里根本看不见。左键拖动旋转，滚轮缩放，右键拖动平移。',
        SectionLattice: '掺杂的本质——晶格（分子动力学）',
        ActionImplantAndPassThe: '注入后把晶圆传给炉子',
        UnitPctOnTarget: '%（相对目标）',
        NoteNoLotOnTheEndstation: '终端台上没有批次。把一片带掩模的晶圆拿到这里来注入。',
        NoteCrystalline: '晶态',
        NoteAmorphisedTheAnnealIs: '已非晶化——退火不是可选项',
        NoteThePanelAboveRuns: '上面的面板跑的是四条拟合公式：射程用幂律，离散取它的一个比例，沟道化比例用倾角高斯，非晶化用剂量阈值。下面，同一次注入被求解——三千个离子在晶体中逐次碰撞地追踪。在解析式被拟合过的区间，两者一致；离开那里就不一致，而这个差距正是要造求解器的理由。',
        NoteTheCriticalAngleIs: '对这种离子在这个能量下，临界角是 ' +
          '{{a}}°，而且其中没有任何拟合参数——它直接由晶格常数和两个原子序数导出。倾角设在它以内，束流就沟道化；设在它以外就不会。上面的公式对所有种类、所有能量只有一条倾角曲线，所以它没法告诉你：砷在七度时仍会沟道化，而硼不会。',
        ValueThresholdAsOneNumber: '阈值的 {{a}} 倍，只有一个数',
        ValueVacanciesPerIon: '每离子 {{a}} 个空位，以分布给出',
        MetricDamage: '损伤',
        ValueZeroGaussian: '0（高斯）',
        MetricDistributionSkew: '分布偏度',
        ValueNoSuchQuantity: '无此量',
        MetricCriticalAngle: '临界角',
        MetricChannelled: '沟道化比例',
        MetricStraggle: '射程离散',
        MetricProjectedRange: '投影射程',
        LegendTheFittedFormula: '拟合公式',
        LegendIonsTraced: '追踪 {{a}} 个离子',
        LabelPeakConcentration: '峰值浓度',
        SecondaryLabel: '剂量非均匀性',
        ToolLabel: 'IMP-10 大束流离子注入机',
        NextBothOfThoseProfilesGet: '这两种分布都要经过退火。炉子对哪一种改变更大——这让浅注入比这里看起来更好还是更糟？',
        AxisXEnergyKev: '能量 (keV)',
        TitleProjectedRangeAgainstEnergy: '投影射程随能量的变化',
        LabelStraggleDeep: '离散，深注入',
        LabelStraggleShallow: '离散，浅注入',
        HypEnergyBuysDepthButThe: '能量买到深度。但分布的展宽并不按比例缩小，所以浅注入相对而言应比深注入更宽——射程越小，离散占射程的比例越大。如果这成立，"更浅"和"更陡"就不是同一个要求。',
        AskedWhatSetsHowDeepThe: '是什么决定掺杂进多深？做浅要付出什么？',
        SumEnergySetsDepthAndDose: '能量决定深度，剂量决定浓度——两者不可互换。',
        NextThrm04HasToAnneal: 'THRM-04 得把这些损伤退火掉，而这次退火花的是与栅氧化层同一份热预算。问问炉子：修复这些损伤要让你刚放好的结付出什么。',
        LabelDamageRatio: '损伤比',
        AxisYDamageAmorphisationThreshold: '损伤 / 非晶化阈值',
        AxisXDoseCm: '剂量 (nm⁻²)',
        TitleLatticeDamageAgainstDose: '晶格损伤随剂量的变化',
        LabelUniformity: '均匀性',
        LabelDamageRatioL226: '损伤比',
        HypEveryIonKnocksLatticeAtoms: '每个离子在进入时都把晶格原子撞离原位。累积密度低于某个值时，晶体能在级联之间自我修复；高于它，损伤彼此重叠，整层完全非晶化。如果是这样，剂量上应存在一个阈值而不是渐变——而且让晶圆热着注入应把阈值推高，因为温热的晶格边受损边退火。',
        AskedHowMuchDamageIsThis: '这次注入造成了多少损伤？什么时候变成非晶？',
        SumLatticeDamageAgainstDoseAnd: '晶格损伤随剂量的变化，以及炉子被要求修复的是什么。',
        NextTheStageHasATilt: '载台自身有远小于一度的倾角误差。看看这条曲线最陡的地方——半度的载台误差在那里要付出什么？在 7° 处又付出什么？',
        LabelTailDepth: '尾部深度',
        TitleAndTheDepthItReaches: '以及它到达的深度',
        AxisYFractionOfDose: '剂量比例',
        AxisXTiltDegrees: '倾角（度）',
        TitleChannelledFractionAgainstTilt: '沟道化比例随倾角的变化',
        LabelTailAt7: '7° 时的尾部',
        LabelTailOnAxis: '沿轴向的尾部',
        LabelTableRange: '表列射程',
        HypSiliconIsACrystalNot: '硅是晶体，不是无定形固体，沿低指数方向它大部分是空的。顺着沟道进入的离子损失能量的速度远低于数据表的假设。如果确实如此，深度应强烈依赖于倾角而与能量几乎无关——而且几度就足以把沟道关上。',
        AskedWhyDoesTheImplantGo: '为什么注入比投影射程预测的更深？',
        SumChannellingDownTheLatticeAnd: '沿晶格的沟道效应，以及挡住它的那七度倾角。',
        fault: {
          weakSource: {
            Evidence: [
              '源压力 35%，下限 85%。',
              '方块电阻分布超限。',
              '扫描均匀性正常——束被扫得没错，只是没有一束稳定的束可扫。',
            ],
            Truth: '源压力掉到标称的 ' +
              '35%。饿着的源打出不稳的束，不稳的束注出剂量不匀的晶圆——和扫描坏掉造成的不均匀一模一样，只是出在束线的另一头。能分开两者的是扫描的读数。',
            Complaint: '束流不稳，节拍变长，分布图发花。',
            Label: '离子源失效中',
          },
          scanDrift: {
            Evidence: [
              '扫描均匀性 30%，下限 90%。',
              '方块电阻分布超过 1% 的不均匀度限值。',
              '监控片总剂量正确，分布也没有尾巴。',
            ],
            Truth: '扫描系统的均匀性因子只剩 30%。剂量平均起来是对的，具体到每个位置都是错的——总电荷正确，法拉第杯心满意足，而晶圆没有被均匀注入。',
            Complaint: '方块电阻分布图沿扫描方向出现了条纹。',
            Label: '扫描波形劣化',
          },
          tiltZeroLost: {
            Evidence: [
              '倾角回读 0.6°，设定值是 7°。',
              'SIMS 显示 40% 的剂量在一条深尾巴里。',
              '退火监控片的剂量正确——法拉第杯没问题。',
            ],
            Truth: '倾角编码器丢了零点，台面偏了 6.4°，要 7° 的配方实际在 0.6° ' +
              '下注入——正对沟道。四成的束流钻到三倍深的地方，而峰值原地不动，于是均值没问题，尾巴才是结。方块电阻一积分就把尾巴抹掉了，这就是它能一路漏到产品的原因。',
            Complaint: '某个产品结漏电。方块电阻在目标上，剂量在目标上，分布峰值也恰好在该在的位置。',
            Label: '倾角台零点丢失',
          },
          driftedCup: {
            Evidence: [
              '校准标签在有效期内——好设备上它也在有效期内。',
              '退火后的监控片只有预期剂量的 87%。',
              '倾角、扫描、离子源全都正常。',
            ],
            Truth: '剂量积分器高读 ' +
              '13%，于是设备提前停止注入，还报告足量。它没有撒谎——它在精确地测一个错的东西，这更糟，因为全厂每一份配方都是照着那个数写的。抓住它的唯一办法，是别再测束流，去测晶圆。',
            Complaint: '凡是过这台注入机的产品方块电阻都超差。设备咬定它给的剂量分毫不差，还有积分电荷为证。',
            Label: '法拉第杯读数偏高',
          },
          impHealthy: {
            Evidence: [
              '台上的每个读数都正常。',
              '监控片的剂量和分布都核对无误。',
            ],
            Truth: '什么问题都没有。正确的判定就是放行这一批。',
            Complaint: '例行批次。设备报的剂量和配方要的一致。',
            Label: '一切正常',
          },
        },
        NoteMaOfBeam: '束流 {{a}} mA',
        NoteTailToNmAtTilt: '{{b}}° 倾角下尾巴伸到 {{a}} nm',
        NoteCmDelivered: '实注 {{a}}/nm²',
        NoteStraggleNm: '离散 ±{{a}} nm',
        SectionTheFailureThatHidesIn: '藏在平均值里的失效',
        NoteSiliconIsACrystalAnd: '硅是晶体，沿主轴方向大部分是空的。把倾角去掉，很大一部分束流会顺着沟道直落下去，停在三倍深的地方——而峰值恰好留在配方说的位置。平均值没问题。尾巴才是结。',
        SectionSolvedBesideTheFormulaIt: '数值解与它取代的公式并排',
        SectionBeamlineState: '束线状态',
        LabelProjectedRange: '投影射程',
        LabelChannelled: '沟道化份额',
        LabelLatticeDamage: '晶格损伤',
        LabelDoseNonUniformity: '剂量不均匀度',
        LabelImplantTime: '注入时间',
        AssistantLead: 'IMP-10 探究助手',
        AssistantBlurb: '可以问：结为什么比手册深、晶格什么时候变成非晶、能量到底决定了什么。每个回答都是在这条束线上真实跑出来的一次注入。',
        reading: {
          uniformityMap: {
            Label: '方块电阻标测，监控片 49 点（3.0 小时）',
          },
          sims: {
            Label: 'SIMS 深度剖析——破坏性，外送（24 小时）',
          },
          sheetRho: {
            Label: '监控片退火后四探针测量（6.0 小时）',
          },
          sourcePressure: {
            Label: '源压力',
          },
          scanUniformity: {
            Label: '扫描均匀性因子',
          },
          tiltReadback: {
            Label: '倾角回读对设定值',
          },
          doseCal: {
            Value: '在有效期内',
            Limit: '在有效期内',
            Label: '法拉第杯校准日期',
          },
        },
        aspect: {
          source: {
            Action: '更换离子源并重新起弧',
            Label: '离子源',
          },
          scan: {
            Action: '重调扫描波形并重测全片均匀性',
            Label: '束流扫描系统',
          },
          tiltStage: {
            Action: '倾角编码器归零并对机械基准核验',
            Label: '倾角台',
          },
          faraday: {
            Action: '对参考杯重新校准积分器并在监控片上重新认证',
            Label: '法拉第杯与剂量积分器',
          },
        },
      },
      facilities: {
        SecondaryLabel: '抽气到 10⁻⁵',
        ToolLabel: 'FAC-06 · 工艺真空',
        LabelPureOutgassing: '纯放气',
        LabelPureLeak: '纯漏气',
        LabelThisChamber: '该腔体',
        LabelReaches1e5: '达到 10⁻⁵',
        LabelBaseAt24H: '24 小时后本底压力',
        LabelBaseAt1H: '1 小时后本底压力',
        DesignATwentyMinuteRateOf: '对腔体现状做二十分钟的压升测试，并与调到相同初始斜率的纯漏气和纯放气曲线并列——这样就只有形状能区分它们。',
        HypBothRaiseBasePressureAnd: '两者都会抬高本底压力，而且机制相同——都是泵必须排走的气体流量。但漏气由大气供给，永远不会枯竭；放气由有限的吸附物供给，会枯竭。所以关上阀门：漏气应给出一条永远笔直的直线，放气则应逐渐弯平。',
        AskedTheChamberWillNotReach: '腔体到不了本底压力。是空气在漏进来，还是腔壁在放气？',
        SumSeparateARealLeakFrom: '通过压升曲线的形状区分真实漏气与腔壁放气。',
        NextConductanceSetsHowFastYou: '流导决定你多快到达，但不决定你停在哪里。是什么决定腔体最终稳定的本底压力？为什么一台完美的泵仍然到不了那里？',
        TitleEffectiveSpeedAgainstLineDiameter: '有效抽速随管道直径的变化，泵不变',
        LabelTripleThePump: '泵抽速增至三倍',
        AskedPumpingIsSlowerThanExpected: '抽气比预期慢。是泵太小，还是有什么东西挡在中间？',
        SumShowThatThePipeNot: '证明通常是管道而不是泵决定腔体处的抽速。',
        fault: {
          wornPump: {
            Evidence: [
              '本底压力合格。每片晶圆都能出货。这就是陷阱。',
              '抽到切换点的时间从几秒变成了远超限值。',
              '泵口实测抽速远低于额定——一锤定音，代价是两个小时。',
              '管路流导健康，怪不到管子头上。',
              '升压率形状正常，没有什么在喂腔体。',
            ],
            Truth: '轴承和叶片间隙都不行了；涡轮泵只剩额定抽速的约二十分之一。规管什么都不说，而且有它的道理：本底压力等于气载除以抽速，抽了一整夜的腔体气载几乎为零，随便多少抽速都能到达一个漂亮的本底。一台快死的泵只显形于它花了多久，从不显形于它停在哪里。这是这个故障还能被找到的唯一原因，也是记录抽气时间的厂务日志比记录本底压力的日志更值钱的原因。',
            Complaint: '没有任何报警。抽气时间明显比日志里过去的记录长，泵的声音也不一样了。本底压力没问题。',
            Label: '泵组老化',
          },
          throttledLine: {
            Evidence: [
              '腔体处的有效抽速只是泵额定值的一小部分——这就是那个决定性的数字。',
              '升压率的斜率和形状都正常，既没有漏气，壁面也是干的。',
              '本底压力最终能到；错的是时间，不是终点。',
            ],
            Truth: '腔体挂在一根 5 cm ' +
              '粗、两米长的管子后面。流导随直径的立方变化，这根管子只放得过泵所能吞的一小部分，腔体从来没见过花钱买来的那个抽速。把泵换成三倍大几乎什么都不会变，因为泵和它前面的管子像串联电阻一样相加。',
            Complaint: '抽气慢得要命。已经有人去询价三倍大的泵了。',
            Label: '连接管路又长又细',
          },
          wetChamber: {
            Evidence: [
              '随着表面放空，升压率的斜率在二十分钟里塌下去。这个衰减就是全部诊断；一小时时的压力不是。',
              '泵速、流导、密封全都检查无误。',
              '接着抽下去，本底压力持续变好。要是漏气，它早就不再变好了。',
            ],
            Truth: '腔体开过大气，装回去时带着潮气。壁面吸满了水，正在慢慢放出来。什么都没坏，什么都不用拆——它需要的是时间，或者一次烘烤。为了找漏把这个腔拆开要花六个小时，而且什么也找不到。',
            Complaint: '跟上个月门封那次一样：抽一个小时下不去 10⁻⁵ 出头。',
            Label: '腔体装回时带潮',
          },
          realLeak: {
            Evidence: [
              '升压率的斜率二十分钟都不衰减。喂着腔体的那个来源不会耗尽，而任何放气都做不到这一点。',
              '腔体已经抽了一整夜，壁面是干的——这也是这个测试能拍板的唯一前提。',
              '泵速和管路流导都正常。',
            ],
            Truth: '上次保养后重新装配时，一条门封被夹了。空气在持续漏入。随之而来的有两件事，第二件才是全厂在乎的：本底压力卡住不动，而随空气进来的氧在刻蚀机里清除氟——于是两层楼上的一位工艺技师看到一个谜，而一位厂务技师看到的是一条门封。',
            Complaint: '腔体降不到 10⁻⁵ 出头以下。上周还抽得好好的。楼上的刻蚀机也在变慢，气流正常、射频正常。',
            Label: '门封被夹伤',
          },
          healthy: {
            Evidence: [
              '本底压力在规格内，升压率平坦。',
            ],
            Truth: '什么问题都没有。真空不是你的问题。',
            Complaint: '系统抽了一整夜，停在本底压力上。没有任何报告。',
            Label: '腔体在本底',
          },
        },
        TextH: '{{a}}——{{b}} 小时。',
        NoteVSEff: 'V / S_eff',
        reading: {
          speedcheck: {
            Label: '腔体处通量检查（1 小时）',
          },
          pumpspeed: {
            Limit: '> 额定值的 70%',
            Label: '泵口抽速测量（2 小时）',
          },
          outgas: {
            Label: '抽一整夜后复测（14 小时）',
          },
          riseShape: {
            Limit: '致密',
            Label: '关阀升压率，20 分钟（25 分钟）',
          },
          conductance: {
            Label: '管路流导',
          },
          throttle: {
            Limit: '> 720 L/s（泵的 60%）',
            Label: '腔体处抽速',
          },
          timeto: {
            Label: '抽到 10⁻⁵ Torr 的时间',
          },
          base: {
            Label: '1 小时时的本底压力',
          },
        },
        aspect: {
          walls: {
            Action: '继续抽，或对腔体烘烤除气',
            Label: '腔体表面',
          },
          seals: {
            Action: '氦检漏，重新压紧门封',
            Label: '密封与法兰',
          },
          line: {
            Action: '改用更短更粗的管路重新连接',
            Label: '连接管路',
          },
          pump: {
            Action: '维修或更换涡轮泵及其前级泵',
            Label: '泵组',
          },
        },
        SectionSystem: '系统',
        SectionPumpdown: '抽真空',
        PumpdownNote: '从粗抽真空开始。前面快的那一段是泵在抽空腔室里的气体；后面长长的拖尾，是腔壁在把开腔期间吸进去的东西慢慢吐出来。本底压力，就是进气量和抽气量相等的那个点。',
        SectionRise: '升压率——决定性的一测',
        RiseNote: '关掉隔离阀，然后看着。真正的漏以恒定速率往腔里灌气，所以曲线是一条永远不弯的直线。放气则随着表面被抽空而衰减，所以曲线会拐弯。两者起始斜率相同——形状才是全部的诊断。',
        RunRise: '做一次升压率测试（20 分钟）',
        SectionConductance: '流导——为什么管路比泵更要紧',
        ConductanceNote: '在你现有这台泵的条件下，腔室处的有效抽速随管径的变化。流导正比于管径的三次方，所以管子稍微细一点，代价远比看上去大——而管子那一头再大的泵也补不回来。',
        SectionConfig: '系统配置',
        SectionStored: '已存配置',
        SectionShift: '值班',
        SectionUpw: '超纯水厂',
        UpwNote:
          '每个湿法工步都要喝的回路。这里有三个习惯。电导表先读温度后读纯度——45 °C 的完全纯水' +
          '读数不到 7 MΩ·cm，所以每支真实探头都补偿到 25 °C。混床在几乎整个寿命里稳住 18.2，' +
          '然后在最后几个百分点内崩溃，所以按处理水量再生，绝不按读数。电阻率只数离子——回路可以' +
          '带着十倍于限值的 TOC 而表针安然停在 18.2，所以 TOC 是独立的仪器。',
        TileUpwRaw: '探头（未补偿）',
        NoteUpwRaw: '纯度与温度混在一起',
        TileUpwComp: '仪表（25 °C 补偿）',
        NoteUpwComp: 'CLN-09 用来冲洗的就是它',
        TileUpwLeakage: '离子污染',
        TileUpwToc: '使用点 TOC',
        NoteUpwToc: '电阻率表看不见它',
        ChartUpwTitle: '回路电阻率 vs 混床寿命',
        ChartUpwX: '混床容量用量（%）',
        SeriesUpwLoop: '25 °C 下的回路',
        AnnUpwBed: '你的混床',
        AnnUpwFloor: 'CLN-09 下限',
        SectionUtilities: '公用系统——整座大楼实时状态',
        DeskNote:
          '调度台。这里的每个数字都是大楼此刻的状态；数字背后的仪器在地下厂务层的设备上——' +
          '走这台设备后面的楼梯下去，点击设备即可。维修也在下面进行。',
        SectionHouse: '厂务系统——整座工厂',
        HouseHealthy: '厂务正常——无事件',
        HouseNote: '楼里发生一件事，接在它上面的每一台设备都会感觉到。挂一个上去，然后到车间走一圈：各处的症状彼此毫不相像，而且没有一个能在它显现的那台设备上修好。',
        TileConductance: '管路流导',
        TileSpeed: '腔室处抽速',
        NotePercentOfPump: '泵能力的 {{pct}}%',
        TilePumpRating: '泵标称抽速',
        NoteOwnInlet: '在泵自己的入口处',
        TileBase1h: '1 小时后的本底',
        TileBase24h: '24 小时后的本底',
        NoteLeakFloor: '漏气给出了一个下限',
        NoteOutgassed: '放气已经衰减掉了',
        TileReaches: '达到 10⁻⁵ Torr',
        Never: '永远达不到',
        TileTimeConstant: '抽气时间常数',
        TileVerdict: '升压率判定',
        NotRun: '未测',
        RunTheTest: '先做一次测试',
        NoteSlopeRatio: '斜率比 {{ratio}}',
        VerdictLeak: '漏气',
        VerdictOutgassing: '放气',
        VerdictMixed: '两者兼有',
        VerdictTight: '气密良好',
        TileThroughput: '进气流量',
        NoteAt1h: '1 小时处的 Torr·L/s',
        ChartPumpTitle: '从粗抽真空开始的抽气过程',
        ChartTimeS: '时间（s）',
        ChartLogPressure: 'log₁₀ 压力（Torr）',
        SeriesChamber: '腔室压力',
        AnnBase1h: '1 小时处的本底',
        NotRunYet: '尚未运行',
        ChartRiseTitle: '升压率——读作"{{verdict}}"',
        ChartTimeAfterValve: '关阀之后的时间（s）',
        ChartPressureU: '压力（µTorr）',
        SeriesMeasured: '实测',
        SeriesPureLeak: '纯漏气会长成这样',
        ChartConductanceTitle: '腔室处抽速随管径的变化（管长 {{cm}} cm）',
        ChartDiameter: '管径（cm）',
        ChartEffectiveSpeed: '有效抽速（L/s）',
        SeriesEffectiveSpeed: '有效抽速',
        AnnYourLine: '你的管路',
        AnnPumpRating: '泵标称值',
        AssistantBlurb: '提出问题，我会设计一个实验，用这台求解器把它跑出来，并报告真正得到的结果——然后再还你一个更锋利的问题。',
        AssistantLead: '楼上那台刻蚀机怪到这个房间头上的次数，比它自己以为的多。问我真空到底是不是问题，以及是真空的哪一部分。',
      },
      epi: {
        SectionWell: '只长几个纳米——随之出现的能级阶梯（薛定谔方程，一维）',
        WellNote: '夹在硅之间的一薄层 SiGe，对空穴而言其能量低于两侧的硅，于是这层里的空穴就处在一个盒子中——而盒中粒子不能取任意能量。它得到一架允许能级的阶梯，间距由盒子宽度决定，而盒子正是这道配方所生长的厚度。把层变薄，每一级都会升高，因为压缩一个波会缩短它的波长，而更短的波长要付出能量。升得够高，最上面那一级便触到壁而消失：原本容纳五级的势阱只剩四级。这里没有任何东西是画上去的——能级就是所生长剖面的本征值，当求解器不再能在势垒之下找到它时，它就从列表中消失。',
        WellCarrierNote: '是空穴而不是电子，而这种不对称正是要点。硅锗生长在硅上时，其能带偏移几乎全部落在价带：导带几乎不动，因此电子几乎看不到势阱，而空穴看到的却很深。这正是 SiGe 用于异质结双极晶体管的基区、用于 p 沟道 FET 的应变源漏，而不用于约束电子的原因。偏移取硅衬底上应变层的标准值，每单位锗组分 0.74 eV；质量取沿生长方向的重空穴质量。轻空穴处在同一个势阱中，有自己的一架阶梯，这里略去，因为两套交错的线条什么也教不了。',
        WellChartTitle: '生长的这一层作为一个盒子，以及它所允许的能级',
        WellChartX: '沿叠层的位置 (nm)',
        WellChartY: '空穴的能量 (eV)',
        WellProfile: '生长的剖面',
        WellDensity: '最低空穴所在之处',
        WellTop: '势阱顶',
        WellLevel: 'E{{n}}',
        WellStatus: '{{width}} nm、锗含量 {{ge}}% 中有 {{n}} 个束缚能级 · 阱深 {{depth}} meV · 最低能级 {{ground}} meV',
        WellWidth: '生长厚度',
        WellGe: '锗组分',
        WellGeValue: '{{pct}}% · 阱深 {{depth}} meV',
        WellFailed: '量子阱求解器加载失败。',
        WellLoading: '正在加载量子阱求解器…',
        StepBake: '氢气烘烤',
        StepGrowth: '外延生长',
        ReactorDepletion: '到远端已消耗',
        ReactorLayer: '主流高于表面',
        SectionReactor: '反应器内部的同一套物理（气相输运）',
        ReactorNote: ' ' +
          '气体从左侧进入，掠过被加热的基座；颜色表示还剩多少源气体。这里有两件事是上面那条曲线在结构上无法呈现的。原料沿流动方向被逐渐耗尽——到出口边缘大约已少了三分之一，所以那一端的生长比进气端慢，这正是基座必须旋转的全部原因，也是外延以均匀性而非速率来评判的原因。还有，边界层是一段真实的距离：上方几毫米处的主流仍保持满浓度，而其下的表面却在挨饿，这个落差就是曲线上画成虚线上限的那个输运阻力。调大氢气载气可以让这层变薄；升高温度则让表面消耗得更猛，把自己饿得更厉害。有一处标签值得多看一眼：上面的配方计量的是三氯氢硅，而这一幕用的是 Energy2D 自带的**硅烷**反应器，所以传感器上写着的是这台机台并不使用的气体。究竟是哪种分子发生热解，改变的是数字而不是这幅图——原料沿流动方向被耗尽、边界层有一个实实在在的厚度，这是任何硅源气体在热基座上都会发生的事。形状看这里，速率看上面那条曲线。',
        fault: {
          spentSusceptor: {
            Evidence: [
              '基座小时数远超涂层更换间隔。',
              '监控片上的新增缺陷密度上升一个数量级。',
              '49 点厚度图上有淡淡的斑驳——发射率不均——而速率、雾值和动力学探针都保持干净。',
            ],
            Truth: '基座涂层已经磨穿，正往气流里掉屑。反应器能测量的一切都健康——速率、均匀性、雾值——因为这些都不由涂层决定。它只表现为外延层上的缺陷，那是 ' +
              'INSP-13 的证据，不是反应器的证据。',
            Complaint: '颗粒增量一批比一批高。其他什么都没动。',
            Label: '基座涂层耗尽',
          },
          wetHydrogen: {
            Evidence: [
              '机台处露点远高于 -85 °C 限值。',
              '雾值扫描超限一个数量级，而生长速率完全正常。',
              '延长烘烤没有用——这排除了烘烤配方错误。',
            ],
            Truth: 'H2 ' +
              '纯化床已经穿透，载气带着水。烘烤剥掉自然氧化层，潮湿的气氛立刻把它放回去，所以不管烘烤多久，表面都在引出层错。生长动力学几乎无感——速率说一切正常，而表面说一切都不正常。',
            Complaint: '从周末起每片晶圆都触发雾值报警。速率和厚度与配方分毫不差。',
            Label: '氢气中有水汽',
          },
          tiltedLamps: {
            Evidence: [
              '灯象限电流散布远超 3% 限值。',
              '49 点厚度图显示倾斜；平均厚度接近标称。',
              '缀饰腐蚀下晶圆边缘可见滑移线。',
              '动力学探针与生产工作点一致——温度控制没问题，温度分布有问题。',
            ],
            Truth: '一个灯象限输出偏低，径向分布倾斜了 ' +
              '9%。在输运平台区平均速率几乎不动——但梯度带来的应力在生长温度下驱动位错，边缘就滑移了。这是平台无法原谅的故障，因为滑移取决于梯度，不取决于平均值。',
            Complaint: '最近两批出现边缘滑移，厚度图长出了径向倾斜。平均速率没问题。',
            Label: '灯象限失效',
          },
          coldPyrometer: {
            Evidence: [
              '动力学探针与生产工作点不一致：1120 °C 时亏缺不大，950 °C 时崩塌。',
              '鼓泡器液位和源管线输送验证无误。',
              '均匀性略差，因为更冷的晶圆在平台上坐得更低。',
            ],
            Truth: '高温计漂移了 60 °C、读数偏乐观，所以环路把晶圆压得比所有人以为的低 60 °C。在生产工作点，输运平台把大部分亏缺藏了起来；降到 950 °C ' +
              '亏缺就急剧放大，因为在阿伦尼乌斯曲线下坡，速率本身就是温度计。与鼓泡器同样的症状，相反的区间签名。',
            Complaint: '和气路问题一样的抱怨：外延层偏薄。气路面板检查无误，鼓泡器昨天刚补满。',
            Label: '高温计读数偏高',
          },
          starvedBubbler: {
            Evidence: [
              '动力学探针显示 950 °C 与 1120 °C 的百分比亏缺完全相同——这是气路侧的签名，任何温度故障都做不出来。',
              '鼓泡器液位读数低于浸入管。',
              '均匀性和雾值不受影响。',
            ],
            Truth: '鼓泡器液位已低于浸入管，实际输送的 TCS 只有指令值的约 55%。在 1120 °C ' +
              '工艺处于输运受限区，速率随气体一比一变化——而温度环路说的是实话，这正是盯着它看毫无结果的原因。',
            Complaint: '生产配方下外延层薄了 40%。整个运行期间温度回读稳如磐石。',
            Label: 'TCS 鼓泡器供料不足',
          },
          epiHealthy: {
            Evidence: [
              '速率、均匀性和雾值全部在范围内。',
              '动力学探针与生产工作点一致。',
            ],
            Truth: '什么问题都没有。正确的判定就是放行这一批。',
            Complaint: '例行班次。没有人对反应器提出任何问题。',
            Label: '一切正常',
          },
        },
        reading: {
          hazeCheck: {
            Limit: '< 3 ppm',
            Label: '裸硅烘烤后表面雾值扫描（1.0 小时）',
          },
          uniformityCheck: {
            Value: '半程 {{a}} %',
            Limit: '< 2%',
            Label: '监控晶圆 49 点厚度图（1.5 小时）',
          },
          kineticProbe: {
            Value: '{{b}} °C 时为标称的 {{a}}%，950 °C 时为 {{c}}%',
            Limit: '两者应一致',
            Label: '动力学探针——950 °C 重新生长并对比（2.5 小时）',
          },
          susceptorHours: {
            Limit: '< 240 h',
            Label: '基座涂层后小时数',
          },
          dewpoint: {
            Limit: '< -85 °C',
            Label: '机台处 H2 露点',
          },
          bubblerLevel: {
            ValueOk: '在范围内',
            ValueLow: '低于浸入管',
            Limit: '高于浸入管',
            Label: 'TCS 鼓泡器液位',
          },
          pyro: {
            Value: '一致（闭环——偏移在这里显不出来）',
            Limit: '需用测温晶圆验证',
            Label: '待机时高温计对设定点',
          },
          lampCurrent: {
            Limit: '< 3%',
            Label: '灯象限电流散布',
          },
        },
        aspect: {
          susceptor: {
            Action: '换下基座，运行涂层配方',
            Label: '基座涂层',
          },
          ambient: {
            Action: '上报气站——H2 管线的纯化床已经穿透',
            Label: '氢气品质',
          },
          gasline: {
            Action: '补满鼓泡器，并在源管线上做压升测试验证输送量',
            Label: 'TCS 输送',
          },
          pyrometer: {
            Action: '用装有热电偶的晶圆重新校准高温计',
            Label: '高温计校准',
          },
          lamps: {
            Action: '更换偏弱的灯象限，并重新标定径向温度分布',
            Label: '灯组平衡',
          },
        },
        SecondaryLabel: '厚度非均匀性',
        ToolLabel: 'EPI-20 减压外延反应器',
        NextIfTheLayerMustBe: '如果外延层必须 4 µm、一天要出 20 批，你会用 TCS 买时间，还是用第二台反应器——两者各要付出什么对方不用付的代价？',
        AxisXTcsTorr: 'TCS (Torr)',
        TitleRateAndItsPrice: '速率及其代价',
        LabelDefectsAtMax: '最大值时缺陷',
        LabelRateAtMax: '最大值时速率',
        HypOnTheTransportPlateauThe: '在输运平台上，速率应随供给一比一变化。但表面的消耗能力有限——把气相推得够猛，它就会自行成核，而每个核都是一颗落在外延层上的颗粒。',
        AskedIsTcsAFreeThroughput: 'TCS 是个免费的产能旋钮吗？',
        SumRateFollowsTheGasOne: '速率随气体一比一变化——直到气相自己开始成核。',
        NextTheHazeScanIsClean: '雾值扫描是干净的，但烘烤跑长了。损伤会显现在哪里？产线下游哪台设备会最先看到它？',
        AxisYHazePpm: '雾值 (ppm)',
        AxisXBakeMin: '烘烤 (min)',
        TitleTheTwoCostsOfThe: '烘烤的两笔代价',
        LabelSeamAsSet: '当前设置的界面宽度',
        LabelHazeAsSet: '当前设置的雾值',
        HypTheBakeDoesTwoThings: '烘烤同时做两件事，没法只让它做一件。它剥掉自然氧化层——跳过它，氧化层就会引出读作雾状缺陷的层错——同时又把掺杂从热衬底里蒸出来，生长前沿再把它吃成一道抹开的界面。如果两者都真实，雾值应随烘烤时间下降，而自掺杂过渡层随之变宽。',
        AskedWhatDoesThePreGrowth: '生长前烘烤实际买到了什么？又付出了什么？',
        SumTheBakeStripsOxideAnd: '烘烤既剥氧化层又抹开界面——两件事总是同时发生、成比例发生。',
        NextLayersAreComingOutThin: '外延层出来偏薄，而温度回读很稳。你会在哪个区重新测量？每种答案各说明什么？',
        TitleGrowthRateAgainstTemperature: '生长速率随温度的变化',
        LabelRegimeAsSet: '当前设置所在区',
        LabelRatePlateau: '速率，平台区',
        LabelRateColdEnd: '速率，低温端',
        HypTheGasHasToArrive: '气体必须先到达、再反应——两个速率串联，慢的那个说了算。如果这是对的，速率应在低温端沿阿伦尼乌斯墙攀升，然后弯上一个温度碰不到的平台，因为在那上面鼓泡器才是瓶颈。',
        AskedDoesTemperatureKeepBuyingGrowth: '温度会一直买到生长速率吗？',
        NoteSurface: '表面反应占 {{a}}%',
        TextThisRecipeOnThisSubstrate: '这份配方在这块衬底上：{{a}} µm，',
        LabelGrowthRate: '生长速率',
        LabelTransportCeiling: '传输上限',
        SectionResult: '生长所得的外延层',
        SectionSiGe: '锗硅——合金的带隙',
        SiGeNote: '打开锗源阀门，这层就不再是硅了。AIMS 会在你选定的组分下求解合金的紧束缚模型：带隙从硅的 1.12 eV 向锗的 0.66 eV 下降，这正是 SiGe 基区或应变沟道所要买的东西。但这条曲线来自"虚拟晶体"——其中每个原子都是硅与锗的虚构平均——而真实合金有一个平均做不出来的特征：它的导带最低点在 x ≈ 0.85 之前一直是硅型的，之后才切换到锗型，留下一个任何光滑曲线都重现不了的拐点。正因如此，计算曲线旁边不画任何实测曲线。',
        SiGeCurveTitle: '带隙随组分的变化',
        SiGeCurveX: '锗组分 x',
        SiGeCurveY: '带隙（eV）',
        SiGeCurveSeries: '紧束缚带隙',
        SiGeAtX: 'x = {{x}}',
        SiGeCrossover: '真实合金中的 Δ → L',
        SiGeGeFraction: '锗组分 {{x}}',
        SectionRegime: '串联的两个阻力',
        RegimeLead: '两个速率串联，由较慢的那个决定。图上的点就是当前配方。',
        LotLead: '晶体会反过来影响外延层：衬底掺杂剂会扩散进外延层最初的零点几微米。',
        RegimeNote: '气体要先到达,然后才能反应——两个速率串联,慢者为王。交叉点以下反应是瓶颈,速率就是一支温度计;以上则由鼓泡器决定速率,五十度几乎无关紧要。量产刻意跑在平台区:那是能原谅灯组的区间。图上的点就是当前配方。',
        SectionRecipe: '配方',
        SectionStored: '已存配方',
        SectionLot: '基座上的批次',
        LotNote:
          '外延层长在 CZ-07 ' +
          '拉出的晶体之上,而晶体会回话:掺杂剂从衬底蒸发,弥散进最初的零点几微米。衬底掺得越重——烘烤越久——弥散的接缝就越宽。层一旦存在,器件就住在其中:下游每个结都对着外延掺杂形成,而不是对着体材料。',
        LotEmpty: '基座上没有批次。请从 WPR-08 或 CLN-09 送晶圆来生长。',
        LotIngot: '该批次还是晶锭。要先经 WPR-08 切片才能生长外延层。',
        LotCard: '流程卡',
        LotBlank: '库存空白片——衬底按 1.0 × 10⁻⁶/nm³ 假定。',
        LotRun: '生长外延层并传递批次',
        SectionShift: '班次',
        TileRate: '生长速率',
        NoteSpitting: '已超过气相成核极限',
        TileRegime: '限制性阻力',
        TileThickness: '厚度',
        TileNu: '不均匀性',
        TileSeam: '自掺杂接缝',
        NoteSeam: '衬底在回话',
        NoteSeamQuiet: '轻掺衬底,接缝安静',
        TileHaze: '雾状缺陷',
        TileSlip: '滑移',
        SlipYes: '边缘出现',
        SlipNo: '无',
        TileCycle: '周期',
        ChartHere: '当前配方',
        ChartSpit: '气相成核',
        AssistantBlurb: '提出一个问题,我会设计实验、在该求解器上运行,并汇报真实结果——然后把一个更锋利的问题交还给你。',
        AssistantLead: '扫一遍温度,看生长速率如何弯向平台;然后问我为什么量产都住在那上面。',
      },
      dicing: {
        NoteNoStressRelief: '未做应力释放',
        NoteAfterStressRelief: '应力释放后',
        ActionGrindDiceAndPass: '研磨、切割，把裸片传下去',
        NoteNoLotOnTheChuck: '吸盘上没有批次。把测过针的晶圆送到这里来研磨和切割。',
        SecondaryLabel: '边缘崩边',
        ToolLabel: 'DICE-15 背面研磨与切割',
        NextTheseDieGoToBond: '这些裸片接下来去 BOND-16，那里的劈刀会以一定的力压在它们上面。更弱的裸片不只在这里碎——问问键合机，薄硅上的弹坑风险长什么样。',
        TitleDieLostAtSingulation: '切割分离时损失的裸片',
        LabelResidualDamage: '残余损伤',
        AxisYDamageM: '损伤 (µm)',
        AxisXGrit: '粒度 (#)',
        TitleDamageAndDieLostAgainst: '损伤与损失裸片数随砂轮粒度的变化',
        NoteCoarseToFine: '从粗到细',
        LabelDieSaved: '省下的裸片',
        LabelWeibullM: '威布尔 m',
        LabelStrength: '强度',
        HypDamageDepthScalesWithAbrasive: '损伤深度随磨料尺寸缩放，而强度是损伤的负幂函数，不是线性关系。如果这是对的，换更细的磨粒应买到不成比例的强度——而且因为碎裂服从威布尔分布而不是硬阈值，回报会表现为损失的裸片变少，而不是某个极限的移动。',
        AskedIsAFinerFinishingWheel: '更细的精磨砂轮值得它在产能上的代价吗？',
        SumGritAgainstDamageDepthAnd: '磨粒粒度对损伤深度，以及幸存者的威布尔统计。',
        NextNarrowerStreetsMeanMoreDie: '切割道越窄，每片晶圆的裸片越多，而那三种方法里只有一种经得起窄道。按这个配方的裸片数算，30 µm 的切割道值多少？',
        LabelReachesTheDie: '碰到裸片',
        AxisYChipM: '崩边 (µm)',
        AxisXFeedRateMmS: '进给速度 (mm/s)',
        TitleChipOutAgainstFeedRate: '崩边随进给速度的变化',
        HypABladeCuttingFasterRemoves: '刀片切得越快，每转去除的材料越多，就从磨削变成撕扯，所以崩边应随进给速度增大。这里的极限是硬的而不是软的：崩边要么留在切割道内，要么碰到裸片，一旦越界，切口看着再漂亮裸片也报废了。',
        AskedWhatDoesCuttingFasterCost: '切得更快要付出什么？极限在哪里？',
        SumFeedRateAgainstChipOut: '进给速度对崩边，以及必须容纳它的切割道宽度。',
        NextWpr08RemovesSawDamage: 'WPR-08 用的正是这套物理去除锯痕损伤，但那是在有 775 µm 余量的晶圆上。这里一点余量都没有。这说明两台设备里哪台用得起粗砂轮？',
        LabelBackSide: '背面',
        AxisXThicknessM: '厚度 (µm)',
        TitleBreakingStrengthAgainstTargetThickness: '断裂强度随目标厚度的变化',
        LabelBackStrength: '背面强度',
        HypTheWheelLeavesCracksOf: '砂轮留下的裂纹深度由磨粒决定，而这个深度不会随晶圆变薄而变浅。所以晶圆越薄，损伤层占的比例越大，裸片变弱的速度快于仅凭厚度的预测。如果是这样，极限根本不是一个厚度——而是损伤与厚度之比，去除损伤就该移动这个极限。',
        AskedHowThinCanThisWafer: '这片晶圆能磨到多薄，裸片才不至于开始碎裂？',
        SumGrindingDamageAgainstThicknessAnd: '磨削损伤对厚度，以及为什么应力释放会改变答案。',
        fault: {
          weakTape: {
            Evidence: [
              '剥离力低于 1.9 N 的限值。',
              '崩边高，而切缝和刀都正常。',
              '吸盘真空良好，排除了晶圆移动的另一条路。',
            ],
            Truth: '这批胶带粘不牢，刀过时裸片跟着动，在出刀边上崩角。锯没有问题；坏的是耗材，而这是这台设备上最便宜的维修。',
            Complaint: '换了新批次胶带后，裸片在切割中发生位移。',
            Label: '贴片胶带粘性不足',
          },
          chuckLeak: {
            Evidence: [
              '吸盘真空低于 70 kPa 的限值。',
              '崩边远超限值而切缝宽度正常——磨钝的刀会两个都动。',
              '刀片米数在限值内。',
            ],
            Truth: '吸盘漏了，晶圆吸不平，在刀下发生微观移动。刀碰到哪里，哪里就崩。看着像刀的问题，而刀的读数干干净净。',
            Complaint: '一台切割机上崩边猛涨，裸片还不好拾取。',
            Label: '吸盘漏真空',
          },
          wornBlade: {
            Evidence: [
              '刀片切割米数超过 450 m 的限值。',
              '切缝宽度超过 45 µm 的限值。',
              '裸片强度正常——这个故障在边缘上，不在表面之下。',
            ],
            Truth: '刀片超过了切割寿命。磨钝的刀切出更宽的缝、崩出更多的边——和磨轮那个故障不同，这个只要有人量一量切缝，立刻就在晶圆上现形。',
            Complaint: '切割道越吃越宽，两个产品的密封环快保不住了。',
            Label: '切割刀片磨损',
          },
          glazedWheel: {
            Evidence: [
              '磨轮小时数超过修锐限值。',
              '三点弯曲显示强度远低于 300 MPa 的下限。',
              '切缝和崩边都正常，这正是切割自认无辜的原因。',
            ],
            Truth: '磨轮釉化了，在犁而不是在磨，留下三倍的亚表面损伤。裸片数一颗不少——每颗都在，看着都好——而它们底下的强度分布已经崩了。PKG-17 的模压机一压 ' +
              '183 MPa，真相大白。',
            Complaint: '组装在两站之后的模压机上损裸片。切割说自己的裸片数完美无缺，拒绝背锅。',
            Label: '磨轮釉化',
          },
          diceHealthy: {
            Evidence: [
              '台上的每个读数都正常。',
              '切缝和崩边都在限值内。',
            ],
            Truth: '什么问题都没有。正确的判定就是放行这一批。',
            Complaint: '例行批次。裸片数和外观都正常。',
            Label: '一切正常',
          },
        },
        NoteLostMinWafer: '损失 {{a}}，每片 {{b}} 分钟',
        NoteKerfMIntoAM: '{{b}} µm 切割道里 {{a}} µm 锯缝',
        NoteAtMpaFromThePackage: '封装应力按 {{a}} MPa 计',
        NoteWeibullM: '威布尔 m = {{a}}',
        SectionWhyThisToolIsDangerous: '为什么这台设备危险',
        NoteNothingYouCanMeasureHere: '在这里你能测到的任何东西，都不会告诉你裸片能不能活下来。磨削损伤是看不见的，在这台设备上它一颗裸片都不损失，却决定了两台设备之后的模压机上有多少封装会裂。',
        NoteThisLotIsStillAn: '这批货还是一根晶棒。得先由 WPR-08 切片，这台设备才碰得了它。',
        LabelDieThickness: '裸片厚度',
        LabelGrindingDamage: '磨削损伤',
        LabelBackSideStrength: '背面强度',
        LabelEdgeStrength: '边缘强度',
        NoteSetByChippingNotBy: '由崩边决定，不由磨轮决定',
        LabelSurvivesTheMouldPress: '能扛过模压',
        LabelChipOut: '崩边',
        LabelGoodDie: '合格裸片',
        AssistantLead: 'DICE-15 探究助手',
        AssistantBlurb: '可以问：晶圆能减薄到多少、更细的砂轮值不值、切得更快的代价是什么。每个回答都是用这套配方真实跑出来的一次减薄与切割。',
        reading: {
          chipping: {
            Label: '边缘检查，50 颗裸片（1.5 小时）',
          },
          kerf: {
            Label: '监控片切缝宽度（1.0 小时）',
          },
          strength: {
            Label: '20 颗裸片三点弯曲——破坏性（2.0 小时）',
          },
          tapePeel: {
            Label: '胶带剥离力',
          },
          chuckVacuum: {
            Label: '吸盘真空',
          },
          bladeCuts: {
            Label: '刀片已切米数',
          },
          wheelHours: {
            Label: '磨轮距上次修锐小时数',
          },
        },
        aspect: {
          tape: {
            Action: '改用新批次胶带并重新贴膜',
            Label: '贴片胶带',
          },
          chuck: {
            Action: '找到并封住漏点，整盘复测吸持力',
            Label: '吸盘真空',
          },
          blade: {
            Action: '换刀片并在监控片上重新认证切缝宽度',
            Label: '切割刀片',
          },
          wheel: {
            Action: '修锐磨轮；修不动就更换',
            Label: '磨轮',
          },
        },
      },
      bonding: {
        failure: {
          'attach void': '贴片空洞',
          intermetallic: '金属间化合物',
          'cratered pad': '焊盘弹坑',
          'lifted ball': '焊球脱落',
          none: '无',
        },
        ActionAttachBondAndPass: '贴片、键合，把裸片传下去',
        UnitPctWelded: '%（焊合面积）',
        NoteNoDieOnTheBonder: '键合机上没有裸片。在 DICE-15 切割一片晶圆，把裸片拿到这里来。',
        SecondaryLabel: '贴片空洞',
        ToolLabel: 'BOND-16 贴片与引线键合',
        NextFt18AcceleratesExactlyThis: 'FT-18 加速的正是这种扩散，用来找出那些会在现场失效的产品。问问它：这些金属间化合物折合多少小时的老化。',
        LabelImcThickness: 'IMC 厚度',
        AxisXCureTemperatureC: '固化温度 (°C)',
        TitleIntermetallicAgainstCureTemperature: '金属间化合物随固化温度的变化',
        LabelFromTheBond: '来自焊接本身',
        LabelImcTotal: 'IMC 总量',
        HypGoldAluminiumIntermetallicGrowsBy: '金铝金属间化合物靠扩散生长，所以它的厚度应只取决于时间和温度，而与焊接方式无关。焊接本身只持续毫秒，环氧固化却持续数小时——所以如果机制是扩散，几乎所有生长都应发生在固化期间，而没人把这一步当作键合的一部分。',
        AskedHowMuchIntermetallicIsThis: '这个配方在长多少金属间化合物？在哪里长？',
        SumTimeTemperatureGrowthOfIntermetallic: '金属间化合物随时间-温度的生长，以及为什么固化是键合的一部分。',
        NextPrb14HasTheSame: 'PRB-14 有同样的算术，只是把裸片上的引线换成了晶圆上的裸片。两者都是别人选定的某个数目的指数函数——工艺工程师对这两个里的哪一个真正有办法？',
        AxisXWiresPerDie: '每裸片引线数',
        TitleDieYieldAgainstWireCount: '裸片良率随引线数的变化',
        LabelPerWireYield: '单线良率',
        HypADieIsGoodOnly: '裸片只有在每根引线都合格时才合格，所以裸片良率应是单线良率的线数次方。这是引脚数的指数函数，意味着在低引脚数下完全够用的焊接质量，到高引脚数就不能用了——而键合机什么都没变。',
        AskedWhyDoesAHigherPin: '配方没变，为什么引脚数更多的产品良率更差？',
        SumPerWireYieldRaisedTo: '单线良率的线数次方，以及这对每个焊点提出的要求。',
        NextThatDieYieldIsOne: '这个裸片良率是单线良率的线数次方。问我：换成六百根引线的产品而不是这个，它会变成什么样。',
        TitleDieYieldAcrossTheWindow: '跨窗口的裸片良率',
        LabelCrateringRisk100: '弹坑风险 ×100',
        LabelBondedArea: '焊合面积',
        AxisXUltrasonicPowerMw: '超声功率 (mW)',
        TitleTwoFailureModesOneKnob: '两种失效模式，一个旋钮',
        NotePerBond: '每个焊点',
        LabelCratering: '弹坑',
        LabelBondedAreaL113: '焊合面积',
        HypUltrasonicEnergyIsWhatScrubs: '超声能量的作用是擦掉焊盘上的氧化物并把焊球焊上去，所以能量太少会留下没焊牢、会脱落的球。而当焊接已经完成时，同样的能量总要去某个地方——去了焊盘下面的硅里。如果是这样，两种失效模式就坐在同一个旋钮的两端，窗口在两者同时都小的地方，而不是某一个最小的地方。',
        AskedWhereIsTheBondingWindow: '键合窗口在哪里？窗口两侧各是什么失效？',
        SumLiftedBallsAtOneEnd: '一端是焊球脱落，另一端是焊盘弹坑，中间是工艺窗口。',
        fault: {
          starvedDispense: {
            Evidence: [
              '点胶量 45%，下限 90%。',
              'X 光空洞占比超过 10% 的限值。',
              '打线焊点完美——这个故障在裸片底下，不在上面。',
            ],
            Truth: '点胶泵只给出标称量的 45%。环氧不够润湿整个裸片底面，黏晶于是带洞——那是一个散热通道问题，器件要到在现场跑热了才会因它失效。',
            Complaint: '一批键合完美的货，X 光在裸片底下照出了空洞。',
            Label: '点胶量不足',
          },
          dirtyPads: {
            Evidence: [
              '焊盘沾污 55%，限值 15%。',
              '输出功率、焊球几何、键合计数全都正常。',
              '只出在一个产品上——设备故障会出在所有产品上。',
            ],
            Truth: '键合机没有任何问题。焊盘到货就是氧化的——上游漏做了一次等离子清洗——而铝的氧化层正是超声能量必须击穿的东西。换一只好好的换能器要五个小时，什么也治不了。',
            Complaint: '第一焊点就不粘，只出在一个产品上，而键合机每项检查都过。',
            Label: '焊盘到货带污',
          },
          wornCapillary: {
            Evidence: [
              '距换刀的键合次数超过 500,000 的限值。',
              '焊球直径超过 62 µm 的限值。',
              '超声实际输出功率正常。',
            ],
            Truth: '劈刀超过了键合寿命。磨损的刀头压出更宽、更不规则的球，在细间距上就是短路风险——它还夹持不稳，焊点本身也更差。它同时是后段最便宜的维修，这就是值得把它和换能器分清楚的原因。',
            Complaint: '焊球又大又不规则，有两个已经和邻点短路了。',
            Label: '劈刀磨损',
          },
          tiredTransducer: {
            Evidence: [
              '实际输出功率 62%，下限 90%。',
              '拉线显示焊合面积低于 70% 的限值。',
              '焊盘干净，劈刀也在寿命之内。',
            ],
            Truth: '换能器只输出指令功率的 62%。设定值没错，到达焊球的能量错了——这就是配方看起来无辜的原因。焊合面积掉了下来，而拉线测试是唯一能让它现形的东西。',
            Complaint: '一台键合机上的不粘失效多了起来。配方没动过，功率设定值读着也对。',
            Label: '换能器输出低于所报',
          },
          bondHealthy: {
            Evidence: [
              '台上的每个读数都正常。',
              '焊合面积高于 70% 的下限。',
            ],
            Truth: '什么问题都没有。正确的判定就是放行这一批。',
            Complaint: '例行批次。键合良率在它该在的地方。',
            Label: '一切正常',
          },
        },
        NoteSPerDie: '每颗 {{a}} 秒',
        NoteOverWires: '共 {{a}} 根线',
        SectionTheWindowHasAFloor: '窗口有下限也有上限',
        NoteUltrasonicEnergyHasToBreak: '超声能量必须先破掉铝的氧化层，金属才可能焊合，所以能量太小焊球会翘起。焊点形成之后，同样的能量会打进硅里，所以能量太大会把焊盘打出弹坑。把功率调上去，两头都得盯着。',
        LabelWeldedArea: '焊合面积',
        NoteOfTheBallFootprint: '占焊球footprint的比例',
        LabelBallShear: '推球力',
        LabelCrateringRisk: '弹坑风险',
        NotePerWire: '每根线',
        LabelIntermetallic: '金属间化合物',
        NoteVoidingPast15M: '超过 1.5 µm 出现空洞',
        LabelDieYield: '裸片良率',
        LabelFailureMode: '失效模式',
        AssistantBlurb: '可以问：键合窗口在哪里、引线数量为什么改变良率、固化对金属间化合物做了什么。每个回答都是在这台设备上真实跑出来的一次键合。',
        AssistantLead: 'BOND-16 探究助手',
        reading: {
          attachVoid: {
            Label: '黏晶 X 光检查，10 只（1.5 小时）',
          },
          ballShear: {
            Label: '推球至破坏，20 点（1.0 小时）',
          },
          pullStrength: {
            Label: '拉线至断裂，30 根（2.0 小时）',
          },
          dispenseVolume: {
            Label: '点胶量',
          },
          padClean: {
            Label: '来料焊盘沾污',
          },
          capillaryBonds: {
            Label: '距换劈刀的键合次数',
          },
          ultrasonicDelivered: {
            Label: '超声实际输出功率',
          },
        },
        aspect: {
          epoxy: {
            Action: '重新校准点胶泵并排空针筒',
            Label: '点胶',
          },
          pads: {
            Action: '这里修不了——焊盘被污染，归上游等离子清洗管',
            Label: '来料焊盘状况',
          },
          capillary: {
            Action: '更换劈刀并重新认证焊球几何',
            Label: '劈刀',
          },
          transducer: {
            Action: '更换换能器并对参考件重新校准实际输出功率',
            Label: '超声换能器',
          },
        },
      },
      packaging: {
        failure: {
          'wire sweep': '冲线',
          'mould void': '模塑空洞',
          popcorn: '爆米花开裂',
          warpage: '翘曲',
          none: '无',
        },
        ActionMouldCureReflow: '模塑、固化、回流焊，把封装传下去',
        NoteNoDieOnThePress: '模压机上没有裸片。在 BOND-16 键合一批，拿到这里来。',
        SecondaryLabel: '回流焊内部蒸汽',
        ToolLabel: 'PKG-17 模塑与固化',
        NextEveryPackageThatLeavesHere: '每个离开这里的封装都要去 FT-18，然后到客户手里。这个面板上的失效里，哪些老化能找到，哪些只会在电路板上现身？',
        AxisXCureMin: '固化 (min)',
        TitlePackageYieldAgainstPostMould: '封装良率随模后固化的变化',
        HypTransferMouldingFillsACavity: '传递模塑用黏稠的模塑料填满型腔，包住那些可能被冲歪的引线。随后的固化让它交联。如果固化的意义在力学而不在外观，那么在产品看起来已完工之后，翘曲和良率还应继续变化——而完全没有引线的封装形式应能绕开一整类失效模式。',
        AskedWhatIsTheCureFor: '固化是干什么用的？封装形式又改变了什么？',
        SumTransferMouldingVoidsCureAnd: '传递模塑、空洞、固化，以及封装形式改变了什么。',
        NextTheStressFreeTemperatureIs: '无应力温度就是模塑温度。模塑温度降 30 °C 会对这两条曲线做什么——又要在固化和流动性上付出什么？',
        LabelAtRoomTemperature: '室温时',
        LabelAtReflow: '回流焊时',
        AxisXMouldCapM: '模帽 (µm)',
        TitleWarpageAgainstMouldCapThickness: '翘曲随模帽厚度的变化',
        LabelMouldVoids: '模塑空洞',
        NoteTheOneThatMatters: '要紧的那个',
        LabelWarpAtReflow: '回流焊翘曲',
        LabelWarpAtRoom: '室温翘曲',
        HypMouldCompoundSiliconAndLaminate: '模塑料、硅和基板的膨胀系数各不相同，而它们是在模塑温度下粘到一起的。那是封装唯一无应力的温度；在其他任何温度它都会卷曲。如果是这样，翘曲应取决于各层的厚度比而不是任何单独一层，而且应该存在一个平衡点。',
        AskedWhatMakesThePackageWarp: '是什么让封装翘曲？哪个翘曲才是要紧的？',
        SumABimetallicStripMadeOf: '一根由三种材料做成的双金属片，以及决定焊球命运的翘曲。',
        NextReflowPeakIsTheOther: '回流焊峰值温度是这件事的另一半。如果组装厂实际比曲线标称高 5 °C，这些数字里哪个会动——有人会在产品上板之前发现吗？',
        AxisYFractionOfSaturation: '饱和度比例',
        LabelSteamPressure: '蒸汽压',
        AxisXHoursOnTheFloor: '在车间的小时数',
        TitleSteamPressureAgainstFloorLife: '蒸汽压随车间寿命的变化',
        LabelSteamPressureL116: '蒸汽压',
        LabelMoisture: '水汽含量',
        HypMouldCompoundAbsorbsWaterFrom: '模塑料从空气中吸水，缓慢而可逆。回流焊时产品几秒内冲过 250 ' +
          '°C，这些水在密封的腔体里变成蒸汽。如果机制是这个，失效应取决于产品搁了多久，而不是对它做过什么——一个由存放制造出来的缺陷。',
        AskedWhyDoesAPackageThat: '为什么一个什么都通过了的封装，一过回流焊就失效？',
        SumAbsorbedMoistureSteamPressureAt: '吸收的水汽、裸片界面处的蒸汽压，以及车间寿命时钟。',
        fault: {
          wetParts: {
            Evidence: [
              '出袋时间远超 48 小时的限值。',
              '回流后剖切显示分层超过 2% 的限值。',
              '炉温曲线在目标上——这不是超温失效。',
            ],
            Truth: '干燥包装被打开了，器件在外面放了一个星期。吸进去的水汽在回流时闪蒸成蒸汽，从里面把封装炸开——经典的爆米花裂纹。PKG-17 ' +
              '什么都没坏；坏的是操作纪律，而唯一的解药是 24 小时的烘烤。',
            Complaint: '过完回流出现分层和封装开裂，而这批货模封和测试都干干净净。',
            Label: '器件吸了潮',
          },
          staleCompound: {
            Evidence: [
              '模封料时龄到了保质期的 85%，限值 30%。',
              '流动长度偏短。',
              '转移压力正常——压机在干它的活。',
            ],
            Truth: '模封料在袋子里就预固化了，凝胶化来得比该来的早。它做出的短射和压机无力一模一样——而压机的读数完全正常，这是在台边能把两者分开的唯一依据。',
            Complaint: '还是充填不满，而压机上周才重新校准过。',
            Label: '模封料过保质期',
          },
          lowTransfer: {
            Evidence: [
              '转移压力 45%，下限 90%。',
              '流动长度低于 55 mm 的限值。',
              '模封料时龄远在保质期内，排除了那个长得像的故障。',
            ],
            Truth: '转移柱塞只给出标称压力的 45%。模封料在凝胶化之前到不了型腔尽头，远端的角落就空着。这是压机的问题，而模封料在效期内。',
            Complaint: '大尺寸封装体的远端角落充填不满。',
            Label: '转移压力偏低',
          },
          pkgHealthy: {
            Evidence: [
              '台上的每个读数都正常。',
              '流动长度和过回流存活都很好。',
            ],
            Truth: '什么问题都没有。正确的判定就是放行这一批。',
            Complaint: '例行批次。翘曲和外观都正常。',
            Label: '一切正常',
          },
        },
        NoteHAtRh: '{{b}}% 湿度下 {{a}} 小时',
        NoteMmBody: '{{a}} mm 封装体',
        SectionWarpageIsGeometryNotProcess: '翘曲是几何，不是工艺',
        NoteThePackageIsFlatAt: '封装只在模封温度下是平的，其他任何温度都不平。之后的一切都是温差作用在膨胀失配上，而且随封装体尺寸的平方增长——所以要靠材料或厚度来解决，从来不是在模压机上解决。',
        LabelWarpageAtRoom: '室温翘曲',
        LabelWarpageAtReflow: '回流时翘曲',
        Note100MToSeatThe: '< 100 µm 才能坐实焊球',
        LabelMoistureAbsorbed: '吸入水汽',
        LabelSteamAtTheInterface: '界面处蒸汽压',
        NoteInterfaceHolds26Bar: '界面能扛 26 bar',
        LabelWireSweep: '冲线',
        NoteWiresTouchPast04: '超过 0.4 引线就会相碰',
        LabelPackageYield: '封装良率',
        AssistantLead: 'PKG-17 探究助手',
        AssistantBlurb: '可以问：好裸片为什么在回流时失效、封装为什么翘曲、后固化是干什么的。每个回答都是用这套配方真实跑出来的一次塑封与回流。',
        reading: {
          popcorn: {
            Limit: '无裂纹，< 2 bar',
            Label: '20 只过回流并剖切（4.0 小时）',
          },
          mouldVoid: {
            Label: '12 只剖切查空洞——破坏性（2.5 小时）',
          },
          ovenPeak: {
            Label: '回流峰值偏差',
          },
          moisture: {
            Label: '干燥包装外暴露时间',
          },
          compoundAge: {
            Label: '模封料对保质期的时龄',
          },
          transferPressure: {
            Label: '转移压力',
          },
        },
        aspect: {
          oven: {
            Action: '用测温板重新标定炉温曲线',
            Label: '回流炉温曲线',
          },
          drypack: {
            Action: '125 °C 烘烤除湿，并恢复干燥包装纪律',
            Label: '干燥包装管理',
          },
          compound: {
            Action: '报废已开封的料饼，装入在效期内的新批次',
            Label: '模封料',
          },
          press: {
            Action: '重新校准转移压力并检查柱塞密封',
            Label: '模压机',
          },
        },
      },
      finaltest: {
        ValueNo: '否',
        ValueYes: '是',
        ValueDays: '{{a}} 天',
        ActionBurnInTestAndBin: '老化、测试、分级',
        UnitPoints: '个百分点',
        NoteNoLotOnTheHandler: '分选机上没有批次。在 PKG-17 封装一批，拿到这里来。',
        NoteThisPopulationIsNot: '这个群体没有早期失效',
        NoteInfantMortalityScreening: '早期失效——筛选正在清除它',
        LabelEscapes: '漏检',
        SecondaryLabel: '老化淘汰率漂移',
        ToolLabel: 'FT-18 终测与老化',
        LabelLotHours: '批次小时数',
        TitleAndWhatItCostsThe: '以及产线为此付出的',
        AxisYPartsEscaping: '漏检件数',
        AxisXTestSecondsPerPart: '每件测试秒数',
        TitleEscapesAgainstTestTime: '漏检随测试时间的变化',
        LabelRoomOnlyEscapes: '仅常温测试的漏检',
        LabelLotTime: '批次时间',
        HypMoreTestTimeMeansMore: '测试时间越长，跑的图形越多，产品被覆盖的部分越多，漏检应随之下降——但收益递减，因为容易的失效最先被找到。测试时间要乘以批量，所以边际上的每一秒都很贵，按单件算时极易低估。',
        AskedHowMuchTestTimeDoes: '这个产品需要多少测试时间？花在哪里最划算？',
        SumTestTimeAgainstEscapesAnd: '测试时间对漏检，以及最花钱的温度角。',
        NextBond16GrowsIntermetallicThat: 'BOND-16 长出的金属间化合物在工作温度下还会继续长。那是老化能抓住的机制，还是会被老化加速、把本来没问题的产品搞坏的机制？',
        LabelLotTimeL241: '批次时间',
        AxisYLotHours: '批次小时数',
        TitleWhatItCosts: '它的代价',
        AxisYFractionScreened: '筛出比例',
        AxisXBurnInHours: '老化小时数',
        TitleScreenedFractionAndLotTime: '筛出比例与批次时间随老化时长的变化',
        LabelJustified: '是否值得',
        LabelScreened: '筛出比例',
        LabelFieldHours: '折合现场小时',
        LabelAcceleration: '加速因子',
        HypBurnInWorksByRunning: '老化的原理是让产品在高温下运行，使失效机制比在现场使用中进行得更快。它只有在群体确有早期失效可筛——威布尔形状参数小于一——时才筛得出东西。对健康的群体，老化的每一小时都在消耗本来就没问题的产品的有效寿命。',
        AskedIsBurnInEarningThe: '在这个产品群体上，老化对得起它花掉的小时数吗？',
        SumInfantMortalityTheAccelerationFactor: '早期失效、加速因子，以及老化什么时候只是在消耗寿命。',
        NextPrb14AlreadyAppliedA: 'PRB-14 已经对这些产品里的裸片加过一道保护带。两道保护带串联，报废的比任何一道单独都多——第二道找到了第一道没找到的东西吗？',
        LabelGoodPartsScrapped: '报废的好品',
        LabelBadPartsShipped: '发出的坏品',
        AxisXGuardBandFractionOfSpec: '保护带（占规格宽度比例）',
        TitleTwoErrorsOneKnob: '两种错误，一个旋钮',
        HypTheTesterMeasuresWithAn: '测试机自身带有测量不确定度，所以靠近限值的产品可能读在限值任一侧。把限值往里收能抓住更多边缘的坏品，也必然误杀更多边缘的好品。如果这是对的，就不存在两者皆零的设置——只有企业更愿意犯哪种错误的选择。',
        AskedWhatDoesMovingTheTest: '把测试限往里收，实际买到什么、付出什么？',
        SumEscapesAgainstOverkillAndWhy: '漏检对误杀，以及为什么两者都不可能为零。',
        fault: {
          infantMortality: {
            Evidence: [
              'Weibull 形状参数 0.35，下限 0.45——比当初为之设计老化的群体更糟。',
              '老化淘汰率远超限值，而复测一致性完美。',
              '量具、测试座、分选机全都正常。',
            ],
            Truth: '测试机没有任何问题。来料的 Weibull 形状参数掉到了 ' +
              '0.35——一条脆弱的早期尾巴，而早夭就是这个意思——老化恰恰在干它的本职：把它找出来。问题出在前段，而老化正是它没有落到客户手里的唯一原因。',
            Complaint: '老化淘汰率翻了三倍。测试工程要求重新校准测试机。',
            Label: '来料群体存在早夭',
          },
          coldHandler: {
            Evidence: [
              '分选机浸温偏差 −18 °C，窗口是 ±3 °C。',
              '复测一致性正常，因为测试机是好的。',
              '是现场退货而不是良率损失——漏检，不是过杀。',
            ],
            Truth: '分选机比日志声称的温度低 18 ' +
              '°C。器件是在没热起来的状态下做的高温测试，高温判限根本没被真正压过——测试在放行本该判死的器件，那是漏检，是代价昂贵的那一边。',
            Complaint: '高温测试过得有点太顺了，而且一个客户那边开始出现现场退货。',
            Label: '分选机浸温不到位',
          },
          wornContactor: {
            Evidence: [
              '插拔次数超过 300,000 的限值。',
              '复测不一致超限。',
              '量具 sigma 在限值内，测量链路本身是健康的。',
            ],
            Truth: '测试座插拔超限，接触电阻上来了。它造成的复测不一致和测试机噪声大一模一样——而插拔计数直接点它的名，量具校准则干干净净。',
            Complaint: '四工位分选机上有一个工位在挂导通。',
            Label: '测试座磨损',
          },
          noisyTester: {
            Evidence: [
              '量具 sigma 16%，限值 6%。',
              '复测不一致远超 0.5%——同一批器件两次分级不一样。',
              '测试座插拔数和分选机温度都正常。',
            ],
            Truth: '测试机的量具 sigma 是规格宽度的 16%，限值 ' +
              '6%。贴着判限的好品被判了死刑——损失是过杀，不是缺陷，而且除非有人去复测被拒的那批，它就一直隐形。每个班都在白扔两个点的良率。',
            Complaint: '一级品良率掉了两个点，而客户一件退货都没有。',
            Label: '测试机测量链路噪声大',
          },
          ftHealthy: {
            Evidence: [
              '台上的每个读数都正常。',
              '复测一致性在限值内。',
            ],
            Truth: '什么问题都没有。正确的判定就是出货。',
            Complaint: '例行批次。分级结果和预测吻合。',
            Label: '一切正常',
          },
        },
        NotePartsAtS: '{{a}} 只，每只 {{b}} 秒',
        NoteGoodPartsScrapped: '误杀好品 {{a}} 只',
        NotePartsEachOneAReturn: '{{a}} 只——每一只都是一次退货',
        NoteFromHOfBurnIn: '来自 {{a}} 小时老化',
        NoteCAgainstCUse: '{{a}} C，使用温度 {{b}} C',
        SectionTheGuardBandIsThe: '保护带就是全部的决定',
        NoteATesterMeasuresWithError: '测试机的测量带误差，于是有坏品测成好、也有好品测成坏。把判限往里挪，买到的是更少的漏检，代价是一比一的过杀。没有哪个设定能两头都躲开，你在这笔交易里坐在哪，是一道关于退货成本的算术题。',
        LabelAccelerationFactor: '加速因子',
        LabelEquivalentFieldLife: '等效现场寿命',
        LabelBurnInJustified: '老化是否划算',
        LabelOverkill: '过杀',
        LabelTestFloorTime: '测试机时',
        AssistantBlurb: '可以问：保护带的代价是什么、老化测试值不值那些小时、测试时间到底要多久。每个回答都是在这台测试机上真实跑出来的一个批次。',
        AssistantLead: 'FT-18 探究助手',
        reading: {
          burnInFallout: {
            Label: '500 只老化淘汰率（12 小时）',
          },
          retest: {
            Label: '复测 200 只并比对分级（1.0 小时）',
          },
          weibull: {
            Limit: '> 0.45（认证值：0.55）',
            Label: '来料 Weibull 形状参数',
          },
          handlerTemp: {
            Label: '分选机浸温偏差',
          },
          contactorInsertions: {
            Label: '测试座插拔次数',
          },
          gaugeSigma: {
            Label: '测试机量具 sigma',
          },
        },
        aspect: {
          population: {
            Action: '这里修不了——裸片存在早夭，归前段工序管',
            Label: '来料总体',
          },
          handler: {
            Action: '重新校准分选机浸温热电偶',
            Label: '分选机温度',
          },
          contactor: {
            Action: '更换测试座并用黄金样品复测导通',
            Label: '测试座',
          },
          gauge: {
            Action: '重新校准测试机并用黄金样品核验',
            Label: '测试机测量链路',
          },
        },
      },
      common: {
        StartProcess: '开始工艺',
        HoldProcess: '暂停工艺',
        LoadWafer: '装入新片',
        Measuring: '测量中……',
        SessionRecord: 'SimFab — 会话记录',
        ExportRecord: '导出会话记录',
      },
      scope: {
        Pending: '这个站点已经就位、可以进入，但还没有仿真。今天有六个模块跑的是完整物理——刻蚀、沉积、光刻、热处理、量测与厂务——在这六个模块上验证过的架构，就是其余站点将要建立的基础。',
        NothingFaked: '在此期间不会有任何假造。下面是 {{tag}} 需要覆盖的范围：这正是那六个已上线模块各自当初据以构建的同一份文件。',
        WhereItSits: '{{zone}}——这一站在流程中的位置',
        Takes: '输入',
        Makes: '输出',
        Macro: '宏观——规程与工艺',
        Micro: '微观——底下的科学',
        Engines: '计划中的求解器',
        Competency: '这一站服务的能力目标',
        BackToFloor: '回到车间',
      },
      deposition: {
        MdChartTitle: '成膜原子数随时间变化',
        MdChartX: '模拟时间 (ps)',
        MdChartY: '成膜原子数',
        MdChartSeries: '已成膜',
        MdChartAllVapor: '全部气相原子',
        MdStatus: '{{b}} 个前驱体原子中 {{a}} 个已成膜 · 晶圆 {{c}} K · {{d}} ps',
        MdTemp: '晶圆温度',
        MdReset: '重置',
        MdPause: '暂停',
        MdRun: '运行',
        MdFailed: '分子动力学引擎加载失败。',
        MdLoading: '正在加载分子动力学引擎……',
        MdNote: '这是实时运行的分子动力学模拟，而非动画：气相原子落向固定的晶圆基板，伦纳德-琼斯力，恒温器作用于所有可动原子，再加上一个温和的向下通量代表输运气流。没有任何代码命令原子黏附——冷晶圆把蒸气捕获成不断生长的薄膜，热晶圆则把它重新蒸发。把温度拖动几百开尔文，观察凝结与脱附此消彼长。',
        RxStatus: '{{b}} 个前驱体 Si 中 {{a}} 个已长入晶圆 · 表面 H {{c}} 个 · 晶圆 {{d}} K · {{e}} ps',
        RxGas: '气相中：',
        RxGasEmpty: '气相中已无剩余',
        RxChartTitle: '长入的硅与表面氢随时间变化',
        RxChartY: '原子数',
        RxChartGrown: '长入晶圆的 Si',
        RxChartSurfaceH: '表面上的 H',
        RxChartAllSi: '全部前驱体 Si',
        RxChartAllSites: '全部悬挂键',
        RxPrecursor: '前驱体',
        RxSilane: '硅烷（SiH₄，热 CVD）',
        RxSilyl: '硅基自由基（SiH₃，等离子体 CVD）',
        RxSurface: '晶圆表面',
        RxBare: '裸硅',
        RxHydrogen: '氢终止',
        SectionRx: '另一半——会反应的前驱体：化学键的生成与断裂（反应性分子动力学）',
        RxNote: '同一引擎换上反应性力场：每一对原子都带有实时键级，凡本步超过阈值处即画出一根键棒。一个硅烷分子抵达晶圆，其一个 Si-H 键在悬挂键处断裂，生成一个 Si-Si 键，这个硅便成为薄膜的一部分——这是被观察到的化学吸附，没有任何脚本在驱动它。前驱体用的是硅烷在硅上，而不是本配方的三甲基铝：力场包含硅而不含铝，而且硅烷在硅上正是上方窗口图中与 ALD 相竞争的 CVD 化学，所以在这里观察它恰如其分，并非替代品。两个切换是化学：硅烷需要表面先断开一个键才能附着，等离子体产生的硅基自由基不需要——这正是等离子体 CVD 能低几百开尔文运行的原因；而氢终止的晶圆（HF 末道清洗后的状态）在氢以 H₂ 形式离开之前什么都不接受。',
        RxTimeNote: '请看机理与先后次序，而不是速率。在 900 K 反应器中需要微秒的事件在这里等不起，所以恒温器把腔室加热到同一机理能在皮秒内发生的温度，而硅的参数是代表性数值，并非某个已发表参数集的照录。经过这种压缩仍然成立的才是要点：自由基先于分子附着，裸硅先于钝化硅接受前驱体，氢必须先离开生长才能继续。不成立的是以纳米每分钟计的生长速率——那是 EPI 工具中反应器求解器的任务。',
        SectionMd: '低于窗口——前驱体不反应而是凝结（分子动力学）',
        MdWindowNote:
          '请把这一幕读作窗口的低温端，而不是上面那层薄膜的生长方式。更上面那个表面模型让四个过程互相竞争，' +
          '**凝结**就是其中之一：低于窗口温度时，前驱体不再与表面反应，而只是以多层物理吸附堆积起来——' +
          '这种生长在位点占满之后并不会停下。这正是这里所展示的失效，而且值得一看，因为一个发生凝结的剂量' +
          '看上去就是漂亮的高生长速率，直到你去量厚度分布图为止。它**没有**展示的，是构成窗口平坦中段的那半步反应。' +
          'ALD 靠的是**化学吸附**——一次配体交换，真实地成键与断键，然后就无处可去了——而化学键恰恰是' +
          '伦纳德-琼斯势所没有的东西。所以这里的原子是一种通用的可凝结气体，而不是本配方的三甲基铝：' +
          '物理吸附势阱对"黏不黏得住"是诚实的，对化学反应则是沉默的；假装它能代表化学，恰恰就是这块面板' +
          '花了三台仪器去证明 ALD 并非如此的那件事。',
        presetLabel: {
          highAspect: '高深宽比',
          tooHot: '高于窗口',
          tooCold: '低于窗口',
          shortPurge: '短吹扫',
          starvedDose: '短剂量',
          baseline: 'BASELINE-AL2O3',
        },
        SecondaryLabel: '非均匀性',
        ToolLabel: 'ALD-01 · Al2O3 沉积',
        fault: {
          wetHouseGas: {
            Evidence: [
              '每循环生长量高、寄生 CVD 占比大——吹扫故障的特征，这正是它如此有迷惑性的原因。',
              '前级抽速检查是干净的。正是这条读数把它从一次维修变成一次上报。',
              '饱和度仍是 100%，测温晶圆在设定值上，供给和温度都不涉及。',
              '炉管在同一时间抱怨着一件听起来毫不相干的事。它不是不相干。',
            ],
            Truth: '厂里的干燥气干燥器周末失效带水了，总管里带着约 2% 的水——是液态携带，不是干燥器饱和后 ppm ' +
              '级的缓慢爬升。它到达每一台呼吸厂务氮气或氧气的设备，而到了之后干什么，完全取决于那台设备拿这股气做什么用。注意两个阈值差得有多远：个位数 ppm ' +
              '的水就能毁掉 ' +
              'ALD，因为水是它的共反应物；而用蒸汽长氧化层的炉管，低于约百分之一什么都察觉不到。要两台设备同时叫起来，得是一次粗暴的失效，而这正是两个组几乎从不把各自的问题联系起来的原因。在这台设备上它比全厂任何地方都糟，因为水正是这道工艺的共反应物。带水的吹扫气在 ' +
              'TMA 剂量期间把水放上表面，而这恰恰是吹扫存在的唯一目的——于是薄膜看到的和吹扫太短时一模一样，而且再怎么吹扫都治不好。',
            Complaint: '生长偏高、薄膜回蚀不均——跟前级管路那次一样。可管路上个月刚清过，抽速检查也没问题。同时 THRM-04 在报氧化层偏厚。',
            Label: '厂务吹扫气带水',
          },
          heaterReadsLow: {
            Evidence: [
              '每循环生长量远超标称，寄生占比非常大。',
              '测温晶圆读数明显高于设定值——和另一个加热器故障符号相反，这也是同一种维修能治好两者的原因。',
              '吹扫长短在这里几乎不起作用，这排除了前级管路。',
            ],
            Truth: '热电偶往另一边漂了：控制器为了维持 200 C 的读数拼命加热，晶圆实际上接近 350 C。这高于 ALD 窗口，TMA ' +
              '不等共反应物就自行分解。结果是披着 ALD 配方的 CVD：又快又厚，保形全靠运气。',
            Complaint: '生长量高得离谱，高深宽比批次的台阶覆盖率也挂了。',
            Label: '加热器热电偶读数偏低',
          },
          heaterReadsHigh: {
            Evidence: [
              '每循环生长量高、寄生占比高——形态上和前级堵塞一模一样。',
              '测温晶圆是唯一能把两者分开的读数，它读出比设定值低约 85 C。',
              '饱和度仍是 100%：这不是供给问题。',
            ],
            Truth: '热电偶漂了，控制器以为在 200 C，晶圆实际上接近 115 C。这低于 ALD 窗口，TMA ' +
              '在表面上冷凝而不是化学吸附成单层。冷凝的前驱体不自限制，于是每循环生长量反而升高——这就是为什么晶圆偏冷看起来像吹扫故障，而不像安瓿偏冷。',
            Complaint: '跟上季度那次前级管路的问题一模一样：生长高、膜发雾、HF 回蚀不均。',
            Label: '加热器热电偶读数偏高',
          },
          restrictedExhaust: {
            Evidence: [
              '每循环生长量高于标称，寄生 CVD 占比明显大于零。',
              '饱和度仍是 100%——剂量本身是健康的，这排除了供给故障。',
              '测温晶圆读数在设定值上，这正是它与加热器故障的分界——那是这台设备上得到高生长加高 CVD 的另一条路。',
              '把配方里的吹扫拉长，速率就恢复了，这本身就是一次诊断。',
            ],
            Truth: '前级管路里的沉积物把有效抽速砍到了三分之一，吹扫期间腔室已经排不干净。水进来时 TMA 还在，于是一部分生长变成了 CVD：更快、不自限制、也不均匀。',
            Complaint: '每循环生长量偏高，而且薄膜在 HF 里回蚀不均匀。',
            Label: '前级管路堵塞',
          },
          coldAmpoule: {
            Evidence: [
              '饱和度掉到 95% 以下——剂量已经吃不满了。这是最关键的读数；这台设备上没有别的故障会碰它。',
              '不均匀度严重超差而均值几乎没动，这是跌出饱和的特征，不是晶圆偏热或偏冷的特征。',
              '把剂量拉长，薄膜就恢复了。温度故障不会对此有反应。',
              '安瓿温度远低于设定值。',
            ],
            Truth: '安瓿加热器往低漂了，于是 TMA ' +
              '的蒸气压——连同腔室内的前驱体分压——不到标称值的一半。剂量不再饱和。这才是它严重的原因：剂量饱和时，薄膜不在乎前驱体是怎么分布的；一旦不再饱和，薄膜就只在乎这一件事。均匀性比均值先垮。',
            Complaint: '最近三批的晶圆图变成了靶心状，均值略微偏低。配方没动过。',
            Label: 'TMA 安瓿温度偏低',
          },
          healthy: {
            Evidence: [
              '每循环生长量在认证值上，剂量处于饱和。',
              '台上的每个读数都在限值以内。',
            ],
            Truth: '什么问题都没有。正确的判定就是直接跑片。',
            Complaint: '设备今早刚通过认证。没有任何报告。',
            Label: '已认证设备',
          },
        },
        reading: {
          flowcheck: {
            Label: '喷淋头流量检查（60 分钟）',
          },
          houseutilities: {
            Limit: '总管不漏，PCW 在供应温度，干燥气 < 50 ppm',
            Label: '与厂务共同检查公用系统（2 小时）',
          },
          foreline: {
            Label: '前级抽速检查（40 分钟）',
          },
          parasitic: {
            Label: '监控片 HF 回蚀（45 分钟）',
          },
          instrumented: {
            Label: '测温晶圆（90 分钟）',
          },
          ampoule: {
            Label: '安瓿温度检查（20 分钟）',
          },
          saturation: {
            Label: '剂量饱和曲线（25 分钟）',
          },
          setpoint: {
            Limit: '按配方',
            Label: '加热器设定值',
          },
          step: {
            Label: '10:1 台阶覆盖率',
          },
          edge: {
            Label: '边缘减中心',
          },
          nu: {
            Label: '不均匀度',
          },
          gpc: {
            Label: '每循环生长量',
          },
        },
        aspect: {
          house: {
            Action: '停止在本机上的操作，知会厂务',
            Label: '本机无事可修——上报厂务',
          },
          showerhead: {
            Action: '拆下清洗喷淋头，更换密封垫',
            Label: '喷淋头',
          },
          heater: {
            Action: '用测温晶圆重新校准加热器',
            Label: '衬底加热器',
          },
          purge: {
            Action: '清除前级管路堵塞并复测抽速',
            Label: '吹扫 / 排气路径',
          },
          precursor: {
            Action: '重新加注并加热安瓿，对照流量计核实供给',
            Label: 'TMA 供给',
          },
        },
        knob: {
          doseMs: 'TMA 脉冲时间',
          purgeMs: 'TMA 后吹扫',
          coReactantMs: 'H2O 脉冲时间',
          coPurgeMs: 'H2O 后吹扫',
          pressureMtorr: '腔室压力',
          carrierSccm: '载气流量',
          substrateTempC: '衬底温度',
          cycles: '循环数',
          aspectRatio: '结构深宽比',
        },
        preset: {
          baseline: 'ALD 窗口内的已认证配方：脉冲已饱和，吹扫充分。',
          starvedDose: 'TMA 脉冲缩到 15 ms。低于饱和，因此每循环生长量下降——更糟的是，它现在取决于有多少前驱体到达晶圆的各个位置，均匀性也跟着一起垮。',
          shortPurge: '吹扫缩到 200 ms。水汽进来时腔室里还残留着前驱体，于是一部分生长变成了寄生 CVD：更快，但既不自限制也不均匀。',
          tooCold: '衬底 100 C。太冷，表面反应无法完成。',
          tooHot: '衬底 360 C。前驱体自行分解，生长不再是 ALD。',
          highAspect: '同一份配方用在 60:1 的结构上。沟槽底部需要的暴露量远大于顶部，而这个脉冲量给不了。',
        },
        SectionProcessState: '工艺状态',
        SectionRunControl: '运行控制',
        RunControlNote: 'ALD 是按循环数算的，不是按秒算的。没有速率可设——你决定重复多少次循环，每次拿走多少则由表面自己决定。',
        StartCycling: '开始循环',
        Hold: '暂停',
        RunToCompletion: '跑完全部循环',
        LoadBareWafer: '装入裸片',
        SectionWaferMap: '晶圆图 — 沉积膜厚',
        ModeThickness: '膜厚',
        ModeDeviation: '相对均值的偏差',
        SectionRadialProfile: '径向分布',
        SectionSaturation: '饱和曲线 — 这个脉冲量是自限制的吗？',
        SaturationNote:
          '在其他条件不变的前提下，每循环生长量对脉冲时间作图。真正的 ALD ' +
          '工艺会先上升然后走平：一旦表面位点被占满，再多前驱体也没用。如果这条曲线一直往上爬，这个工艺就不是自限制的，也就不是 ALD。',
        MeasureSaturation: '测量饱和曲线',
        SectionWindow: 'ALD 窗口 — 这个工艺成立的温度区间',
        WindowNote: '低于窗口，前驱体会以多层形式凝结；高于窗口，它不等共反应物就自行分解。两种情况都会让膜长得更快，而且都不是自限制的。窗口就是中间剩下的那段平台。',
        MeasureWindow: '测量工艺窗口',
        SectionRecipe: '配方 — {{code}}',
        SectionStoredRecipes: '已存配方',
        TileGpc: '每循环生长量',
        TileThickness: '膜厚',
        TileNonUniformity: '不均匀度',
        TileSaturation: '饱和度',
        TileParasitic: '寄生 CVD',
        TileStepCoverage: '台阶覆盖率',
        TileCycles: '循环数',
        TileCycleTime: '单循环时间',
        TileRunTime: '总运行时间',
        SelfLimiting: '自限制',
        DoseStarved: '脉冲量不足',
        TrueAld: '真正的 ALD',
        NotSelfLimiting: '非自限制',
        AtAspect: '深宽比 {{ratio}}:1',
        PurgeResidence: '吹扫为驻留时间的 {{factor}} 倍',
        CaptionNonUniformity: '不均匀度 {{percent}}%',
        CaptionNoFilm: '每循环生长量 — 还没有沉积任何膜',
        ChartProfileThickness: '沿半径的沉积膜厚',
        ChartProfileGpc: '沿半径的每循环生长量',
        ChartProfileX: 'r / R（0 = 中心）',
        ChartSeriesFilm: '薄膜',
        NotMeasured: '尚未测量',
        ChartSaturationTitle: '每循环生长量对脉冲时间 — 走平即为自限制',
        ChartSaturationX: 'TMA 脉冲时间（ms）',
        ChartSeriesGpc: '每循环生长量',
        AnnYourDose: '你的脉冲量',
        ChartWindowTitle: '每循环生长量对温度',
        ChartWindowX: '衬底温度（°C）',
        AnnYourSetpoint: '你的设定值',
        ChartParasiticTitle: '这些生长里有多少不是自限制的',
        ChartParasiticY: '寄生占比 %',
        ChartSeriesParasitic: '寄生 CVD',
        AnnAldLimit: '5% — ALD 的实用上限',
        SectionOnChuck: '卡盘上',
        SendToMet: '把晶圆送到 MET-05',
        SectionAtoms: '原子尺度——一个循环，一颗一颗分子看',
        AtomsNote:
          '这不是分子动力学——真正的 MD ' +
          '以飞秒计，而这里是完整的一个循环。它是一排表面位点加一组写定的反应规则，其统计行为受上方各图所用的同一套速率方程约束，并由本面板当前的配方驱动。缩短一次吹扫，或走出温度窗口，看看化学会作何反应。',
        AtomsRun: '继续',
        AtomsPause: '暂停',
        AtomsReset: '重置',
        AtomsPhasePurge: '吹扫',
        AtomsLegendOh: 'OH 位点——可接受 TMA',
        AtomsLegendCh3: 'CH₃ 封端——已占满',
        AtomsLegendCh4: 'CH₄ 逸出',
        AtomsLegendCvd: '气相 CVD 颗粒',
        AtomsCycle: '第 {{n}} 个循环',
        AtomsCoverage: '覆盖率',
        AtomsFilm: '膜厚',
        AtomsCvdCount: 'CVD 事件',
        AssistantBlurb: '提出问题，我会设计一个实验，用这台求解器把它跑出来，并报告真正得到的结果——然后再还你一个更锋利的问题。',
        AssistantLead: '这里真正要紧的问题不是膜长得多快，而是当表面被填满时生长会不会停下来。让我来帮你验证。',
      },
      metrology: {
        D3Note: '上面那个盒子让波打在一排原子上，它的屏是一条线。真实样品是一层原子，在两个方向上都有周期性，它的屏是一个布满斑点的平面——这才是衍射仪器真正交到技术员手里的东西。把它读成翻转过来的晶格：斑点相距远，说明原子靠得近；把网格沿一个方向拉长，斑点就只沿那个方向靠拢，别的方向不变。把能量调低，虚线圆会缩小到只包住中心的十字；低于 ' +
          '1.5 eV ' +
          '时波长比网格还长，这层原子就让波作为一整束原样通过，束里几乎没有任何东西能说明那里有过原子。说“几乎”，是因为这么短的波包并不只有一个能量：它的高速尾部仍在阈值之上，会撑起一圈很淡的一级斑，亮度只有五分之一，直到旋钮远低于阈值才消失。斑点是求解器所传播的波的傅里叶变换，不是画上去的：十字和圆是预测，是事后叠加到屏上的。网格取 ' +
          '1 nm，比真实的表面网格更宽，好让两圈斑点都在旋钮范围内出现；这套几何关系与尺度无关。原子实是光滑的排斥势垒，只有一层，也没有任何非弹性散射。',
        D3Energy: '电子能量',
        D3Rect: '沿 z 拉长',
        D3Square: '正方网格',
        D3ScreenCaption: '远处的屏：穿过原子层的波要去往哪里。十字是网格的倒易点阵，即 2π 除以间距；虚线圆是电子自身的波数。只有圆内的十字上才可能亮起斑点。',
        D3Idle: '按“运行”：波包在工作线程上传播，每次运行五到十秒。',
        D3Gain: '波的亮度比实际放大了 ×{{m}}',
        D3Status: '{{fs}} / {{run}} fs · {{fwd}} % 已穿过原子层 · Σ|ψ|² = {{norm}}（含吸收边界）',
        D3NoOrders: '不存在衍射束：波长比网格还长',
        D3FirstOrder: '一个一级斑占 {{p}} %，偏离束方向 {{deg}}°',
        D3Straight: '落在直通斑上的份额',
        D3NoNextRing: '这个旋钮范围内的每一圈都已出现',
        D3NextRing: '下一圈斑点在 {{e}} eV 出现',
        D3Beams: '这层原子能产生的束数',
        D3MeshRect: '网格为 {{a}} nm × {{b}} nm',
        D3MeshSquare: '网格为 {{a}} nm 的正方形',
        D3Wavelength: '波长与网格的比较',
        D3Circle: '束必须落在 k = {{k}} nm⁻¹ 之内',
        D3ScreenX: '横向波矢 k_y (nm⁻¹)',
        D3About: '这里展示了什么，模型又略去了什么',
        SectionDiffraction3d: '一层原子，以及它留下的斑点——三维电子衍射（薛定谔方程，分裂 Cayley 步进）',
        UnitNmReported: 'nm（报出值）',
        LabelFitResidual: '拟合残差',
        SecondaryLabel: '量具 R&R',
        ToolLabel: 'METR-05 · 光谱反射仪',
        fault: {
          wrongIndex: {
            Evidence: [
              '残差涨了五倍而探测器噪声正常。这个组合就是全部诊断：数据吵会同时抬高残差和 R&R，模型错只抬高残差。',
              '量具 R&R 正常，测量是可重复的——它在可重复地拟合一种错误的材料。',
              '椭偏仪同时解 n 和厚度，解出的折射率远高于氧化物的值。',
            ],
            Truth: '沉积设备往富氮方向漂了，膜其实是折射率接近 1.54 的氮氧化物，而测量配方还按纯氧化物的 1.46 ' +
              '假设。拟合被要求用错误的材料去重现条纹，它只能用唯一的办法补偿——报一个更厚的膜。这是唯一一个量测设备无辜的故障：它报告的是上游一次真实的变化。',
            Complaint: '氧化层厚度高报 4%，拟合看起来也比平时略差。',
            Label: '薄膜不是配方以为的那种',
          },
          badStandard: {
            Evidence: [
              '量具 R&R 和残差都正常——又一次，没有任何东西像坏测量。',
              '误差是固定的纳米数，不是百分比。两点核查上，薄标准片和厚标准片都是同样的 3 nm，而波长误差恰恰不会这样。',
              '标准片送去重新认证，发现它比证书上更厚。',
            ],
            Truth: '设备用来归零的认证标准片在抽屉里放久了，长出了自然氧化层，真实厚度已经不是证书上写的那个数。设备对着一个错误的数字归了零，现在把这个误差加进它量的每一样东西。',
            Complaint: '什么都厚了约 3 nm，跟去年光栅那次一样。',
            Label: '校准标准片长了自然氧化层',
          },
          gratingDrift: {
            Evidence: [
              '量具 R&R 正常，残差正常。这里没有任何东西像一次坏的测量。',
              '误差和厚度成正比——厚膜薄膜都是 3%。用两种厚度的认证标准片做两点核查，误差随膜厚一起长，这就是它和坏标准片的分界。',
              '这台设备精密而错误，这是任何控制图都永远抓不住的失效模式。',
            ],
            Truth: '单色仪的光栅滑了。每个波长都被高报约 3%，条纹看起来落在比实际更长的波长上，拟合于是回报厚 3% ' +
              '的膜厚。测量完全可重复，残差恰好正常，因为数据本身没有任何问题——错的只是写在旁边的那个数。一个工艺组追着一台从来没坏的设备跑了两个星期。',
            Complaint: '沉积膜厚偏厚 3% 已经两周了。沉积设备重新校准了两次，刻蚀组已经开始做补偿。',
            Label: '单色仪失准',
          },
          dimLamp: {
            Evidence: [
              '量具 R&R 超过 30%，控制图上的波动大半是量具自己。',
              '重复测量的均值仍在认证值上——准确度没丢，丢的只是精密度。',
              '残差随噪声成比例上升，数据吵时就该这样，模型错时不是这样。',
            ],
            Truth: '灯老化了，收光光路也脏了，探测器噪声是标称的五倍。拟合被要求在更吵的光谱里找一个厚度，于是它游移不定。沉积设备没有任何问题，答案本身也没有问题——设备仍然围着正确值散布。丢掉的是分辨真实工艺漂移和测量散布的能力，而那是这台设备存在的唯一意义。',
            Complaint: '测量结果满天飞。同一片晶圆量五次给五个数。工程师认为是沉积设备不稳了。',
            Label: '灯老化，探测器噪声上升',
          },
          healthy: {
            Evidence: [
              '报出厚度在认证值上，量具 R&R 低于 10%。',
            ],
            Truth: '什么问题都没有。这台设备说的是真话，而且说得可重复。',
            Complaint: '每日标准片检查通过。没有任何报告。',
            Label: '量具已认证',
          },
        },
        reading: {
          lampcheck: {
            Label: '灯输出与暗电流检查（1 小时）',
          },
          ellipsometer: {
            Label: '椭偏仪交叉核对，解出 n（4 小时）',
          },
          certcheck: {
            Label: '标准片送外重新认证（5 天）',
          },
          twopoint: {
            Limit: '两点均在 +/- 1 nm 内',
            Label: '两点核查，20 与 400 nm 标准片（3 小时）',
          },
          model: {
            Limit: '按配方',
            Label: '所设膜折射率',
          },
          repeat: {
            Label: '重复性，1 sigma',
          },
          residual: {
            Label: '拟合残差（RMS）',
          },
          grr: {
            Label: '量具 R&R',
          },
          reported: {
            Label: '认证标准片读数',
          },
        },
        aspect: {
          filmmodel: {
            Action: '重测该膜的 n 和 k，更新配方模型',
            Label: '薄膜光学模型',
          },
          standard: {
            Action: '召回标准片、重新认证、机台重新归零',
            Label: '校准标准片',
          },
          grating: {
            Action: '用发射线光源重新校准光栅',
            Label: '单色仪波长标尺',
          },
          lamp: {
            Action: '更换灯泡，清洁收光光路',
            Label: '灯与探测器',
          },
        },
        SectionMeasurement: '量测',
        SectionSpectrum: '反射光谱与拟合',
        SpectrumNote: '这台设备并不测厚度。它测的是反射光随波长的变化，然后用一个膜层结构去拟合这些干涉条纹。它报出来的数字，就是那次拟合收敛到的结果。',
        SectionTruth: '晶圆上真正是什么',
        TruthNote: '膜本身。学生通常并不会知道这一项。',
        SectionAssumed: '设备配方假设的是什么',
        AssumedNote: '把这两项设成不同的膜，设备照样会报出一个精确到小数点后两位的厚度，而且错得很离谱。量测本身就是一个模型，而模型可以错得信心十足。',
        SectionGauge: '量具能力',
        GaugeNote: '对同一片不变的晶圆反复测量多次。这个散布来自设备，不是来自制程。真正要紧的，是把它和制程本身实际的变异放在一起比：量具从来没有绝对的好坏，只有对某项工作够不够好。',
        RunGauge: '做一次量具研究（重复 25 次）',
        SectionControl: '管制图——60 批',
        ControlNote: '每一点都是制程真值加上量测噪声。管制规则对两者都会触发，而图本身无法告诉你它反应的是哪一个。判断这件事，正是这份工作。',
        RunStable: '跑一个稳定的制程',
        RunDrift: '跑一个缓慢漂移的制程',
        RunNoisy: '用一台噪声大的量具来跑',
        ResetGauge: '重置量具',
        SectionAbsorption: '折射率从何而来——衬底对光的吸收（紧束缚能带）',
        AbsNote: '上面的拟合需要膜层中每一种材料的折射率，而折射率不是材料的常数：它是材料中的电子在该能量的光到来时的响应。低于带隙时没有光子能把电子抬过去，晶体透明，n 几乎不动；高于带隙时晶体吸收，k 从零升起，n 随之摆动。这里的谱由 CZ-07 所画的同一套紧束缚能带算出：整个布里渊区内从满带到空带的每一次竖直跃迁，按其强度加权。硅的吸收起点在其直接带隙处，约 3 eV，远高于 1.1 eV 的基本带隙，因为单个光子无法提供间接跃迁所需的动量：所以晶圆在反射仪的红端是透明的，只在蓝端强烈吸收。锗和砷化镓起点低，吸收整个扫描范围。形状与起点是计算结果；标度为相对值，因为模型不含激子与局域场修正。',
        AbsChartTitle: '吸收随光子能量变化',
        AbsChartX: '光子能量 (eV)',
        AbsChartY: 'ε₂，相对值',
        AbsOnset: '起点 {{ev}} eV（直接带隙）',
        AbsGap: '带隙 {{ev}} eV，间接',
        AbsOnsetGap: '起点 {{ev}} eV，带隙 {{gap}} eV',
        AbsShort: '{{formula}} 自 {{onset}} eV 起吸收 · 带隙 {{gap}} eV，{{kind}}',
        AbsHelp: '以纳米表示的吸收起点，以及琥珀色标线的含义',
        AbsStatus: '{{formula}} 自 {{onset}} eV（{{nm}} nm）开始吸收 · 基本带隙 {{gap}} eV，{{kind}} · 琥珀色标线：反射仪 800-400 nm 扫描范围',
        AbsIndirect: '间接',
        AbsDirect: '直接',
        AbsCrystal: '晶体',
        AbsFailed: '能带结构引擎加载失败。',
        AbsLoading: '正在加载能带结构引擎…',
        SectionDiffraction: '仪器能分辨什么——电子波遇到靶（薛定谔方程，二维）',
        PhononAcoustic: '声学支',
        PhononOptical: '光学支',
        PhononChartTitle: '声子色散',
        PhononChartX: '沿 Γ–X–W–K–Γ–L 的波矢',
        PhononChartY: '频率 (cm⁻¹)',
        PhononMeasured: '实测 {{cm}} cm⁻¹',
        PhononRamanTitle: '一阶拉曼谱线',
        PhononRamanX: '拉曼位移 (cm⁻¹)',
        PhononRamanY: '强度，峰值 = 1',
        PhononRamanSeries: '计算值：[001] 单态',
        PhononFailed: '晶体引擎加载失败。',
        PhononShort: '{{material}}：拉曼线 {{line}} cm⁻¹（实测 {{measured}}）',
        PhononHelp: '这次求解的全部数值',
        PhononStatus: '{{material}}：拉曼谱线（[001] 单态）位于 {{line}} cm⁻¹（实测 {{measured}}）· 面内双态 {{doublet}} cm⁻¹ · 沿 [100] 声速 {{sound}} km/s（实测 {{soundMeasured}}）· {{ms}} ms',
        PhononImaginary: '不稳定：出现虚频模式',
        PhononLoading: '正在加载晶体引擎…',
        PhononStrain: '双轴应变',
        PhononCrystal: '晶体',
        SectionPhonons: '拉曼仪读到的是什么——晶体的声子，以及随应变移动的谱线（Stillinger–Weber 晶格动力学）',
        PhononsNote: '拉曼光谱仪测的是从晶圆返回、恰好移动了一个晶格振动量子的光；晶圆厂之所以配备它，是因为这个量子会随应变移动：压缩晶格，键变硬，谱线上移；拉伸晶格，谱线下移。下面没有任何一条是记住的曲线。在 IMP-10 晶格所用的同一个 Stillinger–Weber 力场上，以周期性边界搭建所选晶体的 3×3×3 超胞，把每个子晶格中的一个原子沿各轴推动百分之一埃，再读出落在其他所有原子上的力——十二次力计算就是双原子基元所需的全部 Hessian。由此组装的动力学矩阵给出沿 Γ–X–W–K–Γ–L 的六支色散、旁边的态密度，以及 Γ 点的拉曼谱线。请预期诚实力场给出的数字，而不是实测值：SW 把硅的谱线放在约 593 cm⁻¹，实测为 520，因为它是按熔点和结合能拟合的，而不是按声子——正如 CZ-07 的能带视图同时显示计算带隙与实测带隙。在双轴应变下，三重简并的谱线分裂为沿 [001] 偏振的单态和面内的双态；正对 (001) 晶圆测量的仪器只看得到单态，双态被这一几何禁戒，所以画成虚线。SW 的方向是对的——拉伸时下移，压缩时上移——但大小和先后不对：实测形变势给出硅的单态每 1% 约 −7 至 −8 cm⁻¹，比双态移得更多；SW 给出 −4.4，反而是双态移得更多；锗的单态 SW 给出 −1.4，实测为 −4.1。省略了：非谐线宽与温度（谱线以固定 3 cm⁻¹ 宽度绘制）以及同位素质量无序。',
        Mode3dLoading: '正在构建晶体的振动模式…',
        Mode3dStatusStill: 'Γ 点的 {{name}} · 0 cm⁻¹ · 整块晶体一起平移：没有回复力，所以没有频率',
        Mode3dWaveInfinite: '无穷大',
        Mode3dShort: '{{name}} · {{cm}} cm⁻¹ · 波长 {{wave}}',
        Mode3dHelp: '关于这个模式和这块晶体的更多信息',
        Mode3dStatus: '{{name}} · {{cm}} cm⁻¹（{{thz}} THz）· 波长 {{wave}} · 一个周期 {{period}} fs，画面放慢了 {{slow}} 倍',
        Mode3dTogether: '同向运动',
        Mode3dAgainst: '彼此反向运动',
        Mode3dMixed: '部分同步',
        Mode3dSublattices: '两套子晶格{{how}}（{{phase}}）',
        PhononRamanForbidden: '面内双态，正对 (001) 时被禁戒',
        PhononDosTitle: '态密度',
        PhononDosX: '每 cm⁻¹ 的模式数',
        Mode3dPartial: '这列波比晶块更长，所以只显示了一个波长的一部分。',
        Mode3dBlock: '{{atoms}} 个原子，{{cells}} 个晶胞',
        Mode3dLegend: '琥珀色和蓝色是金刚石结构中相互嵌套的两套子晶格。',
        Mode3dAmplitude: '绘制振幅',
        Mode3dAmplitudeValue: '{{a}} Å（300 K 下真实的热运动约为 {{thermal}} Å）',
        Mode3dPause: '暂停',
        Mode3dPlay: '播放',
        Mode3dChartMark: '下方绘制的模式',
        Mode3dSegment: '波矢所在路径',
        Mode3dFraction: '在该段上的位置',
        Mode3dBranch: '分支',
        DiffNote: '波无法分辨比自身波长更小的细节，这一个事实决定了车间里每台仪器的极限：反射仪几百纳米的光能看到膜厚，却看不到膜的纹理；SEM 的 1 keV 电子波长 0.04 nm，能看到 20 nm 的线条。这里在网格能够呈现的能量下展示这一事实。一个高斯电子波包遇到两条狭缝或一排原子，穿过去的部分按角度收集在屏幕上。间距为 p 的周期性靶把波长 λ 送入 sin θ = mλ/p 的各级衍射——图上的标线——而当 λ 大于 p 时任何级次都不存在。把能量降到那一步，各级衍射消失：波作为单一光束离开，样品便没有可见的结构。没有任何东西是画上去的；波包遇到势场，盒子的吸收边缘记录到达的一切。从靶上反弹回来的概率也被计入，所以总和为一。',
        DiffChartTitle: '到达屏幕的分布（按角度）',
        DiffChartX: '与前进方向的夹角 (°)',
        DiffChartY: '每度概率，峰值 = 1',
        DiffChartSeries: '屏幕上',
        DiffOrder: 'm = {{m}}',
        DiffShort: 'λ = {{lambda}} nm · 前向 {{forward}}%，后向 {{back}}%',
        DiffHelp: '间距、衍射级与时间',
        DiffStatus: 'λ = {{lambda}} nm，间距 {{spacing}} nm · 衍射级在 {{orders}} · 前向 {{forward}}%，后向 {{back}}% · {{fs}} fs',
        DiffNoOrders: '无——波长大于间距',
        DiffEnergy: '电子能量',
        DiffTarget: '靶',
        DiffSlits: '双缝，间距 1.5 nm',
        DiffAtoms: '一排原子，间距 0.5 nm',
        DiffRun: '运行',
        DiffPause: '暂停',
        DiffReset: '重置',
        DiffFailed: '波动求解器加载失败。',
        DiffLoading: '正在加载波动求解器…',
        SectionShift: '值班',
        TileToolReports: '设备报告值',
        TileActuallyIs: '实际值',
        TileError: '误差',
        TileFitResidual: '拟合残差',
        TileRepeatability: '重复性',
        TileGaugeRR: '量具 R&R',
        TileCpk: 'Cpk',
        TileChartAlarms: '管制图警报',
        TileGaugeNoise: '量具噪声',
        NoteAssuming: '假设为 {{material}}',
        NoteWrongModel: '假设的模型不对',
        NoteModelCorrect: '模型正确',
        NoteResidualRaised: '几乎没升高——它不会警告你',
        NoteResidualFloor: '处在检测器噪声底线',
        NotMeasured: '尚未量测',
        NoteToolSigma: '设备的 1σ',
        Capable: '合格',
        NotCapable: '不合格',
        RunAStudy: '先做一次研究',
        NoteAllFalse: '制程是稳定的——全是误报',
        NoteInLots: '在 60 批中',
        NoteMeasurementSigma: '每批的量测 σ',
        SectionLot: '载台上的批次',
        StageEmpty: '载台是空的。先在 DEP-02 跑一次沉积，把晶圆送到这里再量——这台设备报出来的数字，是第一次有人知道那份配方到底做出了什么。',
        TravelCard: '流程卡',
        TileReported: '报告厚度',
        TileOffTarget: '偏离目标',
        TileNonUniformity: '不均匀度',
        NoteModelMatch: '模型拟合得有多好',
        TileAssumedIndex: '假设折射率',
        NoteFromRecipe: '来自量测配方',
        TileVerdict: '判定',
        VerdictShips: '放行',
        VerdictHold: '扣留',
        NineSites: '中心到边缘共九个点。这台设备既没有做出这层膜，也修不了它——如果数字不对，答案在上游；而交接班报告里的量具校验，就是你先把这台设备排除掉的办法。',
        ChartSpectrumTitle: '实测光谱与拟合模型对照',
        ChartWavelength: '波长（nm）',
        ChartReflectance: '反射率',
        SeriesMeasured: '实测',
        SeriesFitted: '拟合模型',
        ChartGaugeTitle: '对同一片不变晶圆量测 25 次——σ {{sigma}} nm',
        ChartGaugeEmpty: '尚未量测',
        ChartReadingNumber: '第几次读数',
        ChartReportedThickness: '报告厚度（nm）',
        SeriesReadings: '读数',
        AnnTrueValue: '真值',
        ChartSpcTitle: '管制图——{{alarms}} 次警报，Cpk {{cpk}}',
        ChartSpcEmpty: '尚未运行',
        ChartLot: '批次',
        ChartThickness: '厚度（nm）',
        SeriesTrue: '真值（操作员看不到）',
        AnnCentre: '中心线',
        AnnSpec: '规格',
        AssistantBlurb: '提出问题，我会设计一个实验，用这台求解器把它跑出来，并报告真正得到的结果——然后再还你一个更锋利的问题。',
        AssistantLead: '这台设备只负责看。你可以问我：它告诉你的这件事是真的吗——还是量具、模型，或者只是运气。',
      },
    },

    house: {

      gowning: {
        AssistantBlurb:
          '可以问：监测读数在洁净等级内的区域，为什么仍然在污染晶圆；一件无尘服到底值多少颗粒。每个回答都是在这个区域当前状态下真实跑出来的。',
        AssistantLead: 'GOWN-00 探究助手',
      },

      crystal: {
        AssistantBlurb:
          '可以问：电阻率沿硅锭的偏析、这根晶体能拉多快、氧从哪里来。每个回答都是在这台单晶炉上真实跑出来的一次生长。',
        AssistantLead: 'CZ-07 探究助手',
      },

      waferprep: {
        AssistantBlurb:
          '可以问：锯切损失了多少硅、损伤有多深、晶圆为什么会翘曲。每个回答都是在这条线上真实跑出来的。',
        AssistantLead: 'WPR-08 探究助手',
      },

      clean: {
        AssistantBlurb:
          '可以问：延长槽内时间有没有用、HF 之后的排队时间代价是什么、第二道槽是干什么的。每个回答都是在这台清洗台上真实跑出来的一次清洗。',
        AssistantLead: 'CLN-09 探究助手',
      },

      implant: {
        AssistantBlurb:
          '可以问：结为什么比手册深、晶格什么时候变成非晶、能量到底决定了什么。每个回答都是在这条束线上真实跑出来的一次注入。',
        AssistantLead: 'IMP-10 探究助手',
      },

      patterning: {
        AssistantBlurb:
          '可以问：间距为什么一宽一窄、最终节距到底由什么决定、这条工艺路线在哪一步没地方了。每个回答都是用这套配方真实跑完的八步流程。',
        AssistantLead: 'PAT-21 探究助手',
      },

      plating: {
        AssistantBlurb:
          '可以问：沟槽为什么留了空洞、边缘为什么镀得更厚、要镀多久。每个回答都是在这个镀液里真实跑出来的一次填充。',
        AssistantLead: 'ECD-12 探究助手',
      },

      inspection: {
        AssistantBlurb:
          '可以问：降低阈值买到了什么、这套配方根本看不见什么、部分扫描够不够。每个回答都是在这台设备上真实跑出来的一次扫描。',
        AssistantLead: 'INSP-13 探究助手',
      },

      probe: {
        AssistantBlurb:
          '可以问：芯片面积大了良率为什么差这么多、这条线该用哪个良率模型、损失是晶圆的还是探针卡的。每个回答都是针对这批片子真实跑出来的一次分选。',
        AssistantLead: 'PRB-14 探究助手',
      },

      dicing: {
        AssistantBlurb:
          '可以问：晶圆能减薄到多少、更细的砂轮值不值、切得更快的代价是什么。每个回答都是用这套配方真实跑出来的一次减薄与切割。',
        AssistantLead: 'DICE-15 探究助手',
      },

      bonding: {
        AssistantBlurb:
          '可以问：键合窗口在哪里、引线数量为什么改变良率、固化对金属间化合物做了什么。每个回答都是在这台设备上真实跑出来的一次键合。',
        AssistantLead: 'BOND-16 探究助手',
      },

      packaging: {
        AssistantBlurb:
          '可以问：好裸片为什么在回流时失效、封装为什么翘曲、后固化是干什么的。每个回答都是用这套配方真实跑出来的一次塑封与回流。',
        AssistantLead: 'PKG-17 探究助手',
      },

      finaltest: {
        AssistantBlurb:
          '可以问：保护带的代价是什么、老化测试值不值那些小时、测试时间到底要多久。每个回答都是在这台测试机上真实跑出来的一个批次。',
        AssistantLead: 'FT-18 探究助手',
      },

      gas: {
        AssistantBlurb:
          '可以问：到底需要几次吹扫循环、检测器能不能及时报警、最后排到烟囱的是什么。每个回答都是在这个气柜面板上真实跑出来的一次评估。',
        AssistantLead: 'GAS-19 探究助手',
      },

      system: {
        vacuum: {
          Label: '厂务工艺真空',
          Action: '上报厂务：对共用真空总管做检漏',
        },
        pcw: {
          Label: '工艺冷却水',
          Action: '上报厂务：把冷冻水供水温度恢复正常',
        },
        gas: {
          Label: '厂务干燥气体总管',
          Action: '上报厂务：更换总管干燥器，吹扫并重新认证',
        },
        upw: {
          Label: '超纯水厂',
          Action: '上报厂务：再生抛光混床并重新认证回路',
        },
        hvac: {
          Label: '洁净室空调送风',
          Action: '上报厂务：恢复循环空调机组到设计风量',
        },
        mau: {
          Label: '新风机组',
          Action: '升级到厂务：更换新风初效过滤器并重新平衡压差梯度',
        },
        pumps: {
          Label: '机台干泵',
          Action: '升级到厂务：更换机台下方的干泵并重新验证前级管路',
        },
        chem: {
          Label: '化学品输送',
          Action: '升级到厂务：隔离泄漏管线，更换内管并重新认证双套管',
        },
      },
      fault: {
        headerLeak: {
          Label: '共用工艺真空总管泄漏',
          Truth:
            '上周末次厂房施工时动到了共用工艺真空总管上的一处法兰，现在正在漏。挂在这条总管上的' +
            '每一个腔室都会把它当成自己的漏点——因为从腔室内部看，它就是。',
        },
        warmPcw: {
          Label: '工艺冷却水温度偏高',
          Truth:
            '冷冻水站送来的供水比标称高了约 22 C。任何一台设备本身都没有故障；只是所有把热排进' +
            '这条回路的设备，都已经守不住自己的设定值了。',
        },
        wetHeader: {
          Label: '厂务干燥气体总管带水',
          Truth:
            '厂务干燥气体的干燥器周末失效带水，总管里现在大约含 2% 的水——这是液带，' +
            '不是干燥器饱和时那种 ppm 级的缓慢爬升。它会抵达每一台呼吸厂务氮气或氧气的设备，' +
            '而到达之后造成什么后果，完全取决于那台设备拿这股气做什么。注意两个阈值相差有多远：' +
            'ALD 在个位数 ppm 就已经被毁掉，因为水正是它的共反应物；而用水汽长氧化层的炉管，' +
            '一个百分点以下根本察觉不到。要两台设备同时喊问题，得是一次严重失效——' +
            '而这恰恰就是两个组很少把各自的问题联系起来的原因。',
        },
        spentBed: {
          Label: '抛光混床耗尽',
          Truth:
            '超纯水回路的抛光混床已用掉 91% 的交换容量，钠开始穿透：上周还读 18.1 MΩ·cm 的回路' +
            '今天只有 11。没人预见到，因为根本无从预见——离子交换床在几乎整个寿命里都把表针稳在' +
            ' 18.2，然后在最后几个百分点内崩溃，这正是按处理水量而不是按读数再生的原因。冲洗水是' +
            '晶圆在湿法台看到的最后一道水，于是那台设备表现得像两种化学同时失效——单个槽体不可能' +
            '造出的特征。',
        },
        mauFiltersLoaded: {
          Label: '新风初效过滤器堵塞',
          Truth: '新风机组的初效过滤器经过一季花粉和施工粉尘已经堵塞，机组只送出约 55% 的设计新风量。循环空调机组正常，各区域仍以全速换气——但排风机是以新风量来平衡的，平衡一失，整个洁净室都低于走廊压力。所有区域同时读到负压，走廊空气从每条缝隙倒灌进来；任何区域对自身排风的处理都无法恢复建筑已失去的压差梯度。在楼下更换初效过滤器需要四小时。',
        },
        dryPumpFailed: {
          Label: 'PRB-14 下方的干泵故障',
          Truth: 'PRB-14 下方的干泵轴承损坏卡死：原本保持在 50 mTorr 的前级管路现在停在 600 Torr，只比大气压低一点。探针台靠真空把每片晶圆吸在卡盘上，吸力只剩五分之一，晶圆就松了——卡盘中心密封还抓得住的地方是平的，抓不住的边缘翘起来。探针卡在翘起的芯片上落针偏轻，这些芯片就接触失败，看起来和探针卡不平完全一样；但探针卡没问题，探针也没问题，厂里其他机台都没变，因为没有别的机台共用这台泵。到楼下换泵需要三小时。',
        },
        chemLineLeak: {
          Label: 'ECD-12 下方电镀化学品管线泄漏',
          Truth: 'ECD-12 下方电镀化学品管线的内管在双套管内裂开。套管起了作用：化学品流到柜下的托盘，托盘传感器在四升时报警，泄漏联锁关停了输送泵——这正是它的用途，因为地下层地面上的电镀液就是一起酸泄漏。楼上的镀液整个班次都没有补液，而每片晶圆都会从镀液里带走铜，所以镀槽只剩配方所假设铜量的三分之一在运行。其他机台都没变：每条管线各自独立。隔离管线、更换内管并重新认证双套管需要五小时。',
        },
        ahuBeltSlip: {
          Label: '循环空调机组送风不足',
          Truth:
            '四台循环空调机组里有两台皮带打滑，各洁净间只拿到约 55% 的设计风量。天花板里的每' +
            '块过滤器都好，上面的每台风机都好——风就是没送到。同样的人数下房间读数变脏，暴露它' +
            '的数字是恢复时间：洁净间消化一次扰动要多久只由换气次数决定，别的什么都动不了它，' +
            '所以过滤器干净而恢复缓慢，问题在大楼，不在房间。在这上面换天花板过滤器要花十二小' +
            '时，什么都换不回来。',
        },
      },
    },

    footer: {
      Built: '构建于 {{date}}。',
    },

    menu: {
      Menu: '菜单',
      Settings: '设置',
      SessionNote: '你的班次——日志、批次与设备设置——保存在本机,刷新后仍在。',
      SessionClear: '开始新会话',
      Entrance: '返回入口',
      EntranceNote: '回到入口界面。你的日志、批次与设备设置保留。',
      Model: 'AI 模型',
      Auto: '自动',
      AutoNote: '按地区',
      LocalNote: '没有配置托管模型，问题在本机匹配。',
      Lighting: '照明',
      LightBright: '白光',
      LightBrightNote: '明亮偏冷——运转中的工厂',
      LightBlue: '蓝光',
      LightBlueNote: '下班后的自动化厂区',
      LightYellow: '黄光',
      LightYellowNote: '安全灯——不含使光刻胶曝光的紫外线',
      Graphics: '图形',
      QualityAuto: '自动',
      QualityAutoNote: '默认',
      QualityHigh: '更多灯光',
      QualityHighNote: '更逼真',
      QualityLow: '更少灯光',
      QualityLowNote: '较不逼真',
      Loading: '正在加载',
      HallBayChase: '厂房 A',
      HallBayChaseNote: '工作区加维修通道布局：设备正面嵌在隔墙内，维修通道在墙后',
      HallBallroom: '厂房 B',
      HallBallroomNote: '大开间布局：开放式厂房，所有主机一览无余',
      Sound: '音效',
      SoundOn: '开启',
      SoundOnNote: '厂房的真实环境声',
      SoundOff: '静音',
      SoundOffNote: '无声 — 按 M 键切换',
      DesignFab: '设计一座晶圆厂',
      DesignFabNote: '用这二十五台设备排出你自己的厂房',
      YourFabs: '你建的厂',
      CustomFabNote: '{{n}} 台设备，{{hall}}布局',
      Fabs: '我的厂区',
      FabsNote: '你自己的厂区：打开一个、保存当前的，或设计一个新的',
      OpenFab: '打开…',
      OpenFabNote: '保存在你账户里的厂区',
      SaveFab: '保存',
      SaveFabNote: '按现名保存你正站着的这个厂区',
      SaveNothing: '厂区 A 和 B 是内置的；先设计一个才有可保存的',
      SaveFabAs: '另存为…',
      SaveFabAsNote: '以新名字存一份副本，并切换到副本',
      SaveAsName: '副本的名字',
      SaveAsGo: '保存副本',
      NoSavedFabs: '这个账户还没有保存过厂区。',
      LibrarySignedOut: '登录（右上角的账户按钮）后厂区会保存到你的账户；访客登录也可以。',
      LibraryDenied: '这个账户无权读取那个库。',
      LibraryOffline: '厂区库连不上，请检查网络后重试。',
      LibraryLoading: '正在获取你的厂区…',
      FabSaved: '已保存 {{name}}。',
      DeleteFab: '从我的厂区中删除',
      DeleteFabConfirm: '从你的账户删除“{{name}}”？你正站着的厂区不受影响。',
      SavedOn: '保存于 {{when}}',
    },

    routing: {
      Via: '由 {{label}}（{{model}}）作答',
      OnDevice: '由本机的模式匹配作答',
      Unreachable: '由本机作答——托管模型无法连接',
      Assumed: '推定为{{region}}',
      RegionUs: '美国',
      RegionIntl: '国际',
    },

    shift: {
      Debrief: '本班总结',
      SessionTallyRuns: '本次会话：{{tools}} 个站点共 {{runs}} 次 · {{scrapped}} 次超出规格。',
      Performance: '本站表现',
      KpiDowntime: '设备停机',
      KpiDowntimeNote: '本班',
      KpiOutOfSpec: '超出规格',
      KpiOutOfSpecNote: '共 {{runs}} 次',
      CallsHeading: '你当时的判断',
      CallsNote: '这些是你在设备给出结果之前说的。这里没有判定对错——请自己对照着看。',
      CallsSaid: '你说',
      CallsMeasured: '实测',
      CallsExplained: '被问到原因时',
      Hours: '{{hours}} 小时',
      CostTotal: '合计',
      NothingYet: '这个站点还没有可测量的结果。',
      CostEscalation: '上报 — {{label}}',
      CostRepair: '维修 — {{label}}',
      CostRepairBoth: '维修 — {{wrong}}，之后又修 {{actual}}',
      SessionTally:
        '本次会话：{{shifts}} 个班次 · {{called}} 次判定中 {{correct}} 次正确 · ' +
        '停机 {{hours}} 小时 · {{scrapped}} 次超出规格。',

      Title: '交接班报告——{{tool}}',
      AsFoundInSpec: '按现状，这台设备在规格内——{{primary}}，{{secondary}}。把这批跑掉，是站得住脚的决定。',
      AsFoundOutOfSpec: '按现状，这台设备已超规格——{{primary}}，{{secondary}}。你跑出来的每一片都是报废。',
      AtTheTool: '设备上就有——免费',
      Investigate: '进一步查——要花设备时间',
      TakeIt: '去测',
      SpentSoFar:
        '到目前已花掉 {{hours}} 小时。这里的每一个小时，都是设备不产出晶圆的一个小时——' +
        '而这正是真实排班要做的取舍。',
      InvestigateNote:
        '这里每一项都会把设备、或一名技术员，从产线上拉走。只做那些会改变你判断的；' +
        '全都做一遍，本身也是一种决定。',
      CallIt: '下判断',
      CallItNote: '说出你要修的那一件事。在你提交之前答案都不会揭晓，而判断错了，停机代价要付两次。',
      Correct: '正确——{{label}}。',
      Wrong: '错了——你判的是 {{label}}。',
      RepairRight: '{{action}}。停机 {{hours}} 小时，设备修好回来。',
      RepairWrong:
        '{{action}} 花掉 {{hours}} 小时，设备回来还是坏的。接着 {{realAction}} 又要 ' +
        '{{realHours}} 小时。总计 {{total}} 小时。',
      TheRealRepair: '真正该做的修理',
      ThisShift: '这个班：排查 {{investigating}} 小时，修理 {{repairing}} 小时，' +
        '你犹豫期间报废了 {{scrapped}} 片晶圆。',
      WhatEachRepair: '每一种修理原本能挽回多少',
      WhatEachRepairNote:
        '每一行都是把那一项单独恢复到标称、其余一切保持现状，再把设备跑一遍的结果。' +
        '这是方法，不是排行榜。',
      Recovered: '挽回 {{pct}}%——{{primary}}，{{secondary}}',
      Decidable: '最好的修理比第二名高出 {{points}} 个百分点。这一次，光靠反事实推演就能定案。',
      TooClose:
        '最好的两种修理只差 {{points}} 个百分点，因为设备的这两部分互相影响。' +
        '这一次靠试修是定不了案的——只有靠一次测量。',
      StartShift: '开始一个班（随机故障）',
      RevealCause: '揭晓原因',
      HideAnswer: '隐藏答案',
      Instructor: '教师——挂载一个场景',
    },

    journal: {
      OrderComplete: '工单完成',
      ByStation: '各站点表现',
      ColTool: '设备',
      ColRuns: '次数',
      ColOutOfSpec: '超规格',
      ColDowntime: '停机',
      ColCalls: '判定',
      CallsRight: '{{calls}} 次中 {{correct}} 次正确',
      NotApplicable: '—',
      Unattributed: '（未记录设备）',

      Title: '# SimFab — 会话记录',
      NoActivity: '_没有记录到任何活动。_',
      ShiftStart: '班次开始',
      TookReading: '读取了',
      RanWafer: '跑了一片晶圆',
      InSpec: '合格',
      OutOfSpec: '**超出规格**',
      Changed: '修改了',
      Asked: '提问',
      Maintenance: '执行了维护',
      CalledIt: '判定',
      Correct: '正确',
      Incorrect: '错误',
      ReadingsBefore: '判定前采集的读数',
      WafersRun: '已跑晶圆数',
      QuestionsAsked: '提问次数',
      ActualCause: '真正的原因是',
      NoFault: '根本没有故障',
    },

    author: {
      Title: '设计一座晶圆厂',
      Subtitle: '二十五台设备任你挑。你留下什么不重要，你不要什么才是决定——产品工艺路线会告诉你代价。',
      Ask: '说说要改什么……',
      Send: '发送',
      Working: '处理中……',
      NewFloor: '新厂房',
      SavedFloors: '你建的厂',
      NoSaved: '还没建过。可以从一座现成的厂开始，或者直接说出你要的设备。',
      Build: '建起来，走进去',
      Rebuild: '重建并走进去',
      Standing: '你正站在这一座里',
      Save: '保存',
      Delete: '删除',
      DeleteActive: '你正站在这一座里。先去 Fab A 或 Fab B。',
      DeleteConfirm: '删除“{{name}}”？忘掉的是这张厂房平面图，设备不会丢——还是那二十五台。',
      Cancel: '留着',
      Undo: '撤销',
      SortShort: '流程顺序',
      allAlias: '所有设备|全部设备|所有的|全部|所有站|每一台',
      renameStop: '然后|并且|再|和',
      NotOnFloor: '不在这座厂里：{{tags}}',
      LineNotOnFloor: '这是参考产线——Fab A 设备上的完整产品工艺路线。你所在的这座厂没有 {{tags}}，所以这里的批次在这些工序上都得送出厂外。下面的数字属于设计中的产线，不属于这座厂。',
      NamePlaceholder: '给这座厂起个名',
      HallLabel: '厂房',
      OnTheFloor: '在厂房里，按晶圆经过的顺序',
      Palette: '不在这座厂里',
      AddTool: '放进厂房',
      RemoveTool: '搬出厂房',
      MoveEarlier: '在流程中往前',
      MoveLater: '在流程中往后',
      RequiredTool: '气闸是进门的路。每座厂都得有一个。',
      HallSize: '主通道 {{length}} m，{{bays}} 个工位区',
      Suggestions: '可以这样问',
      Local: '在本机上匹配',
      CoverageTitle: '对照产品工艺路线',
      CoverageBar: '{{steps}} 道工序中的 {{covered}} 道',
      MaskCount: '{{masks}} 道光罩工序中的 {{masksCovered}} 道',
      SegmentRuns: '能从头跑到尾',
      SegmentPart: '{{steps}} 道中的 {{covered}} 道',
      Empty: '这座厂里除了气闸什么都没有。',
      ListSeparator: '、',
      hall: {
        bayChase: '隔间式',
        ballroom: '大厅式',
      },
      hallNote: {
        bayChase: '设备正面嵌在墙里，主机在后面的技术夹道中',
        ballroom: '一整片开放厂房，每台主机都看得见',
      },
      hallAlias: {
        bayChase: '隔间|隔间式|有墙|隔墙|封闭工位|bay and chase',
        ballroom: '大厅|大厅式|开放厂房|开放式|没有墙|无墙|ballroom',
      },
      zone: {
        entry: '入口',
        substrate: '衬底',
        frontend: '前道',
        metrology: '量测',
        interconnect: '互连',
        sort: '晶圆测试分选',
        assembly: '封装与测试',
        quantum: '量子器件',
        support: '配套',
      },
      zoneAlias: {
        entry: '进厂|进门的地方',
        substrate: '晶圆供应|从硅锭到晶圆|衬底线',
        frontend: 'feol|器件层|晶体管|前段',
        metrology: '在线测量',
        interconnect: 'beol|后段|金属层|布线|大马士革',
        sort: '探针区|晶圆测试',
        assembly: '后道|封装线|osat|组装线',
        quantum: '量子比特|量子芯片|量子计算|量子产线',
        support: '基础设施|厂务',
      },
      segment: {
        substrate: '衬底模块',
        gate: '栅极模块',
        metal: '金属层循环',
        backEnd: '封装与测试',
      },
      archetype: {
        idm: {
          Name: '集成器件制造商（IDM）',
          Purpose: '沙子从一头进，分好档的芯片从另一头出。二十五台设备全在。',
        },
        foundry: {
          Name: '纯晶圆代工厂',
          Purpose: '买抛光片进来，交成品晶圆出去。没有单晶炉，也从不碰封装。',
        },
        substrate: {
          Name: '衬底供应商',
          Purpose: '晶圆厂开工用的那片晶圆是从这里来的。它本身根本不是晶圆厂。',
        },
        osat: {
          Name: '封装测试厂',
          Purpose: '进来成品晶圆，出去分好档的芯片。整个厂房里一台光刻机都没有。',
        },
        power: {
          Name: '功率分立器件线',
          Purpose: '厚外延、深注入、铝布线——所以没有大马士革循环，这是有意为之。',
        },
        teaching: {
          Name: '教学厂房',
          Purpose: '还能做出一层图形的最小厂房。九台设备，一个循环。',
        },
      },
      archetypeAlias: {
        idm: 'idm|集成器件|全都要|完整的厂|全套',
        foundry: '代工|代工厂|纯代工|foundry',
        substrate: '衬底供应商|晶圆供应商|硅锭厂|做晶圆的',
        osat: 'osat|封测厂|测试厂|封装厂',
        power: '功率线|分立器件|mosfet|igbt|功率器件',
        teaching: '教学|课堂|培训厂房|最小的厂|最简单的厂',
      },
      alias: {
        gowning: '更衣|洁净服|气闸|更衣室|洁净室入口',
        crystal: '单晶炉|直拉|硅锭|晶棒|长晶炉',
        waferprep: '晶圆制备|切片|研磨|抛光片|衬底制备|线锯',
        epi: '外延|外延炉|外延反应器|epi',
        clean: '湿法台|湿法清洗|rca 清洗|清洗台|预清洗',
        implant: '离子注入|注入机|掺杂|掺杂剂|注入',
        etch: '刻蚀|刻蚀机|干法刻蚀|icp|等离子刻蚀机',
        deposition: 'cvd|pecvd|ald|薄膜沉积|沉积设备|镀膜',
        litho: '光刻|光刻机|扫描机|步进机|曝光机|涂胶显影',
        patterning: '多重图形|多重曝光|saqp|侧墙|自对准|双重图形|四重图形|节距分割',
        thermal: '炉管|退火|快速热处理|rtp|氧化|扩散|热处理设备',
        metrology: '量测|椭偏仪|散射测量|在线量测',
        facilities: '厂务|厂务系统|公用工程盘|厂务面板|阀门盘',
        cmp: 'cmp|抛光机|平坦化|化学机械抛光|研磨抛光',
        plating: '电镀|电镀设备|ecd|铜填充|电化学沉积',
        inspection: '缺陷检查|缺陷检测|颗粒扫描|检查机',
        probe: '探针台|晶圆分选|探针卡|参数测试|电性分选',
        dicing: '划片|划片机|切割|减薄切割|划片锯|背面减薄',
        bonding: '装片|键合|引线键合|键合机|焊线',
        packaging: '塑封|封装|模压|底部填充|包封',
        finaltest: '成品测试|老化|烧机|分档|测试机|ate',
        gas: '大宗气体|气瓶柜|气体站|气体库|特气|氮气供应',
        euv: 'euv|euv光刻机|极紫外|极紫外光刻|锡光源',
        ebeam: 'cd-sem|扫描电镜|电子束|电子束检测|扫描电子显微镜',
        quantum: '量子器件测试|量子比特测试|稀释制冷机|低温恒温器|transmon|自旋量子比特',
      },
      action: {
        add: {
          Summary: '把一台设备，或者一整段流程，放进厂房',
          Example: '加一台 CMP 抛光机',
        },
        remove: {
          Summary: '把一台设备搬出厂房',
          Example: '把单晶炉搬走',
        },
        archetype: {
          Summary: '从一座现成的厂重新开始',
          Example: '给我建一座纯代工厂',
        },
        hall: {
          Summary: '带墙的隔间，还是一整片开放厂房',
          Example: '改成大厅式',
        },
        rename: {
          Summary: '给这座厂起个名字',
          Example: '叫它试产线',
        },
        sort: {
          Summary: '把通道恢复成晶圆经过设备的顺序',
          Example: '按流程顺序排',
        },
        clear: {
          Summary: '清空厂房，从头再来',
          Example: '清空厂房',
        },
        assess: {
          Summary: '这座厂能做什么，还缺什么？',
          Example: '我还缺什么？',
        },
        catalogue: {
          Summary: '有哪些设备可以选？',
          Example: '我能选哪些设备？',
        },
      },
      trigger: {
        add: '加一台|加上|加个|添加|放一台|放进|放个|装一台|安装|装上|包括|我要|我想要|我需要|给我',
        remove: '去掉|删掉|移除|搬走|拿走|撤掉|不要|卖掉',
        archetype: '从头按|从一座|基于|给我建|给我做|照着|模板|预设|按照|建一座|做一座|改成一座',
        hall: '大厅|隔间|开放厂房|有墙|隔墙|布局|厂房样式',
        rename: '叫它|叫做|改名|命名|起名',
        sort: '流程顺序|按顺序|重新排|重排|排序|工艺顺序',
        clear: '清空|全部清掉|从头再来|腾空|什么都不留',
        assess: '能做什么|能不能|这座厂能|还缺什么|缺什么|覆盖|完整度|评估|够不够|行不行',
        catalogue: '有哪些设备|哪些设备|有哪些站|哪些站|列出|有什么可选|我能选|我的选项',
      },
      renameLead: '叫它|叫做|改名为|命名为|给这座厂起名',
      reply: {
        Added: '现在厂里有了：{{tags}}。',
        Removed: '已搬出厂房：{{tags}}。',
        Refused: '{{tags}} 留下。那是进门的路。',
        AlreadyThere: '本来就在：{{tags}}。',
        NotThere: '这座厂里原本就没有：{{tags}}。',
        Hall: '厂房样式：{{hall}}。',
        Renamed: '起名为“{{name}}”。',
        Sorted: '通道现在按晶圆经过设备的顺序排列了。',
        Archetype: '从这里重新开始：{{name}}。',
        NothingToDo: '这个我会做——{{summary}}——但那句话没告诉我对什么做。',
        SameArchetype: '这座厂已经就是{{name}}了。',
        Unplaced: '我没法把那句话变成对厂房的改动。我能做的是：',
        Complete: '产品工艺路线的每一道工序在这座厂里都有设备可用——全部 {{steps}} 道，其中 {{masks}} 道要光罩。',
        Coverage: '产品工艺路线的 {{steps}} 道工序里，有 {{covered}} 道在这座厂里找得到设备——{{percent}}%。',
        CoverageNone: '产品工艺路线的 {{steps}} 道工序，这座厂一道也找不到设备。',
        SegmentsRun: '能从头跑到尾的是：{{segments}}。',
        SegmentsOpened: '这样就把 {{segments}} 补齐了，它现在能从头跑到尾。',
        SegmentsBroken: '这样就把 {{segments}} 打断了，它不再能从头跑到尾。',
        BiggestGap: '最大的缺口是 {{tag}}——{{process}}——有 {{count}} 道工序没有设备可跑。',
        BiggestGap_one: '最大的缺口是 {{tag}}——{{process}}——有 {{count}} 道工序没有设备可跑。',
        BiggestGap_other: '最大的缺口是 {{tag}}——{{process}}——有 {{count}} 道工序没有设备可跑。',
        CatalogueHead: '目录里有 {{n}} 台设备，这座厂里放了 {{chosen}} 台。打勾的表示已经放进去了。',
        CatalogueZone: '{{zone}}：{{tools}}',
        EmptyFloor: '这座厂里除了气闸什么都没有。说一座可以照着开始的厂，或者直接说出你要的设备。',
        Floor: '{{n}} 台设备：{{tools}}。',
        NoHouse: '这座厂没有 {{tags}}。这里每一台设备都挂在工艺真空、冷却水、大宗氮气和排气上——把它们留在外面，就是把真正会出故障的那半栋楼留在外面。',
        Unused: '产品工艺路线从来不经过 {{tags}}。这不是批评：它们在这里，是为了这项衡量衡量不到的东西。',
      },
    },

  },
}
